<template>
<div class="pos-wrapper">
  <div class="pos-background"></div>
  <div class="pos-container">
    <b-overlay :show="loading" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm" style="margin-top: 10px;">
      <div class="pos-main-container" :class="{ 'is-editing': invoice.edit }">

        <!-- ===================================================== -->
        <!-- UTILITY MODALS                                        -->
        <!-- ===================================================== -->
        <b-modal id="modal-deliveries" scrollable title="Scheduled deliveries" size="lg" hide-footer content-class="pos-modal" dialog-class="pos-dialog">
          <Deliveries :viewdata="scheduled_deliveries" />
        </b-modal>

        <b-modal id="modal-image-viewer" size="lg" centered title="Image" content-class="pos-modal" dialog-class="pos-dialog">
          <div class="text-center">
            <b-img :src="currentFullSizeImage" fluid alt="Full size image" style="max-height: 70vh; object-fit: contain;" />
            <div class="mt-3"><strong>{{ currentProductName }}</strong></div>
          </div>
          <template #modal-footer>
            <b-button size="sm" class="pos-btn-ghost" @click="$bvModal.hide('modal-image-viewer')">Close</b-button>
          </template>
        </b-modal>

        <b-modal id="modal-delivery-calendar" size="xl" title="Delivery calendar" hide-footer scrollable centered
                 content-class="pos-modal" dialog-class="pos-dialog" @shown="onDeliveryCalendarShown">
          <DeliveryCalendar ref="deliveryCalendar"
                            :selected-location-id="getSelectedLocationId()"
                            :location-name="getSelectedLocationName()" />
        </b-modal>

        <b-modal id="modal-barcode-scanner" title="Scan barcode" size="lg" hide-footer content-class="pos-modal" dialog-class="pos-dialog"
                 @shown="startScanner" @hidden="stopScanner">
          <div class="scanner-container">
            <QuaggaScanner ref="quaggaScanner" @scan="handleScannedBarcode" :facingMode="currentCamera" />
            <div class="mt-3 text-center">
              <b-button size="sm" class="pos-btn-ghost" @click="toggleCamera">Switch camera</b-button>
              <b-button size="sm" variant="outline-danger" class="ml-2" @click="stopScanner">Cancel</b-button>
            </div>
          </div>
        </b-modal>

        <b-modal id="modal-signature" size="lg" hide-footer centered title="Customer signature"
                 content-class="pos-modal" dialog-class="pos-dialog" @hidden="resetSignatureModal">
          <div class="signature-section">
            <p class="pos-modal-lead mb-2">Have the customer sign inside the box.</p>
            <SignaturePad ref="signaturePad" :width="signaturePadWidth" :height="signaturePadHeight" :disabled="signatureUploading" />
            <div class="mt-3 d-flex justify-content-between">
              <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('modal-signature')" :disabled="signatureUploading">Cancel</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="saveSignature" :disabled="signatureUploading">
                <b-spinner small v-if="signatureUploading"></b-spinner>
                {{ signatureUploading ? 'Saving signature...' : 'Save signature' }}
              </b-button>
            </div>
          </div>
        </b-modal>

        <b-modal id="modal-scanner" scrollable title="Scan item" size="lg" hide-footer content-class="pos-modal" dialog-class="pos-dialog">
          <SimpleScanner @scanCompleted="fetchScannedItem" />
        </b-modal>

        <!-- ===================================================== -->
        <!-- REFUND OTP (2FA)                                      -->
        <!-- ===================================================== -->
        <b-modal id="pos-modal-refund-otp"
                 title="Verify refund with customer"
                 size="md" centered
                 content-class="pos-modal" dialog-class="pos-dialog"
                 no-close-on-backdrop no-close-on-esc
                 @hidden="resetRefundOtp">
          <p class="pos-modal-lead">
            A 6-digit verification code has been sent to the customer's email.
            Ask the customer for the code and enter it below to approve the refund.
          </p>

          <div class="pos-panel">
            <div class="refund-otp-email-row">
              <b-icon icon="envelope" class="refund-otp-icon"></b-icon>
              <div>
                <div class="refund-otp-label">Code sent to</div>
                <div class="refund-otp-email">{{ refundOtp.maskedEmail || '—' }}</div>
              </div>
              <b-button size="sm" class="pos-btn-ghost ml-auto"
                        :disabled="refundOtp.sending || refundOtp.cooldown > 0"
                        @click="resendRefundOtp">
                <b-spinner small v-if="refundOtp.sending"></b-spinner>
                <span v-if="refundOtp.cooldown > 0">Resend in {{ refundOtp.cooldown }}s</span>
                <span v-else>Resend code</span>
              </b-button>
            </div>

            <b-form-group label="6-digit code" label-class="pos-label" class="mt-3">
              <b-input v-model="refundOtp.code"
                       class="refund-otp-input"
                       maxlength="6"
                       inputmode="numeric"
                       placeholder="••••••"
                       @keyup.enter="verifyRefundOtp" />
            </b-form-group>

            <b-alert v-if="refundOtp.error" variant="danger" show class="mb-0">
              {{ refundOtp.error }}
            </b-alert>

            <small class="text-muted d-block mt-2">
              Refunding <strong>{{ money(refundOtp.amount) }}</strong> for invoice
              <strong>{{ invoice.invoice_id }}</strong>.
            </small>
          </div>

          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="cancelRefundOtp">
                Cancel refund
              </b-button>
              <b-button class="pos-btn-primary"
                        :disabled="!refundOtp.code || refundOtp.code.length !== 6 || refundOtp.verifying"
                        @click="verifyRefundOtp">
                <b-spinner small v-if="refundOtp.verifying"></b-spinner>
                <b-icon v-else icon="shield-check"></b-icon>
                Verify &amp; refund
              </b-button>
            </div>
          </template>
        </b-modal>

        <!-- ===================================================== -->
        <!-- PAGE HEADER                                            -->
        <!-- ===================================================== -->
        <header class="pos-head">
          <div class="pos-head-bar">
            <div class="pos-brand-mark">
              <span class="pos-brand-logo">FM</span>
              <div class="pos-brand-text">
                <div class="pos-brand-name">Furniture Mecca</div>
                <div class="pos-brand-sub">myfurnituremecca.com &nbsp;|&nbsp; {{ formatPhone('2158771200') }}</div>
              </div>
            </div>

            <div class="pos-head-meta">
              <div class="pos-meta-block">
                <span class="pos-meta-icon"><b-icon icon="receipt"></b-icon></span>
                <div class="pos-meta-text">
                  <div class="pos-brand-label">{{ invoice.edit ? 'Editing invoice' : 'Invoice' }}</div>
                  <div class="pos-brand-invoice">{{ invoice.invoice_id || 'Not started' }}</div>
                </div>
              </div>
              <div class="pos-meta-block pos-meta-block--wide">
                <span class="pos-meta-icon"><b-icon icon="geo-alt-fill"></b-icon></span>
                <div class="pos-meta-text">
                  <div class="pos-brand-label">Store location</div>
                  <div class="pos-brand-strong">{{ locationAddressDisplay }}</div>
                  <div class="pos-brand-sub"><b-icon icon="telephone"></b-icon> {{ locationPhoneDisplay }}</div>
                </div>
              </div>
              <div class="pos-meta-block">
                <span class="pos-meta-icon"><b-icon icon="person-badge"></b-icon></span>
                <div class="pos-meta-text">
                  <div class="pos-brand-label">Salesperson</div>
                  <div class="pos-brand-strong">{{ salespersonName || '—' }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="pos-progress-wrap">
            <div class="pos-progress">
              <div class="pos-progress-fill"
                   :style="{ width: (posTabs.length ? Math.round(posTabs.filter(t => t.done).length / posTabs.length * 100) : 0) + '%' }"></div>
            </div>
            <span class="pos-progress-label">{{ posTabs.filter(t => t.done).length }} of {{ posTabs.length }} sections done</span>
          </div>

          <div class="pos-head-actions">
            <b-button class="pos-btn-ghost" @click="openInvoicePreview">
              <b-icon icon="file-earmark-text"></b-icon> View invoice
            </b-button>
            <template v-if="!invoice.edit">
              <b-button class="pos-btn-ghost" @click="saveDraftInvoice" :disabled="loading || !isPhoneNumberValid || !storeReady">
                <b-icon icon="bookmark"></b-icon> Save draft
              </b-button>
              <b-button class="pos-btn-ghost" @click="sendDraftToCustomer" :disabled="loadingDraftSend || !isDraftSendEnabled">
                <b-spinner small v-if="loadingDraftSend"></b-spinner>
                <b-icon v-else icon="envelope"></b-icon> Email estimate
              </b-button>
              <b-button class="pos-btn-primary" @click="submitInvoice" :disabled="!isSubmitEnabled">
                <b-icon icon="check2-circle"></b-icon> Submit sale
              </b-button>
            </template>
            <!--
              ✅ CHANGED — Update button no longer disabled by hasMerchandiseDue.
              Reselect is allowed while merchandise is pending; new items land on
              the EX invoice with state=15 (PENDING) until the receipt completes.
            -->
            <b-button v-else class="pos-btn-primary" @click="handleUpdate">
              {{ hasMerchandiseDue ? 'Update sale (merchandise pending)' : 'Update sale' }}
            </b-button>
            <div v-if="submitBlockers.length" class="pos-blockers">
              <b-icon icon="info-circle"></b-icon> Still needed: {{ submitBlockers.join(', ') }}
            </div>
          </div>
        </header>

        <!-- ===================================================== -->
        <!-- HORIZONTAL TABS                                        -->
        <!-- ===================================================== -->
        <nav class="pos-tabs" role="tablist" aria-label="Sale sections">
          <button v-for="(tab, i) in posTabs" :key="tab.key" type="button" role="tab"
                  class="pos-tab"
                  :class="{ 'is-done': tab.done, 'is-next': tab.key === nextTabKey, 'is-blocked': tab.key === 'merchandisedue' && hasMerchandiseDue }"
                  :aria-label="tab.label + (tab.done ? ' (complete)' : '')"
                  @click="openPosTab(tab.key)">
            <span class="pos-tab-index">
              <b-icon v-if="tab.done" icon="check2"></b-icon>
              <b-icon v-else-if="tab.key === 'merchandisedue'" icon="hourglass-split"></b-icon>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="pos-tab-label">{{ tab.label }}</span>
            <span v-if="tab.count" class="pos-tab-count">{{ tab.count }}</span>
          </button>
        </nav>

        <div v-if="invoice.edit && hasMerchandiseDue" class="pos-merch-due-banner">
          <span class="pos-banner-icon"><b-icon icon="hourglass-split"></b-icon></span>
          <span>
            <strong>{{ merchandiseDueUnits }} unit(s)</strong> from returned merchandise are still awaiting physical receipt.
            Refunds are blocked. Reselect is allowed — new items will be marked <strong>PENDING</strong> until the return is received.
          </span>
          <b-button size="sm" class="pos-btn-primary" @click="openPosTab('merchandisedue')">Open Merchandise due</b-button>
        </div>

        <div v-if="!storeReady" class="pos-store-warning">
          <span class="pos-banner-icon"><b-icon icon="exclamation-triangle-fill"></b-icon></span>
          <span>No store selected yet. Click the <strong>Store</strong> tab to choose a location before submitting.</span>
          <b-button size="sm" class="pos-btn-primary" @click="openPosTab('store')">Set store</b-button>
        </div>

        <!-- ===================================================== -->
        <!-- INVOICE SHEET                                          -->
        <!-- ===================================================== -->
        <section class="pos-sheet">

          <!-- CUSTOMER -->
          <article class="pos-tile pos-tile--wide" :class="{ 'is-empty': !customerReady }">
            <div class="pos-tile-head">
              <div class="pos-tile-title">
                <span class="pos-tile-icon"><b-icon icon="person"></b-icon></span>
                <div>
                  <h3>Customer</h3>
                  <p class="pos-tile-sub">Contact and delivery details for this sale</p>
                </div>
              </div>
              <b-button variant="link" class="pos-link" @click="openPosTab('customer')">
                {{ customerReady ? 'Edit' : 'Add customer' }}
              </b-button>
            </div>

            <div v-if="invoice.customer && invoice.customer.fullname" class="pos-customer pos-customer--full">
              <div class="pos-customer-row pos-customer-row--identity">
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="person"></b-icon> First name</span>
                  <span class="pos-customer-value">{{ customerFirstName || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="person"></b-icon> Last name</span>
                  <span class="pos-customer-value">{{ customerLastName || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="envelope"></b-icon> Email</span>
                  <span class="pos-customer-value">{{ invoice.customer.email || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="telephone"></b-icon> Phone</span>
                  <span class="pos-customer-value">{{ invoice.customer.phone || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="telephone-plus"></b-icon> Phone 2</span>
                  <span class="pos-customer-value">{{ invoice.customer.phone_2 || '—' }}</span>
                </div>
              </div>

              <div class="pos-customer-row pos-customer-row--address">
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="geo-alt"></b-icon> Address 1</span>
                  <span class="pos-customer-value">{{ invoice.customer.address_line_1 || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="geo-alt"></b-icon> Address 2</span>
                  <span class="pos-customer-value">{{ invoice.customer.address_line_2 || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="building"></b-icon> City</span>
                  <span class="pos-customer-value">{{ invoice.customer.city || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="map"></b-icon> State</span>
                  <span class="pos-customer-value">{{ invoice.customer.state || '—' }}</span>
                </div>
                <div class="pos-customer-field">
                  <span class="pos-customer-label"><b-icon icon="mailbox"></b-icon> Zip</span>
                  <span class="pos-customer-value">
                    {{ invoice.customer.zipcode || '—' }}
                    <b-badge v-if="blockedZone" variant="info" class="ml-2" pill>
                      {{ blockedZone.zone_code }}
                    </b-badge>
                  </span>
                </div>
              </div>

              <div class="pos-customer-policy"
                   :class="invoice.address_confirmation_agreed ? 'is-ok' : 'is-warn'">
                <b-icon :icon="invoice.address_confirmation_agreed ? 'check-circle-fill' : 'exclamation-circle-fill'"></b-icon>
                <span>
                  {{ invoice.address_confirmation_agreed
                    ? 'Customer confirmed that the shipping address and phone number are correct and acknowledged the $200 redelivery fee for incorrect information.'
                    : 'Address and phone number not yet confirmed. Customer must acknowledge the $200 redelivery fee for incorrect information.' }}
                </span>
              </div>

              <div class="pos-customer-row pos-customer-row--chips">
                <span v-if="invoice.customer.floor" class="pos-chip"><b-icon icon="layers"></b-icon> {{ invoice.customer.floor }}</span>
              </div>
            </div>

            <p v-else class="pos-empty">Add the customer's name, phone, email and delivery address.</p>
          </article>

          <!-- PRODUCTS -->
          <article class="pos-tile pos-tile--wide" :class="{ 'is-empty': !visibleItems.length }">
            <div class="pos-tile-head">
              <div class="pos-tile-title">
                <span class="pos-tile-icon"><b-icon icon="cart3"></b-icon></span>
                <div>
                  <h3>
                    Products
                    <small v-if="visibleItems.length" class="pos-muted-inline">{{ visibleItems.length }} in cart</small>
                  </h3>
                  <p class="pos-tile-sub">Items, delivery and pricing on this sale</p>
                </div>
              </div>
              <b-button variant="link" class="pos-link" @click="openPosTab('products')">
                {{ visibleItems.length ? 'Edit cart' : 'Add products' }}
              </b-button>
            </div>

            <div v-if="visibleItems.length" class="pos-product-cards">
              <div
                v-for="(item, idx) in visibleItems"
                :key="'sum-' + idx"
                class="pos-product-card"
                :class="{ 'is-group': item.is_group_item || (item.children && item.children.length) }"
              >
                <div class="pos-product-card-main">
                  <div class="pos-product-card-image">
                    <img
                      v-if="getProductImage(item)"
                      :src="getProductImage(item)"
                      :alt="item.product_name || item.product_sku"
                    />
                    <b-icon v-else icon="image"></b-icon>
                  </div>

                  <div class="pos-product-card-info">
                    <div class="pos-product-card-title">{{ item.product_name || item.product_sku }}</div>
                    <div class="pos-product-card-sku">SKU: {{ item.product_sku }}</div>
                    <div class="pos-product-card-sku">SOURCE: {{ item.sourceContext }}</div>

                    <div v-if="shouldShowBackorder(item)" class="pos-product-card-backorder">
                      <b-icon icon="exclamation-triangle-fill"></b-icon> BACKORDER
                    </div>
                    <div v-else class="pos-product-card-stock">
                      <b-icon icon="check-circle-fill"></b-icon> In stock
                    </div>

                    <div class="pos-product-card-tags">
                      <span v-if="item.is_group_item || (item.children && item.children.length)" class="pos-chip">
                        Group · {{ (item.children || []).length }} pcs
                      </span>
                      <span v-if="item.is_damaged" class="pos-chip is-warn">As is</span>
                      <span v-if="item.existing" class="pos-chip">On invoice</span>
                      <span v-if="refund_items.some(r => r.itemid === item.itemid)" class="pos-chip is-warn">Returning</span>
                    </div>
                  </div>

                  <div class="pos-product-card-meta">
                    <div class="pos-product-card-meta-row">
                      <span class="pos-product-card-meta-label">
                        <b-icon icon="calendar-event"></b-icon> Delivery date
                      </span>
                      <span class="pos-product-card-meta-value">
                        {{ item.order_type === 'customer_takes' ? today_date : (item.delivery_date || '—') }}
                      </span>
                    </div>
                    <div class="pos-product-card-meta-row">
                      <span class="pos-product-card-meta-label">
                        <b-icon icon="truck"></b-icon> Order type
                      </span>
                      <span class="pos-product-card-meta-value">{{ orderTypeLabel(item.order_type) }}</span>
                    </div>
                    <div class="pos-product-card-meta-row">
                      <span class="pos-product-card-meta-label">
                        <b-icon icon="geo-alt"></b-icon> Delivery from
                      </span>
                      <span class="pos-product-card-meta-value2">
                        {{ getLocationName(item.drop_locsub) || '—' }}
                      </span>
                    </div>
                  </div>

                  <div class="pos-product-card-pricing">
                    <div class="pos-product-card-price-row">
                      <span class="pos-product-card-price-label">Quantity</span>
                      <span class="pos-product-card-price-value">{{ itemQty(item) }}</span>
                    </div>

                    <!-- Regular / MSRP -->
                    <div class="pos-product-card-price-row">
                      <span class="pos-product-card-price-label">Regular price</span>
                      <span class="pos-product-card-price-value pos-product-card-price-value--strike">
                        {{ money(getRegularPrice(item)) }}
                      </span>
                    </div>

                    <!-- Selling price (always) -->
                    <div class="pos-product-card-price-row">
                      <span class="pos-product-card-price-label">Selling price</span>
                      <span class="pos-product-card-price-value">
                        {{ money(getSellingPrice(item)) }}
                      </span>
                    </div>

                    <!-- Special / discounted price -->
                    <div
                      v-if="getDiscountedPrice(item) !== null"
                      class="pos-product-card-price-row pos-product-card-price-row--save"
                    >
                      <span class="pos-product-card-price-label">
                        <b-icon icon="tag-fill"></b-icon> {{ getSaleTypeLabel(item) }}
                      </span>
                      <span class="pos-product-card-price-value">
                        {{ money(getDiscountedPrice(item)) }}
                      </span>
                    </div>

                    <!-- Manual discount -->
                    <div
                      class="pos-product-card-price-row"
                      v-if="parseFloat(item.discount_offered) > 0"
                    >
                      <span class="pos-product-card-price-label">Manual discount</span>
                      <span class="pos-product-card-price-value">-{{ money(item.discount_offered) }}</span>
                    </div>

                    <!-- You save -->
                    <div
                      class="pos-product-card-price-row pos-product-card-price-row--save"
                      v-if="getItemSavings(item) > 0"
                    >
                      <span class="pos-product-card-price-label">
                        <b-icon icon="tag-fill"></b-icon> You save
                      </span>
                      <span class="pos-product-card-price-value">{{ money(getItemSavings(item)) }}</span>
                    </div>

                    <div class="pos-product-card-price-row pos-product-card-price-row--total">
                      <span class="pos-product-card-price-label">Total × {{ itemQty(item) }}</span>
                      <span class="pos-product-card-price-value">{{ money(lineTotal(item)) }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="item.children && item.children.length" class="pos-product-children-wrap">
                  <div class="pos-product-children-head">
                    <b-icon icon="diagram-3"></b-icon>
                    Included in this set
                    <span class="pos-muted-inline">{{ item.children.length }} pcs</span>
                  </div>

                  <div class="pos-child-list">
                    <div
                      v-for="(child, cidx) in item.children"
                      :key="'child-' + (child.product_id || child.product_sku) + '-' + cidx"
                      class="pos-child-card"
                    >
                      <div class="pos-child-card-image" @click="getProductImage(child) && showFullImage(child)">
                        <img
                          v-if="getProductImage(child) && !child._imgError"
                          :src="getProductImage(child)"
                          :alt="child.product_name || child.product_sku"
                          @error="$set(child, '_imgError', true)"
                        />
                        <b-icon v-else icon="image"></b-icon>
                      </div>

                      <div class="pos-child-card-info">
                        <div class="pos-child-card-title">
                          {{ child.product_name || child.description || child.product_sku || '—' }}
                        </div>
                        <div class="pos-child-card-line">SKU: <strong>{{ child.product_sku || '—' }}</strong></div>
                        <div v-if="child.quantity_per_box > 1" class="pos-child-card-line">
                          QTY/BOX: <strong>{{ child.quantity_per_box }}</strong>
                        </div>
                        <div class="pos-product-card-tags">
                          <span class="pos-chip" :class="(child.status || '').toLowerCase() === 'active' ? 'is-ok' : 'is-warn'">
                            {{ child.status || 'No status' }}
                          </span>
                          <span class="pos-chip" :class="{ 'is-warn': childAvailable(child) !== null && childAvailable(child) < 5 }">
                            Available: {{ childAvailable(child) !== null ? childAvailable(child) : '—' }}
                          </span>
                        </div>
                      </div>

                      <div class="pos-child-card-meta">
                        <div class="pos-product-card-meta-row">
                          <span class="pos-product-card-meta-label"><b-icon icon="calendar-event"></b-icon> Delivery date</span>
                          <span class="pos-child-card-value">
                            {{ item.order_type === 'customer_takes' ? today_date : (item.delivery_date || '—') }}
                          </span>
                        </div>
                        <div class="pos-product-card-meta-row">
                          <span class="pos-product-card-meta-label"><b-icon icon="truck"></b-icon> Order type</span>
                          <span class="pos-child-card-value">{{ orderTypeLabel(item.order_type) }}</span>
                        </div>
                      </div>

                      <div class="pos-child-card-pricing">
                        <div class="pos-product-card-price-row">
                          <span class="pos-product-card-price-label">Quantity</span>
                          <span class="pos-product-card-price-value">{{ childQty(child) }}</span>
                        </div>
                        <div class="pos-product-card-price-row">
                          <span class="pos-product-card-price-label">Unit price</span>
                          <span class="pos-product-card-price-value"
                                :class="{ 'pos-product-card-price-value--strike': getChildDiscount(child) > 0 }">
                            {{ money(childUnitPrice(child)) }}
                          </span>
                        </div>
                        <div v-if="getChildDiscount(child) > 0"
                             class="pos-product-card-price-row pos-product-card-price-row--save">
                          <span class="pos-product-card-price-label"><b-icon icon="tag-fill"></b-icon> Discount</span>
                          <span class="pos-product-card-price-value">-{{ money(getChildDiscount(child)) }}</span>
                        </div>
                        <div class="pos-product-card-price-row pos-child-card-price-total">
                          <span class="pos-product-card-price-label">Price</span>
                          <span class="pos-product-card-price-value">{{ money(childSellPrice(child)) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p v-else class="pos-empty">Search, scan, or add a special order item to start the cart.</p>
          </article>

          <div class="pos-sheet-row">
            <!-- Extra Charges -->
            <article class="pos-tile" :class="{ 'is-empty': !invoice.items.length }">
              <div class="pos-tile-head">
                <div class="pos-tile-title">
                  <span class="pos-tile-icon"><b-icon icon="truck"></b-icon></span>
                  <h3>
                    <template v-if="invoice.edit && hasNewItems">New extra charges</template>
                    <template v-else-if="invoice.edit">Invoice extra charges</template>
                    <template v-else>Extra charges</template>
                  </h3>
                </div>
                <b-button variant="link" class="pos-link" @click="openPosTab('charges')">Edit</b-button>
              </div>

              <dl v-if="!invoice.edit || !hasNewItems" class="pos-dl">
                <dt>Delivery</dt><dd>{{ money(invoice.delivery_charges) }}</dd>
                <dt>White Glove</dt><dd>{{ money(invoice.whiteglove_charges) }}</dd>
                <dt>Setup</dt><dd>{{ money(invoice.setup_charges) }}</dd>
                <dt>Warranty</dt><dd>{{ money(invoice.warranty_charges) }}</dd>
                <dt class="pos-dl-strong">Total charges</dt>
                <dd class="pos-dl-strong">{{ money(invoice.edit ? getOriginalChargesTotal() : getChargesTotal()) }}</dd>
              </dl>

              <dl v-else class="pos-dl">
                <dt>Delivery</dt><dd>{{ money(invoice.new_delivery_charges) }}</dd>
                <dt>White Glove</dt><dd>{{ money(invoice.new_whiteglove_charges) }}</dd>
                <dt>Setup</dt><dd>{{ money(invoice.new_setup_charges) }}</dd>
                <dt>Warranty</dt><dd>{{ money(invoice.new_warranty_charges) }}</dd>
                <dt class="pos-dl-strong">Total charges</dt>
                <dd class="pos-dl-strong">{{ money(getChargesTotal()) }}</dd>
              </dl>

              <div class="mt-2" v-if="invoice.is_layaway"><span class="pos-chip is-warn">Layaway</span></div>
            </article>

            <!-- Total Charges -->
            <article class="pos-tile pos-tile--summary" :class="{ 'is-empty': !invoice.items.length }">
              <div class="pos-tile-head">
                <div class="pos-tile-title">
                  <span class="pos-tile-icon"><b-icon icon="receipt"></b-icon></span>
                  <h3>
                    <template v-if="invoice.edit && hasNewItems">New items charges</template>
                    <template v-else-if="invoice.edit">Invoice charges</template>
                    <template v-else>Order summary</template>
                  </h3>
                </div>
                <b-button variant="link" class="pos-link" @click="openPosTab('charges')">Edit</b-button>
              </div>

              <dl v-if="!invoice.edit" class="pos-dl">
                <dt>Order total</dt><dd>{{ money(getOrderTotal()) }}</dd>
                <dt>Other charges</dt><dd>{{ money(getChargesTotal()) }}</dd>
                <dt v-if="getTotalDiscount() > 0">Discounts</dt>
                <dd v-if="getTotalDiscount() > 0" class="is-good">-{{ money(getTotalDiscount()) }}</dd>
                <dt class="pos-dl-sub">Subtotal</dt><dd class="pos-dl-sub">{{ money(getSubTotal()) }}</dd>
                <dt class="pos-dl-sub">Taxable subtotal</dt><dd class="pos-dl-sub">{{ money(adjust_to_taxable_amount(getSubTotal())) }}</dd>
                <dt>Sales tax</dt><dd>{{ money(getSalesTax()) }}</dd>
                <dt class="pos-dl-strong pos-dl-grand">Grand total</dt><dd class="pos-dl-strong pos-dl-grand">{{ money(getGrandTotal()) }}</dd>
              </dl>

              <dl v-else-if="!hasNewItems" class="pos-dl">
                <dt>Order total</dt><dd>{{ money(getExistingOrderTotal()) }}</dd>
                <dt>Other charges</dt><dd>{{ money(getOriginalChargesTotal()) }}</dd>
                <dt v-if="getExistingDiscount() > 0">Discounts</dt>
                <dd v-if="getExistingDiscount() > 0" class="is-good">-{{ money(getExistingDiscount()) }}</dd>
                <dt class="pos-dl-sub">Subtotal</dt><dd class="pos-dl-sub">{{ money(getExistingSubTotal()) }}</dd>
                <dt class="pos-dl-sub">Taxable subtotal</dt><dd class="pos-dl-sub">{{ money(adjust_to_taxable_amount(getExistingSubTotal())) }}</dd>
                <dt>Sales tax</dt><dd>{{ money(getExistingSalesTax()) }}</dd>
                <dt class="pos-dl-strong pos-dl-grand">Grand total</dt><dd class="pos-dl-strong pos-dl-grand">{{ money(getExistingGrandTotal()) }}</dd>
              </dl>

              <dl v-else class="pos-dl">
                <dt>Order total</dt><dd>{{ money(getOrderTotal()) }}</dd>
                <dt>Other charges</dt><dd>{{ money(getChargesTotal()) }}</dd>
                <dt v-if="getTotalDiscount() > 0">Discounts</dt>
                <dd v-if="getTotalDiscount() > 0" class="is-good">-{{ money(getTotalDiscount()) }}</dd>
                <dt class="pos-dl-sub">Subtotal</dt><dd class="pos-dl-sub">{{ money(getSubTotal()) }}</dd>
                <dt class="pos-dl-sub">Taxable subtotal</dt><dd class="pos-dl-sub">{{ money(adjust_to_taxable_amount(getSubTotal())) }}</dd>
                <dt>Sales tax</dt><dd>{{ money(getSalesTax()) }}</dd>
                <dt v-if="getStoreCreditApplied() > 0">Store credit applied</dt>
                <dd v-if="getStoreCreditApplied() > 0" class="is-good">-{{ money(getStoreCreditApplied()) }}</dd>
                <dt class="pos-dl-strong pos-dl-grand">Grand total</dt><dd class="pos-dl-strong pos-dl-grand">{{ money(getGrandTotal()) }}</dd>
              </dl>

              <dl class="pos-dl pos-dl-savings mt-3" v-if="!invoice.edit && getCartSavings() > 0">
                <dt class="is-good"><b-icon icon="tag-fill"></b-icon> Total savings</dt>
                <dd class="is-good">{{ money(getCartSavings()) }}</dd>
              </dl>
              <div class="mt-2" v-if="invoice.is_layaway"><span class="pos-chip is-warn">Layaway</span></div>
            </article>

            <!-- PAYMENT -->
            <article class="pos-tile" :class="{ 'is-empty': !activePaymentMethods.length }">
              <div class="pos-tile-head">
                <div class="pos-tile-title">
                  <span class="pos-tile-icon"><b-icon icon="credit-card"></b-icon></span>
                  <h3>Payment</h3>
                </div>
                <b-button variant="link" class="pos-link" @click="openPosTab('payment')">
                  {{ activePaymentMethods.length ? 'Edit' : 'Add payment' }}
                </b-button>
              </div>
              <dl class="pos-dl" v-if="activePaymentMethods.length || (invoice.edit && payment_history.length)">
                <template v-if="invoice.edit && payment_history.length">
                  <dt>Paid before</dt><dd>{{ money(getTotalPaymentsFromHistory()) }}</dd>
                </template>
                <template v-for="(pm, i) in activePaymentMethods">
                  <dt :key="'pmdt' + i">{{ paymentMethodLabel(pm.value) }}<span v-if="pm.value === 'cc' && pm.name"> ****{{ pm.name }}</span><span v-if="pm.value === 'finance' && pm.name"> ({{ pm.name }})</span></dt>
                  <dd :key="'pmdd' + i">{{ money(pm.amount) }}</dd>
                </template>
                <dt>Store credit</dt>
                <dd v-if="getAvailableStoreCredit() > 0.005">{{ money(getAvailableStoreCredit()) }}</dd>
                <dt class="pos-dl-strong">Balance</dt>
                <dd class="pos-dl-strong" :class="{ 'is-due': currentBalance > 0.009 }">{{ money(currentBalance) }}</dd>
              </dl>
              <template v-else>
                <button type="button" class="pos-tile-cta" @click="openPosTab('payment')">
                  <b-icon icon="plus"></b-icon> Add payment
                </button>
                <div class="pos-balance-line">
                  <span>Balance due</span>
                  <strong :class="{ 'is-due': currentBalance > 0.009 }">{{ money(currentBalance) }}</strong>
                </div>
              </template>
            </article>
          </div>

          <!-- MERCHANDISE DUE SUMMARY -->
          <article v-if="invoice.edit && merchandiseDueItems.length" class="pos-tile pos-tile--wide">
            <div class="pos-tile-head">
              <div class="pos-tile-title">
                <span class="pos-tile-icon pos-tile-icon--warn"><b-icon icon="hourglass-split"></b-icon></span>
                <h3>
                  Merchandise due
                  <small v-if="hasMerchandiseDue" class="pos-muted-inline">{{ merchandiseDueUnits }} unit(s) pending</small>
                  <small v-else class="pos-muted-inline">All received</small>
                </h3>
              </div>
              <b-button variant="link" class="pos-link" @click="openPosTab('merchandisedue')">
                {{ hasMerchandiseDue ? 'Mark received' : 'View' }}
              </b-button>
            </div>

            <div class="pos-history-grid">
              <div v-for="(row, idx) in merchandiseDueItems" :key="'mdsum-' + idx"
                   class="pos-history-row"
                   :class="row.state === 'received' ? 'is-cash' : 'is-finance'">
                <div class="pos-history-top">
                  <span class="pos-history-method">
                    {{ row.state === 'received' ? 'RECEIVED' : 'PENDING' }}
                  </span>
                  <strong class="pos-history-amount">{{ row.received_quantity }}/{{ row.return_quantity }}</strong>
                </div>
                <div class="pos-history-meta">
                  <span>{{ row.product_sku }}</span>
                </div>
                <div class="pos-history-date">
                  {{ (row.product_name || '').substring(0, 40) }}
                </div>
              </div>
            </div>
          </article>

          <!-- PAYMENT HISTORY -->
          <article v-if="invoice.edit" class="pos-tile pos-tile--wide">
            <div class="pos-tile-head">
              <div class="pos-tile-title">
                <span class="pos-tile-icon"><b-icon icon="clock-history"></b-icon></span>
                <h3>Payment history</h3>
              </div>
              <b-button variant="link" class="pos-link" @click="openPosTab('paymenthistory')">
                View all
              </b-button>
            </div>

            <div v-if="payment_history.length" class="pos-history-grid">
              <div
                v-for="(pm, pmidx) in payment_history"
                :key="pmidx"
                class="pos-history-row"
                :class="'is-' + (pm.value || 'default')"
              >
                <div class="pos-history-top">
                  <span class="pos-history-method">{{ paymentMethodLabel(pm.value) }}</span>
                  <strong class="pos-history-amount">{{ money(pm.amount) }}</strong>
                </div>

                <div class="pos-history-meta">
                  <span v-if="pm.value == 'cc' && pm.name">•••• {{ pm.name }}</span>
                  <span v-if="pm.value == 'store_credit' && pm.store_credit_invoice_id">Inv {{ pm.store_credit_invoice_id }}</span>
                  <span v-if="pm.value == 'finance' && pm.name">{{ pm.name }}</span>
                  <span v-if="pm.value == 'finance' && pm.lease_id">Lease {{ pm.lease_id }}</span>
                  <span v-if="pm.value == 'tax_exempt' && pm.name">{{ pm.name }}</span>
                  <span v-if="pm.value == 'tax_exempt' && pm.ein">EIN {{ pm.ein }}</span>
                  <span v-if="pm.layaway_sls_tax">Sales tax payment</span>
                </div>

                <div class="pos-history-date">{{ pm.ts ? payment_ts_format(pm.ts) : 'Pending' }}</div>
              </div>
            </div>

            <p v-else class="pos-empty">
              No payments recorded yet.
              <b-button variant="link" class="pos-link" @click="loadPaymentMethodsOnly" :disabled="loading">
                Load payment history
              </b-button>
            </p>

            <dl class="pos-dl mt-3" v-if="payment_history.length">
              <dt class="pos-dl-strong">Total paid</dt>
              <dd class="pos-dl-strong">{{ money(getTotalPaymentsFromHistory()) }}</dd>
              <dt v-if="getAvailableStoreCredit() > 0">Available store credit</dt>
              <dd v-if="getAvailableStoreCredit() > 0" class="is-good">{{ money(getAvailableStoreCredit()) }}</dd>
            </dl>
          </article>

          <!-- Account activity -->
          <article v-if="invoice.edit" class="pos-tile pos-tile--wide">
            <div class="pos-tile-head">
              <div class="pos-tile-title">
                <span class="pos-tile-icon"><b-icon icon="arrow-left-right"></b-icon></span>
                <h3>Account activity</h3>
              </div>
            </div>

            <div v-if="hasMerchandiseDue" class="pos-exchange-strip is-pending">
              <div>
                <strong>{{ merchandiseDuePending.length }} line(s) awaiting receipt.</strong>
                Refunds are blocked. You can still reselect items — they'll be marked PENDING until the return is received.
              </div>
              <b-button size="sm" class="pos-btn-primary" @click="openPosTab('merchandisedue')">Open Merchandise due</b-button>
            </div>
            <div v-else-if="refund_items.length" class="pos-exchange-strip is-pending">
              <div><strong>{{ refund_items.length }} item(s) marked to cancel or return.</strong> Validate quantities and charges to create store credit.</div>
              <b-button size="sm" class="pos-btn-primary" @click="openPosTab('returns')">Validate returns</b-button>
            </div>
            <div v-else-if="getAvailableStoreCredit() > 0.005" class="pos-exchange-strip" :class="'is-' + exchangeSummary.outcome">
              <div>
                <strong>{{ money(getAvailableStoreCredit()) }} store credit.</strong>
                <template v-if="exchangeSummary.hasNewItems"> {{ exchangeOutcomeLabel }}: {{ money(exchangeOutcomeAmount) }}.</template>
                <template v-else> Refund it or reselect items.</template>
              </div>
              <b-button size="sm" class="pos-btn-primary" @click="openPosTab('returns')">{{ exchangeSummary.hasNewItems ? 'Settle exchange' : 'Refund or reselect' }}</b-button>
            </div>

            <div class="pos-activity">
              <button type="button" class="pos-activity-btn" @click="openPosTab('returns')">
                <b-icon icon="box-arrow-in-left" class="pos-activity-icon"></b-icon>
                <strong>{{ refund_items.length }}</strong><span>Items set to return</span>
              </button>
              <button type="button" class="pos-activity-btn" @click="openPosTab('paymenthistory')">
                <b-icon icon="cash-stack" class="pos-activity-icon"></b-icon>
                <strong>{{ money(getTotalPaymentsFromHistory()) }}</strong><span>Paid so far</span>
              </button>
              <button type="button" class="pos-activity-btn" @click="openPosTab('refunds')">
                <b-icon icon="arrow-return-left" class="pos-activity-icon"></b-icon>
                <strong>History</strong><span>Refund history</span>
              </button>
              <button type="button" class="pos-activity-btn" @click="openPosTab('storecredits')">
                <b-icon icon="wallet2" class="pos-activity-icon"></b-icon>
                <strong>{{ money(getAvailableStoreCredit()) }}</strong><span>Store credit</span>
              </button>
              <button type="button" class="pos-activity-btn" @click="openPosTab('refundmethods')">
                <b-icon icon="arrow-repeat" class="pos-activity-icon"></b-icon>
                <strong>{{ money(getRemainingStoreCredit()) }}</strong><span>To refund</span>
              </button>
            </div>
          </article>

        </section>

        <!-- STICKY TOTALS BAR -->
        <footer class="pos-totals-bar" v-if="storeReady">
          <div class="pos-totals-stats">
            <div class="pos-totals-item pos-totals-item--items">
              <b-icon icon="bag"></b-icon>
              <div><span>Items</span><strong>{{ visibleItems.length }}</strong></div>
            </div>
            <div class="pos-totals-item">
              <div>
                <span>Subtotal</span>
                <strong>{{ money(invoice.edit && !hasNewItems ? getExistingSubTotal() : getSubTotal()) }}</strong>
              </div>
            </div>
            <div class="pos-totals-item">
              <div>
                <span>Sales tax</span>
                <strong>{{ money(invoice.edit && !hasNewItems ? getExistingSalesTax() : getSalesTax()) }}</strong>
              </div>
            </div>
            <div class="pos-totals-item is-grand">
              <div>
                <span>Grand total</span>
                <strong>{{ money(invoice.edit && !hasNewItems ? getExistingGrandTotal() : getGrandTotal()) }}</strong>
              </div>
            </div>
          </div>
          <div class="pos-totals-actions">
            <div class="pos-totals-balance" :class="{ 'is-due': currentBalance > 0.009 }">
              <span>Balance</span><strong>{{ money(currentBalance) }}</strong>
            </div>
            <b-button v-if="nextTabKey" class="pos-btn-primary pos-btn-lg" @click="openPosTab(nextTabKey)">
              Continue to {{ (posTabs.find(t => t.key === nextTabKey) || {}).label }}
              <b-icon icon="arrow-right-short"></b-icon>
            </b-button>
             <b-button v-if="!invoice.edit" class="pos-btn-ghost" @click="saveDraftInvoice" :disabled="loading || !isPhoneNumberValid || !storeReady">
                <b-icon icon="bookmark"></b-icon> Save draft
              </b-button>
          </div>
        </footer>

        <!-- ===================================================== -->
        <!-- TAB MODALS                                             -->
        <!-- ===================================================== -->

        <!-- 1. STORE -->
        <b-modal id="pos-modal-store" title="Store" size="lg" centered content-class="pos-modal" dialog-class="pos-dialog"
                 no-close-on-backdrop no-close-on-esc :hide-header-close="!storeReady">
          <p class="pos-modal-lead">Choose where this sale is being made. Changing the store later starts the cart over.</p>
          <div class="pos-panel">
            <b-row>
              <b-col md="6">
                <b-form-group label="Location" label-class="pos-label">
                  <b-form-select v-model="invoice.selected_locsub" @change="set_location" :disabled="invoice.edit == true">
                    <template #first>
                      <b-form-select-option :value="null" disabled>Select a location</b-form-select-option>
                    </template>
                    <b-form-select-option v-for="locsub in storeLocationOptions" :key="locsub.value" :value="locsub.value">
                      {{ locsub.text.split('|')[0].trim() }}
                    </b-form-select-option>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Sold from (state)" label-class="pos-label">
                  <b-form-select v-model="invoice.sold_from_state" :options="allowed_states" :disabled="invoice.edit == true"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Salesperson" label-class="pos-label">
                  <div class="pos-readonly">{{ salespersonName }}</div>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Date" label-class="pos-label">
                  <div class="pos-readonly">{{ invoice.ts || ts }}</div>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <span class="pos-foot-note">{{ storeReady ? 'Store is set.' : 'Select a location to continue.' }}</span>
              <b-button class="pos-btn-primary" @click="saveStoreSection" :disabled="!storeReady">Continue</b-button>
            </div>
          </template>
        </b-modal>

        <!-- 2. CUSTOMER -->
        <b-modal id="pos-modal-customer" title="Customer" size="lg" centered scrollable content-class="pos-modal" dialog-class="pos-dialog">
          <p class="pos-modal-lead">Name, email, phone and zip code are required. The delivery team uses the address and floor.</p>
          <div class="pos-panel">
            <b-form autocomplete="off" @submit.prevent="saveCustomerSection">
              <b-row>
                <b-col md="12">
                  <b-form-group label="Full name" label-class="pos-label">
                    <b-input v-model="invoice.customer.fullname" autocomplete="new-password" placeholder="Customer full name"></b-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Email" label-class="pos-label">
                    <b-input v-model="invoice.customer.email" type="email" autocomplete="new-password" placeholder="name@example.com"></b-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Phone" label-class="pos-label">
                    <VuePhoneNumberInput v-model="invoice.customer.phone" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Second phone (optional)" label-class="pos-label">
                    <VuePhoneNumberInput v-model="invoice.customer.phone_2" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Floor" label-class="pos-label">
                    <b-form-select v-model="invoice.customer.floor" :options="floor_options">
                      <template #first>
                        <b-form-select-option :value="null" disabled>Select floor</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </b-col>
                <template v-if="show_custom_floor || invoice.customer.floor === 'Other'">
                  <b-col md="6">
                    <b-form-group label="Custom floor" label-class="pos-label">
                      <b-input v-model="custom_floor_display" placeholder="Basement, penthouse..."></b-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Floor number" label-class="pos-label">
                      <b-input v-model="custom_floor_number" type="number" placeholder="Floor #"></b-input>
                    </b-form-group>
                  </b-col>
                </template>
                <b-col md="6">
                  <b-form-group label="Address line 1" label-class="pos-label">
                    <b-input v-model="invoice.customer.address_line_1" autocomplete="new-password" placeholder="Street address"></b-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Address line 2" label-class="pos-label">
                    <b-input v-model="invoice.customer.address_line_2" autocomplete="new-password" placeholder="Apt, suite, unit"></b-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Zip code" label-class="pos-label">
                    <vue-simple-suggest
                      v-model="invoice.customer.zipcode"
                      display-attribute="zip_code"
                      value-attribute="zip_code"
                      :list="zipcodes"
                      :filter-by-query="true"
                      @select="set_zipcode_and_state_city"
                      :filter="filter_zipcodes"
                      ref="zip_code"
                      class="input-field"
                      placeholder="Zip code">
                      <div slot="suggestion-item" slot-scope="{ suggestion }">
                        <span>{{ suggestion.zip_code }} ({{ suggestion.city }}, {{ suggestion.state }})</span>
                      </div>
                    </vue-simple-suggest>
                    <small v-if="blockedLoading" class="text-muted d-block mt-1">
                      <b-spinner small></b-spinner> Loading zone rules...
                    </small>
                    <small v-else-if="blockedZone" class="text-success d-block mt-1">
                      <b-icon icon="geo-alt-fill"></b-icon>
                      Zone: <strong>{{ blockedZone.zone_code }}</strong> — {{ blockedZone.zone_name }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="State" label-class="pos-label">
                    <b-form-select v-model="invoice.customer.state" :options="allowed_states">
                      <template #first>
                        <b-form-select-option :value="null" disabled>State</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="City" label-class="pos-label">
                    <b-input v-model="invoice.customer.city" autocomplete="new-password" placeholder="City"></b-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="pos-confirm">
                <b-form-checkbox v-model="invoice.address_confirmation_agreed">
                  I confirm that my shipping address and phone number are correct and I understand that a $200 redelivery fee applies for incorrect information.
                </b-form-checkbox>
              </div>
            </b-form>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-customer')">Close</b-button>
                <b-button class="pos-btn-primary" size="sm" @click="saveCustomerSection">Save customer</b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- 3. PRODUCTS -->
        <b-modal id="pos-modal-products" title="Products" size="xl" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <div v-if="showExchangeBar" class="pos-exchange-bar">
            <div><span>Store credit</span><strong>{{ money(exchangeSummary.credit) }}</strong></div>
            <div><span>New items with tax</span><strong>{{ money(exchangeSummary.newTotal) }}</strong></div>
            <div :class="'is-' + exchangeSummary.outcome"><span>{{ exchangeOutcomeLabel }}</span><strong>{{ money(exchangeOutcomeAmount) }}</strong></div>
          </div>
          <b-tabs v-model="productModalTab" class="pos-subtabs" nav-class="pos-subtabs-nav" content-class="pt-3">

            <!-- ADD PRODUCTS -->
            <b-tab>
              <template #title><b-icon icon="plus-circle"></b-icon> Add products</template>

              <b-tabs pills small class="pos-pilltabs">
                <b-tab active>
                  <template #title><b-icon icon="search"></b-icon> Search</template>
                  <div class="pos-panel mt-2">
                    <b-form @submit="search" autocomplete="off" class="pos-search-row">
                      <b-form-input v-model="searchitems.keyword" required placeholder="SKU or product name" class="pos-search-input"></b-form-input>
                      <b-button class="pos-btn-primary" type="submit">Search</b-button>
                      <b-form-checkbox v-model="search_in_my_location" @change="handleOnlyInMyLocationChange">Only in my location</b-form-checkbox>
                      <b-form-checkbox v-model="search_only_from_damages" @change="handleOnlyFromDamagesChange" :disabled="!search_in_my_location">As is</b-form-checkbox>
                    </b-form>

                    <div class="pos-inline-actions mt-3">
                      <b-button size="sm" class="pos-btn-ghost" @click="get_scheduled_deliveries">
                        <b-icon icon="truck"></b-icon> Scheduled deliveries
                      </b-button>
                      <b-button size="sm" class="pos-btn-ghost" @click="openDeliveryCalendar" :disabled="!invoice.selected_locsub">
                        <b-icon icon="calendar3"></b-icon> Delivery calendar
                      </b-button>
                    </div>
                  </div>

                  <b-overlay :show="searchitems.search_being_loaded" variant="white" opacity="0.85" blur="1px" rounded="sm" class="search-loading-overlay">
                    <template #overlay>
                      <div class="product-search-loader">
                        <div class="loader-card">
                          <div class="loader-text">Searching products...</div>
                          <div class="loader-progress"><div class="loader-progress-bar"></div></div>
                        </div>
                      </div>
                    </template>

                    <div v-if="searchitems.result.length > 0" class="product-cards-container">
                      <div class="product-cards-grid">
                        <div v-for="(sitem, sidx) in searchitems.result" :key="sitem.product_sku + '-' + sidx"
                             class="product-card" :class="{ 'out-of-stock': sitem.available <= 0 }">
                          <div class="product-card-image">
                            <div v-if="getProductImage(sitem)" class="image-wrapper">
                              <img :src="getProductImage(sitem)" :alt="sitem.product_name || sitem.product_sku"
                                   @error="handleImageError($event, sitem)" @load="handleImageLoad($event, sitem)" @click="showFullImage(sitem)" />
                              <div class="image-overlay" @click="showFullImage(sitem)"><b-icon-zoom-in font-scale="1.5"></b-icon-zoom-in></div>
                            </div>
                            <div v-else class="no-image"><b-icon-image font-scale="2.5"></b-icon-image><span>No image</span></div>
                            <b-badge v-if="sitem.is_damaged && sitem.damage_info && sitem.damage_info.damage_id" variant="danger" class="damage-badge">D-{{ sitem.damage_info.damage_id }}</b-badge>
                            <b-badge v-if="hasActiveDiscount(sitem)" variant="success" class="discount-badge">
                              {{ getSaleTypeShortLabel(sitem).toUpperCase() }}
                            </b-badge>
                          </div>

                          <div class="product-card-info">
                            <div class="product-sku">{{ sitem.product_sku }}</div>
                            <div class="product-name">{{ sitem.product_name || 'N/A' }}</div>
                            <div class="product-qty-box">
                              <span class="qty-box-label">QTY/BOX:</span>
                              <span class="qty-box-value">{{ sitem.quantity_per_box || 1 }}</span>
                            </div>
                          </div>

                          <div class="product-card-actions">
                            <div class="price-block">
                              <div class="price-row">
                                <span class="price-label">Regular</span>
                                <span class="original-price">${{ getRegularPrice(sitem).toFixed(2) }}</span>
                              </div>
                              <div class="price-row">
                                <span class="price-label">Selling</span>
                                <div class="price-value-group">
                                  <span class="current-price">${{ getSellingPrice(sitem).toFixed(2) }}</span>
                                </div>
                              </div>
                              <div v-if="getDiscountedPrice(sitem) !== null" class="price-row">
                                <span class="price-label">{{ getSaleTypeShortLabel(sitem) }}</span>
                                <span class="current-price discounted">${{ getDiscountedPrice(sitem).toFixed(2) }}</span>
                              </div>
                              <div class="quantity-block">
                                <label class="qty-label">Qty</label>
                                <b-input size="sm" class="qty-input" v-model="sitem.quantity" type="number" min="1"
                                         :class="{ 'is-invalid': hasQuantityError(sitem) }" @blur="validateQuantityPerBox(sitem)"></b-input>
                              </div>
                              <div class="price-row total-row">
                                <span class="price-label">Total</span>
                                <span class="total-price">${{ (getDisplayPrice(sitem) * (parseInt(sitem.quantity) || 0)).toFixed(2) }}</span>
                              </div>
                            </div>
                          </div>

                          <div class="product-card-actions">
                            <div class="stock-block">
                              <div class="stock-item in-stock" :class="{ 'low': sitem.in_stock < 5 }"><span class="stock-label">IN STOCK</span><span class="stock-value">{{ sitem.in_stock }}</span></div>
                              <div class="stock-item reserved" :class="{ 'low': sitem.reserved < 5 }"><span class="stock-label">RESERVED</span><span class="stock-value">{{ sitem.reserved }}</span></div>
                              <div class="stock-item available" :class="{ 'low': sitem.available < 5 }"><span class="stock-label">AVAILABLE</span><span class="stock-value">{{ sitem.available }}</span></div>
                            </div>
                            <div v-if="showLocationBreakdown" class="locations-block">
                              <div class="locations-header">LOCATIONS</div>
                              <div v-if="sitem.sublocation_breakdown && sitem.sublocation_breakdown.length > 0" class="locations-list">
                                <div v-for="loc in getFilteredSublocationBreakdown(sitem)" :key="loc.sublocation_id" class="location-item">
                                  <span class="location-name"><span class="location-dot" :class="getLocationClass(loc.sublocation_name)"></span>{{ getShortLocationName(loc.sublocation_name) }}</span>
                                  <span class="location-qty" :class="{ 'low-stock': loc.available < 3 }">{{ loc.available }}</span>
                                </div>
                              </div>
                              <div v-else class="no-location-data">No location data</div>
                            </div>
                            <b-button variant="success" block @click="addProduct(sitem)" :disabled="hasQuantityError(sitem)" class="add-btn">
                              <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> Add to cart
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p v-else class="pos-empty pos-empty--pad">Search by SKU or name to see products and stock.</p>
                  </b-overlay>
                </b-tab>

                <b-tab>
                  <template #title><b-icon icon="upc-scan"></b-icon> Scan barcode</template>
                  <div class="pos-panel mt-2">
                    <b-form @submit.prevent="handleBarcodeScan">
                      <b-input-group>
                        <b-form-input v-model="barcodeInput" placeholder="Scan barcode or type SKU" style="max-width: 320px;"
                                      ref="barcodeInput" autofocus @keyup.enter="handleBarcodeScan"></b-form-input>
                        <b-input-group-append>
                          <b-button class="pos-btn-primary" type="submit"><b-icon-upc-scan></b-icon-upc-scan> Scan</b-button>
                          <b-button variant="success" @click="openCameraScanner" title="Use camera to scan barcode"><b-icon-camera></b-icon-camera></b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form>
                    <p class="pos-empty mt-2">A single match is added to the cart right away.</p>
                  </div>
                </b-tab>

                <b-tab>
                  <template #title><b-icon icon="plus-circle"></b-icon> Special order</template>
                  <template v-if="refund_items.length <= 0 && !hasDeliveredItems">
                    <div class="pos-panel mt-2">
                      <div class="special-order-image-section mb-3">
                        <div class="d-flex align-items-center gap-3 flex-wrap">
                          <div class="position-relative" style="width: 80px; height: 80px;">
                            <div v-if="nonInventoryImage.previewUrl" class="image-preview-wrapper"
                                 style="width: 80px; height: 80px; border: 2px solid #dee2e6; border-radius: 8px; overflow: hidden; background: white; display: flex; align-items: center; justify-content: center;">
                              <img :src="nonInventoryImage.previewUrl" style="width: 100%; height: 100%; object-fit: contain;" alt="Product preview" />
                              <b-button variant="danger" size="sm" class="remove-image-btn"
                                        style="position: absolute; top: -6px; right: -6px; width: 22px; height: 22px; padding: 0; border-radius: 50%; font-size: 12px; line-height: 1;"
                                        @click="clearNonInventoryImage" title="Remove image"><b-icon-x font-scale="1"></b-icon-x></b-button>
                            </div>
                            <div v-else class="upload-placeholder"
                                 style="width: 80px; height: 80px; border: 2px dashed #ced4da; border-radius: 8px; background: white; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center;"
                                 @click="$refs.nonInventoryFileInput.click()">
                              <b-icon-camera font-scale="1.5" variant="secondary"></b-icon-camera>
                              <div class="small text-muted" style="font-size: 8px;">Upload</div>
                            </div>
                          </div>
                          <div class="upload-controls">
                            <div class="d-flex align-items-center gap-2">
                              <b-button variant="outline-primary" size="sm" @click="$refs.nonInventoryFileInput.click()" :disabled="nonInventoryImage.uploading"><b-icon-upload class="mr-1"></b-icon-upload> Choose</b-button>
                              <b-button variant="outline-success" size="sm" @click="openCamera" :disabled="nonInventoryImage.uploading || !isCameraSupported"><b-icon-camera class="mr-1"></b-icon-camera> Camera</b-button>
                              <b-spinner small v-if="nonInventoryImage.uploading" variant="primary"></b-spinner>
                              <small v-if="nonInventoryImage.uploadedUrl" class="text-success"><b-icon-check-circle></b-icon-check-circle> Uploaded</small>
                            </div>
                            <input ref="nonInventoryFileInput" type="file" accept="image/*" style="display: none" @change="handleNonInventoryImageUpload" />
                            <input ref="cameraInput" type="file" accept="image/*" capture="environment" style="display: none" @change="handleNonInventoryImageUpload" />
                          </div>
                        </div>
                      </div>

                      <b-row class="g-2">
                        <b-col cols="12" sm="6" md="4" class="mb-2">
                          <label class="pos-label">Category <span class="text-danger">*</span></label>
                          <b-form-select :options="categoryOptions" v-model="special_item.category_id" size="sm" :state="special_item.category_id ? null : false" @change="onSpecialCategoryChange">
                            <template #first><b-form-select-option :value="null" disabled>Select category</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="12" sm="6" md="4" class="mb-2">
                          <label class="pos-label">Sub category <span class="text-danger">*</span></label>
                          <b-form-select :options="specialSubcategoryOptions" v-model="special_item.subcategory_id" size="sm" :state="special_item.subcategory_id ? null : false" :disabled="!special_item.category_id">
                            <template #first><b-form-select-option :value="null" disabled>Select sub category</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="6" md="2" class="mb-2">
                          <label class="pos-label">Color</label>
                          <b-form-select :options="colorOptions" v-model="special_item.color_id" size="sm">
                            <template #first><b-form-select-option :value="null">Select</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="6" md="2" class="mb-2">
                          <label class="pos-label">Size</label>
                          <b-form-select :options="sizeOptions" v-model="special_item.size_id" size="sm">
                            <template #first><b-form-select-option :value="null">Select</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="12" sm="6" md="4" class="mb-2">
                          <label class="pos-label">SKU <span class="text-danger">*</span></label>
                          <b-input size="sm" v-model="special_item.product_sku" placeholder="Enter SKU" :state="special_item.product_sku ? null : false"></b-input>
                        </b-col>
                        <b-col cols="12" sm="6" md="4" class="mb-2">
                          <label class="pos-label">Description <span class="text-danger">*</span></label>
                          <b-input size="sm" v-model="special_item.product_name" placeholder="Enter description" :state="special_item.product_name ? null : false"></b-input>
                        </b-col>
                        <b-col cols="12" sm="6" md="4" class="mb-2">
                          <label class="pos-label">Vendor <span class="text-danger">*</span></label>
                          <b-form-select :options="sortedVendors" v-model="special_item.vendor" size="sm" :state="special_item.vendor ? null : false">
                            <template #first><b-form-select-option :value="null" disabled>Select vendor</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="6" md="2" class="mb-2">
                          <label class="pos-label">Qty <span class="text-danger">*</span></label>
                          <b-input size="sm" type="number" min="1" step="1" v-model="special_item.quantity" :state="special_item.quantity > 0 ? null : false" placeholder="0"></b-input>
                        </b-col>
                        <b-col cols="6" md="3" class="mb-2">
                          <label class="pos-label">Price <span class="text-danger">*</span></label>
                          <b-input-group size="sm" prepend="$">
                            <b-input type="number" min="0.01" step="0.01" v-model="special_item.selling_price" :state="special_item.selling_price > 0 ? null : false" placeholder="0.00"></b-input>
                          </b-input-group>
                        </b-col>
                        <b-col cols="12" md="3" class="mb-2 d-flex align-items-end">
                          <b-button size="sm" variant="success" @click="addNonInventoryItem" :disabled="!canAddNonInventoryItem" class="w-100 py-2 font-weight-bold">
                            <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> Add to cart
                          </b-button>
                        </b-col>
                      </b-row>

                      <div v-if="!canAddNonInventoryItem" class="mt-2 small text-muted">
                        Still needed:
                        <span v-if="!special_item.category_id" class="text-danger">category </span>
                        <span v-if="!special_item.subcategory_id && special_item.category_id" class="text-danger">sub category </span>
                        <span v-if="!special_item.product_sku" class="text-danger">SKU </span>
                        <span v-if="!special_item.product_name" class="text-danger">description </span>
                        <span v-if="!special_item.vendor" class="text-danger">vendor </span>
                        <span v-if="!special_item.quantity || special_item.quantity <= 0" class="text-danger">quantity </span>
                        <span v-if="!special_item.selling_price || special_item.selling_price <= 0" class="text-danger">price</span>
                      </div>
                    </div>
                  </template>
                  <b-alert v-else-if="hasDeliveredItems" variant="info" show class="mt-2">Special order items can't be added after items have been delivered.</b-alert>
                  <b-alert v-else variant="info" show class="mt-2">Special order items can't be added while returns are pending.</b-alert>
                </b-tab>

                <b-tab>
                  <template #title><b-icon icon="box"></b-icon> Non-inventory</template>
                  <template v-if="refund_items.length <= 0 && ['admin', 'officemanager', 'locationmanager', 'locationadmin'].includes(userDetails.userrole) && !hasDeliveredItems">
                    <div class="pos-panel mt-2">
                      <div class="d-flex flex-wrap align-items-start mb-3" style="gap: 16px;">
                        <div class="position-relative" style="width: 120px;">
                          <div v-if="nonInventoryImage.previewUrl" class="image-preview-container text-center"
                               style="width: 120px; height: 120px; border: 2px solid #dee2e6; border-radius: 8px; overflow: hidden; background: white;">
                            <img :src="nonInventoryImage.previewUrl" style="width: 100%; height: 100%; object-fit: contain;" alt="Product preview" />
                            <b-button variant="danger" size="sm" class="remove-image-btn"
                                      style="position: absolute; top: -8px; right: -8px; width: 24px; height: 24px; padding: 0; border-radius: 50%;"
                                      @click="clearNonInventoryImage" title="Remove image"><b-icon-x font-scale="1.2"></b-icon-x></b-button>
                          </div>
                          <div v-else class="upload-placeholder text-center p-3"
                               style="width: 120px; height: 120px; border: 2px dashed #ced4da; border-radius: 8px; background: white; cursor: pointer;"
                               @click="$refs.nonInventoryFileInput.click()">
                            <b-icon-camera font-scale="2.5" variant="secondary" class="mb-2"></b-icon-camera>
                            <div class="small text-muted">Click to upload</div>
                          </div>
                        </div>
                        <div class="upload-controls flex-grow-1">
                          <div class="d-flex align-items-center mb-2">
                            <b-button variant="outline-primary" size="sm" class="mr-2" @click="$refs.nonInventoryFileInput.click()" :disabled="nonInventoryImage.uploading"><b-icon-upload class="mr-1"></b-icon-upload> Choose file</b-button>
                            <b-button variant="outline-success" size="sm" class="mr-2" @click="openCamera" :disabled="nonInventoryImage.uploading || !isCameraSupported"><b-icon-camera class="mr-1"></b-icon-camera> Camera</b-button>
                            <b-spinner small v-if="nonInventoryImage.uploading" variant="primary"></b-spinner>
                          </div>
                          <input ref="nonInventoryFileInput" type="file" accept="image/*" style="display: none" @change="handleNonInventoryImageUpload" />
                          <input ref="cameraInput" type="file" accept="image/*" capture="environment" style="display: none" @change="handleNonInventoryImageUpload" />
                          <small class="text-muted d-block">Max size 5MB (JPG, PNG, GIF)</small>
                          <small v-if="nonInventoryImage.uploadedUrl" class="text-success d-block mt-1">Image uploaded</small>
                        </div>
                      </div>

                      <b-row>
                        <b-col cols="12" md="6" lg="3" class="mb-3">
                          <label class="pos-label">Category <span class="text-danger">*</span></label>
                          <b-form-select :options="categoryOptions" v-model="nonInventoryItem.category_id" size="sm" :state="nonInventoryItem.category_id ? null : false" @change="onCategoryChange">
                            <template #first><b-form-select-option :value="null" disabled>Select category</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="12" md="6" lg="3" class="mb-3">
                          <label class="pos-label">SKU <span class="text-danger">*</span></label>
                          <b-input size="sm" v-model="nonInventoryItem.product_sku" placeholder="Enter SKU" :state="nonInventoryItem.product_sku ? null : false"></b-input>
                        </b-col>
                        <b-col cols="12" md="6" lg="3" class="mb-3">
                          <label class="pos-label">Description <span class="text-danger">*</span></label>
                          <b-input size="sm" v-model="nonInventoryItem.product_name" placeholder="Enter description" :state="nonInventoryItem.product_name ? null : false"></b-input>
                        </b-col>
                        <b-col cols="12" md="6" lg="3" class="mb-3">
                          <label class="pos-label">Vendor <span class="text-danger">*</span></label>
                          <b-form-select :options="sortedVendors" v-model="nonInventoryItem.vendor" size="sm" :state="nonInventoryItem.vendor ? null : false">
                            <template #first><b-form-select-option :value="null" disabled>Select vendor</b-form-select-option></template>
                          </b-form-select>
                        </b-col>
                        <b-col cols="6" md="3" lg="2" class="mb-3">
                          <label class="pos-label">Quantity <span class="text-danger">*</span></label>
                          <b-input size="sm" type="number" min="1" step="1" v-model="nonInventoryItem.quantity" :state="nonInventoryItem.quantity > 0 ? null : false" placeholder="Qty"></b-input>
                        </b-col>
                        <b-col cols="6" md="3" lg="2" class="mb-3">
                          <label class="pos-label">Price <span class="text-danger">*</span></label>
                          <b-input-group size="sm" prepend="$">
                            <b-input type="number" min="0.01" step="0.01" v-model="nonInventoryItem.selling_price" :state="nonInventoryItem.selling_price > 0 ? null : false" placeholder="0.00"></b-input>
                          </b-input-group>
                        </b-col>
                        <b-col cols="12" lg="2" class="mb-3 d-flex align-items-end">
                          <b-button size="sm" variant="success" @click="addNonInventoryItemToSale" :disabled="!canAddNonInventoryItemToSale" class="w-100 py-2 font-weight-bold">
                            <b-icon-plus-circle class="mr-2"></b-icon-plus-circle> Add to cart
                          </b-button>
                        </b-col>
                      </b-row>
                    </div>
                  </template>
                  <b-alert v-else-if="!['admin', 'officemanager', 'locationmanager', 'locationadmin'].includes(userDetails.userrole)" variant="warning" show class="mt-2">
                    Your role can't add non-inventory items. Ask a manager or administrator.
                  </b-alert>
                  <b-alert v-else-if="refund_items.length > 0" variant="info" show class="mt-2">Non-inventory items can't be added while returns are pending.</b-alert>
                  <b-alert v-else variant="info" show class="mt-2">Non-inventory items can't be added after items have been delivered.</b-alert>
                </b-tab>
              </b-tabs>
            </b-tab>

            <!-- CART -->
            <b-tab>
              <template #title><b-icon icon="cart3"></b-icon> Cart <span class="pos-tab-count">{{ visibleItems.length }}</span></template>

              <div v-if="invoice.items.length == 0" class="pos-panel">
                <p class="pos-empty">The cart is empty. Use Add products to search, scan or create a special order.</p>
                <b-button size="sm" class="pos-btn-primary mt-2" @click="productModalTab = 0">Add products</b-button>
              </div>

              <div v-else class="pos-cart">
                <!-- STICKY CART SUB-HEADER -->
                <div class="pos-cart-bar">
                  <div class="pos-cart-bar-stats">
                    <span><b-icon icon="bag"></b-icon> {{ visibleItems.length }} items</span>
                    <span v-if="blockedZone" class="pos-cart-chip is-new">
                      <b-icon icon="geo-alt-fill"></b-icon> Zone: {{ blockedZone.zone_code }}
                    </span>
                    <span v-if="existingItems.length" class="pos-cart-chip is-existing">
                      {{ existingItems.length }} on invoice
                    </span>
                    <span v-if="newItems.length" class="pos-cart-chip is-new">
                      {{ newItems.length }} new
                    </span>
                    <span v-if="refund_items.length" class="pos-cart-chip is-warn">
                      {{ refund_items.length }} returning
                    </span>
                  </div>
                  <div class="pos-cart-bar-actions">
                    <span class="pos-cart-bar-total">{{ money(getGrandTotal()) }}</span>
                    <span class="pos-cart-bar-label">Cart total</span>
                  </div>
                </div>

                <!-- EXISTING ITEMS -->
                <div v-if="hasExistingItems" class="pos-cart-section">
                  <div class="pos-cart-section-head">
                    <span class="pos-cart-section-title">
                      <b-icon icon="check2-square"></b-icon> Already on this invoice
                    </span>
                    <span class="pos-cart-section-sub">Cancel before delivery, or return after delivery. Click Undo to keep.</span>
                  </div>

                  <div v-if="refund_items.length" class="pos-exchange-strip is-pending">
                    <div>
                      <strong>{{ refund_items.length }} item(s) marked to cancel or return.</strong>
                      Set quantities and charges, then create store credit.
                    </div>
                    <b-button size="sm" class="pos-btn-primary" @click="openReturnsFromCart">
                      Validate returns
                    </b-button>
                  </div>

                  <transition-group name="pos-cart-list" tag="div" class="pos-cart-list">
                    <div
                      v-for="(item, idx) in existingItems"
                      :key="'existing-' + (item.itemid || idx)"
                      class="pos-cart-item"
                      :class="{
                        'is-returning': isMarkedForReturn(item),
                        'is-as-is': item.is_damaged,
                        'is-backorder': shouldShowBackorder(item),
                      }"
                    >
                      <div class="pos-cart-item-image" @click="getProductImage(item) && showFullImage(item)">
                        <img v-if="getProductImage(item)" :src="getProductImage(item)" :alt="item.product_name" />
                        <b-icon v-else icon="image"></b-icon>
                        <span class="pos-cart-item-num">
                          {{ getExistingItemNumber(idx) }}
                        </span>
                      </div>

                      <div class="pos-cart-item-info">
                        <div class="pos-cart-item-title">
                          <span v-if="invoice.edit && item.parent_product_sku" class="pos-cart-item-parent">
                            ({{ item.parent_product_sku }})
                          </span>
                          {{ item.product_name || item.product_sku }}
                        </div>
                        <div class="pos-cart-item-meta">
                          <span class="pos-chip">SKU: {{ item.product_sku }}</span>
                          <span
                            class="pos-chip"
                            :class="(item.sourceContext || 'warehouse') === 'store' ? 'is-ok' : ''"
                          >
                            <b-icon :icon="(item.sourceContext || 'warehouse') === 'store' ? 'shop' : 'building'"></b-icon>
                            {{ (item.sourceContext || 'warehouse') === 'store' ? 'Store' : 'Warehouse' }}
                          </span>
                          <span v-if="hasActiveDiscount(item)" class="pos-chip is-ok">
                            <b-icon icon="tag-fill"></b-icon> Discount
                          </span>
                          <span v-if="item.is_damaged" class="pos-chip is-warn">
                            <b-icon icon="exclamation-triangle-fill"></b-icon> As is
                            <template v-if="item.damage_info && item.damage_info.damage_id">
                              · D-{{ item.damage_info.damage_id }}
                            </template>
                          </span>
                          <span v-if="shouldShowBackorder(item)" class="pos-chip is-warn">
                            <b-icon icon="clock-history"></b-icon> Backorder
                          </span>
                          <span v-if="isMarkedForReturn(item)" class="pos-chip is-warn">
                            <b-icon icon="arrow-return-left"></b-icon>
                            {{ get_item_status(item) === 'delivered' ? 'Returning' : 'Cancelling' }}
                          </span>
                        </div>
                        <div v-if="item.is_damaged" class="pos-cart-item-warning">
                          ⚠️ AS-IS item — cannot be returned or refunded.
                        </div>
                        <div v-if="hasActiveDiscount(item)" class="pos-cart-item-info-line">
                          <b-icon icon="tag-fill"></b-icon>
                          Special price: <strong>{{ money(getDisplayPrice(item)) }}</strong>
                          <small>(reg. {{ money(getSellingPrice(item)) }})</small>
                        </div>

                        <div class="pos-cart-item-delivery">
                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">Type</label>
                            <b-form-select
                              v-model="item.order_type"
                              :options="get_order_types(item)"
                              size="sm"
                              @change="del_date_set(item)"
                            >
                              <template #first>
                                <b-form-select-option :value="null" disabled>Pick</b-form-select-option>
                              </template>
                            </b-form-select>
                          </div>

                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">Date</label>
                            <span v-if="item.order_type === 'customer_takes'" class="pos-cart-readonly">
                              {{ today_date }}
                            </span>
                            <date-picker
                              v-else
                              v-model="item.delivery_date"
                              format="MM-DD-YYYY"
                              valueType="format"
                              :disabled-date="disablePreviousDates"
                              @change="(date) => onDeliveryDateChange(item, date)"
                            />
                            <small v-if="getBlockReasonForDate(item.delivery_date)" class="pos-cart-hint pos-cart-hint--warn">
                              <b-icon icon="exclamation-triangle-fill"></b-icon>
                              {{ getBlockReasonForDate(item.delivery_date) }}
                            </small>
                          </div>

                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">
                              <template v-if="item.order_type === 'customer_takes'">Fulfill from</template>
                              <template v-else-if="item.order_type === 'store_pickup'">Customer pickup from</template>
                              <template v-else>Delivery from</template>
                            </label>
                            <b-form-select
                              v-if="item.order_type === 'customer_takes'"
                              size="sm"
                              :options="packet.locsubs.filter(d => d.location_id == invoice.location_id && d.sublocation_type_id == 2)"
                              v-model="item.locsub"
                              :disabled="item.existing || !search_in_my_location"
                            ></b-form-select>
                            <b-form-select
                              v-else-if="item.order_type === 'store_pickup'"
                              size="sm"
                              :options="get_pickup_locations(item)"
                              v-model="item.drop_locsub"
                              :disabled="item.existing"
                            ></b-form-select>
                            <b-form-select
                              v-else
                              size="sm"
                              :options="getDeliveryLocations(item)"
                              v-model="item.drop_locsub"
                              :disabled="item.existing"
                            ></b-form-select>
                          </div>
                        </div>
                      </div>

                      <div class="pos-cart-item-pricing">
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Price</label>
                          <b-input
                            size="sm"
                            v-model="item.new_selling_price"
                            class="pos-cart-price-input"
                            :disabled="item.existing"
                            @blur="validatePriceChange(item, $event)"
                          ></b-input>
                        </div>
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Qty</label>
                          <span class="pos-cart-readonly">
                            {{ item.quantity - item.refunded_so_far_quantity }}
                          </span>
                        </div>
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Discount</label>
                          <span class="pos-cart-readonly">
                            {{ money(item.discount_offered) }}
                          </span>
                        </div>
                        <div class="pos-cart-price-row pos-cart-price-row--total">
                          <span class="pos-cart-field-label">Line total</span>
                          <strong>{{ money(lineTotal(item)) }}</strong>
                        </div>
                      </div>

                      <div class="pos-cart-item-actions">
                        <b-button
                          v-if="isMarkedForReturn(item)"
                          size="sm"
                          class="pos-btn-ghost"
                          @click="unmarkReturn(item)"
                          title="Keep this item on the sale"
                        >
                          <b-icon icon="arrow-counterclockwise"></b-icon> Undo
                        </b-button>
                        <b-button
                          v-else
                          variant="outline-danger"
                          size="sm"
                          @click="removeItem(getItemOriginalIndex(item))"
                        >
                          <b-icon :icon="get_item_status(item) === 'delivered' ? 'arrow-return-left' : 'x-circle'"></b-icon>
                          {{ get_item_status(item) === 'delivered' ? 'Return' : 'Cancel' }}
                        </b-button>
                        <b-button
                          v-if="invoice.is_layaway"
                          variant="warning"
                          size="sm"
                          @click="removeLayawayItem(item)"
                          title="Remove item from layaway (no refund)"
                        >
                          <b-icon icon="trash"></b-icon> Remove
                        </b-button>
                      </div>
                    </div>
                  </transition-group>
                </div>

                <!-- NEW ITEMS -->
                <div v-if="hasNewItems" class="pos-cart-section">
                  <div class="pos-cart-section-head">
                    <span class="pos-cart-section-title">
                      <b-icon icon="plus-square"></b-icon>
                      {{ invoice.edit ? 'Items being added' : 'Cart items' }}
                    </span>
                    <div v-if="newItems.length > 1" class="pos-cart-section-bulk">
                      <span class="pos-cart-section-sub">Set date for all</span>
                      <date-picker
                        v-model="commonDeliveryDate"
                        format="MM-DD-YYYY"
                        valueType="format"
                        :disabled-date="disablePreviousDates"
                        @change="setCommonDeliveryDateForNewItems"
                      />
                      <small v-if="getBlockReasonForDate(commonDeliveryDate)" class="pos-cart-hint pos-cart-hint--warn">
                        <b-icon icon="exclamation-triangle-fill"></b-icon>
                        {{ getBlockReasonForDate(commonDeliveryDate) }}
                      </small>
                    </div>
                  </div>

                  <transition-group name="pos-cart-list" tag="div" class="pos-cart-list">
                    <div
                      v-for="(item, idx) in newItems"
                      :key="'new-' + (item.product_sku || idx) + '-' + idx"
                      class="pos-cart-item"
                      :class="{
                        'is-as-is': item.is_damaged || item.is_as_is,
                        'is-backorder': shouldShowBackorder(item),
                      }"
                    >
                      <div class="pos-cart-item-image" @click="getProductImage(item) && showFullImage(item)">
                        <img v-if="getProductImage(item)" :src="getProductImage(item)" :alt="item.product_name" />
                        <b-icon v-else icon="image"></b-icon>
                        <span class="pos-cart-item-num">{{ getNewItemNumber(idx) }}</span>
                      </div>

                      <div class="pos-cart-item-info">
                        <div class="pos-cart-item-title">
                          <span v-if="invoice.edit && item.parent_product_sku" class="pos-cart-item-parent">
                            ({{ item.parent_product_sku }})
                          </span>
                          {{ item.product_name || item.product_sku }}
                        </div>
                        <div class="pos-cart-item-meta">
                          <span class="pos-chip">SKU: {{ item.product_sku }}</span>
                          <span v-if="item.quantity_per_box > 1" class="pos-chip">
                            Qty/box: {{ item.quantity_per_box }}
                          </span>
                          <span
                            v-if="item.sourceContext"
                            class="pos-chip"
                            :class="item.sourceContext === 'store' ? 'is-ok' : ''"
                          >
                            <b-icon :icon="item.sourceContext === 'store' ? 'shop' : 'building'"></b-icon>
                            {{ item.sourceContext === 'store' ? 'Store' : 'Warehouse' }}
                          </span>
                          <span v-if="hasActiveDiscount(item)" class="pos-chip is-ok">
                            <b-icon icon="tag-fill"></b-icon> Discount
                          </span>
                          <span v-if="item.is_damaged" class="pos-chip is-warn">
                            <b-icon icon="exclamation-triangle-fill"></b-icon> As is
                            <template v-if="item.damage_info && item.damage_info.damage_id">
                              · D-{{ item.damage_info.damage_id }}
                            </template>
                          </span>
                          <span v-if="shouldShowBackorder(item)" class="pos-chip is-warn">
                            <b-icon icon="clock-history"></b-icon> Backorder
                          </span>
                        </div>
                        <div v-if="item.is_damaged" class="pos-cart-item-warning">
                          ⚠️ AS-IS item — cannot be returned or refunded.
                        </div>
                        <div v-if="hasActiveDiscount(item)" class="pos-cart-item-info-line">
                          <b-icon icon="tag-fill"></b-icon>
                          Special price: <strong>{{ money(getDisplayPrice(item)) }}</strong>
                          <small>(reg. {{ money(getSellingPrice(item)) }})</small>
                        </div>

                        <div class="pos-cart-item-delivery">
                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">Type</label>
                            <b-form-select
                              v-model="item.order_type"
                              :options="get_order_types(item)"
                              size="sm"
                              @change="del_date_set(item)"
                            >
                              <template #first>
                                <b-form-select-option :value="null" disabled>Pick</b-form-select-option>
                              </template>
                            </b-form-select>
                          </div>

                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">Date</label>
                            <span v-if="item.order_type === 'customer_takes'" class="pos-cart-readonly">
                              {{ today_date }}
                            </span>
                            <date-picker
                              v-else
                              v-model="item.delivery_date"
                              format="MM-DD-YYYY"
                              valueType="format"
                              :disabled-date="disablePreviousDates"
                              @change="(date) => onDeliveryDateChange(item, date)"
                            />
                            <small v-if="getBlockReasonForDate(item.delivery_date)" class="pos-cart-hint pos-cart-hint--warn">
                              <b-icon icon="exclamation-triangle-fill"></b-icon>
                              {{ getBlockReasonForDate(item.delivery_date) }}
                            </small>
                          </div>

                          <div class="pos-cart-field">
                            <label class="pos-cart-field-label">
                              <template v-if="item.order_type === 'customer_takes'">Fulfill from</template>
                              <template v-else-if="item.order_type === 'store_pickup'">Customer pickup from</template>
                              <template v-else>Delivery from</template>
                            </label>
                            <b-form-select
                              v-if="item.order_type === 'customer_takes'"
                              size="sm"
                              :options="packet.locsubs.filter(d => d.location_id == invoice.location_id && d.sublocation_type_id == 2)"
                              v-model="item.locsub"
                              :disabled="item.existing || !search_in_my_location"
                            ></b-form-select>
                            <b-form-select
                              v-else-if="item.order_type === 'store_pickup'"
                              size="sm"
                              :options="get_pickup_locations(item)"
                              v-model="item.drop_locsub"
                              :disabled="item.existing"
                            ></b-form-select>
                            <b-form-select
                              v-else
                              size="sm"
                              :options="getDeliveryLocations(item)"
                              v-model="item.drop_locsub"
                              :disabled="item.existing"
                            ></b-form-select>
                          </div>
                        </div>

                        <div v-if="item.is_as_is" class="pos-cart-item-upload">
                          <template v-if="getProductImagedamage(item)">
                            <img :src="getProductImagedamage(item)" class="pos-cart-upload-thumb" @click="showFullImagedamage(item)" />
                            <b-button size="sm" variant="outline-danger" @click="removeItemImage(item)">
                              <b-icon icon="x"></b-icon> Remove photo
                            </b-button>
                          </template>
                          <template v-else-if="imageUpload.currentItem === item && imageUpload.previewUrl">
                            <img :src="imageUpload.previewUrl" class="pos-cart-upload-thumb" />
                            <b-button size="sm" variant="success" @click="uploadItemImage" :disabled="imageUpload.uploading">
                              <b-spinner small v-if="imageUpload.uploading"></b-spinner>
                              Save photo
                            </b-button>
                            <b-button size="sm" variant="outline-secondary" @click="cancelImageUpload" :disabled="imageUpload.uploading">
                              Cancel
                            </b-button>
                          </template>
                          <template v-else>
                            <b-button size="sm" variant="outline-primary" @click="$refs[`fileInput-${getItemOriginalIndex(item)}`][0].click()">
                              <b-icon icon="camera"></b-icon> Add as-is photo
                            </b-button>
                            <input
                              :ref="`fileInput-${getItemOriginalIndex(item)}`"
                              type="file"
                              accept="image/*"
                              style="display: none"
                              @change="handleImageUpload(item, $event)"
                            />
                          </template>
                        </div>
                      </div>

                      <div class="pos-cart-item-pricing">
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Selling price</label>
                          <span class="pos-cart-readonly">
                            {{ money(getSellingPrice(item)) }}
                          </span>
                        </div>
                        <div v-if="getDiscountedPrice(item) !== null" class="pos-cart-price-row">
                          <label class="pos-cart-field-label">{{ getSaleTypeLabel(item) }}</label>
                          <span class="pos-cart-readonly is-discounted">
                            {{ money(getDiscountedPrice(item)) }}
                          </span>
                        </div>
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Price</label>
                          <b-input
                            size="sm"
                            v-model="item.new_selling_price"
                            class="pos-cart-price-input"
                            :disabled="item.existing"
                            @blur="validatePriceChange(item, $event)"
                          ></b-input>
                        </div>
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">Qty</label>
                          <b-input
                            size="sm"
                            type="number"
                            v-model="item.quantity"
                            class="pos-cart-qty-input"
                          ></b-input>
                        </div>
                        <div class="pos-cart-price-row">
                          <label class="pos-cart-field-label">
                            Discount
                            <small v-if="canApplyManualDiscount(item) && item.quantity > 0" class="pos-cart-hint">
                              max {{ money(calculateMaxDiscount(item)) }}
                            </small>
                          </label>
                          <b-input
                            size="sm"
                            type="number"
                            :min="0"
                            :max="calculateMaxDiscount(item)"
                            v-model="item.discount_offered"
                            class="pos-cart-discount-input"
                            :disabled="!canApplyManualDiscount(item)"
                            :class="{ 'is-invalid': hasDiscountError(item) }"
                            @change="onDiscountChange(item)"
                            @blur="onDiscountChange(item)"
                          ></b-input>
                        </div>
                        <div class="pos-cart-price-row pos-cart-price-row--total">
                          <span class="pos-cart-field-label">Line total</span>
                          <strong>{{ money(lineTotal(item)) }}</strong>
                        </div>
                      </div>

                      <div class="pos-cart-item-actions">
                        <b-button variant="outline-danger" size="sm" @click="removeItem(getItemOriginalIndex(item))">
                          <b-icon icon="trash"></b-icon> Remove
                        </b-button>
                      </div>
                    </div>
                  </transition-group>
                </div>

                <div v-if="!visibleItems.length" class="pos-cart-empty">
                  <b-icon icon="cart" font-scale="3"></b-icon>
                  <p>The cart is empty.</p>
                  <b-button size="sm" class="pos-btn-primary" @click="productModalTab = 0">
                    <b-icon icon="plus"></b-icon> Add products
                  </b-button>
                </div>

                <div v-if="visibleItems.length" class="pos-cart-footer">
                  <div class="pos-cart-footer-stats">
                    <div>
                      <span>Subtotal</span>
                      <strong>{{ money(invoice.edit && !hasNewItems ? getExistingSubTotal() : getSubTotal()) }}</strong>
                    </div>
                    <div>
                      <span>Tax</span>
                      <strong>{{ money(invoice.edit && !hasNewItems ? getExistingSalesTax() : getSalesTax()) }}</strong>
                    </div>
                    <div class="is-grand">
                      <span>Grand total</span>
                      <strong>{{ money(invoice.edit && !hasNewItems ? getExistingGrandTotal() : getGrandTotal()) }}</strong>
                    </div>
                  </div>
                  <div class="pos-cart-footer-actions">
                    <span class="pos-cart-balance" :class="{ 'is-due': currentBalance > 0.009 }">
                      <span>Balance</span>
                      <strong>{{ money(currentBalance) }}</strong>
                    </span>
                    <b-button size="sm" class="pos-btn-ghost" @click="productModalTab = 0">
                      <b-icon icon="plus"></b-icon> Add more
                    </b-button>
                    <b-button
                      v-if="showExchangeBar"
                      class="pos-btn-primary"
                      size="sm"
                      @click="continueToSettlement"
                    >
                      Continue to settlement
                    </b-button>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>

          <template #modal-footer>
  <div class="pos-modal-foot">
    <b-button variant="link" class="pos-link" @click="openInvoicePreview">
      <b-icon icon="file-earmark-text"></b-icon> View invoice
    </b-button>
    <div class="pos-modal-foot-actions">
      <span class="pos-foot-note">{{ visibleItems.length }} in cart, {{ money(getOrderTotal()) }}</span>
      <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-products')">Close</b-button>
      <b-button v-if="productModalTab === 0" class="pos-btn-primary" size="sm"
                @click="productModalTab = 1"
                :disabled="!invoice.items.length">
        Done → Cart ({{ visibleItems.length }})
      </b-button>
      <b-button v-else-if="showExchangeBar" class="pos-btn-primary" size="sm" @click="continueToSettlement">
        Continue to settlement
      </b-button>
      <b-button v-else class="pos-btn-primary" size="sm" @click="saveProductsSection">
        Save products
      </b-button>
    </div>
  </div>
</template>
        </b-modal>

        <!-- MERCHANDISE DUE -->
        <b-modal v-if="invoice.edit" id="pos-modal-merchandisedue"
                 title="Merchandise due" size="xl" scrollable centered
                 content-class="pos-modal" dialog-class="pos-dialog">
          <p class="pos-modal-lead">
            These items were refunded and store credit has been issued.
            The physical merchandise must be marked received back at the store
            or warehouse before the refund can be processed.
            When you mark it received, the stock is added back to the
            <strong>return-to</strong> location you pick below, and any exchange
            items that were on hold will move into the normal fulfillment pipeline.
          </p>

          <div v-if="merchandiseDueLoading && !merchandiseDueItems.length" class="pos-panel text-center">
            <b-spinner small></b-spinner>
            <span class="ml-2">Loading merchandise due...</span>
          </div>

          <div v-else-if="!merchandiseDueItems.length" class="pos-panel text-center">
            <b-icon icon="check-circle-fill" font-scale="2" class="text-success"></b-icon>
            <p class="pos-empty mt-2 mb-0">No merchandise is due for this invoice.</p>
          </div>

          <div v-else class="pos-panel">
            <div class="d-flex justify-content-between align-items-center flex-wrap mb-2" style="gap: 10px;">
              <div>
                <strong>{{ merchandiseDuePending.length }}</strong> line(s) pending,
                <strong>{{ merchandiseDueUnits }}</strong> unit(s) to receive.
              </div>
              <b-button size="sm" class="pos-btn-primary"
                        :disabled="!merchandiseDuePending.length || merchandiseDueLoading"
                        @click="markAllReceived">
                <b-icon icon="check2-all"></b-icon> Mark all received
              </b-button>
            </div>

            <div style="overflow-x: auto;">
              <table class="pos-mini-table pos-md-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>SKU</th>
                    <th class="text-right">Return qty</th>
                    <th class="text-right">Received</th>
                    <th class="text-right">Pending</th>
                    <th>Status</th>
                    <th style="min-width: 460px;">Receive now</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in merchandiseDueItems" :key="'mdr-' + row.id"
                      :style="{ background: row.state === 'received' ? '#E4F0E9' : '#FFF7E6' }">
                    <td>
                      <div class="pos-doc-strong">{{ row.product_name || row.product_sku }}</div>
                      <small class="text-muted" v-if="row.return_locsub && row.state === 'received'">
                        Returned to: {{ formatReceiptLocsub(row.return_locsub) }}
                      </small>
                    </td>
                    <td>{{ row.product_sku }}</td>
                    <td class="text-right">{{ row.return_quantity }}</td>
                    <td class="text-right">{{ row.received_quantity }}</td>
                    <td class="text-right pos-doc-strong">{{ row.pending_quantity }}</td>
                    <td>
                      <span class="pos-chip"
                            :class="row.state === 'received' ? 'is-ok' : 'is-warn'">
                        {{ row.state === 'received' ? 'Received' : 'Pending receipt' }}
                      </span>
                    </td>
                    <td>
                      <template v-if="row.state === 'pending_receipt'">
                        <div class="pos-md-receive-cell">

                          <div class="pos-md-field">
                            <label class="pos-md-field-label">Return to</label>
                            <b-form-select
                              size="sm"
                              class="pos-md-locsub"
                              v-model="merchandiseDueReturnLocsub[row.id]"
                              :options="getReturnLocationOptions(row)"
                              :disabled="merchandiseDueLoading"
                            ></b-form-select>
                            <small
                              v-if="getReturnLocationHint(row)"
                              class="pos-md-hint"
                              :class="getReturnLocationHintClass(row)"
                            >
                              <b-icon :icon="getReturnLocationHintIcon(row)"></b-icon>
                              {{ getReturnLocationHint(row) }}
                            </small>
                          </div>

                          <div class="pos-md-qty-row">
                            <div class="pos-md-field pos-md-field--qty">
                              <label class="pos-md-field-label">Qty</label>
                              <b-input
                                size="sm"
                                type="number"
                                min="1"
                                :max="row.pending_quantity"
                                v-model="merchandiseDueReceivedQty[row.id]"
                                :disabled="merchandiseDueLoading"
                              ></b-input>
                            </div>

                            <b-button
                              size="sm"
                              class="pos-btn-primary pos-md-receive-btn"
                              @click="markReceiptReceived(row)"
                              :disabled="merchandiseDueLoading || !merchandiseDueReturnLocsub[row.id]"
                            >
                              <b-spinner small v-if="merchandiseDueLoading"></b-spinner>
                              <b-icon v-else icon="check2"></b-icon>
                              Receive
                            </b-button>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="pos-md-received-info">
                          <div>
                            <b-icon icon="check-circle-fill" class="text-success"></b-icon>
                            <strong>Received</strong>
                          </div>
                          <small class="text-muted">
                            by {{ row.received_by || '—' }}
                            <template v-if="row.received_ts">
                              · {{ formatReceivedTs(row.received_ts) }}
                            </template>
                          </small>
                          <small v-if="row.return_locsub" class="text-muted">
                            <b-icon icon="geo-alt-fill"></b-icon>
                            {{ formatReceiptLocsub(row.return_locsub) }}
                          </small>
                        </div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview">
                <b-icon icon="file-earmark-text"></b-icon> View invoice
              </b-button>
              <div class="pos-modal-foot-actions">
                <span class="pos-foot-note">
                  <template v-if="hasMerchandiseDue">
                    Refund is blocked until all units are received. Reselect is allowed.
                  </template>
                  <template v-else>
                    All merchandise received — refunds and reselect both work normally now.
                  </template>
                </span>
                <b-button class="pos-btn-ghost" size="sm"
                          @click="$bvModal.hide('pos-modal-merchandisedue')">Close</b-button>
                <b-button class="pos-btn-primary" size="sm"
                          @click="$bvModal.hide('pos-modal-merchandisedue'); $bvModal.show('pos-modal-returns')">
                  Continue to returns
                </b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- RETURNS & EXCHANGE -->
        <b-modal v-if="invoice.edit" id="pos-modal-returns" title="Returns and exchange" size="xl" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <ol class="pos-steps" aria-label="Return steps">
            <li :class="{ 'is-active': returnsStep === 1, 'is-done': returnsStep > 1 }"><span>1</span> Items and charges</li>
            <li :class="{ 'is-active': returnsStep === 2, 'is-done': returnsStep > 2 }"><span>2</span> Add to store credit</li>
            <li :class="{ 'is-active': returnsStep === 3 }"><span>3</span> Refund or reselect</li>
          </ol>

          <template v-if="returnsStep === 1">
            <p class="pos-modal-lead">Set how many of each item is coming back and any delivery, white glove, setup or warranty to refund, then validate.</p>

            <div v-if="creditRefreshing" class="pos-panel text-center mb-3">
              <b-spinner small></b-spinner> <span class="ml-2">Creating store credit...</span>
            </div>

            <div class="pos-panel mb-3" v-else>
              <table class="pos-mini-table" v-if="refund_items.length > 0">
                <thead>
                  <tr><th>Item</th><th>Status</th><th>Return qty</th><th class="text-right">Credit</th><th></th></tr>
                </thead>
                <tbody>
                  <tr v-for="(ritem, ridx) in refund_items" :key="'ri-' + ridx">
                    <td>
                      <div class="pos-doc-strong">{{ ritem.product_name || ritem.product_sku }}</div>
                      <small class="text-muted">{{ ritem.product_sku }}<span v-if="ritem.itemid"> (item {{ ritem.itemid }})</span></small>
                    </td>
                    <td>
                      <span class="pos-chip" :class="get_item_status(ritem) === 'delivered' ? 'is-warn' : ''">
                        {{ get_item_status(ritem) === 'delivered' ? 'Returned' : 'Cancelled' }}
                      </span>
                    </td>
                    <td>
                      <div class="d-flex align-items-center" style="gap: 6px;">
                        <b-input size="sm" v-model="ritem.return_quantity" type="number" min="1" style="width: 80px;"
                                 :max="ritem.quantity - (ritem.refunded_so_far_quantity || 0)"
                                 @input="current_refund_validated = false"></b-input>
                        <span class="text-muted">of {{ ritem.quantity - (ritem.refunded_so_far_quantity || 0) }}</span>
                      </div>
                    </td>
                    <td class="text-right pos-doc-strong">{{ money(refundLineCredit(ritem)) }}</td>
                    <td class="text-right">
                      <b-button size="sm" class="pos-btn-ghost" @click="keepReturnItem(ritem)">Keep item</b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-center">
                <p class="pos-empty mb-2">No items are marked to cancel or return. Open the cart and use Cancel or Return on an item.</p>
                <b-button size="sm" class="pos-btn-primary" @click="openCartForReturns">Open cart</b-button>
              </div>
            </div>

            <div class="pos-panel">
              <div class="pos-label mb-2">Charges to refund</div>
              <b-row>
                <b-col md="3">
                  <b-form-group label="Delivery" label-class="pos-label">
                    <b-input-group size="sm" prepend="$" :append="'of ' + money(invoice.delivery_charges - refund_charges.delivery_refunded_sofar)">
                      <b-input v-model="refund_charges.delivery_refunded_currently" type="number" @input="current_refund_validated = false"
                               :disabled="invoice.delivery_charges - refund_charges.delivery_refunded_sofar == 0"></b-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="White Glove" label-class="pos-label">
                    <b-input-group size="sm" prepend="$" :append="'of ' + money(invoice.whiteglove_charges - refund_charges.whiteglove_refunded_sofar)">
                      <b-input v-model="refund_charges.whiteglove_refunded_currently" type="number" @input="current_refund_validated = false"
                               :disabled="invoice.whiteglove_charges - refund_charges.whiteglove_refunded_sofar == 0"></b-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Setup" label-class="pos-label">
                    <b-input-group size="sm" prepend="$" :append="'of ' + money(invoice.setup_charges - refund_charges.setup_refunded_sofar)">
                      <b-input v-model="refund_charges.setup_refunded_currently" type="number" @input="current_refund_validated = false"
                               :disabled="invoice.setup_charges - refund_charges.setup_refunded_sofar == 0"></b-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Warranty" label-class="pos-label">
                    <b-input-group size="sm" prepend="$" :append="'of ' + money(invoice.warranty_charges - refund_charges.warranty_refunded_sofar)">
                      <b-input v-model="refund_charges.warranty_refunded_currently" type="number" @input="current_refund_validated = false"
                               :disabled="invoice.warranty_charges - refund_charges.warranty_refunded_sofar == 0"></b-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
              <small v-if="invoice.is_layaway" class="text-muted d-block">Returns on layaway invoices are capped at what the customer has paid so far.</small>
            </div>
          </template>

          <template v-else-if="returnsStep === 2">
            <p class="pos-modal-lead">Check the amounts. Adding them creates store credit on this invoice and takes the items off the sale.</p>
            <div class="pos-panel">
              <dl class="pos-dl">
                <template v-for="(ritem, ridx) in refund_items">
                  <dt :key="'c2dt' + ridx ">{{ ritem.product_name || ritem.product_sku }} x {{ parseInt(ritem.return_quantity) || 0 }}</dt>
                  <dd :key="'c2dd' + ridx">{{ money(refundLineCredit(ritem)) }}</dd>
                </template>
                <template v-if="parseFloat(refund_charges.delivery_refunded_currently) > 0">
                  <dt>Delivery refund</dt><dd>{{ money(refund_charges.delivery_refunded_currently) }}</dd>
                </template>
                <template v-if="parseFloat(refund_charges.whiteglove_refunded_currently) > 0">
                  <dt>White Glove refund</dt><dd>{{ money(refund_charges.whiteglove_refunded_currently) }}</dd>
                </template>
                <template v-if="parseFloat(refund_charges.setup_refunded_currently) > 0">
                  <dt>Setup refund</dt><dd>{{ money(refund_charges.setup_refunded_currently) }}</dd>
                </template>
                <template v-if="parseFloat(refund_charges.warranty_refunded_currently) > 0">
                  <dt>Warranty refund</dt><dd>{{ money(refund_charges.warranty_refunded_currently) }}</dd>
                </template>
                <dt>Sales tax refund</dt><dd>{{ money(refund_charges.tax_refunded_currently) }}</dd>
                <dt class="pos-dl-strong">Store credit to add</dt><dd class="pos-dl-strong is-good">{{ money(refund_charges.total_refunded_currently) }}</dd>
              </dl>
            </div>
          </template>

          <template v-else>
            <div class="pos-credit-hero">
              <span>Store credit on this invoice</span>
              <strong>{{ money(getAvailableStoreCredit()) }}</strong>
            </div>

            <!--
              ✅ CHANGED — refund still blocked when merchandise pending;
              reselect allowed.
            -->
            <div v-if="hasMerchandiseDue" class="pos-exchange-strip is-pending mb-3">
              <div>
                <strong>{{ merchandiseDuePending.length }} item(s) still awaiting receipt.</strong>
                Refunds are blocked until the physical merchandise is received.
                <strong>Reselect is allowed</strong> — new items will be marked PENDING until the return is received.
              </div>
              <b-button size="sm" class="pos-btn-primary"
                        @click="$bvModal.hide('pos-modal-returns'); openPosTab('merchandisedue')">
                Open Merchandise due
              </b-button>
            </div>

            <div class="pos-choice-grid">
              <!-- Refund card stays disabled while merchandise is pending -->
              <button type="button" class="pos-choice" :class="{ 'is-selected': exchangeChoice === 'refund', 'is-disabled': hasMerchandiseDue }" @click="chooseRefundCredit">
                <b-icon icon="cash-stack" font-scale="1.4"></b-icon>
                <strong>Refund the customer</strong>
                <span v-if="hasMerchandiseDue">Blocked until all returned merchandise is marked received.</span>
                <span v-else>Pay the credit back by cash, card, finance or another method.</span>
              </button>
              <!-- Reselect card is always available -->
              <button type="button" class="pos-choice" :class="{ 'is-selected': exchangeChoice === 'reselect' }" @click="chooseReselect">
                <b-icon icon="arrow-left-right" font-scale="1.4"></b-icon>
                <strong>Reselect items</strong>
                <span>Use the credit toward new products. Items stay PENDING until the return is received.</span>
              </button>
            </div>

            <div v-if="exchangeSummary.hasNewItems" class="pos-panel mt-3">
              <h5 class="pos-cart-heading">Exchange summary</h5>
              <dl class="pos-dl">
                <dt>Store credit</dt><dd class="is-good">{{ money(exchangeSummary.credit) }}</dd>
                <dt>New items ({{ newItems.length }}) with charges and tax</dt><dd>{{ money(exchangeSummary.newTotal) }}</dd>
                <dt>Credit used</dt><dd>-{{ money(exchangeSummary.applied) }}</dd>
              </dl>
              <div class="pos-settle-result" :class="'is-' + exchangeSummary.outcome">
                <div>
                  <span>{{ exchangeOutcomeLabel }}</span>
                  <strong>{{ money(exchangeOutcomeAmount) }}</strong>
                </div>
                <p v-if="exchangeSummary.outcome === 'refund'">The credit is more than the new items. Refund the difference to finish the exchange.</p>
                <p v-else-if="exchangeSummary.outcome === 'pay'">The new items cost more than the credit. Take the difference in Payment.</p>
                <p v-else>The credit covers the new items exactly. Update the sale to finish.</p>
              </div>
            </div>

            <b-button variant="link" class="pos-link mt-3" @click="startNewReturn">Return more items or charges</b-button>
          </template>

          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <template v-if="returnsStep === 1">
                  <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-returns')">Close</b-button>
                  <b-button class="pos-btn-primary" size="sm" @click="validate_refund" :disabled="!canValidateReturn || creditRefreshing">Validate</b-button>
                </template>
                <template v-else-if="returnsStep === 2">
                  <b-button class="pos-btn-ghost" size="sm" @click="current_refund_validated = false">Back</b-button>
                  <b-button variant="danger" size="sm" @click="submit_refund_wrapper" :disabled="loading">
                    Add {{ money(refund_charges.total_refunded_currently) }} to store credit
                  </b-button>
                </template>
                <template v-else>
                  <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-returns')">Close</b-button>
                  <b-button v-if="exchangeSummary.hasNewItems" class="pos-btn-primary" size="sm" @click="settleExchange">{{ exchangeActionLabel }}</b-button>
                </template>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- PAYMENT HISTORY -->
        <b-modal v-if="invoice.edit" id="pos-modal-paymenthistory" title="Payment history" size="lg" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <div v-if="payment_history.length === 0" class="pos-panel text-center">
            <p class="pos-empty">No payments loaded yet.</p>
            <b-button v-if="invoice.invoice_id" class="pos-btn-primary mt-2" size="sm" @click="loadPaymentMethodsOnly" :disabled="loading">
              <b-spinner small v-if="loading"></b-spinner> Load payment history
            </b-button>
          </div>
          <div v-else class="pos-history">
            <div v-for="(pm, pmidx) in payment_history" :key="pmidx" class="pos-history-row">
              <div>
                <div class="pos-line-title">{{ paymentMethodLabel(pm.value) }}</div>
                <div class="pos-line-sub">
                  <span v-if="pm.value == 'cc' && pm.name">Card ****{{ pm.name }}</span>
                  <span v-if="pm.value == 'store_credit' && pm.store_credit_invoice_id">Invoice {{ pm.store_credit_invoice_id }}</span>
                  <span v-if="pm.value == 'finance' && pm.name">{{ pm.name }}</span>
                  <span v-if="pm.value == 'finance' && pm.lease_id">Lease {{ pm.lease_id }}</span>
                  <span v-if="pm.value == 'tax_exempt' && pm.name">{{ pm.name }}</span>
                  <span v-if="pm.value == 'tax_exempt' && pm.ein">EIN {{ pm.ein }}</span>
                  <span>{{ pm.ts ? payment_ts_format(pm.ts) : 'Pending' }}</span>
                </div>
                <small v-if="pm.layaway_sls_tax" class="text-muted">Sales tax payment</small>
                <small v-if="pm.value == 'finance' && pm.charged_tax_itself == true && pm.name != 'TAX EXEMPT'" class="text-muted">Finance company charges tax itself</small>
              </div>
              <strong>{{ money(pm.amount) }}</strong>
            </div>
            <dl class="pos-dl mt-3">
              <dt class="pos-dl-strong">Total paid</dt><dd class="pos-dl-strong">{{ money(getTotalPaymentsFromHistory()) }}</dd>
              <dt v-if="getAvailableStoreCredit() > 0">Available store credit</dt><dd v-if="getAvailableStoreCredit() > 0">{{ money(getAvailableStoreCredit()) }}</dd>
            </dl>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('paymenthistory')">Done</b-button>
            </div>
          </template>
        </b-modal>

        <!-- REFUNDS -->
        <b-modal v-if="invoice.edit" id="pos-modal-refunds" title="Refund history" size="xl" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-panel">
            <Refundsummarysales v-on:refunds-history="summarize_refunds_history"
                                :viewdata="{ invoice_id: invoice.invoice_id, packet: packet, filterType: 'refund' }"
                                :key="'refunds-' + refundsSummaryKey" />
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('refunds')">Done</b-button>
            </div>
          </template>
        </b-modal>

        <!-- STORE CREDITS -->
        <b-modal v-if="invoice.edit" id="pos-modal-storecredits" title="Store credits" size="xl" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-panel">
            <Refundsummarysales v-on:refunds-history="summarize_refunds_history"
                                :viewdata="{ invoice_id: invoice.invoice_id, packet: packet, filterType: 'return' }"
                                :key="'store-credits-' + refundsSummaryKey" />
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('storecredits')">Done</b-button>
            </div>
          </template>
        </b-modal>

        <!-- CHARGES -->
        <b-modal id="pos-modal-charges" title="Charges" size="lg" centered scrollable content-class="pos-modal" dialog-class="pos-dialog">
          <b-row>
            <b-col md="5">
              <div class="pos-panel mb-3">
                <template v-if="invoice.edit == true">
                  <div class="pos-charge-grid">
                    <div class="pos-charge-field">
                      <label class="pos-label">Delivery</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.new_delivery_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">White Glove</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.new_whiteglove_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">Setup</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.new_setup_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">Warranty</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.new_warranty_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="pos-charge-grid">
                    <div class="pos-charge-field">
                      <label class="pos-label">Delivery</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.delivery_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">White Glove</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.whiteglove_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">Setup</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.setup_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                    <div class="pos-charge-field">
                      <label class="pos-label">Warranty</label>
                      <b-input-group size="sm" prepend="$">
                        <b-input v-model="invoice.warranty_charges" type="number" placeholder="0.00"></b-input>
                      </b-input-group>
                    </div>
                  </div>
                  <div class="pos-charge-layaway mt-3">
                    <label class="pos-label">Layaway</label>
                    <b-form-select size="sm" :options="layaway_options" v-model="invoice.is_layaway" v-on:change="reset_pms_if_layaway"></b-form-select>
                    <small class="pos-charge-hint">Layaway removes sales tax and clears entered payments.</small>
                  </div>
                </template>
              </div>
            </b-col>
            <b-col md="7">
              <div class="pos-panel">
                <dl class="pos-dl pos-breakdown">
                  <dt>Total discount</dt><dd>{{ money(getTotalDiscount()) }}</dd>
                  <dt>Order total</dt><dd>{{ money(getOrderTotal()) }}</dd>
                  <dt>Total charges</dt><dd>{{ money(getChargesTotal()) }}</dd>
                  <dt>Subtotal</dt><dd>{{ money(getSubTotal()) }}</dd>
                  <dt>Taxable subtotal</dt><dd>{{ money(adjust_to_taxable_amount(getSubTotal())) }}</dd>
                  <dt>Sales tax</dt><dd>{{ money(getSalesTax()) }}</dd>
                  <dt>Special discounts</dt><dd class="is-good">{{ money(getTotalSpecialDiscounts()) }}</dd>
                  <template v-if="invoice.edit">
                    <dt>Available credit</dt><dd class="is-good">{{ money(getAvailableStoreCredit()) }}</dd>
                    <dt>Credit applied</dt><dd class="is-good">{{ money(getStoreCreditApplied()) }}</dd>
                    <dt>Credit left to refund</dt><dd>{{ money(getRemainingStoreCredit()) }}</dd>
                  </template>
                  <dt class="pos-dl-strong">Grand total</dt><dd class="pos-dl-strong">{{ money(getGrandTotal()) }}</dd>
                  <dt>Balance</dt><dd :class="{ 'is-due': currentBalance > 0.009 }">{{ money(currentBalance) }}</dd>
                </dl>
              </div>
            </b-col>
          </b-row>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-charges')">Close</b-button>
                <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('charges')">Save charges</b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- TERMS -->
        <b-modal id="pos-modal-terms" title="Terms and signature" size="lg" centered scrollable content-class="pos-modal" dialog-class="pos-dialog">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-2" style="gap: 10px;">
            <p class="pos-modal-lead mb-0">Read the policy with the customer, check each item, then collect the signature.</p>
            <div class="btn-group btn-group-sm" role="group" aria-label="Policy language">
              <b-button :class="policyLanguage === 'en' ? 'pos-btn-primary' : 'pos-btn-ghost'" @click="policyLanguage = 'en'" size="sm">English</b-button>
              <b-button :class="policyLanguage === 'es' ? 'pos-btn-primary' : 'pos-btn-ghost'" @click="policyLanguage = 'es'" size="sm">Español</b-button>
            </div>
          </div>
          <div class="pos-panel pos-policy mb-3">
            <div v-html="currentPolicyText" class="policy-content"></div>
          </div>
          <div class="pos-panel pos-agree">
            <b-form-checkbox v-model="policyAgreed" class="mb-3">
              <span v-if="policyLanguage === 'es'">He leído y acepto los Términos y Condiciones</span>
              <span v-else>I have read and agree to the Terms &amp; Conditions</span>
            </b-form-checkbox>
            <b-form-checkbox v-model="warrantyRefused" class="mb-3" :disabled="hasWarrantyCharges">
              <span v-if="policyLanguage === 'es'">El cliente se negó a comprar garantías extendidas</span>
              <span v-else>Customer refused to purchase extended warranties</span>
              <small class="d-block text-muted" v-if="hasWarrantyCharges">
                {{ policyLanguage === 'es'
                  ? 'Deshabilitado: los cargos de garantía (' + formatCurrency(getWarrantyTotal()) + ') están incluidos en esta venta'
                  : 'Disabled because warranty charges (' + formatCurrency(getWarrantyTotal()) + ') are on this sale' }}
              </small>
            </b-form-checkbox>
            <b-form-checkbox v-model="deliveryWaiverAgreed" class="mb-3">
              <span v-if="policyLanguage === 'es'">Entiendo que la Entrega Estándar NO incluye la eliminación de cajas/embalajes.</span>
              <span v-else>I understand that Standard Delivery does NOT include the removal of boxes/packaging.</span>
            </b-form-checkbox>
            <b-form-checkbox v-model="spacePreparationAgreed" class="mb-3">
              <span v-if="policyLanguage === 'es'">Falla en la Preparación del Espacio: Si la habitación designada no está despejada y lista para la instalación al momento de la entrega, el equipo de entrega realizará solo una "Entrega sin instalación".</span>
              <span v-else>Failure to Prepare Space: If the designated room is not cleared and ready for setup upon arrival, the delivery team will perform a "Drop-Off Delivery" only.</span>
            </b-form-checkbox>
            <b-form-checkbox v-if="hasStorePickupItem" v-model="deliveryDamageWaiverAgreed" class="mb-3">
              <span v-if="policyLanguage === 'es'">Una vez que el mueble ha sido recogido y sale de la propiedad de la tienda, la tienda no se hace responsable de ningún daño que ocurra durante la carga, tránsito, descarga o montaje. Los clientes no pueden abrir ningún reclamo por daños físicos después de tomar posesión de los artículos.</span>
              <span v-else>Once the furniture has been picked up and leaves the store property, the store is not responsible for any damages that occur during loading, transit, unloading, or assembly. Customers cannot open any claims for physical damage after taking possession of the items.</span>
            </b-form-checkbox>

            <div class="pos-signature-row">
              <div>
                <div class="pos-label mb-0">Customer signature</div>
                <small class="text-muted">{{ invoice.has_signature ? 'Signature captured.' : 'Required before the sale can be submitted.' }}</small>
              </div>
              <b-button :class="invoice.has_signature ? 'pos-btn-ghost' : 'pos-btn-primary'" size="sm" @click="openInvoiceSignatureModal" :disabled="invoice.has_signature">
                <b-icon :icon="invoice.has_signature ? 'check2' : 'pen'"></b-icon>
                {{ invoice.has_signature ? 'Signed' : 'Collect signature' }}
              </b-button>
            </div>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-terms')">Close</b-button>
                <b-button class="pos-btn-primary" size="sm" @click="saveTermsSection">Save agreements</b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- PAYMENT -->
        <b-modal id="pos-modal-payment" title="Payment" size="xl" centered scrollable content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-pay-summary">
            <div v-if="invoice.edit && getStoreCreditApplied() > 0"><span>Store credit used</span><strong class="is-good-text">-{{ money(getStoreCreditApplied()) }}</strong></div>
            <div v-if="(parseFloat(invoice.gift_card_discount) || 0) > 0"><span>Gift card applied</span><strong class="is-good-text">-{{ money(invoice.gift_card_discount) }}</strong></div>
            <div><span>{{ (invoice.edit && getStoreCreditApplied() > 0) || (parseFloat(invoice.gift_card_discount) || 0) > 0 ? 'Customer pays' : 'Grand total' }}</span><strong>{{ money(getGrandTotal()) }}</strong></div>
            <div><span>Entered</span><strong>{{ money(activePaymentMethods.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)) }}</strong></div>
            <div :class="{ 'is-due': currentBalance > 0.009 }"><span>Balance</span><strong>{{ money(currentBalance) }}</strong></div>
          </div>
          <b-alert show variant="info" class="pos-alert">Add non-taxable forms of payment (finance, tax exempt) first so tax is calculated correctly.</b-alert>

          <div v-if="invoice.edit == true" class="pos-pay-grid">
            <div v-for="(payment_method, pmidx) in invoice.new_payment_methods" :key="'npm-' + pmidx" class="pos-pay-card">
              <div class="pos-pay-card-head">
                <b-form-select value-field="value" text-field="text" :options="new_payment_method_options" v-model="invoice.new_payment_methods[pmidx].value" size="sm">
                  <template #first><b-form-select-option :value="null" disabled>Choose method</b-form-select-option></template>
                </b-form-select>
                <b-button size="sm" variant="link" class="text-danger" @click="removePaymentMethod(pmidx)" title="Remove payment"><b-icon icon="trash"></b-icon></b-button>
              </div>

              <template v-if="invoice.new_payment_methods[pmidx].value == 'cash'">
                <b-form-group label="Amount tendered" label-class="pos-label">
                  <b-input size="sm" type="number" v-model="invoice.new_payment_methods[pmidx].tendered_amount" @input="calculateCashChangeNew(pmidx)"
                           :state="getCashChangeStateNew(pmidx)" placeholder="Cash received" step="0.01" min="0"></b-input>
                </b-form-group>
                <dl class="pos-dl">
                  <dt>Amount due</dt><dd>{{ money(getCashAmountDueNew(pmidx)) }}</dd>
                  <dt>Change due</dt><dd :class="{ 'is-good': getCashChangeNew(pmidx) > 0 }">{{ money(getCashChangeNew(pmidx)) }}</dd>
                  <dt class="pos-dl-strong">Applied to sale</dt><dd class="pos-dl-strong">{{ money(invoice.new_payment_methods[pmidx].amount) }}</dd>
                </dl>
              </template>

              <template v-if="invoice.new_payment_methods[pmidx].value == 'cc'">
                <b-form-group label="Amount" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                <b-form-group label="Last 4 digits" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].name" maxlength="4"></b-input></b-form-group>
              </template>

              <template v-if="invoice.new_payment_methods[pmidx].value == 'finance'">
                <b-form-group label="Company" label-class="pos-label">
                  <b-form-select value-field="value" text-field="text" :options="finance_only_companies" v-model="invoice.new_payment_methods[pmidx].name"
                                 v-on:change="onFinanceCompanySelected(invoice.new_payment_methods[pmidx])" size="sm">
                    <template #first><b-form-select-option :value="null" disabled>Select finance company</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
                <template v-if="invoice.new_payment_methods[pmidx].name != null">
                  <b-form-group label="Approved amount" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].approved_amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Used amount" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Lease #" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].lease_id"></b-input></b-form-group>
                  <b-form-group label="Misc info" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].misc_info"></b-input></b-form-group>
                </template>
              </template>

              <template v-if="invoice.new_payment_methods[pmidx].value == 'tax_exempt'">
                <b-form-group label="Tax exempt company" label-class="pos-label">
                  <b-form-select value-field="value" text-field="text" :options="tax_exempt_companies" v-model="invoice.new_payment_methods[pmidx].name" size="sm"
                                 @change="checkTaxExemptPasswordRequirement(invoice.new_payment_methods[pmidx])">
                    <template #first><b-form-select-option :value="null" disabled>Select tax exempt company</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
                <template v-if="invoice.new_payment_methods[pmidx].name">
                  <b-form-group label="Amount" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Company password" label-class="pos-label">
                    <b-input size="sm" :type="invoice.new_payment_methods[pmidx].showPassword ? 'text' : 'password'" v-model="invoice.new_payment_methods[pmidx].tax_exempt_password"
                             :state="invoice.new_payment_methods[pmidx].passwordValid" @blur="validateTaxExemptPassword(invoice.new_payment_methods[pmidx])"
                             :disabled="invoice.new_payment_methods[pmidx].passwordValid === true"></b-input>
                    <b-form-checkbox v-model="invoice.new_payment_methods[pmidx].showPassword" size="sm" class="mt-1">Show password</b-form-checkbox>
                  </b-form-group>
                  <b-form-group label="Misc info" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].misc_info"></b-input></b-form-group>
                </template>
              </template>

              <template v-if="invoice.new_payment_methods[pmidx].value == 'store_credit'">
                <b-form-group label="Invoice #" label-class="pos-label">
                  <b-input size="sm" v-model="invoice.new_payment_methods[pmidx].store_credit_invoice_id"></b-input>
                  <b-button size="sm" class="pos-btn-ghost mt-2" @click="apply_store_credit(pmidx)">Get remaining amount</b-button>
                </b-form-group>
                <b-form-group label="Available" label-class="pos-label"><b-input size="sm" v-model="invoice.new_payment_methods[pmidx].available_amount" :disabled="true"></b-input></b-form-group>
                <b-form-group v-if="invoice.new_payment_methods[pmidx].available_amount > 0" label="Apply amount" label-class="pos-label" description="Must be less than the available amount.">
                  <b-input size="sm" v-model="invoice.new_payment_methods[pmidx].amount" type="number"></b-input>
                </b-form-group>
              </template>
            </div>
            <button type="button" class="pos-pay-add" @click="addPaymentMethod"><b-icon icon="plus"></b-icon> Add payment</button>
          </div>

          <div v-else class="pos-pay-grid">
            <div v-for="(payment_method, pmidx) in invoice.payment_methods" :key="'pm-' + pmidx" class="pos-pay-card">
              <div class="pos-pay-card-head">
                <b-form-select value-field="value" text-field="text" :options="payment_method_options" v-model="invoice.payment_methods[pmidx].value" size="sm">
                  <template #first><b-form-select-option :value="null" disabled>Choose method</b-form-select-option></template>
                </b-form-select>
                <b-button size="sm" variant="link" class="text-danger" @click="removePaymentMethod(pmidx)" title="Remove payment"><b-icon icon="trash"></b-icon></b-button>
              </div>

              <template v-if="invoice.payment_methods[pmidx].value == 'cash'">
                <b-form-group label="Amount tendered" label-class="pos-label">
                  <b-input size="sm" type="number" v-model="invoice.payment_methods[pmidx].tendered_amount" @input="calculateCashChange(pmidx)"
                           :state="getCashChangeState(pmidx)" placeholder="Cash received" step="0.01" min="0"></b-input>
                </b-form-group>
                <dl class="pos-dl">
                  <dt>Amount due</dt><dd>{{ money(getCashAmountDue(pmidx)) }}</dd>
                  <dt>Change due</dt><dd :class="{ 'is-good': getCashChange(pmidx) > 0 }">{{ money(getCashChange(pmidx)) }}</dd>
                  <dt class="pos-dl-strong">Applied to sale</dt><dd class="pos-dl-strong">{{ money(invoice.payment_methods[pmidx].amount) }}</dd>
                </dl>
              </template>

              <template v-if="invoice.payment_methods[pmidx].value == 'cc'">
                <b-form-group label="Amount" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                <b-form-group label="Last 4 digits" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].name" maxlength="4"></b-input></b-form-group>
              </template>

              <template v-if="invoice.payment_methods[pmidx].value == 'finance'">
                <b-form-group label="Company" label-class="pos-label">
                  <b-form-select value-field="value" text-field="text" :options="finance_only_companies" v-model="invoice.payment_methods[pmidx].name"
                                 v-on:change="onFinanceCompanySelected(invoice.payment_methods[pmidx])" size="sm">
                    <template #first><b-form-select-option :value="null" disabled>Select finance company</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
                <template v-if="invoice.payment_methods[pmidx].name != null">
                  <b-form-group label="Approved amount" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].approved_amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Used amount" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Lease #" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].lease_id"></b-input></b-form-group>
                  <b-form-group label="Misc info" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].misc_info"></b-input></b-form-group>
                </template>
              </template>

              <template v-if="invoice.payment_methods[pmidx].value == 'tax_exempt'">
                <b-form-group label="Tax exempt company" label-class="pos-label">
                  <b-form-select value-field="value" text-field="text" :options="tax_exempt_companies" v-model="invoice.payment_methods[pmidx].name" size="sm"
                                 @change="checkTaxExemptPasswordRequirement(invoice.payment_methods[pmidx])">
                    <template #first><b-form-select-option :value="null" disabled>Select tax exempt company</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
                <template v-if="invoice.payment_methods[pmidx].name">
                  <b-form-group label="Amount" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].amount" type="number"></b-input></b-form-group>
                  <b-form-group label="Company password" label-class="pos-label">
                    <b-input size="sm" :type="invoice.payment_methods[pmidx].showPassword ? 'text' : 'password'" v-model="invoice.payment_methods[pmidx].tax_exempt_password"
                             :state="invoice.payment_methods[pmidx].passwordValid" @blur="validateTaxExemptPassword(invoice.payment_methods[pmidx])"
                             :disabled="invoice.payment_methods[pmidx].passwordValid === true"></b-input>
                    <b-form-checkbox v-model="invoice.payment_methods[pmidx].showPassword" size="sm" class="mt-1">Show password</b-form-checkbox>
                  </b-form-group>
                  <b-form-group label="Cheque #" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].ein"></b-input></b-form-group>
                  <b-form-group label="Misc info" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].misc_info"></b-input></b-form-group>
                </template>
              </template>

              <template v-if="invoice.payment_methods[pmidx].value == 'store_credit'">
                <b-form-group label="Invoice #" label-class="pos-label">
                  <b-input size="sm" v-model="invoice.payment_methods[pmidx].store_credit_invoice_id"></b-input>
                  <b-button size="sm" class="pos-btn-ghost mt-2" @click="apply_store_credit(pmidx)">Get remaining amount</b-button>
                </b-form-group>
                <template v-if="invoice.payment_methods[pmidx].available_amount">
                  <b-form-group label="Available" label-class="pos-label"><b-input size="sm" v-model="invoice.payment_methods[pmidx].available_amount" :disabled="true"></b-input></b-form-group>
                  <b-form-group label="Apply amount" label-class="pos-label" description="Must be less than the available amount.">
                    <b-input size="sm" v-model="invoice.payment_methods[pmidx].amount" type="number"></b-input>
                  </b-form-group>
                </template>
              </template>
            </div>
            <button type="button" class="pos-pay-add" @click="addPaymentMethod"><b-icon icon="plus"></b-icon> Add payment</button>
          </div>

          <div class="pos-giftcard-block ">
            <div v-if="invoice.edit && invoice.gift_card_code" class="pos-giftcard-readonly">
              <div class="pos-giftcard-readonly-head">
                <span class="pos-tile-icon"><b-icon icon="gift"></b-icon></span>
                <div>
                  <div class="pos-label mb-0">Gift card (already applied)</div>
                  <small class="text-muted">Gift cards can't be changed when editing an existing invoice.</small>
                </div>
              </div>
              <dl class="pos-dl">
                <dt>Code</dt><dd>{{ invoice.gift_card_code }}</dd>
                <dt>Applied</dt><dd class="is-good">-{{ money(invoice.gift_card_discount) }}</dd>
              </dl>
            </div>

            <template v-else-if="!invoice.edit">
              <button v-if="!giftCardApplied && !showGiftCardPanel" type="button" class="pos-pay-add2 pos-giftcard-toggle"
                      @click="showGiftCardPanel = true">
                <b-icon icon="gift"></b-icon> Add gift card
              </button>

              <div v-else class="pos-pay-card pos-giftcard-card">
                <div class="pos-pay-card-head">
                  <span class="pos-label mb-0"><b-icon icon="gift"></b-icon> Gift card</span>
                  <b-button size="sm" variant="link" class="text-danger"
                            @click="cancelGiftCardPanel"
                            title="Remove gift card">
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                </div>

                <b-form-group label="Gift card code" label-class="pos-label">
                  <b-input-group>
                    <b-form-input v-model="giftCardCode"
                                  placeholder="GC-XXXX-XXXX"
                                  :disabled="giftCardApplied"
                                  @keyup.enter="validateGiftCard" />
                    <b-input-group-append>
                      <b-button class="pos-btn-primary"
                                @click="validateGiftCard"
                                :disabled="!giftCardCode || giftCardApplied || giftCardValidating">
                        <b-spinner small v-if="giftCardValidating" />
                        {{ giftCardValidating ? 'Checking...' : (giftCardApplied ? 'Applied' : 'Apply') }}
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>

                <dl v-if="giftCardApplied" class="pos-dl">
                  <dt>Applied to this sale</dt><dd class="is-good">-{{ money(giftCardAmount) }}</dd>
                  <dt>Left on card</dt><dd>{{ money(giftCardRemaining) }}</dd>
                </dl>

                <b-alert v-if="giftCardError" variant="danger" show class="mt-2 mb-0">{{ giftCardError }}</b-alert>
              </div>
            </template>
          </div>

          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <b-button class="pos-btn-ghost" size="sm" @click="$bvModal.hide('pos-modal-payment')">Close</b-button>
                <b-button v-if="invoice.edit && newItems.length && currentBalance <= 0.009" class="pos-btn-primary" size="sm" @click="handleUpdate">Update sale</b-button>
                <b-button v-else class="pos-btn-primary" size="sm" @click="savePaymentSection">Save payments</b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- REFUND METHODS -->
        <b-modal v-if="invoice.edit" id="pos-modal-refundmethods" title="Refund methods" size="xl" centered scrollable content-class="pos-modal" dialog-class="pos-dialog"
                 @show="prefillRefundMethod">
          <p class="pos-modal-lead" v-if="newItems.length">
            Store credit of {{ money(getEffectiveStoreCredit()) }} covers the new items. Refund the {{ money(getRemainingStoreCredit()) }} left over, then the sale is updated.
          </p>
          <p class="pos-modal-lead" v-else>
            Split the {{ money(getRemainingStoreCredit()) }} store credit across the methods the customer originally paid with.
          </p>
          <div class="pos-pay-summary">
            <div><span>Store credit to refund</span><strong>{{ money(getRemainingStoreCredit()) }}</strong></div>
            <div><span>Allocated</span><strong>{{ money(refund_methods.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0)) }}</strong></div>
            <div class="pos-pay-summary-actions">
              <b-button size="sm" class="pos-btn-ghost" @click="refreshAvailableRefundAmounts" :disabled="!invoice.invoice_id">
                <b-icon-arrow-repeat class="mr-1"></b-icon-arrow-repeat> Refresh limits
              </b-button>
            </div>
          </div>

          <div class="pos-panel mb-3">
            <div class="pos-label">Available by payment method</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <span v-for="(amount, method) in positiveRefundAmounts" :key="method" class="pos-chip">{{ paymentMethodLabel(method) }} {{ money(amount) }}</span>
              <span v-if="!Object.keys(positiveRefundAmounts).length" class="pos-empty">No refund methods available</span>
            </div>
          </div>

          <div class="pos-pay-grid">
            <div v-for="(refund_method, rmidx) in refund_methods" :key="'refund-' + rmidx" class="pos-pay-card">
              <div class="pos-pay-card-head">
                <b-form-select :value="refund_method.value"
                               @input="val => { refund_method.value = val; updateRefundMethodDisplay(); }"
                               :options="[{ value: null, text: 'Choose refund method', disabled: true }, ...getFilteredRefundMethodOptions().filter(opt => !refund_methods.some((rm, idx) => rm.value === opt.value && idx !== rmidx))]"
                               size="sm" :disabled="getFilteredRefundMethodOptions().length === 0"></b-form-select>
                <b-button size="sm" variant="link" class="text-danger" @click="removeRefundMethod(rmidx)" title="Remove refund method"><b-icon icon="trash"></b-icon></b-button>
              </div>

              <template v-if="refund_method.value">
                <b-form-group :label="'Refund amount (available ' + money(getRemainingAmountForMethod(refund_method.value)) + ')'" label-class="pos-label">
                  <b-input size="sm" type="number" v-model="refund_method.amount" :max="getRemainingAmountForMethod(refund_method.value)"
                           @input="validateRefundAmount(refund_method)"
                           :disabled="refund_method.value === 'cash' && getRemainingAmountForMethod('cash') <= 0"
                           :state="refund_method.amount ? !showRefundWarning(refund_method, refund_method.value) : null"></b-input>
                  <b-form-invalid-feedback :state="!showRefundWarning(refund_method, refund_method.value)">Exceeds the available amount for this method.</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group v-if="refund_method.value === 'cc'" label="Last 4 digits" label-class="pos-label">
                  <b-input size="sm" v-model="refund_method.name" maxlength="4"></b-input>
                </b-form-group>

                <template v-if="refund_method.value === 'finance'">
                  <b-form-group label="Finance company" label-class="pos-label">
                    <b-form-select :options="finance_only_companies" v-model="refund_method.name" size="sm">
                      <template #first><b-form-select-option :value="null" disabled>Select finance company</b-form-select-option></template>
                    </b-form-select>
                  </b-form-group>
                  <b-row>
                    <b-col cols="6"><b-form-group label="Lease #" label-class="pos-label"><b-input size="sm" v-model="refund_method.lease_id"></b-input></b-form-group></b-col>
                    <b-col cols="6"><b-form-group label="Approved amt" label-class="pos-label"><b-input size="sm" type="number" v-model="refund_method.approved_amount"></b-input></b-form-group></b-col>
                  </b-row>
                  <b-form-group label="Misc info" label-class="pos-label"><b-input size="sm" v-model="refund_method.misc_info"></b-input></b-form-group>
                </template>

                <b-form-group v-if="refund_method.value === 'store_credit'" label="Notes (optional)" label-class="pos-label">
                  <b-input size="sm" v-model="refund_method.notes"></b-input>
                </b-form-group>

                <template v-if="refund_method.value === 'tax_exempt'">
                  <b-form-group label="Company name" label-class="pos-label"><b-input size="sm" v-model="refund_method.name"></b-input></b-form-group>
                  <b-form-group label="EIN number" label-class="pos-label"><b-input size="sm" v-model="refund_method.ein"></b-input></b-form-group>
                  <b-form-group label="Certificate number" label-class="pos-label"><b-input size="sm" v-model="refund_method.certificate"></b-input></b-form-group>
                </template>
              </template>
            </div>
            <button type="button" class="pos-pay-add" @click="addRefundMethod"><b-icon icon="plus"></b-icon> Add refund method</button>
          </div>

          <div v-if="refund_methods.length > 0 && !isRefundMethodsValid()" class="validation-summary mt-3">
            <b-icon-exclamation-triangle class="warning-icon"></b-icon-exclamation-triangle>
            <span class="small">Allocated {{ money(refund_methods.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0)) }} of {{ money(getRemainingStoreCredit()) }} required.</span>
          </div>

          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <div class="pos-modal-foot-actions">
                <b-button class="pos-btn-ghost" size="sm" @click="finishPosTab('refundmethods')">Save and close</b-button>
                <b-button v-if="newItems.length" class="pos-btn-primary" size="sm" @click="handleUpdate" :disabled="!isRefundMethodsValid() || hasMerchandiseDue">
                  Refund {{ money(getRemainingStoreCredit()) }} and update sale
                </b-button>
                <b-button v-else class="pos-btn-primary" size="sm" @click="validateAndSubmitRefund" :disabled="!isRefundMethodsValid() || hasMerchandiseDue">
                  Refund {{ money(getRemainingStoreCredit()) }}
                </b-button>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- CUSTOMER ID -->
        <b-modal id="pos-modal-identity" title="Customer ID" size="lg" centered content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-panel">
            <b-row>
              <b-col md="6">
                <b-form-group label="ID type" label-class="pos-label">
                  <b-form-select v-model="invoice.customer_id_card_type" :options="idCardOptions" size="sm">
                    <template #first><b-form-select-option :value="null" disabled>Select ID type</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="ID number (optional)" label-class="pos-label">
                  <b-input size="sm" v-model="invoice.customer_id_card_number" placeholder="ID number"></b-input>
                </b-form-group>
              </b-col>
            </b-row>
            <div class="pos-id-upload">
              <div class="pos-id-preview">
                <div v-if="getCustomerIdCardImage()" class="position-relative" style="cursor: pointer;" @click="showFullIdCardImage()">
                  <img :src="getCustomerIdCardImage()" alt="ID card" />
                  <b-button variant="danger" size="sm" class="remove-id-btn"
                            style="position: absolute; top: -8px; right: -8px; width: 26px; height: 26px; padding: 0; border-radius: 50%;"
                            @click.stop="removeIdCardImage" title="Remove ID"><b-icon-x font-scale="1"></b-icon-x></b-button>
                </div>
                <div v-else class="pos-id-placeholder" @click="$refs.idCardFileInput.click()">
                  <b-icon-card-image font-scale="2"></b-icon-card-image>
                  <span>Front of ID</span>
                </div>
              </div>
              <div>
                <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
                  <b-button class="pos-btn-ghost" size="sm" @click="$refs.idCardFileInput.click()" :disabled="idCardUpload.uploading"><b-icon-upload class="mr-1"></b-icon-upload> Choose file</b-button>
                  <b-button class="pos-btn-ghost" size="sm" @click="openIdCardCamera" :disabled="idCardUpload.uploading || !isCameraSupported"><b-icon-camera class="mr-1"></b-icon-camera> Take photo</b-button>
                  <b-spinner small v-if="idCardUpload.uploading" variant="primary"></b-spinner>
                </div>
                <input ref="idCardFileInput" type="file" accept="image/*" style="display: none" @change="handleIdCardImageUpload" />
                <input ref="idCardCameraInput" type="file" accept="image/*" capture="environment" style="display: none" @change="handleIdCardImageUpload" />
                <small class="text-muted d-block">JPG or PNG, up to 5MB.</small>
                <small v-if="invoice.customer_id_card_url" class="text-success d-block mt-1">Uploaded {{ formatDate(invoice.customer_id_card_uploaded_at) }}</small>
              </div>
            </div>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('identity')">Save ID</b-button>
            </div>
          </template>
        </b-modal>

        <!-- COMMENTS -->
        <b-modal id="pos-modal-comments" title="Comments" size="md" centered content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-panel">
            <b-form-group label="Comments for this sale" label-class="pos-label" description="Printed on the receipt.">
              <b-form-textarea v-model="invoice.comment_txt" rows="5" placeholder="Delivery notes, special requests..."></b-form-textarea>
            </b-form-group>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button variant="link" class="pos-link" @click="openInvoicePreview"><b-icon icon="file-earmark-text"></b-icon> View invoice</b-button>
              <b-button class="pos-btn-primary" size="sm" @click="finishPosTab('comments')">Save comment</b-button>
            </div>
          </template>
        </b-modal>

        <!-- VIEW INVOICE -->
        <b-modal id="pos-modal-invoice" title="Invoice" size="xl" scrollable centered content-class="pos-modal" dialog-class="pos-dialog">
          <div class="pos-doc">
            <div class="pos-doc-head">
              <div>
                <div class="pos-doc-brand">Furniture Mecca</div>
                <div class="pos-doc-sub">{{ storeReady ? getSelectedLocationName() : 'No store selected yet' }}</div>
                <div class="pos-doc-sub" v-if="invoice.location_address">{{ invoice.location_address }}</div>
              </div>
              <dl class="pos-doc-meta">
                <dt>Invoice</dt><dd>{{ invoice.invoice_id || 'Pending' }}</dd>
                <dt>Date</dt><dd>{{ invoice.ts || ts }}</dd>
                <dt>Salesperson</dt><dd>{{ (invoice.username || userDetails.username || '').toUpperCase() }}</dd>
                <dt>Status</dt><dd>{{ invoice.edit ? 'Editing' : (invoice.draft_id ? 'Draft' : 'In progress') }}</dd>
              </dl>
            </div>

            <div class="pos-doc-grid">
              <section>
                <h4>Bill to</h4>
                <template v-if="invoice.customer && invoice.customer.fullname">
                  <div class="pos-doc-strong">{{ invoice.customer.fullname }}</div>
                  <div>{{ invoice.customer.email }}</div>
                  <div>{{ invoice.customer.phone }}<span v-if="invoice.customer.phone_2">, {{ invoice.customer.phone_2 }}</span></div>
                </template>
                <p v-else class="pos-empty">Customer not added.</p>
              </section>
              <section>
                <h4>Deliver to</h4>
                <template v-if="invoice.customer && (invoice.customer.address_line_1 || invoice.customer.city)">
                  <div>{{ invoice.customer.address_line_1 }}<span v-if="invoice.customer.address_line_2">, {{ invoice.customer.address_line_2 }}</span></div>
                  <div>{{ invoice.customer.city }}<span v-if="invoice.customer.state">, {{ invoice.customer.state }}</span> {{ invoice.customer.zipcode }}</div>
                  <div v-if="invoice.customer.floor">{{ invoice.customer.floor }}</div>
                </template>
                <p v-else class="pos-empty">Address not added.</p>
              </section>
              <section>
                <h4>Sold from</h4>
                <div>{{ invoice.sold_from_state || 'Not set' }}</div>
                <div v-if="invoice.tax_perc != null">Tax rate {{ invoice.tax_perc }}%</div>
                <div v-if="invoice.is_layaway"><span class="pos-chip is-warn">Layaway</span></div>
              </section>
            </div>

            <h4 class="pos-doc-h">Items</h4>
            <div style="overflow-x: auto;">
              <table class="pos-doc-table" v-if="visibleItems.length">
                <thead>
                  <tr><th>Item</th><th>Order</th><th>Date</th><th class="text-right">Qty</th><th class="text-right">Price</th><th class="text-right">Discount</th><th class="text-right">Total</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in visibleItems" :key="'doc-' + i">
                    <td>
                      <div class="pos-doc-strong">{{ item.product_name || item.product_sku }}</div>
                      <small>{{ item.product_sku }}<span v-if="item.is_damaged"> (as is)</span><span v-if="item.existing"> (on invoice)</span></small>
                    </td>
                    <td>{{ orderTypeLabel(item.order_type) }}</td>
                    <td>{{ item.order_type === 'customer_takes' ? today_date : (item.delivery_date || 'Not set') }}</td>
                    <td class="text-right">{{ itemQty(item) }}</td>
                    <td class="text-right">{{ money(getEffectiveUnitPrice(item)) }}</td>
                    <td class="text-right">{{ money(item.discount_offered) }}</td>
                    <td class="text-right pos-doc-strong">{{ money(lineTotal(item)) }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="pos-empty">No products added.</p>
            </div>

            <template v-if="refund_items.length">
              <h4 class="pos-doc-h">Returning</h4>
              <table class="pos-doc-table">
                <tbody>
                  <tr v-for="(r, i) in refund_items" :key="'docr-' + i">
                    <td>{{ r.product_name || r.product_sku }}</td>
                    <td class="text-right">Qty {{ r.return_quantity }}</td>
                  </tr>
                </tbody>
              </table>
            </template>

            <template v-if="merchandiseDueItems.length">
              <h4 class="pos-doc-h">Merchandise due</h4>
              <table class="pos-doc-table">
                <tbody>
                  <tr v-for="(r, i) in merchandiseDueItems" :key="'docmd-' + i">
                    <td>{{ r.product_name || r.product_sku }}</td>
                    <td class="text-right">
                      {{ r.received_quantity }}/{{ r.return_quantity }}
                      <span class="pos-chip" :class="r.state === 'received' ? 'is-ok' : 'is-warn'" style="margin-left: 6px;">
                        {{ r.state === 'received' ? 'Received' : 'Pending' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>

            <div class="pos-doc-bottom">
              <div class="pos-doc-notes">
                <section>
                  <h4>Payments</h4>
                  <template v-if="invoice.edit && payment_history.length">
                    <div class="pos-doc-row" v-for="(pm, i) in payment_history" :key="'dph-' + i">
                      <span>{{ paymentMethodLabel(pm.value) }} (paid)</span><span>{{ money(pm.amount) }}</span>
                    </div>
                  </template>
                  <div class="pos-doc-row" v-for="(pm, i) in activePaymentMethods" :key="'dpm-' + i">
                    <span>{{ paymentMethodLabel(pm.value) }}<span v-if="pm.value === 'cc' && pm.name"> ****{{ pm.name }}</span><span v-if="pm.value === 'finance' && pm.name"> ({{ pm.name }})</span></span>
                    <span>{{ money(pm.amount) }}</span>
                  </div>
                  <p v-if="!activePaymentMethods.length && !(invoice.edit && payment_history.length)" class="pos-empty">No payments entered.</p>
                  <div class="pos-doc-row" v-if="(parseFloat(invoice.gift_card_discount) || 0) > 0">
                    <span>Gift card <span v-if="invoice.gift_card_code">{{ invoice.gift_card_code }}</span></span>
                    <span>-{{ money(invoice.gift_card_discount) }}</span>
                  </div>
                </section>
                <section>
                  <h4>Agreements</h4>
                  <ul class="pos-checks">
                    <li v-for="a in agreementList" :key="'da-' + a.label" :class="{ 'is-ok': a.ok }">
                      <b-icon :icon="a.ok ? 'check-circle-fill' : 'circle'"></b-icon> {{ a.label }}
                    </li>
                  </ul>
                </section>
                <section v-if="invoice.customer_id_card_type || invoice.customer_id_card_url">
                  <h4>Customer ID</h4>
                  <div>{{ idCardLabel(invoice.customer_id_card_type) }}<span v-if="invoice.customer_id_card_number">, {{ invoice.customer_id_card_number }}</span></div>
                  <div>{{ invoice.customer_id_card_url ? 'Image on file' : 'No image' }}</div>
                </section>
                <section v-if="invoice.comment_txt">
                  <h4>Comments</h4>
                  <p class="pos-comment">{{ invoice.comment_txt }}</p>
                </section>
              </div>
              <dl class="pos-doc-totals">
                <dt>Products</dt><dd>{{ money(getOrderTotal()) }}</dd>
                <dt>Delivery</dt><dd>{{ money(invoice.edit ? invoice.new_delivery_charges : invoice.delivery_charges) }}</dd>
                <dt>White Glove</dt><dd>{{ money(invoice.edit ? invoice.new_whiteglove_charges : invoice.whiteglove_charges) }}</dd>
                <dt>Setup</dt><dd>{{ money(invoice.edit ? invoice.new_setup_charges : invoice.setup_charges) }}</dd>
                <dt>Warranty</dt><dd>{{ money(invoice.edit ? invoice.new_warranty_charges : invoice.warranty_charges) }}</dd>
                <dt v-if="getTotalDiscount() > 0">Discounts</dt><dd v-if="getTotalDiscount() > 0">-{{ money(getTotalDiscount()) }}</dd>
                <dt>Subtotal</dt><dd>{{ money(getSubTotal()) }}</dd>
                <dt>Sales tax</dt><dd>{{ money(getSalesTax()) }}</dd>
                <template v-if="invoice.edit && getStoreCreditApplied() > 0">
                  <dt>Store credit</dt><dd>-{{ money(getStoreCreditApplied()) }}</dd>
                </template>
                <dt class="is-grand">Grand total</dt><dd class="is-grand">{{ money(getGrandTotal()) }}</dd>
                <dt>Balance</dt><dd :class="{ 'is-due': currentBalance > 0.009 }">{{ money(currentBalance) }}</dd>
              </dl>
            </div>
          </div>
          <template #modal-footer>
            <div class="pos-modal-foot">
              <b-button class="pos-btn-ghost" size="sm" @click="printReceipt" :disabled="!visibleItems.length">
                <b-icon icon="printer"></b-icon> Print receipt
              </b-button>
              <b-button class="pos-btn-primary" size="sm" @click="$bvModal.hide('pos-modal-invoice')">Close</b-button>
            </div>
          </template>
        </b-modal>

      </div>

      <LoaderAnimation ref="loaderAnimation" />
      <SuccessAnimation ref="successAnimation" :message="successMessage" :duration="2500" />
    </b-overlay>
  </div>
</div>
</template>

<script>
import moment from "moment"
import global_vars from "@/utilities/global_vars"
import Requestor from "@/utilities/Requestor"
import SimpleScanner from "@/components/importables/SimpleScanner"
import Refundsummarysales from "@/components/importables/Refundsummarysales"
import Deliveries from "@/components/importables/Deliveries"
import SignaturePad from "@/components/importables/SignaturePad.vue"
import QuaggaScanner from "@/components/importables/QuaggaScanner.vue"
import DeliveryCalendar from "@/components/AdminTools/AdminToolsDeliveryCalendar.vue"
import SuccessAnimation from "@/components/importables/SuccessAnimation.vue"
import LoaderAnimation from "@/components/importables/LoaderAnimation.vue"

import DatePicker from "vue2-datepicker"
import "vue2-datepicker/index.css"

import VuePhoneNumberInput from "vue-phone-number-input"
import "vue-phone-number-input/dist/vue-phone-number-input.css"

import VueSimpleSuggest from "vue-simple-suggest"
import "vue-simple-suggest/dist/styles.css"

const emptySpecialItem = () => ({
    product_id: null,
    product_sku: null,
    product_name: null,
    category_id: null,
    subcategory_id: null,
    color_id: null,
    size_id: null,
    quantity: null,
    selling_price: null,
    new_selling_price: null,
    discount_offered: 0.0,
    vendor: null,
});

const emptyNonInventoryItem = () => ({
    category_id: null,
    product_sku: null,
    product_name: null,
    vendor: null,
    quantity: null,
    selling_price: null,
    new_selling_price: null,
    discount_offered: 0.0,
    image_url: null,
});

const emptyImage = () => ({ uploading: false, previewUrl: null, uploadedUrl: null });

const buildRefundCharges = (invoice) => ({
    delivery_refunded_sofar: parseFloat((invoice || {}).total_refunded_delivery) || 0.0,
    delivery_refunded_currently: 0.0,
    whiteglove_refunded_sofar: parseFloat((invoice || {}).total_refunded_whiteglove) || 0.0,
    whiteglove_refunded_currently: 0.0,
    setup_refunded_sofar: parseFloat((invoice || {}).total_refunded_setup) || 0.0,
    setup_refunded_currently: 0.0,
    warranty_refunded_sofar: parseFloat((invoice || {}).total_refunded_warranty) || 0.0,
    warranty_refunded_currently: 0.0,
    tax_refunded_sofar: parseFloat((invoice || {}).total_refunded_sales_tax) || 0.0,
    tax_refunded_currently: 0.0,
    total_refunded_currently: 0.0,
});

const MECCA_RETURN_LOCSUB = "47|124";

export default {
    name: "NewSale",
    props: ["viewdata"],
    components: {
        SimpleScanner, Deliveries, DatePicker, Refundsummarysales, VuePhoneNumberInput,
        VueSimpleSuggest, SignaturePad, QuaggaScanner, DeliveryCalendar, SuccessAnimation, LoaderAnimation,
    },

    data: function () {
        const invoice = this.get_invoice_data();
        if (!invoice.customer) invoice.customer = {};
        if (invoice.items && invoice.items.length > 0) {
            invoice.items.forEach(item => {
                if (!Object.prototype.hasOwnProperty.call(item, 'is_as_is')) item.is_as_is = false;
            });
        }

        return {
            productModalTab: 0,
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
            tabVisited: {},

            exchangeChoice: null,
            returnsForceSelect: false,
            creditRefreshing: false,

            blockedDates: {},
            blockedZone: null,
            blockedCaps: {},
            blockedLoading: false,

            merchandiseDue: [],
            merchandiseDueLoading: false,
            merchandiseDueReceivedQty: {},
            merchandiseDueReturnLocsub: {},

            refundOtp: {
                sent: false,
                sending: false,
                verifying: false,
                maskedEmail: null,
                code: '',
                error: null,
                cooldown: 0,
                cooldownTimer: null,
                verificationToken: null,
                amount: 0,
            },

            loading: false,
            locationLoading: false,
            loadingDraftSend: false,
            block_post_invoice: false,

            policyText: '',
            policyTextSpanish: '',
            policyLanguage: localStorage.getItem('policyLanguage') || 'en',
            policyAgreed: false,
            warrantyRefused: false,
            deliveryWaiverAgreed: false,
            spacePreparationAgreed: false,
            deliveryDamageWaiverAgreed: false,

            categories: [],
            subcategories: [],
            colorOptions: [{ value: null, text: 'Select Color' }],
            sizeOptions: [{ value: null, text: 'Select Size' }],
            special_item: emptySpecialItem(),
            nonInventoryItem: emptyNonInventoryItem(),
            nonInventoryImage: emptyImage(),
            imageUpload: { uploading: false, currentItem: null, previewUrl: null },

            giftCardCode: '',
            giftCardApplied: false,
            giftCardAmount: 0,
            giftCardRemaining: 0,
            giftCardId: null,
            giftCardError: null,
            giftCardInfo: null,
            giftCardValidating: false,
            showGiftCardPanel: false,

            isCameraSupported: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            barcodeInput: '',
            barcodeProcessing: false,
            scannerError: null,
            currentCamera: 'environment',

            searchitems: { search_being_loaded: false, keyword: null, limit: 500, result: [] },
            hasSearched: false,
            search_in_my_location: null,
            search_only_from_damages: false,

            floor_options: [
                { value: 'First Floor', text: 'First Floor' },
                { value: 'Second Floor', text: 'Second Floor' },
                { value: 'Third Floor', text: 'Third Floor' },
                { value: 'Fourth Floor', text: 'Fourth Floor' },
                { value: 'Other', text: 'Other' },
            ],
            show_custom_floor: false,
            custom_floor_number: null,
            custom_floor_display: null,
            zipcodes: [],

            idCardUpload: emptyImage(),
            idCardOptions: [
                { value: 'drivers_license', text: "Driver's License" },
                { value: 'state_id', text: 'State ID' },
                { value: 'passport', text: 'Passport' },
                { value: 'matricula', text: 'Matricula Consular' },
                { value: 'other', text: 'Other' },
            ],

            signatureItem: null,
            signatureUploading: false,
            currentFullSizeImage: null,
            currentProductName: null,
            successMessage: '',

            today_date: moment().format("MM-DD-YYYY"),
            ts: moment().format("MM-DD-YYYY hh:mm A"),
            global_vars: global_vars,
            userDetails: { properties: {} },
            packet: {},
            default_locsubs: null,
            commonDeliveryDate: null,

            original_payment_methods: [],
            available_refund_amounts: {},
            used_refund_amounts: {},
            refund_methods: [],
            payment_history: [],
            refund_method_options: [
                { value: "cash", text: "CASH" },
                { value: "cc", text: "CREDIT CARD" },
                { value: "finance", text: "FINANCE" },
                { value: "tax_exempt", text: "TAX EXEMPT" },
            ],
            refundsSummaryKey: 0,
            refundsHistory: [],
            total_refunded_value: 0,
            refund_items: [],
            refund_charges: buildRefundCharges(invoice),
            current_refund_validated: false,

            payment_method_options: [
                { value: "cc", text: "CREDIT CARD" },
                { value: "cash", text: "CASH" },
                { value: "finance", text: "FINANCE" },
                { value: "tax_exempt", text: "TAX EXEMPT" },
            ],
            new_payment_method_options: [
                { value: "tax_exempt", text: "TAX EXEMPT" },
                { value: "cc", text: "CREDIT CARD" },
                { value: "cash", text: "CASH" },
                { value: "finance", text: "FINANCE" },
            ],
            finance_companies: [],
            finance_only_companies: [],
            tax_exempt_companies: [],
            all_loc_inventory_dict: {},
            inventory_counts: {},
            layaway_options: [
                { value: false, text: "NO" },
                { value: true, text: "YES" },
            ],
            scheduled_deliveries: [],

            allowed_states: [
                { value: 'PA', text: 'PENNSYLVANIA' }, { value: 'NJ', text: 'NEW JERSEY' }, { value: 'DE', text: 'DELAWARE' },
                { value: 'AL', text: 'ALABAMA' }, { value: 'AK', text: 'ALASKA' }, { value: 'AS', text: 'AMERICAN SAMOA' },
                { value: 'AZ', text: 'ARIZONA' }, { value: 'AR', text: 'ARKANSAS' }, { value: 'CA', text: 'CALIFORNIA' },
                { value: 'CO', text: 'COLORADO' }, { value: 'CT', text: 'CONNECTICUT' }, { value: 'DC', text: 'DISTRICT OF COLUMBIA' },
                { value: 'FM', text: 'FEDERATED STATES OF MICRONESIA' }, { value: 'FL', text: 'FLORIDA' }, { value: 'GA', text: 'GEORGIA' },
                { value: 'GU', text: 'GUAM' }, { value: 'HI', text: 'HAWAII' }, { value: 'ID', text: 'IDAHO' },
                { value: 'IL', text: 'ILLINOIS' }, { value: 'IN', text: 'INDIANA' }, { value: 'IA', text: 'IOWA' },
                { value: 'KS', text: 'KANSAS' }, { value: 'KY', text: 'KENTUCKY' }, { value: 'LA', text: 'LOUISIANA' },
                { value: 'ME', text: 'MAINE' }, { value: 'MH', text: 'MARSHALL ISLANDS' }, { value: 'MD', text: 'MARYLAND' },
                { value: 'MA', text: 'MASSACHUSETTS' }, { value: 'MI', text: 'MICHIGAN' }, { value: 'MN', text: 'MINNESOTA' },
                { value: 'MS', text: 'MISSISSIPPI' }, { value: 'MO', text: 'MISSOURI' }, { value: 'MT', text: 'MONTANA' },
                { value: 'NE', text: 'NEBRASKA' }, { value: 'NV', text: 'NEVADA' }, { value: 'NH', text: 'NEW HAMPSHIRE' },
                { value: 'NM', text: 'NEW MEXICO' }, { value: 'NY', text: 'NEW YORK' }, { value: 'NC', text: 'NORTH CAROLINA' },
                { value: 'ND', text: 'NORTH DAKOTA' }, { value: 'MP', text: 'NORTHERN MARIANA ISLANDS' }, { value: 'OH', text: 'OHIO' },
                { value: 'OK', text: 'OKLAHOMA' }, { value: 'OR', text: 'OREGON' }, { value: 'PW', text: 'PALAU' },
                { value: 'PR', text: 'PUERTO RICO' }, { value: 'RI', text: 'RHODE ISLAND' }, { value: 'SC', text: 'SOUTH CAROLINA' },
                { value: 'SD', text: 'SOUTH DAKOTA' }, { value: 'TN', text: 'TENNESSEE' }, { value: 'TX', text: 'TEXAS' },
                { value: 'UT', text: 'UTAH' }, { value: 'VT', text: 'VERMONT' }, { value: 'VI', text: 'VIRGIN ISLANDS' },
                { value: 'VA', text: 'VIRGINIA' }, { value: 'WA', text: 'WASHINGTON' }, { value: 'WV', text: 'WEST VIRGINIA' },
                { value: 'WI', text: 'WISCONSIN' }, { value: 'WY', text: 'WYOMING' },
            ],

            invoice: invoice,
        };
    },

    watch: {
  
        policyLanguage(newVal) {
            localStorage.setItem('policyLanguage', newVal);
        },
        'invoice.customer.floor'() { this.updateCustomerFloorInfo(); },
        custom_floor_display() { this.updateCustomerFloorInfo(); },
        custom_floor_number() { this.updateCustomerFloorInfo(); },

        'invoice.customer.zipcode'(newVal) {
            const clean = String(newVal || '').replace(/[^0-9]/g, '');
            if (clean.length >= 5) {
                this.loadBlockedDeliveryDates();
            } else {
                this.blockedDates = {};
                this.blockedZone  = null;
                this.blockedCaps  = {};
            }
        },
    },

    computed: {
        signaturePadWidth() {
            const vw = this.viewportWidth || 1280;
            if (vw < 576) return Math.max(260, vw - 40);
            if (vw < 1200) return Math.min(600, Math.round(vw * 0.94) - 64);
            return 600;
        },
        signaturePadHeight() {
            return (this.viewportWidth || 1280) < 576 ? 200 : 250;
        },
        storeLocationOptions() {
            const locsubs = (this.packet.locsubs || []).filter(d => d.sublocation_type_id == 1);
            const lid = this.userDetails && this.userDetails.properties && this.userDetails.properties.location_id;
            return lid ? locsubs.filter(d => d.location_id == lid) : locsubs;
        },
        storeReady() {
            return !!(this.invoice.selected_locsub && this.invoice.sold_from_state);
        },
        customerReady() {
            const c = this.invoice.customer || {};
            return !!(c.fullname && c.email && c.phone && c.zipcode);
        },
        customerFirstName() {
            const c = this.invoice.customer || {};
            if (c.first_name) return c.first_name;
            const full = (c.fullname || '').trim();
            if (!full) return '';
            const parts = full.split(/\s+/);
            return parts[0] || '';
        },
        locationAddressDisplay() {
            return this.invoice.location_address || this.getSelectedLocationName() || 'No store selected';
        },
        locationPhoneDisplay() {
            return this.formatPhone(this.invoice.location_phone) || 'Phone not on file';
        },
        salespersonName() {
            return (this.invoice.username || this.userDetails.username || '').toUpperCase();
        },
        customerLastName() {
            const c = this.invoice.customer || {};
            if (c.last_name) return c.last_name;
            const full = (c.fullname || '').trim();
            if (!full) return '';
            const parts = full.split(/\s+/);
            return parts.length > 1 ? parts.slice(1).join(' ') : '';
        },
        visibleItems() {
            return (this.invoice.items || []).filter(item => this.show_item(item));
        },
        termsReady() {
            return this.policyAgreed &&
                (this.hasWarrantyCharges || this.warrantyRefused) &&
                this.invoice.has_signature === true;
        },
        activePaymentMethods() {
            return (this.invoice.edit ? this.invoice.new_payment_methods : this.invoice.payment_methods) || [];
        },
        currentBalance() {
            return this.invoice.edit ? this.getTotalBalanceWithCredit() : this.getTotalBalance();
        },
        paymentReady() {
            const hasAmount = this.activePaymentMethods.some(p => (parseFloat(p.amount) || 0) > 0);
            if (this.invoice.is_layaway) return hasAmount;
            if (this.invoice.edit) return this.currentBalance <= 0.009;
            return hasAmount && Math.abs(this.currentBalance) <= 0.009;
        },
        positiveRefundAmounts() {
            const out = {};
            Object.keys(this.available_refund_amounts || {}).forEach(k => {
                if (this.available_refund_amounts[k] > 0) out[k] = this.available_refund_amounts[k];
            });
            return out;
        },
        agreementList() {
            return [
                { label: 'Terms and conditions accepted', ok: this.policyAgreed },
                { label: this.hasWarrantyCharges ? 'Warranty purchased' : 'Warranty declined', ok: this.hasWarrantyCharges || this.warrantyRefused },
                { label: 'Packaging removal not included', ok: this.deliveryWaiverAgreed },
                { label: 'Room ready for setup', ok: this.spacePreparationAgreed },
                this.hasStorePickupItem ? { label: 'Pickup damage waiver', ok: this.deliveryDamageWaiverAgreed } : null,
                { label: 'Customer signature', ok: this.invoice.has_signature === true },
            ].filter(Boolean);
        },

        merchandiseDueItems() {
            return (this.merchandiseDue || []).filter(
                r => r.state === 'pending_receipt' || r.state === 'received'
            );
        },
        merchandiseDuePending() {
            return this.merchandiseDueItems.filter(r => r.state === 'pending_receipt');
        },
        hasMerchandiseDue() {
            return this.merchandiseDuePending.length > 0;
        },
        merchandiseDueUnits() {
            return this.merchandiseDuePending.reduce(
                (s, r) => s + (parseInt(r.pending_quantity) || 0), 0
            );
        },

        posTabs() {
            const e = this.invoice.edit === true;
            return [
                { key: 'store', label: 'Store', done: this.storeReady, required: true },
                { key: 'customer', label: 'Customer', done: this.customerReady, required: true },
                { key: 'products', label: 'Products', done: this.visibleItems.length > 0, required: true, count: this.visibleItems.length },
                e && { key: 'returns', label: 'Returns and exchange', done: this.getAvailableStoreCredit() > 0.005 && !this.refund_items.length, count: this.refund_items.length },
                e && this.merchandiseDueItems.length > 0 && { key: 'merchandisedue', label: 'Merchandise due', done: !this.hasMerchandiseDue && this.merchandiseDueItems.length > 0, count: this.merchandiseDueUnits, required: this.hasMerchandiseDue },
                e && { key: 'paymenthistory', label: 'Payment history', done: this.payment_history.length > 0 },
                e && { key: 'refunds', label: 'Refunds', done: !!this.tabVisited.refunds },
                e && { key: 'storecredits', label: 'Store credits', done: !!this.tabVisited.storecredits },
                { key: 'charges', label: 'Charges', done: !!this.tabVisited.charges || this.getChargesTotal() > 0 },
                { key: 'terms', label: 'Terms', done: this.termsReady, required: !e },
                { key: 'payment', label: 'Payment', done: this.paymentReady, required: true },
                e && { key: 'refundmethods', label: 'Refund methods', done: this.refund_methods.length > 0 && this.isRefundMethodsValid() },
                { key: 'identity', label: 'Customer ID', done: !!this.invoice.customer_id_card_url },
                { key: 'comments', label: 'Comments', done: !!this.invoice.comment_txt },
            ].filter(Boolean);
        },
        returnsStep() {
            if (this.current_refund_validated) return 2;
            if (!this.refund_items.length && !this.returnsForceSelect && !this.creditRefreshing && this.getAvailableStoreCredit() > 0.005) return 3;
            return 1;
        },
        canValidateReturn() {
            const charges = ['delivery_refunded_currently', 'whiteglove_refunded_currently', 'setup_refunded_currently', 'warranty_refunded_currently']
                .some(k => (parseFloat(this.refund_charges[k]) || 0) > 0);
            return this.refund_items.length > 0 || charges;
        },
        exchangeSummary() {
            const r = v => Math.round(v * 100) / 100;
            const refundDue = this.getRemainingStoreCredit();
            const balance = this.getTotalBalanceWithCredit();
            return {
                credit: this.getEffectiveStoreCredit(),
                newTotal: r(this.getSubTotal() + this.getSalesTax()),
                applied: this.getStoreCreditApplied(),
                refundDue: refundDue,
                balance: balance,
                hasNewItems: this.newItems.length > 0,
                outcome: refundDue > 0.005 ? 'refund' : (balance > 0.005 ? 'pay' : 'even'),
            };
        },
        exchangeOutcomeLabel() {
            return ({ refund: 'Refund to customer', pay: 'Customer pays', even: 'Even exchange' })[this.exchangeSummary.outcome];
        },
        exchangeOutcomeAmount() {
            const x = this.exchangeSummary;
            return x.outcome === 'refund' ? x.refundDue : (x.outcome === 'pay' ? x.balance : 0);
        },
        exchangeActionLabel() {
            return ({ refund: 'Choose refund methods', pay: 'Take payment', even: 'Update sale' })[this.exchangeSummary.outcome];
        },
        showExchangeBar() {
            return this.invoice.edit === true && !this.refund_items.length && this.getAvailableStoreCredit() > 0.005;
        },
        nextTabKey() {
            const t = this.posTabs.find(tab => tab.required && !tab.done);
            return t ? t.key : null;
        },
        submitBlockers() {
            if (this.invoice.edit) return [];
            const b = [];
            if (!this.storeReady) b.push('store');
            if (!this.customerReady) b.push('customer details');
            else if (!this.isPhoneNumberValid) b.push('a 10-digit phone');
            if (!this.visibleItems.length) b.push('products');
            if (!this.policyAgreed) b.push('terms');
            if (!this.hasWarrantyCharges && !this.warrantyRefused) b.push('warranty decision');
            if (this.invoice.has_signature !== true) b.push('signature');
            if (!this.isPaymentAmountValid) b.push('payments are over the total');
            return b;
        },

        canAddNonInventoryItemToSale() {
            const n = this.nonInventoryItem;
            return !!(n.category_id && n.product_sku && n.product_name && n.vendor && n.quantity > 0 && n.selling_price > 0);
        },
        canAddNonInventoryItem() {
            const s = this.special_item;
            return !!(s.category_id && s.subcategory_id && s.product_sku && s.product_name && s.vendor && s.quantity > 0 && s.selling_price > 0);
        },
        categoryOptions() {
            if (!Array.isArray(this.categories) || this.categories.length === 0) return [];
            return this.categories.map(cat => ({
                value: cat.category_id,
                text: cat.category_name || cat.category_code || 'Unnamed',
            }));
        },
        specialSubcategoryOptions() {
            if (!this.special_item.category_id) return [];
            return this.subcategories
                .filter(sub => sub.category_id == this.special_item.category_id)
                .map(sub => ({ value: sub.id, text: sub.subcategory || sub.name || 'Unnamed' }));
        },
        sortedVendors() {
            if (!Array.isArray(this.packet.vendors) || this.packet.vendors.length === 0) return [];
            const textOf = v => (typeof v === 'string' ? v : (v && (v.text || v.name || v.label || v.value)) || '');
            let unknown = null;
            const others = this.packet.vendors.filter(v => {
                if (textOf(v).toUpperCase() === 'UNKNOWN') { unknown = v; return false; }
                return true;
            });
            others.sort((a, b) => textOf(a).toUpperCase().localeCompare(textOf(b).toUpperCase()));
            return unknown ? [unknown, ...others] : others;
        },
        showLocationBreakdown() {
            return this.search_in_my_location === true;
        },
        currentPolicyText() {
            return this.policyLanguage === 'es' ? this.policyTextSpanish : this.policyText;
        },

        isPhoneNumberValid() {
            const phone = this.invoice.customer && this.invoice.customer.phone;
            if (!phone) return false;
            return String(phone).replace(/\D/g, '').length >= 10;
        },
        isPaymentAmountValid() {
            if (this.invoice.is_layaway) return true;
            return this.getTotalPayments() <= this.getGrandTotal() + 0.01;
        },
        isDraftSendEnabled() {
            const email = this.invoice.customer && this.invoice.customer.email;
            return this.invoice.items.length > 0 && this.isPhoneNumberValid && !!email && email.includes('@');
        },
        isSubmitEnabled() {
            if (this.invoice.edit) return true;
            if (!this.isPhoneNumberValid || !this.isPaymentAmountValid) return false;
            const hasSignature = this.invoice.has_signature === true;
            if (!this.hasWarrantyCharges) return this.policyAgreed && this.warrantyRefused && hasSignature;
            return this.policyAgreed && hasSignature;
        },
        hasWarrantyCharges() {
            return this.getWarrantyTotal() > 0;
        },
        hasStorePickupItem() {
            return this.invoice.items.some(item => item.order_type === 'store_pickup');
        },
        hasDeliveredItems() {
            return this.invoice.items.some(item => item.existing && this.get_item_status(item) === 'delivered');
        },

        existingItems() {
            return this.invoice.items.filter(item => item.existing === true && this.show_item(item));
        },
        newItems() {
            return this.invoice.items.filter(item => item.existing !== true && this.show_item(item));
        },
        hasExistingItems() { return this.existingItems.length > 0; },
        hasNewItems() { return this.newItems.length > 0; },
        hasAsIsItems() { return this.newItems.some(item => item.is_as_is); },
    },

    methods: {
        // ============================================================
        // ZIP → ZONE AWARE DELIVERY DATE LOADING
        // ============================================================
        async loadBlockedDeliveryDates() {
            const zip = (this.invoice.customer && this.invoice.customer.zipcode) || '';
            const cleanZip = String(zip).replace(/[^0-9]/g, '').slice(0, 5);
            if (!cleanZip) {
                this.blockedDates = {};
                this.blockedZone  = null;
                this.blockedCaps  = {};
                return;
            }

            const start = moment().startOf('day');
            const end   = moment().add(60, 'days').endOf('day');

            this.blockedLoading = true;
            const that = this;
            try {
                const response = await Requestor.getAsyncP(
                    '/api/zones/get_blocked_delivery_dates/',
                    {
                        zipcode:    cleanZip,
                        start_date: start.format('MM-DD-YYYY'),
                        end_date:   end.format('MM-DD-YYYY'),
                    },
                    this
                );
                const r = (response.data && response.data.result) || {};
                that.blockedDates = {};
                (r.blocked || []).forEach(d => { that.blockedDates[d] = r.reasons[d] || 'blocked'; });
                that.blockedZone = r.zone_id
                    ? { zone_id: r.zone_id, zone_code: r.zone_code, zone_name: r.zone_name }
                    : null;
                that.blockedCaps = r.caps || {};
            } catch (e) {
                console.error('loadBlockedDeliveryDates', e);
                that.blockedDates = {};
                that.blockedZone  = null;
                that.blockedCaps  = {};
            } finally {
                that.blockedLoading = false;
            }
        },

        getBlockReasonForDate(dateStr) {
            if (!dateStr) return null;
            const reason = this.blockedDates[dateStr];
            if (!reason) return null;
            const z = this.blockedZone
                ? (this.blockedZone.zone_code || this.blockedZone.zone_name)
                : 'this zone';
            if (reason === 'zone_off') {
                return `${z} has no deliveries on this day. Pick another date.`;
            }
            if (reason === 'max_reached') {
                return `${z} is at capacity for this day. Pick another date.`;
            }
            return `${z} isn't available on this day. Pick another date.`;
        },

        getReturnLocationOptions(row) {
            const opts = [];
            const seen = new Set();

            const push = (locsub, label) => {
                if (!locsub) return;
                const key = String(locsub);
                if (seen.has(key)) return;
                seen.add(key);
                opts.push({ value: key, text: label });
            };

            push(MECCA_RETURN_LOCSUB, 'Mecca Shipping — RETURN TO INVENTORY');

            const storeLocId = this.invoice.location_id;
            (this.packet.locsubs || []).forEach(loc => {
                if (!loc.value) return;
                const parts = String(loc.value).split('|');
                if (parseInt(parts[0]) !== storeLocId) return;
                const t = (loc.text || '').toUpperCase();
                if (t.includes('STOCK') || t.includes('FLOOR')) {
                    push(loc.value, loc.text);
                }
            });

            return opts;
        },

        getReturnLocationHint(row) {
            const chosen = this.merchandiseDueReturnLocsub[row.id];
            if (!chosen) return 'Pick a destination to enable Receive.';
            const parts = String(chosen).split('|');
            const locId = parseInt(parts[0]);
            const subId = parseInt(parts[1]);
            if (locId === 47 && subId === 124) {
                return 'Default returns location — stock goes back into sellable inventory.';
            }
            if (locId === 47 && subId === 125) {
                return 'Damaged returns — stock will NOT be sellable.';
            }
            if (locId === this.invoice.location_id) {
                return 'Store location — stock goes back to the sales floor.';
            }
            return null;
        },

        getReturnLocationHintClass(row) {
            const chosen = this.merchandiseDueReturnLocsub[row.id];
            if (!chosen) return 'is-warn';
            const parts = String(chosen).split('|');
            if (parseInt(parts[0]) === 47 && parseInt(parts[1]) === 125) return 'is-warn';
            return 'is-ok';
        },

        getReturnLocationHintIcon(row) {
            const chosen = this.merchandiseDueReturnLocsub[row.id];
            if (!chosen) return 'exclamation-circle';
            const parts = String(chosen).split('|');
            if (parseInt(parts[0]) === 47 && parseInt(parts[1]) === 125) return 'exclamation-triangle-fill';
            return 'check-circle-fill';
        },

        formatReceivedTs(ts) {
            if (!ts) return '';
            return moment(ts).format('MM/DD/YYYY hh:mm A');
        },

        autoSelectDefaultStore() {
            if (this.storeReady || this.invoice.edit) return;
            const role = (this.userDetails && this.userDetails.userrole) || '';
            if (role === 'admin') return;
            const options = this.storeLocationOptions || [];
            if (!options.length) return;
            const userLocId = this.userDetails
                && this.userDetails.properties
                && this.userDetails.properties.location_id;
            let chosen = null;
            if (userLocId) {
                chosen = options.find(o => o.location_id == userLocId) || null;
            }
            if (!chosen) chosen = options[0];
            if (!chosen || !chosen.value) return;
            this.invoice.selected_locsub = chosen.value;
            this.set_location(chosen.value);
        },
        payment_ts_format(ts) {
            if (!ts) return 'Pending';
            return moment(ts).format('MM/DD/YYYY hh:mm A');
        },
        openPosTab(key) {
            if (key === 'products') this.productModalTab = this.invoice.items.length ? 1 : 0;
            if (key === 'merchandisedue') this.loadMerchandiseDue();
            if (key === 'returns') this.loadMerchandiseDue();
            this.$bvModal.show('pos-modal-' + key);
        },
        finishPosTab(key) {
            this.$set(this.tabVisited, key, true);
            this.$bvModal.hide('pos-modal-' + key);
        },
        openInvoicePreview() {
            this.$bvModal.show('pos-modal-invoice');
        },
        posWarn(message) {
            this.$bvToast.toast(message, { title: 'Check this section', variant: 'warning', autoHideDelay: 2500 });
        },
        saveStoreSection() {
            if (!this.storeReady) { this.posWarn('Select a location and state to continue.'); return; }
            this.finishPosTab('store');
        },
        saveCustomerSection() {
            const errors = this.validate_customer_info(this.invoice.customer || {});
            if (errors.length) { this.posWarn(errors[0]); return; }
            if (!this.isPhoneNumberValid) { this.posWarn('Enter a phone number with at least 10 digits.'); return; }
            if (!this.invoice.address_confirmation_agreed) {
                this.posWarn('Have the customer confirm their address and phone number.');
                return;
            }
            this.updateCustomerFloorInfo();
            if (this.invoice.customer.zipcode) this.loadBlockedDeliveryDates();
            this.finishPosTab('customer');
        },
        saveProductsSection() {
            if (!this.invoice.items.length) {
                this.posWarn('Add at least one product to the cart.');
                this.productModalTab = 0;
                return;
            }
            const missing = this.invoice.items.find(i =>
                !i.existing && (!i.order_type || (i.order_type !== 'customer_takes' && !i.delivery_date))
            );
            if (missing) {
                this.posWarn(`Pick an order type and date for ${missing.product_name || missing.product_sku}.`);
                this.productModalTab = 1;
                return;
            }
            this.finishPosTab('products');
        },
        saveTermsSection() {
            if (!this.termsReady) {
                const missing = this.agreementList.filter(a => !a.ok).map(a => a.label.toLowerCase());
                this.$bvToast.toast('Still needed: ' + missing.join(', '), { title: 'Saved', variant: 'info', autoHideDelay: 3000 });
            }
            this.finishPosTab('terms');
        },
        savePaymentSection() {
            if (!this.invoice.is_layaway && this.currentBalance > 0.009) {
                this.$bvToast.toast(
                    `A balance of ${this.money(this.currentBalance)} is still due. You can finish payment later.`,
                    { title: 'Saved', variant: 'info', autoHideDelay: 3000 }
                );
            }
            this.finishPosTab('payment');
        },
        formatPhone(v) {
            if (!v) return '';
            const d = String(v).replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
            return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(v);
        },
        loadLocationContact() {
            const inv = this.invoice;
            const locsub = inv.selected_locsub ||
                (inv.location_id && inv.sublocation_id ? `${inv.location_id}|${inv.sublocation_id}` : null);
            if (!locsub || (inv.location_address && inv.location_phone)) return;
            const that = this;
            Requestor.getAsync("/api/location/get_location_info/", { locsub: locsub },
                function (response) {
                    const r = response.data.result && response.data.result[0];
                    if (!r) return;
                    if (!that.invoice.location_address) that.$set(that.invoice, 'location_address', r.address || r.full_address || r.location_name);
                    if (!that.invoice.location_phone) that.$set(that.invoice, 'location_phone', r.phone || r.phone_number || r.location_phone || null);
                },
                function () {}
            );
        },
        money(v) {
            return '$' + (parseFloat(v) || 0).toFixed(2);
        },
        showLoader(message) {
            if (this.$refs.loaderAnimation) this.$refs.loaderAnimation.show(message || 'Working...');
        },
        hideLoader() {
            if (this.$refs.loaderAnimation) this.$refs.loaderAnimation.hide();
        },

        openRefundOtpModal(amount) {
            this.refundOtp.amount = parseFloat(amount) || 0;
            this.refundOtp.code = '';
            this.refundOtp.error = null;
            this.refundOtp.verificationToken = null;
            this.$bvModal.show('pos-modal-refund-otp');
            this.sendRefundOtp();
        },
        sendRefundOtp() {
            const that = this;
            this.refundOtp.sending = true;
            this.refundOtp.error = null;
            Requestor.postAsyncP('/api/sales/refund_otp/send/', {
                invoice_id: this.invoice.invoice_id,
                username: this.userDetails.username,
                refund_amount: this.refundOtp.amount,
            }, this,
                function (response) {
                    that.refundOtp.sending = false;
                    if (response.data.error) {
                        that.refundOtp.error = response.data.error;
                        return;
                    }
                    that.refundOtp.sent = true;
                    that.refundOtp.maskedEmail = response.data.result.masked_email;
                    that.refundOtp.cooldown = response.data.result.cooldown || 30;
                    that.startRefundOtpCooldown();
                },
                function () {
                    that.refundOtp.sending = false;
                    that.refundOtp.error = 'Could not send the verification code. Try again.';
                }
            );
        },
        resendRefundOtp() {
            if (this.refundOtp.cooldown > 0 || this.refundOtp.sending) return;
            this.refundOtp.code = '';
            this.sendRefundOtp();
        },
        startRefundOtpCooldown() {
            const that = this;
            if (this.refundOtp.cooldownTimer) clearInterval(this.refundOtp.cooldownTimer);
            this.refundOtp.cooldownTimer = setInterval(() => {
                that.refundOtp.cooldown -= 1;
                if (that.refundOtp.cooldown <= 0) {
                    clearInterval(that.refundOtp.cooldownTimer);
                    that.refundOtp.cooldownTimer = null;
                }
            }, 1000);
        },
        verifyRefundOtp() {
            if (!this.refundOtp.code || this.refundOtp.code.length !== 6) return;
            const that = this;
            this.refundOtp.verifying = true;
            this.refundOtp.error = null;
            Requestor.postAsyncP('/api/sales/refund_otp/verify/', {
                invoice_id: this.invoice.invoice_id,
                otp_code: this.refundOtp.code,
                username: this.userDetails.username,
            }, this,
                function (response) {
                    that.refundOtp.verifying = false;
                    if (response.data.error) {
                        that.refundOtp.error = response.data.error;
                        that.refundOtp.code = '';
                        return;
                    }
                    that.refundOtp.verificationToken = response.data.result.verification_token;
                    that.$bvModal.hide('pos-modal-refund-otp');
                    that.submitRefundWithMethods().catch(() => {});
                },
                function () {
                    that.refundOtp.verifying = false;
                    that.refundOtp.error = 'Verification failed. Try again.';
                }
            );
        },
        cancelRefundOtp() {
            this.$bvModal.hide('pos-modal-refund-otp');
            this.resetRefundOtp();
        },
        resetRefundOtp() {
            if (this.refundOtp.cooldownTimer) clearInterval(this.refundOtp.cooldownTimer);
            this.refundOtp = {
                sent: false, sending: false, verifying: false, maskedEmail: null,
                code: '', error: null, cooldown: 0, cooldownTimer: null,
                verificationToken: null, amount: 0,
            };
        },

        loadMerchandiseDue() {
            if (!this.invoice.invoice_id) { this.merchandiseDue = []; return; }
            this.merchandiseDueLoading = true;
            this.showLoader("Loading merchandise due...");
            const that = this;
            Requestor.postAsyncP("/api/sales/get_pending_receipts/",
                { invoice_id: this.invoice.invoice_id }, this,
                function (response) {
                    that.hideLoader();
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {
                            title: "Error", autoHideDelay: 2500, variant: "warning"
                        });
                        that.merchandiseDue = [];
                    } else {
                        that.merchandiseDue = response.data.result || [];
                        const seedQty = {};
                        const seedLoc = {};
                        that.merchandiseDue.forEach(r => {
                            seedQty[r.id] = r.pending_quantity > 0 ? r.pending_quantity : 0;
                            if (r.return_locsub) {
                                seedLoc[r.id] = r.return_locsub;
                            } else {
                                const opts = that.getReturnLocationOptions(r);
                                seedLoc[r.id] = opts.length ? opts[0].value : MECCA_RETURN_LOCSUB;
                            }
                        });
                        that.merchandiseDueReceivedQty = seedQty;
                        that.merchandiseDueReturnLocsub = seedLoc;
                    }
                    that.merchandiseDueLoading = false;
                },
                function () {
                    that.hideLoader();
                    that.merchandiseDueLoading = false;
                    that.merchandiseDue = [];
                    that.$bvToast.toast("Error loading merchandise due.", {
                        title: "Error", autoHideDelay: 2500, variant: "danger"
                    });
                }
            );
        },
                markReceiptReceived(row) {
            if (!row || !row.id) return;
            const qty = parseInt(this.merchandiseDueReceivedQty[row.id]);
            const maxQty = parseInt(row.pending_quantity) || 0;
            const returnLocsub = this.merchandiseDueReturnLocsub[row.id];

            if (!qty || qty <= 0) {
                this.posWarn('Enter how many units were received.');
                return;
            }
            if (qty > maxQty) {
                this.posWarn(`Cannot receive more than ${maxQty} pending unit(s).`);
                return;
            }
            if (!returnLocsub) {
                this.posWarn('Pick the location this merchandise should be returned to.');
                return;
            }

            const that = this;
            this.merchandiseDueLoading = true;
            this.showLoader(`Marking ${row.product_sku || 'item'} received...`);
            Requestor.postAsyncP("/api/sales/mark_item_received/",
                {
                    receipt_id: row.id,
                    received_quantity: qty,
                    received_by: this.userDetails.username,
                    return_locsub: returnLocsub,
                }, this,
                function (response) {
                    that.hideLoader();
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {
                            title: "Error", autoHideDelay: 3000, variant: "warning"
                        });
                    } else {
                        const updated = response.data.result || {};
                        const idx = that.merchandiseDue.findIndex(r => r.id === row.id);
                        if (idx !== -1) that.$set(that.merchandiseDue, idx, updated);
                        if (updated.return_locsub) {
                            that.$set(that.merchandiseDueReturnLocsub, row.id, updated.return_locsub);
                        }
                        that.$bvToast.toast(
                            updated.state === 'received'
                                ? `${row.product_sku} marked fully received and returned to inventory.`
                                : `${row.product_sku} partially received (${updated.received_quantity}/${updated.return_quantity}).`,
                            { title: "Received", autoHideDelay: 2500, variant: "success" }
                        );
                        if (!that.hasMerchandiseDue && that.getAvailableStoreCredit() > 0.005) {
                            that.$bvToast.toast('All merchandise received. You can now refund or reselect.', {
                                title: "Ready", autoHideDelay: 3000, variant: "success"
                            });
                        }
                    }
                    that.merchandiseDueLoading = false;
                },
                function () {
                    that.hideLoader();
                    that.merchandiseDueLoading = false;
                    that.$bvToast.toast("Error marking item received.", {
                        title: "Error", autoHideDelay: 3000, variant: "danger"
                    });
                }
            );
        },
        markAllReceived() {
            if (!this.merchandiseDuePending.length) return;
            const rows = this.merchandiseDuePending.map(r => ({
                id: r.id,
                qty: parseInt(r.pending_quantity) || 0,
                row: r
            }));
            rows.forEach(item => {
                this.$set(this.merchandiseDueReceivedQty, item.id, item.qty);
                if (!this.merchandiseDueReturnLocsub[item.id]) {
                    const opts = this.getReturnLocationOptions(item.row);
                    this.$set(this.merchandiseDueReturnLocsub, item.id,
                              opts.length ? opts[0].value : MECCA_RETURN_LOCSUB);
                }
                this.markReceiptReceived(item.row);
            });
        },
        formatReceiptLocsub(v) {
            if (!v) return '—';
            return this.getLocationName(v) || v;
        },

        // =====================================================
        // DYNAMIC REGULAR PRICE / SAVINGS HELPERS
        // =====================================================
        getRegularPrice(item) {
            if (!item) return 0;

            const selling = parseFloat(item.selling_price) || 0;
            if (selling <= 0) return 0;

            let bump = 0;
            if (selling < 1000) bump = 100;
            else if (selling < 2000) bump = 200;
            else if (selling < 5000) bump = 500;
            else bump = 1000;

            return Math.round((selling + bump) * 100) / 100;
        },

        getSellingPrice(item) {
            if (!item) return 0;
            return Math.round((parseFloat(item.selling_price) || 0) * 100) / 100;
        },

        getDiscountedPrice(item) {
            if (!item) return null;
            if (!this.hasActiveDiscount(item)) return null;
            const sp = parseFloat(item.special_discount_price);
            if (!sp || sp <= 0) return null;
            return Math.round(sp * 100) / 100;
        },

        getEffectiveUnitPrice(item) {
            if (!item) return 0;
            const special = this.getDiscountedPrice(item);
            if (special !== null) return special;
            const override = parseFloat(item.new_selling_price);
            if (!isNaN(override) && override > 0) return Math.round(override * 100) / 100;
            return Math.round((parseFloat(item.selling_price) || 0) * 100) / 100;
        },

        getItemSavings(item) {
            if (!item) return 0;
            const regular = this.getRegularPrice(item);
            if (regular <= 0) return 0;
            const discount = parseFloat(item.discount_offered) || 0;
            const qty = parseInt(item.quantity) || 1;
            const paidPerUnit = this.getEffectiveUnitPrice(item) - discount;
            const savings = (regular - paidPerUnit) * qty;
            return savings > 0 ? Math.round(savings * 100) / 100 : 0;
        },

        getChildDiscount(child) {
            const orig = parseFloat(child.orig_selling_price || child.selling_price) || 0;
            const disc = parseFloat(
                child.discounted_selling_price != null
                    ? child.discounted_selling_price
                    : (parseFloat(child.special_discount_price) > 0
                        ? child.special_discount_price
                        : orig)
            ) || 0;
            const d = orig - disc;
            return d > 0.005 ? d : 0;
        },

        childUnitPrice(child) {
            return parseFloat(child.orig_selling_price || child.selling_price) || 0;
        },
        childSellPrice(child) {
            if (child.discounted_selling_price != null) return parseFloat(child.discounted_selling_price) || 0;
            if (parseFloat(child.special_discount_price) > 0) return parseFloat(child.special_discount_price);
            return this.childUnitPrice(child);
        },
        childAvailable(child) {
            if (child.availability != null) return Number(child.availability);
            if (child.available != null) return Number(child.available);
            return null;
        },
        childQty(child) {
            return child.quantity != null ? child.quantity : 1;
        },

        getCartSavings() {
            const total = (this.visibleItems || [])
                .reduce((sum, item) => sum + this.getItemSavings(item), 0);
            return Math.round(total * 100) / 100;
        },

        orderTypeLabel(t) {
            return ({
                customer_takes: 'Take home',
                store_pickup: 'Store pickup',
                home_delivery: 'Home delivery',
                delivery_from_store: 'Delivery from store',
            })[t] || 'No order type';
        },
        paymentMethodLabel(v) {
            return ({
                cash: 'Cash', cc: 'Credit card', finance: 'Finance',
                tax_exempt: 'Tax exempt', store_credit: 'Store credit', check: 'Check',
            })[v] || 'Method not chosen';
        },
        idCardLabel(v) {
            const o = this.idCardOptions.find(x => x.value === v);
            return o ? o.text : 'Type not chosen';
        },
        itemQty(item) {
            const q = parseInt(item.quantity) || 0;
            return item.existing ? q - (parseInt(item.refunded_so_far_quantity) || 0) : q;
        },
        lineTotal(item) {
            const price = this.getEffectiveUnitPrice(item);
            const disc = parseFloat(item.discount_offered) || 0;
            return Math.max(0, price * this.itemQty(item) - disc);
        },
        formatCurrency(value) {
            return '$' + (parseFloat(value) || 0).toFixed(2);
        },
        formatDate(date) {
            if (!date) return '';
            return moment(date).format('MM/DD/YYYY hh:mm A');
        },
        isMobileDevice() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },

        get_invoice_data() {
            if (this.viewdata) return this.viewdata[0];
            return {
                edit: false, invoice_id: null, draft_id: null, username: null, customer: {}, items: [],
                delivery_charges: 0, whiteglove_charges: 0, setup_charges: 0, warranty_charges: 0,
                new_delivery_charges: 0, new_whiteglove_charges: 0, new_setup_charges: 0, new_warranty_charges: 0,
                payment_methods: [], new_payment_methods: [],
                location_id: null, sublocation_id: null, selected_locsub: null, sold_from_state: null,
                tax_perc: null, short_name: null, location_address: null, location_phone: null,
                sales_tax: 0.0, subtotal: 0.0, taxable_subtotal: 0.0, total: 0.0, comment_txt: null,
                available_credit_value: 0.0, is_layaway: false, has_signature: false, signature_url: null,
                signature_temp_data: null, signature_uploaded_at: null, address_confirmation_agreed: false,
                customer_id_card_url: null, customer_id_card_type: null, customer_id_card_number: null,
                customer_id_card_uploaded_at: null, gift_card_discount: 0, gift_card_code: null, gift_card_id: null,
            };
        },

        fetch_prereqs() {
            const that = this;
            Requestor.getAsync("/api/sales/get_default_locsubs/", {},
                function (response) {
                    if (response.data.result) that.default_locsubs = response.data.result;
                    else that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                },
                function () {
                    that.$bvToast.toast("Error loading default location.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );

            Requestor.getAsync("/api/inventory/get_all_loc_inventory_dict/", {},
                function (response) {
                    if (response.data.result) {
                        that.all_loc_inventory_dict = response.data.result;
                        that.setup_inventory_count();
                    } else {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                function () {
                    that.$bvToast.toast("Error loading inventory.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );

            Requestor.getAsyncP("/api/finance_company/get_all_finance_companies/", {}, this,
                function (response) {
                    if (response.data.result) {
                        const active = response.data.result.filter(c => c.is_active === true);
                        that.finance_companies = active.map(d => ({
                            value: d.finance_company_name,
                            text: d.finance_company_name,
                            phone_number: d.finance_company_phone_number,
                            company_charges_tax_in_states: d.finance_company_charges_tax_in_states,
                            company_type: d.company_type,
                            is_active: d.is_active,
                        }));
                        that.finance_only_companies = that.finance_companies.filter(d => d.company_type === 'finance' || !d.company_type);
                        that.tax_exempt_companies = that.finance_companies.filter(d => d.company_type === 'tax_exempt');
                    } else {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                function () {
                    that.$bvToast.toast("Error loading finance companies.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );

            Requestor.getAsync("/api/sales/get_all_zipcodes", {},
                function (response) {
                    if (response.data.result) that.zipcodes = response.data.result;
                    else that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                },
                function () {
                    that.$bvToast.toast("Error loading zipcodes.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );
        },

        setup_inventory_count() {
            const dictionary = this.all_loc_inventory_dict || {};
            const counts = {};
            Object.keys(dictionary).forEach(key => {
                const pidsku = key.split("|").slice(0, 2).join("|");
                counts[pidsku] = (counts[pidsku] || 0) + dictionary[key];
            });
            this.inventory_counts = counts;
        },

        fetchPolicy() {
            const that = this;
            const fallbackEn = '<p>Please read and agree to our standard terms and conditions.</p>';
            const fallbackEs = '<p>Por favor, lea y acepte nuestros términos y condiciones estándar.</p>';
            const f = function (error) {
                console.error("Error loading policy:", error);
                that.policyText = that.policyText || fallbackEn;
                that.policyTextSpanish = that.policyTextSpanish || fallbackEs;
            };
            Requestor.getAsyncP("/api/policy/get_policy/", { language: 'en' }, this, function (response) {
                that.policyText = (response.data.result && response.data.result.length) ? response.data.result[0].text : fallbackEn;
            }, f);
            Requestor.getAsyncP("/api/policy/get_policy/", { language: 'es' }, this, function (response) {
                that.policyTextSpanish = (response.data.result && response.data.result.length) ? response.data.result[0].text : fallbackEs;
            }, f);
        },

        fetchCategories() {
            const that = this;
            Requestor.getSyncP("/api/category/get_all_categories/", {}, this,
                function (response) {
                    if (response.data && !response.data.error) {
                        if (Array.isArray(response.data.result)) that.categories = response.data.result;
                        else if (Array.isArray(response.data)) that.categories = response.data;
                        else if (response.data.categories) that.categories = response.data.categories;
                        that.fetchAllHierarchyOptions();
                    } else {
                        that.$bvToast.toast(response.data.error || "Error loading categories", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                function (error) {
                    console.error("Error loading categories:", error);
                }
            );
        },

        fetchAllHierarchyOptions(categoryId = null) {
            const that = this;
            const data = categoryId ? { category_id: categoryId } : {};
            Requestor.getSyncP("/api/product/get_hierarchy_options/", data, this,
                function (response) {
                    if (response.data && !response.data.error && response.data.result) {
                        const options = response.data.result;
                        that.colorOptions = [{ value: null, text: 'Select Color' }]
                            .concat((options.colors || []).map(c => ({ value: c.id, text: c.color_name || c.name || 'Unknown' })));
                        that.sizeOptions = [{ value: null, text: 'Select Size' }]
                            .concat((options.sizes || []).map(s => ({ value: s.id, text: s.size_name || s.name || 'Unknown' })));
                        if (options.subcategories) that.subcategories = options.subcategories;
                    }
                },
                function (error) {
                    console.error("Error loading hierarchy options:", error);
                }
            );
        },

        fetchSubcategories(categoryId = null) {
            const that = this;
            const data = categoryId ? { category_id: categoryId } : {};
            Requestor.postSyncP("/api/product/get_subcategories/", data, this,
                function (response) {
                    if (response.data && !response.data.error) that.subcategories = response.data.result || [];
                },
                function (error) {
                    console.error("Error loading subcategories:", error);
                }
            );
        },

        onCategoryChange(categoryId) {
            if (categoryId) this.fetchSubcategories(categoryId);
        },
        onSpecialCategoryChange(categoryId) {
            if (categoryId) this.special_item.subcategory_id = null;
        },

        getSelectedLocationId() {
            if (!this.invoice.selected_locsub) return null;
            return parseInt(String(this.invoice.selected_locsub).split('|')[0]);
        },
        getSelectedLocationName() {
            if (!this.invoice.selected_locsub) return '';
            const loc = (this.packet.locsubs || []).find(l => l.value === this.invoice.selected_locsub);
            if (loc && loc.text) return loc.text.split('|')[0].trim();
            return this.invoice.short_name || 'Selected location';
        },
        getLocationName(locsubValue) {
            if (!locsubValue) return '';
            const loc = (this.packet.locsubs || []).find(l => l.value === locsubValue);
            return loc ? loc.text : 'Unknown location';
        },
        openDeliveryCalendar() {
            if (!this.invoice.selected_locsub) {
                this.posWarn("Select a store first to view the delivery calendar.");
                return;
            }
            this.$bvModal.show('modal-delivery-calendar');
        },
        onDeliveryCalendarShown() {
            if (this.$refs.deliveryCalendar && typeof this.$refs.deliveryCalendar.refreshCalendar === 'function') {
                this.$refs.deliveryCalendar.refreshCalendar();
            }
        },
        get_scheduled_deliveries() {
            const that = this;
            Requestor.getAsync("/api/sales/get_deliveries_by_dates/", {},
                function (response) {
                    if (response.data.result) {
                        that.scheduled_deliveries = response.data.result;
                        that.$bvModal.show("modal-deliveries");
                    } else {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                function () {
                    that.$bvToast.toast("Error loading scheduled deliveries.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );
        },

        set_location: async function (locsub) {
            const selectedLoc = (this.packet.locsubs || []).find(loc => loc.value === locsub);
            const locationName = selectedLoc ? selectedLoc.text.split('|')[0].trim() : 'location';

            this.locationLoading = true;
            this.$refs.loaderAnimation.show(`Setting up ${locationName}...`);
            await new Promise(resolve => setTimeout(resolve, 300));
            this.$refs.loaderAnimation.show(`Loading ${locationName} details...`);

            const that = this;
            const s = function (response) {
                if (response.data.error) {
                    that.$refs.loaderAnimation.hide();
                    that.$bvToast.toast("Error while retrieving location.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                } else if (!response.data.result || response.data.result.length == 0) {
                    that.$refs.loaderAnimation.hide();
                    that.$vex.dialog.alert({ message: "CANNOT FIND THE SALES LOCATION. PLEASE REFRESH YOUR BROWSER." });
                } else {
                    that.$refs.loaderAnimation.show("Applying location settings...");
                    const r = response.data.result[0];
                    that.invoice.location_id = r.location_id;
                    that.invoice.sublocation_id = r.sublocation_id;
                    that.invoice.sold_from_state = r.state;
                    that.invoice.tax_perc = r.tax_perc;
                    that.invoice.short_name = r.short_name;
                    that.invoice.location_address = r.address || r.full_address || r.location_name;
                    that.invoice.location_phone = r.phone || r.phone_number || r.location_phone || null;

                    that.resetSaleData();
                    that.fetchInvoiceId(that.invoice.short_name);

                    that.$bvToast.toast(`Location loaded: ${that.invoice.short_name}`, { title: "Location set", autoHideDelay: 1000, variant: "success" });
                    setTimeout(() => that.$refs.loaderAnimation.hide(), 400);
                }
                that.locationLoading = false;
                that.loading = false;
            };
            const f = function () {
                that.$refs.loaderAnimation.hide();
                that.$bvToast.toast("System error while getting location info. Try again.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                that.locationLoading = false;
                that.loading = false;
            };
            Requestor.getSync("/api/location/get_location_info/", { locsub: locsub }, s, f);
        },

        fetchInvoiceId(shortName) {
            const that = this;
            Requestor.postAsyncP("/api/sales/generate_invoice_id/", { short_name: shortName }, this,
                function (response) {
                    if (response.data.result && response.data.result.invoice_id) {
                        that.invoice.invoice_id = response.data.result.invoice_id;
                        that.invoice.draft_id = that.invoice.invoice_id;
                    }
                },
                function (error) {
                    console.error("Error generating invoice_id:", error);
                    that.invoice.invoice_id = that.generateInvoiceId(shortName);
                    that.invoice.draft_id = that.invoice.invoice_id;
                }
            );
        },
        generateInvoiceId(shortName) {
            return `${shortName || 'NA'}-${Math.floor(Date.now() / 1000)}-${Math.floor(Math.random() * 1000)}`;
        },

        resetSaleData() {
            const fresh = this.get_invoice_data();
            ['location_id', 'sublocation_id', 'sold_from_state', 'tax_perc', 'short_name', 'location_address', 'location_phone', 'selected_locsub']
                .forEach(k => { fresh[k] = this.invoice[k]; });
            this.invoice = fresh;
            this.searchitems = { search_being_loaded: false, keyword: null, limit: 500, result: [] };
            this.special_item = emptySpecialItem();
            this.nonInventoryItem = emptyNonInventoryItem();
            this.tabVisited = {};
            this.refund_items = [];
            this.refund_charges = buildRefundCharges(fresh);
        },

        set_zipcode_and_state_city(e) {
            if (!e) return;
            this.$set(this.invoice.customer, 'zipcode', e.zip_code);
            this.$set(this.invoice.customer, 'city', e.city);
            this.$set(this.invoice.customer, 'state', e.state);
            this.loadBlockedDeliveryDates();
        },
        filter_zipcodes(item, query) {
            return String(item.zip_code).toLowerCase().indexOf(String(query).toLowerCase()) > -1;
        },
        updateCustomerFloorInfo() {
            const c = this.invoice.customer;
            if (!c) return;
            const floorNumbers = { 'First Floor': 1, 'Second Floor': 2, 'Third Floor': 3, 'Fourth Floor': 4 };
            let display = null;
            let number = null;
            if (c.floor === 'Other' || this.show_custom_floor) {
                display = this.custom_floor_display || null;
                number = this.custom_floor_number ? parseInt(this.custom_floor_number) : null;
            } else if (c.floor) {
                display = c.floor;
                number = floorNumbers[c.floor] || null;
            }
            let info = null;
            if (display && number && c.floor === 'Other') info = `${display} (Floor ${number})`;
            else if (display) info = display;
            else if (number) info = `Floor ${number}`;
            this.$set(c, 'floor_display', display);
            this.$set(c, 'floor_number', number);
            this.$set(c, 'floor_info', info);
        },
        validate_customer_info(c) {
            const errors = [];
            if (!c.fullname) errors.push("Please enter customer name.");
            if (!c.email) errors.push("Please enter customer email.");
            if (!c.phone) errors.push("Please enter customer phone.");
            if (!c.zipcode) errors.push("Please enter customer zipcode.");
            return errors;
        },

        getCustomerIdCardImage() {
            const url = this.invoice.customer_id_card_url;
            if (url && url.trim() !== '') {
                if (url.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
                return url;
            }
            return this.idCardUpload.previewUrl || null;
        },
        handleIdCardImageUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            if (!file.type.startsWith('image/')) { this.posWarn('Please select a valid image file.'); return; }
            if (file.size > 5 * 1024 * 1024) { this.posWarn('Image size should be less than 5MB.'); return; }
            this.idCardUpload.uploading = true;
            const reader = new FileReader();
            reader.onload = (e) => {
                this.idCardUpload.previewUrl = e.target.result;
                this.uploadIdCardImage();
            };
            reader.readAsDataURL(file);
            event.target.value = '';
        },
        openIdCardCamera() {
            if (this.isMobileDevice()) this.$refs.idCardCameraInput.click();
            else this.$refs.idCardFileInput.click();
        },
        uploadIdCardImage() {
            if (!this.idCardUpload.previewUrl) { this.idCardUpload.uploading = false; return; }
            const that = this;
            const data = {
                image_data: this.idCardUpload.previewUrl,
                invoice_id: this.invoice.invoice_id || 'temp_' + Date.now(),
                card_type: this.invoice.customer_id_card_type,
                card_number: this.invoice.customer_id_card_number,
                username: this.userDetails.username,
            };
            Requestor.postSyncP("/api/sales/upload_customer_id_card/", data, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(`Failed to upload ID card: ${response.data.error}`, { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                    } else {
                        that.invoice.customer_id_card_url = response.data.result.image_url;
                        that.invoice.customer_id_card_uploaded_at = new Date().toISOString();
                        that.$bvToast.toast('Customer ID uploaded', { title: 'Success', variant: 'success', autoHideDelay: 2000 });
                    }
                    that.idCardUpload.previewUrl = null;
                    that.idCardUpload.uploading = false;
                },
                function () {
                    that.$bvToast.toast('Failed to upload ID card. Please try again.', { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                    that.idCardUpload.previewUrl = null;
                    that.idCardUpload.uploading = false;
                }
            );
        },
        async removeIdCardImage() {
            const confirmed = await this.$bvModal.msgBoxConfirm('Remove the customer ID image?', {
                title: 'Remove ID', size: 'sm', buttonSize: 'sm', okVariant: 'danger',
                okTitle: 'Remove', cancelTitle: 'Cancel', footerClass: 'p-2', centered: true,
            });
            if (!confirmed) return;
            const clearLocal = () => {
                this.invoice.customer_id_card_url = null;
                this.invoice.customer_id_card_uploaded_at = null;
                this.idCardUpload.previewUrl = null;
            };
            if (this.invoice.edit && this.invoice.invoice_id) {
                const that = this;
                Requestor.postSyncP("/api/sales/remove_customer_id_card/", { invoice_id: this.invoice.invoice_id, username: this.userDetails.username }, this,
                    function (response) {
                        if (response.data.error) {
                            that.$bvToast.toast(response.data.error, { title: 'Error', variant: 'danger', autoHideDelay: 3000 });
                        } else {
                            clearLocal();
                            that.$bvToast.toast('ID removed', { title: 'Success', variant: 'success', autoHideDelay: 2000 });
                        }
                    },
                    function () {
                        that.$bvToast.toast('Failed to remove ID card', { title: 'Error', variant: 'danger', autoHideDelay: 3000 });
                    }
                );
            } else {
                clearLocal();
            }
        },
        showFullIdCardImage() {
            const url = this.getCustomerIdCardImage();
            if (!url) return;
            this.currentFullSizeImage = url;
            this.currentProductName = 'Customer ID';
            this.$bvModal.show('modal-image-viewer');
        },

        search(e) {
            if (e) e.preventDefault();
            this.hasSearched = true;
            if (this.search_only_from_damages) { this.search_damaged_items(); return; }

            const data = {
                keyword: this.searchitems.keyword,
                limit: this.searchitems.limit,
                include_sublocation_breakdown: true,
                include_damage_info: true,
                only_damaged: this.search_only_from_damages,
            };
            if (this.search_in_my_location) data.location_id = this.invoice.location_id;

            const that = this;
            this.searchitems.search_being_loaded = true;
            this.searchitems.result = [];
            Requestor.getSyncP("/api/product/search_for_sale/", data, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    } else {
                        that.searchitems.result = (response.data.result || []).map(item => {
                            if (item.damage_info && item.damage_info.is_damaged) {
                                item.is_damaged = true;
                                item.source = 'damage';
                                if (!item.damage_info.damage_id && item.damage_id) item.damage_info.damage_id = item.damage_id;
                            } else {
                                item.is_damaged = false;
                                item.source = 'store';
                            }
                            return item;
                        });
                    }
                    that.searchitems.search_being_loaded = false;
                },
                function () {
                    that.$bvToast.toast("Error occurred while searching product.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    that.searchitems.search_being_loaded = false;
                }
            );
        },

        search_damaged_items(e) {
            if (e) e.preventDefault();
            const that = this;
            const data = {
                keyword: this.searchitems.keyword,
                limit: this.searchitems.limit,
                location_id: this.search_in_my_location ? this.invoice.location_id : null,
            };
            this.searchitems.search_being_loaded = true;
            this.searchitems.result = [];
            Requestor.getSyncP("/api/product/search_damaged_items/", data, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    } else {
                        that.searchitems.result = (response.data.result || []).map(item => {
                            if (!item.damage_info) {
                                item.damage_info = {
                                    damage_id: item.damage_id || null,
                                    damage_grade: item.damage_grade || null,
                                    damage_description: item.damage_description || '',
                                    damage_images: item.damage_images || [],
                                    is_damaged: true,
                                };
                            } else {
                                item.damage_info.is_damaged = true;
                                if (!item.damage_info.damage_id && item.damage_id) item.damage_info.damage_id = item.damage_id;
                            }
                            item.is_damaged = true;
                            item.source = 'damage';
                            item.sourceContext = 'store';
                            return item;
                        });
                        that.$bvToast.toast(`Found ${that.searchitems.result.length} as-is items`, { title: "Search results", autoHideDelay: 1500, variant: "info" });
                    }
                    that.searchitems.search_being_loaded = false;
                },
                function () {
                    that.$bvToast.toast("Error occurred while searching damaged items.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    that.searchitems.search_being_loaded = false;
                }
            );
        },

        handleOnlyInMyLocationChange(isChecked) {
            if (!isChecked) {
                this.search_only_from_damages = false;
                if (this.invoice.items.some(i => !i.existing && i.sourceContext === 'store')) {
                    this.$bvToast.toast("Store-sourced items keep their fulfillment locations while warehouse items can be added.", { title: "Info", autoHideDelay: 3000, variant: "info" });
                }
                return;
            }
            this.invoice.items.forEach(item => {
                if (!item.existing && !item.locsub && item.sourceContext === 'store') {
                    const storeLocations = (this.packet.locsubs || []).filter(d => d.location_id == this.invoice.location_id && d.sublocation_type_id !== 1);
                    if (storeLocations.length) item.locsub = storeLocations[0].value;
                    if (['store_pickup', 'home_delivery'].includes(item.order_type) && !item.drop_locsub && storeLocations.length) {
                        item.drop_locsub = storeLocations[0].value;
                    }
                }
            });
            const warehouseTakeHome = this.invoice.items.filter(i => i.order_type === 'customer_takes' && i.sourceContext === 'warehouse');
            if (warehouseTakeHome.length) {
                this.$vex.dialog.alert({
                    message: `Take Home is not allowed for warehouse-sourced items.\n\nInvalid: ${warehouseTakeHome.map(i => i.product_name || i.product_sku).join(', ')}`,
                });
            }
        },

        handleOnlyFromDamagesChange(isChecked) {
            if (isChecked && this.search_in_my_location) {
                this.search_damaged_items();
            } else if (isChecked) {
                this.search_only_from_damages = false;
                this.$vex.dialog.alert({ message: "Check 'Only in my location' first to search for as-is items." });
            } else {
                this.searchitems.result = [];
            }
        },

        getFilteredSublocationBreakdown(product) {
            if (!product.sublocation_breakdown || !product.sublocation_breakdown.length) return [];
            return product.sublocation_breakdown.filter(loc => loc.available > 0).sort((a, b) => b.available - a.available);
        },
        getShortLocationName(fullName) {
            if (!fullName) return 'Unknown';
            const parts = fullName.split('|');
            if (parts.length > 1) return parts[parts.length - 1].trim();
            return fullName.length > 15 ? fullName.substring(0, 12) + '...' : fullName;
        },
        getLocationClass(locationName) {
            if (!locationName) return 'default';
            const name = locationName.toLowerCase();
            if (name.includes('stock')) return 'stock';
            if (name.includes('floor')) return 'floor';
            if (name.includes('damage') || name.includes('as is')) return 'damage';
            if (name.includes('warehouse')) return 'warehouse';
            if (name.includes('backorder')) return 'backorder';
            return 'default';
        },

        openCameraScanner() {
            this.scannerError = null;
            this.$bvModal.show('modal-barcode-scanner');
        },
        startScanner() {
            if (this.$refs.quaggaScanner) this.$refs.quaggaScanner.startScanner();
        },
        stopScanner() {
            if (this.$refs.quaggaScanner) this.$refs.quaggaScanner.stopScanner();
            this.$bvModal.hide('modal-barcode-scanner');
        },
        toggleCamera() {
            this.currentCamera = this.currentCamera === 'environment' ? 'user' : 'environment';
        },
        handleScannedBarcode(barcodeValue) {
            this.barcodeInput = barcodeValue;
            this.handleBarcodeScan();
            this.stopScanner();
        },
        handleBarcodeScan() {
            if (!this.barcodeInput || this.barcodeInput.trim() === '') {
                this.posWarn("Scan or type a barcode first.");
                return;
            }
            const scannedValue = this.barcodeInput.trim();
            this.barcodeInput = '';
            this.$nextTick(() => { if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus(); });
            this.searchByBarcode(scannedValue);
        },
        searchByBarcode(barcodeValue) {
            this.barcodeProcessing = true;
            this.searchitems.search_being_loaded = true;
            const that = this;
            const refocus = () => that.$nextTick(() => { if (that.$refs.barcodeInput) that.$refs.barcodeInput.focus(); });
            const data = {
                barcode: barcodeValue,
                location_id: this.search_in_my_location ? this.invoice.location_id : null,
                include_sublocation_breakdown: true,
                include_damage_info: true,
            };
            Requestor.getSyncP("/api/product/search_by_barcode/", data, this,
                function (response) {
                    that.barcodeProcessing = false;
                    that.searchitems.search_being_loaded = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Barcode not found", autoHideDelay: 1500, variant: "warning" });
                    } else if (response.data.result && response.data.result.length > 0) {
                        const results = response.data.result.map(item => { item.quantity = 1; return item; });
                        if (results.length === 1) {
                            that.addProduct(results[0]);
                            that.searchitems.result = [];
                        } else {
                            that.searchitems.result = results;
                            that.$bvToast.toast(`Found ${results.length} products for ${barcodeValue}. Pick one in Search.`, { title: "Multiple matches", autoHideDelay: 2000, variant: "info" });
                        }
                    } else {
                        that.$bvToast.toast(`No product found with barcode ${barcodeValue}`, { title: "Not found", autoHideDelay: 1500, variant: "warning" });
                    }
                    refocus();
                },
                function () {
                    that.barcodeProcessing = false;
                    that.searchitems.search_being_loaded = false;
                    that.$bvToast.toast("Error searching by barcode.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                    refocus();
                }
            );
        },
        fetchScannedItem(result) {
            const parts = result.split("|");
            const that = this;
            Requestor.postSyncP("/api/product/search_single_for_sale/", { product_sku: parts[1], product_id: parseInt(parts[2]) }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    } else if (response.data.result.length > 0) {
                        that.searchitems.result = response.data.result;
                        that.productModalTab = 0;
                    }
                    that.$bvModal.hide("modal-scanner");
                },
                function () {
                    that.$bvToast.toast("Error occurred while scanning product.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    that.$bvModal.hide("modal-scanner");
                }
            );
        },

        getProductImage(item) {
            if (!item) return null;
            if (item.is_damaged && item.damage_info && item.damage_info.damage_images) {
                const imgs = item.damage_info.damage_images;
                if (imgs.length > 0 && imgs[0].image_url) return imgs[0].image_url.trim();
            }
            if (item.as_is_image_url && item.as_is_image_url.trim() !== '') return item.as_is_image_url;
            if (item.image_url && item.image_url.trim() !== '') {
                const url = item.image_url.trim();
                if (url.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
                return url;
            }
            return null;
        },
        getProductImagedamage(item) {
            if (item && item.as_is_image_url && item.as_is_image_url.trim() !== '') return item.as_is_image_url;
            return null;
        },
        handleImageError(event) { event.target.style.display = 'none'; },
        handleImageLoad() {},
        showFullImage(item) {
            const url = this.getProductImage(item);
            if (!url) { this.$bvToast.toast("No image for this product", { title: "Info", autoHideDelay: 2000, variant: "info" }); return; }
            this.currentFullSizeImage = url;
            this.currentProductName = item.product_name || item.product_sku || 'Product image';
            this.$bvModal.show('modal-image-viewer');
        },
        showFullImagedamage(item) {
            const url = this.getProductImagedamage(item);
            if (!url) return;
            this.currentFullSizeImage = url;
            this.currentProductName = item.product_name || item.product_sku || 'Product image';
            this.$bvModal.show('modal-image-viewer');
        },
        handleImageUpload(item, event) {
            const file = event.target.files[0];
            if (!file) return;
            if (!file.type.startsWith('image/')) { this.posWarn('Please select a valid image file.'); return; }
            if (file.size > 5 * 1024 * 1024) { this.posWarn('Image size should be less than 5MB.'); return; }
            this.imageUpload.currentItem = item;
            const reader = new FileReader();
            reader.onload = (e) => { this.imageUpload.previewUrl = e.target.result; };
            reader.readAsDataURL(file);
            event.target.value = '';
        },
        uploadItemImage() {
            if (!this.imageUpload.currentItem || !this.imageUpload.previewUrl) return;
            const item = this.imageUpload.currentItem;
            const that = this;
            this.imageUpload.uploading = true;
            let invoiceId = this.invoice.invoice_id;
            if (!invoiceId) { invoiceId = 'temp_' + Date.now(); item.temp_invoice_id = invoiceId; }
            Requestor.postSyncP("/api/sales/upload_item_image/", {
                image_data: this.imageUpload.previewUrl, invoice_id: invoiceId, product_sku: item.product_sku, is_as_is: true,
            }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(`Failed to upload image: ${response.data.error}`, { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                    } else {
                        that.$set(item, 'as_is_image_url', response.data.result.image_url);
                        that.cancelImageUpload();
                    }
                    that.imageUpload.uploading = false;
                },
                function () {
                    that.$bvToast.toast('Failed to upload image. Please try again.', { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                    that.imageUpload.uploading = false;
                }
            );
        },
        removeItemImage(item) {
            const that = this;
            Requestor.postSyncP("/api/sales/remove_item_image/", { invoice_id: this.invoice.invoice_id, product_sku: item.product_sku }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(`Failed to remove image: ${response.data.error}`, { title: 'Error', variant: 'danger', autoHideDelay: 3000 });
                    } else {
                        that.$set(item, 'as_is_image_url', null);
                    }
                },
                function () {
                    that.$bvToast.toast('Failed to remove image', { title: 'Error', variant: 'danger', autoHideDelay: 3000 });
                }
            );
        },
        cancelImageUpload() {
            this.imageUpload = { uploading: false, currentItem: null, previewUrl: null };
        },

        clearNonInventoryImage() {
            this.nonInventoryImage = emptyImage();
        },
        openCamera() {
            if (this.isMobileDevice()) this.$refs.cameraInput.click();
            else this.$refs.nonInventoryFileInput.click();
        },
        handleNonInventoryImageUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            if (!file.type.startsWith('image/')) { this.posWarn('Please select a valid image file.'); return; }
            if (file.size > 5 * 1024 * 1024) { this.posWarn('Image size should be less than 5MB.'); return; }
            this.nonInventoryImage.uploading = true;
            const reader = new FileReader();
            reader.onload = (e) => {
                this.nonInventoryImage.previewUrl = e.target.result;
                this.uploadNonInventoryImage();
            };
            reader.readAsDataURL(file);
            event.target.value = '';
        },
        uploadNonInventoryImage() {
            if (!this.nonInventoryImage.previewUrl) { this.nonInventoryImage.uploading = false; return; }
            const that = this;
            Requestor.postSyncP("/api/sales/upload_non_inventory_image/", {
                image_data: this.nonInventoryImage.previewUrl, temp_id: 'temp_' + Date.now(), username: this.userDetails.username,
            }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(`Failed to upload image: ${response.data.error}`, { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                        that.nonInventoryImage.previewUrl = null;
                    } else {
                        that.nonInventoryImage.uploadedUrl = response.data.result.image_url;
                    }
                    that.nonInventoryImage.uploading = false;
                },
                function () {
                    that.$bvToast.toast('Failed to upload image. Please try again.', { title: 'Upload failed', variant: 'danger', autoHideDelay: 3000 });
                    that.nonInventoryImage.previewUrl = null;
                    that.nonInventoryImage.uploading = false;
                }
            );
        },

        getVendorName(vendorId) {
            if (!vendorId) return 'N/A';
            if (typeof vendorId === 'string' && isNaN(vendorId)) return vendorId;
            if (Array.isArray(this.packet.vendors)) {
                const v = this.packet.vendors.find(x => (typeof x === 'object' ? (x.value == vendorId || x.id == vendorId || x.text == vendorId) : x == vendorId));
                if (v) return typeof v === 'object' ? (v.text || v.name || v.label || v.value || vendorId) : v;
            }
            return vendorId;
        },

        addNonInventoryItem() {
            if (!this.canAddNonInventoryItem) return;
            const s = this.special_item;
            const selectedCategory = this.categories.find(c => c.category_id === s.category_id);
            const selectedSubcategory = this.subcategories.find(x => x.id === s.subcategory_id);
            const selectedColor = this.colorOptions.find(c => c.value === s.color_id);
            const selectedSize = this.sizeOptions.find(x => x.value === s.size_id);
            const description = s.product_name || s.product_sku;
            const vendorName = this.getVendorName(s.vendor);

            this.addProduct({
                product_id: null, product_sku: s.product_sku, product_name: description,
                product_category: s.category_id,
                product_category_name: selectedCategory ? selectedCategory.category_name : null,
                subcategory_id: s.subcategory_id,
                subcategory_name: selectedSubcategory ? selectedSubcategory.subcategory : null,
                color_id: s.color_id, color_name: selectedColor ? selectedColor.text : null,
                size_id: s.size_id, size_name: selectedSize ? selectedSize.text : null,
                vendor: s.vendor, vendor_name: vendorName,
                quantity: parseInt(s.quantity), selling_price: parseFloat(s.selling_price),
                new_selling_price: parseFloat(s.selling_price), discount_offered: 0.0,
                is_special: true, is_non_inventory: false, sourceContext: 'store', source: 'store',
                image_url: this.nonInventoryImage.uploadedUrl || null,
                order_type: 'customer_takes', delivery_date: moment().format("MM-DD-YYYY"),
                existing: false, quantity_per_box: 1,
                special_order_metadata: {
                    category_id: s.category_id,
                    category_name: selectedCategory ? selectedCategory.category_name : null,
                    subcategory_id: s.subcategory_id,
                    subcategory_name: selectedSubcategory ? selectedSubcategory.subcategory : null,
                    color_id: s.color_id, color_name: selectedColor ? selectedColor.text : null,
                    size_id: s.size_id, size_name: selectedSize ? selectedSize.text : null,
                    vendor: s.vendor, vendor_name: vendorName,
                    description: description, product_name: description,
                    added_by: this.userDetails.username, added_at: new Date().toISOString(),
                    status: 'pending', notes: '',
                },
            });
            this.resetSpecialOrderForm();
        },

        addNonInventoryItemToSale() {
            if (!this.canAddNonInventoryItemToSale) return;
            const n = this.nonInventoryItem;
            const selectedCategory = this.categories.find(c => c.category_id === n.category_id);
            const categoryName = selectedCategory ? selectedCategory.category_name : n.category_id;
            const vendorName = this.getVendorName(n.vendor);

            this.addProduct({
                product_id: null, product_sku: n.product_sku, product_name: n.product_name,
                product_category: n.category_id, product_category_name: categoryName,
                vendor: vendorName, quantity: parseInt(n.quantity),
                selling_price: parseFloat(n.selling_price),
                new_selling_price: parseFloat(n.selling_price),
                discount_offered: 0.0, is_special: false, is_non_inventory: true,
                sourceContext: 'store', source: 'store',
                image_url: this.nonInventoryImage.uploadedUrl || null,
                order_type: 'customer_takes', delivery_date: moment().format("MM-DD-YYYY"),
                existing: false, quantity_per_box: 1,
                non_inventory_metadata: {
                    category_id: n.category_id, category_name: categoryName,
                    added_by: this.userDetails.username, added_at: new Date().toISOString(),
                    vendor_name: vendorName,
                },
            });
            this.resetNonInventoryForm();
        },

        resetSpecialOrderForm() { this.special_item = emptySpecialItem(); this.clearNonInventoryImage(); },
        resetNonInventoryForm() { this.nonInventoryItem = emptyNonInventoryItem(); this.clearNonInventoryImage(); },

        hasActiveDiscount(product) {
            if (!product || !product.special_discount_price || product.special_discount_price <= 0) return false;
            if (!product.special_discount_end_date) return false;
            let endDate;
            const d = String(product.special_discount_end_date);
            const parts = d.split('-');
            if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
                endDate = new Date(parts[2], parts[0] - 1, parts[1]);
            } else { endDate = new Date(d); }
            endDate.setHours(23, 59, 59, 999);
            return new Date() <= endDate;
        },

        getSaleTypeLabel(item) {
            if (!item) return 'Promo price';
            const st = String(item.sale_type || '').trim();
            if (!st) return 'Promo price';
            if (st.toLowerCase() === 'discount') return 'Promo price';
            return st + ' price';
        },

        getSaleTypeShortLabel(item) {
            if (!item) return 'Promo';
            const st = String(item.sale_type || '').trim();
            if (!st) return 'Promo';
            if (st.toLowerCase() === 'discount') return 'Promo';
            return st;
        },

        getDisplayPrice(product) {
            if (!product) return 0;
            const special = this.getDiscountedPrice(product);
            if (special !== null) return special;
            return Math.round((parseFloat(product.selling_price) || 0) * 100) / 100;
        },
        applyDiscountToItem(item) {
            if (!item) return item;
            item.new_selling_price = Math.round((parseFloat(item.selling_price) || 0) * 100) / 100;
            if (typeof item.discount_offered === 'undefined' || item.discount_offered === null) {
                item.discount_offered = 0.0;
            }
            return item;
        },
        calculateMaxDiscount(item) {
            if (!item) return 0;
            const basePrice = parseFloat(item.special_discount_price) || parseFloat(item.new_selling_price) || parseFloat(item.selling_price) || 0;
            const quantity = parseInt(item.quantity) || 1;
            let maxDiscount;
            if (item.max_discount_percentage) {
                maxDiscount = basePrice * quantity * (parseFloat(item.max_discount_percentage) / 100);
            } else if (item.max_allowed_discount_per_unit) {
                maxDiscount = (parseFloat(item.max_allowed_discount_per_unit) || 0) * quantity;
            } else {
                maxDiscount = basePrice * quantity * 0.10;
            }
            return Math.round(maxDiscount * 100) / 100;
        },
        hasDiscountError(item) {
            if (!item || !item.discount_offered) return false;
            return (parseFloat(item.discount_offered) || 0) > this.calculateMaxDiscount(item);
        },
        canApplyManualDiscount(item) {
            return !!item && this.calculateMaxDiscount(item) !== 0;
        },
        onDiscountChange(item) {
            if (!item.discount_offered || parseFloat(item.discount_offered) === 0) return;
            const discount = parseFloat(item.discount_offered) || 0;
            const maxDiscount = this.calculateMaxDiscount(item);
            if (maxDiscount === 0) {
                item.discount_offered = 0.0;
                this.posWarn(`Manual discount isn't allowed for ${item.product_sku} (price is $0).`);
                return;
            }
            if (discount > maxDiscount) {
                item.discount_offered = maxDiscount.toFixed(2);
                this.$bvToast.toast(`Discount for ${item.product_sku} capped at $${maxDiscount.toFixed(2)}.`, { title: "Discount capped", variant: "warning", autoHideDelay: 3000 });
                return;
            }
            const totalPrice = (parseFloat(item.new_selling_price || item.selling_price) || 0) * (parseInt(item.quantity) || 1);
            if (discount > totalPrice) item.discount_offered = totalPrice.toFixed(2);
        },
        validateDiscount(item) {
            if (!item.discount_offered || isNaN(parseFloat(item.discount_offered))) return true;
            const price = parseFloat(item.new_selling_price || item.selling_price);
            if (price <= 0) return true;
            const max = this.calculateMaxDiscount(item);
            if (parseFloat(item.discount_offered) > max) {
                this.posWarn(`Discount can't exceed $${max.toFixed(2)} for ${item.product_sku}.`);
                item.discount_offered = max.toFixed(2);
                return false;
            }
            return true;
        },
        validatePriceChange(item, event) {
            const raw = event && event.target ? event.target.value : item.new_selling_price;
            if (raw === '' || raw === null || raw === undefined) return true;
            let entered = parseFloat(raw);
            if (isNaN(entered)) return true;
            const original = parseFloat(item.selling_price) || 0;
            entered = Math.round(entered * 100) / 100;
            const floor = this.hasActiveDiscount(item) ? parseFloat(item.special_discount_price) : original;
            if (entered < floor) {
                item.new_selling_price = floor;
                this.$bvToast.toast(`Price can't go below $${floor.toFixed(2)}. Only increases are allowed.`, { title: "Price restriction", autoHideDelay: 3000, variant: "warning" });
                return false;
            }
            item.new_selling_price = entered;
            return true;
        },
        validateQuantityPerBox(item) {
            const qtyPerBox = parseInt(item.quantity_per_box) || 1;
            if (qtyPerBox <= 1) return true;
            const quantity = parseInt(item.quantity) || 0;
            if (quantity > 0 && quantity % qtyPerBox !== 0) {
                const corrected = Math.max(qtyPerBox, Math.round(quantity / qtyPerBox) * qtyPerBox);
                item.quantity = corrected;
                this.$bvToast.toast(`Quantity adjusted to ${corrected} (multiple of ${qtyPerBox})`, { title: "Quantity adjusted", variant: "info", autoHideDelay: 2000 });
            }
            return true;
        },
        hasQuantityError(item) {
            const qtyPerBox = parseInt(item.quantity_per_box) || 1;
            if (qtyPerBox <= 1) return false;
            const quantity = parseInt(item.quantity) || 0;
            return quantity > 0 && quantity % qtyPerBox !== 0;
        },

        addProduct(item) {
            item = this.applyDiscountToItem(item);
            const isActuallyNonInventory = item.is_non_inventory === true;
            const isActuallySpecialOrder = item.is_special === true;

            let isDamaged = false;
            let damageInfo = null;
            if (item.damage_info) {
                damageInfo = item.damage_info;
                isDamaged = !!(item.damage_info.is_damaged || item.is_damaged);
            }
            if (item.is_damaged === true || item.source === 'damage' || item.damage_id) {
                isDamaged = true;
                if (!damageInfo) {
                    damageInfo = {
                        damage_id: item.damage_id || null, is_damaged: true,
                        damage_grade: item.damage_grade || null,
                        damage_description: item.damage_description || '',
                        damage_images: item.damage_images || [],
                    };
                }
            }
            const isDamagedItem = isDamaged || item.is_as_is;

            if (this.hasQuantityError(item)) {
                this.$vex.dialog.alert({ message: `Quantity must be a multiple of ${item.quantity_per_box}.` });
                return;
            }
            if (!item.quantity || isNaN(parseInt(item.quantity))) {
                this.$vex.dialog.alert({ message: "Please specify a valid quantity." });
                return;
            }
            if (item.discount_offered && parseFloat(item.discount_offered) > 0 && !this.validateDiscount(item)) return;
            if (this.search_in_my_location && item.available !== undefined && parseInt(item.quantity) > parseInt(item.available)) {
                this.$vex.dialog.alert({ message: `Cannot add ${item.quantity} items. Only ${item.available} available in stock.` });
                return;
            }
            const isNewItemAsIs = !!isDamagedItem;
            const dup = this.invoice.items.some(d => !d.existing && d.product_sku === item.product_sku && !!(d.is_damaged || d.is_as_is) === isNewItemAsIs);
            if (dup) {
                const statusText = isNewItemAsIs ? "AS-IS" : "NORMAL";
                this.$vex.dialog.alert({ message: `A ${statusText} copy of SKU ${item.product_sku} is already in the cart. Change its quantity instead.` });
                return;
            }
            if (!item.selling_price || isNaN(parseInt(item.selling_price))) {
                this.$vex.dialog.alert({ message: "Please specify the selling price." });
                return;
            }
            if (!item.product_sku) {
                this.$vex.dialog.alert({ message: "Please specify a valid SKU." });
                return;
            }
            if (item.product_sku !== item.product_sku.replace(/\s/g, "")) {
                this.$vex.dialog.alert({ message: "SKU can't contain spaces." });
                return;
            }

            item.quantity = parseInt(item.quantity);
            const is_special = item.product_id == null;
            if (is_special) {
                if (!item.vendor) {
                    this.$vex.dialog.alert({ message: "Please pick a vendor for this item." });
                    return;
                }
                item.new_selling_price = item.selling_price;
            }

            let context, orderType, deliveryDate;
            if (isActuallyNonInventory || isActuallySpecialOrder) {
                context = 'store';
                orderType = 'customer_takes';
                deliveryDate = moment().format("MM-DD-YYYY");
            } else {
                context = this.search_in_my_location ? 'store' : 'warehouse';
                orderType = item.order_type || "home_delivery";
                deliveryDate = item.delivery_date;
            }
            if (isDamaged) context = 'store';
            if (orderType === 'customer_takes') deliveryDate = moment().format("MM-DD-YYYY");

            let defaultLocsub = null, defaultDropLocsub = null, defaultShipLocsub = null;
            if (context === 'store' && this.invoice.location_id) {
                const storeLocs = (this.packet.locsubs || []).filter(d => d.location_id == this.invoice.location_id);
                const damageLocs = storeLocs.filter(d => this.isDamageLocation(d) && d.sublocation_type_id !== 1);
                const regularLocs = storeLocs.filter(d => !this.isDamageLocation(d) && d.sublocation_type_id !== 1);
                const pickStock = (list) => {
                    const named = list.find(d => d.text.toLowerCase().includes('stock'));
                    return named ? named.value : (list[0] ? list[0].value : null);
                };

                if (isDamagedItem && damageLocs.length) {
                    defaultLocsub = damageLocs[0].value;
                    defaultDropLocsub = defaultLocsub;
                    defaultShipLocsub = defaultLocsub;
                } else {
                    defaultLocsub = pickStock(regularLocs) || (storeLocs[0] ? storeLocs[0].value : null);
                    if (isDamagedItem || isActuallyNonInventory || isActuallySpecialOrder) {
                        defaultDropLocsub = defaultLocsub;
                        defaultShipLocsub = defaultLocsub;
                    } else if (orderType === 'store_pickup' || orderType === 'home_delivery') {
                        const others = regularLocs.filter(d => d.value !== defaultLocsub);
                        defaultDropLocsub = others.length ? others[0].value : (regularLocs[0] ? regularLocs[0].value : null);
                    }
                }
            }

            const invitem = {
                product_id: is_special ? -1 : item.product_id,
                product_sku: item.product_sku, product_name: item.product_name,
                product_category: item.product_category,
                product_category_name: item.product_category_name,
                vendor: item.vendor, quantity: item.quantity,
                selling_price: Math.round((parseFloat(item.selling_price) || 0) * 100) / 100,
                new_selling_price: Math.round((parseFloat(item.selling_price) || 0) * 100) / 100,
                special_discount_price: item.special_discount_price || null,
                special_discount_end_date: item.special_discount_end_date || null,
                has_active_discount: this.hasActiveDiscount(item),
                sale_type: item.sale_type || null,
                discount_percentage: item.discount_percentage || null,
                orig_selling_price:
                    parseFloat(item.orig_selling_price) ||
                    parseFloat(item.original_price) ||
                    parseFloat(item.msrp) ||
                    parseFloat(item.selling_price) ||
                    0,
                discount_offered: 0.0,
                is_group_item: !!(item.elements && item.elements.length > 0),
                children_count: item.elements ? item.elements.length : 0,
                children: item.elements,
                is_special: isActuallySpecialOrder || false,
                is_non_inventory: isActuallyNonInventory || false,
                order_type: orderType, delivery_date: deliveryDate,
                locsub: defaultLocsub, drop_locsub: defaultDropLocsub, ship_locsub: defaultShipLocsub,
                state: !is_special ? 1 : 2, merchandise_status: null,
                cubes: item.cubes, existing: false,
                sourceContext: context, source: isDamaged ? 'damage' : context,
                available: item.available,
                quantity_per_box: item.quantity_per_box || 1,
                image_url: item.image_url, home_delivery_type: null,
                is_as_is: item.is_as_is || false,
                is_damaged: isDamaged, damage_info: damageInfo,
                as_is_image_url: item.as_is_image_url || null,
                sublocation_breakdown: item.sublocation_breakdown || [],
                non_inventory_metadata: item.non_inventory_metadata || null,
                special_order_metadata: item.special_order_metadata || null,
                max_discount_percentage: item.max_discount_percentage || null,
                max_allowed_discount_per_unit: item.max_allowed_discount_per_unit || null,
            };

            if (invitem.is_special) {
                const v = (this.packet.vendors || []).find(d => d.value == invitem.vendor);
                if (v) invitem.vendor = v.text;
            }

            if (invitem.order_type === 'delivery_from_store') this.setShipLocsubForDelivery(invitem);

            this.invoice.items.push(invitem);

            if (isDamaged) {
                this.$bvToast.toast(`${item.product_sku} is an AS-IS item (ID ${damageInfo.damage_id || 'N/A'}). It can't be returned or refunded.`,
                    { title: "As-is item", autoHideDelay: 5000, variant: "danger" });
            }
            this.$bvToast.toast(`Added ${invitem.product_name || invitem.product_sku} to the cart.`, { title: "Added to cart", autoHideDelay: 2000, variant: "success" });
            this.$nextTick(() => this.validateDeliveryFromStoreLocations());
        },

        getExistingItemNumber(index) { return index + 1; },
        getNewItemNumber(index) { return index + 1; },
        getItemOriginalIndex(item) { return this.invoice.items.findIndex(i => i === item); },

        show_item(item) {
            if (item.existing == false || !item.existing) return true;
            return (item.quantity - (item.refunded_so_far_quantity || 0)) !== 0;
        },

        get_order_types(item) {
            const base = [
                { value: "store_pickup", text: "Store Pickup" },
                { value: "home_delivery", text: "Home Delivery" },
                { value: "delivery_from_store", text: "Delivery from Store" },
            ];
            const isFromStore = item.sourceContext === 'store' || (item.existing && (item.locsub || item.source_location_id));
            return isFromStore ? [{ value: "customer_takes", text: "Take Home" }, ...base] : base;
        },

        get_item_status(item) {
            return parseInt(item.state) === 1 ? 'delivered' : 'refundable';
        },

        del_date_set(item) {
            if (item.order_type === "customer_takes") item.delivery_date = moment().format("MM-DD-YYYY");
            if (item.order_type === 'delivery_from_store') this.setShipLocsubForDelivery(item);
            this.$nextTick(() => this.validateDeliveryFromStoreLocations());
        },

        disablePreviousDates(date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (date < today) return true;

            const key = moment(date).format('MM-DD-YYYY');
            if (Object.prototype.hasOwnProperty.call(this.blockedDates, key)) {
                return true;
            }

            const endWindow = moment().add(60, 'days').endOf('day');
            if (moment(date).isAfter(endWindow)) return true;

            return false;
        },
        isDeliveryDateValid(date) {
            if (!date) return false;
            const parts = date.split('-');
            if (parts.length !== 3) return false;
            const d = new Date(parts[2], parts[0] - 1, parts[1]);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            d.setHours(0, 0, 0, 0);
            if (d < today) return false;
            if (Object.prototype.hasOwnProperty.call(this.blockedDates, date)) return false;
            return true;
        },
        onDeliveryDateChange(item, newDate) {
            if (newDate && !this.isDeliveryDateValid(newDate)) {
                if (Object.prototype.hasOwnProperty.call(this.blockedDates, newDate)) {
                    this.posWarn(this.getBlockReasonForDate(newDate) || "That date is not available for this customer.");
                } else {
                    this.posWarn("Delivery date can't be in the past.");
                }
                item.delivery_date = null;
                return false;
            }
            if (item.order_type === 'delivery_from_store') this.validateDeliveryFromStoreLocations();
            return true;
        },
        setCommonDeliveryDateForNewItems(selectedDate) {
            if (selectedDate && Object.prototype.hasOwnProperty.call(this.blockedDates, selectedDate)) {
                this.posWarn(this.getBlockReasonForDate(selectedDate) || 'That date is not available for this customer.');
                this.commonDeliveryDate = null;
                return;
            }
            this.newItems.forEach(item => {
                if (item.order_type !== 'customer_takes') item.delivery_date = selectedDate;
            });
        },

        isDamageLocation(location) {
            if (!location || !location.text) return false;
            const t = location.text.toLowerCase();
            return ['damage', 'damages', 'as is', 'defective', 'broken', 'scratch', 'dent', 'cosmetic', 'imperfect', 'clearance']
                .some(k => t.includes(k));
        },
        isOnlineStoreLocation() {
            if (!this.invoice.selected_locsub) return false;
            const loc = (this.packet.locsubs || []).find(l => l.value === this.invoice.selected_locsub);
            if (!loc || !loc.text) return false;
            const t = loc.text.toLowerCase();
            return t.includes('online store') || t.includes('online sales') || t.includes('ecommerce') ||
                t.includes('e-commerce') || t.includes('web store') || (t.includes('online') && t.includes('store'));
        },

        setShipLocsubForDelivery(item) {
            if (item.order_type !== 'delivery_from_store') return;
            const defaultShipping = (this.packet.locsubs || []).find(d =>
                d.location_id === this.invoice.location_id && d.text.toLowerCase().includes('mecca shipping'));
            const fallback = defaultShipping ? defaultShipping.value : "47|130";
            if (item.locsub && item.drop_locsub) {
                item.ship_locsub = item.locsub === item.drop_locsub ? item.drop_locsub : fallback;
            } else if (item.drop_locsub) {
                item.ship_locsub = item.drop_locsub;
            } else {
                item.ship_locsub = fallback;
            }
        },

        validateDeliveryFromStoreLocations() {
            let ok = true;
            this.invoice.items
                .filter(item => item.order_type === 'delivery_from_store' && !item.existing)
                .forEach(item => {
                    const locations = this.getDeliveryLocations(item);
                    if (!item.drop_locsub || !locations.some(l => l.value === item.drop_locsub)) {
                        if (locations.length) item.drop_locsub = locations[0].value;
                        else {
                            ok = false;
                            this.posWarn(`No delivery locations available for ${item.product_sku}.`);
                        }
                    }
                    this.setShipLocsubForDelivery(item);
                });
            return ok;
        },

        getDeliveryLocations(item) {
            const base = (this.packet.locsubs || []).filter(d => d.sublocation_type_id !== 1 && !this.isDamageLocation(d));
            const meccaCustomerDeliveries = base.filter(d => {
                const t = d.text.toLowerCase();
                return t.includes('customer') && t.includes('deliveries') && (t.includes('mecca shipping') || t.includes('mecca delivery'));
            });

            if (item.order_type === 'home_delivery') return meccaCustomerDeliveries;

            if (item.order_type === 'delivery_from_store') {
                const storeStock = base.filter(d => {
                    const t = d.text.toLowerCase();
                    return d.location_id === this.invoice.location_id && !t.includes('mecca shipping') && !t.includes('mecca delivery');
                });
                let all = [...meccaCustomerDeliveries];
                storeStock.forEach(l => { if (!all.some(x => x.value === l.value)) all.push(l); });
                if (!all.length) all = base.filter(d => d.location_id === this.invoice.location_id);
                return all;
            }
            return base.filter(d => d.location_id === this.invoice.location_id);
        },

        getFulfillLocations(item) {
            const isDamagedItem = item.is_damaged || item.is_as_is;
            if (!this.search_in_my_location) {
                if (!item.locsub) return [];
                const existing = (this.packet.locsubs || []).find(d => d.value === item.locsub);
                if (!existing) return [];
                if (!isDamagedItem && this.isDamageLocation(existing)) return [];
                return [existing];
            }

            const isMecca = d => { const t = d.text.toLowerCase(); return t.includes('mecca shipping') || t.includes('mecca delivery'); };

            if (isDamagedItem) {
                const all = (this.packet.locsubs || []).filter(d => d.location_id === this.invoice.location_id && d.sublocation_type_id !== 1);
                const damage = all.filter(d => this.isDamageLocation(d));
                const regular = all.filter(d => !this.isDamageLocation(d));
                if (item.order_type === 'home_delivery') {
                    let result = damage.filter(d => !isMecca(d));
                    all.filter(isMecca).forEach(l => { if (!result.some(x => x.value === l.value)) result.push(l); });
                    return result.length ? result : all;
                }
                const result = [...damage];
                regular.forEach(l => { if (!result.some(x => x.value === l.value)) result.push(l); });
                return result.length ? result : all;
            }

            const base = (this.packet.locsubs || []).filter(d =>
                d.location_id === this.invoice.location_id && d.sublocation_type_id !== 1 && !this.isDamageLocation(d));
            if (item.order_type === 'home_delivery') {
                const mecca = base.filter(isMecca);
                return mecca.length ? mecca : base;
            }
            return base;
        },

        get_locations(item) {
            const isDamagedItem = item.is_damaged || item.is_as_is || (item.damage_info && item.damage_info.is_damaged);
            if (item.sourceContext === 'warehouse') return [];

            if (!this.search_in_my_location) {
                if (!item.locsub) return [];
                const existing = (this.packet.locsubs || []).find(d => d.value === item.locsub);
                if (!existing) return [];
                if (!isDamagedItem && this.isDamageLocation(existing)) return [];
                return [existing];
            }

            if (item.sourceContext === 'store') {
                const all = (this.packet.locsubs || []).filter(d => d.location_id === this.invoice.location_id && d.sublocation_type_id !== 1);
                const damage = all.filter(d => this.isDamageLocation(d));
                const regular = all.filter(d => !this.isDamageLocation(d));
                let list = isDamagedItem ? (damage.length ? damage : all) : (regular.length ? regular : all);

                if (item.locsub && !list.some(d => d.value === item.locsub)) {
                    const current = (this.packet.locsubs || []).find(d => d.value === item.locsub);
                    if (current && (isDamagedItem || !this.isDamageLocation(current))) list = [current, ...list];
                }
                return list;
            }

            if (item.order_type === 'store_pickup') {
                return (this.packet.locsubs || []).filter(d => d.location_id === this.invoice.location_id && !this.isDamageLocation(d));
            }
            return (this.packet.locsubs || []).filter(d =>
                d.location_id === this.invoice.location_id && d.sublocation_type_id !== 1 && !this.isDamageLocation(d));
        },

        get_pickup_locations(item) {
            const that = this;
            const order_type = item.order_type;
            const sourceContext = item.sourceContext;
            const base = (this.packet.locsubs || []).filter(d => d.sublocation_type_id !== 1 && !this.isDamageLocation(d));
            const subName = d => (d.sublocation_name || '').toLowerCase();
            const isMecca = d => { const t = (d.text || '').toLowerCase(); return t.includes('mecca shipping') || t.includes('mecca delivery'); };
            const isCustomerPickup = d => {
                const n = subName(d);
                return (n.includes('customer pick up') || n.includes('customer pickup') || n.includes('customer pick-up')) && !n.includes('transfer');
            };
            const isCustomerDelivery = d => {
                const n = subName(d);
                return (n.includes('customer deliveries') || n.includes('customer delivery')) && !n.includes('transfer');
            };

            if (!this.search_in_my_location && sourceContext === 'store') {
                if (!item.locsub) return [];
                const existing = (this.packet.locsubs || []).find(d => d.value === item.locsub);
                return existing && !this.isDamageLocation(existing) ? [existing] : [];
            }

            if (order_type === 'customer_takes') {
                return base.filter(d => d.location_id == that.invoice.location_id);
            }

            if (order_type === 'store_pickup') {
                if (sourceContext === 'warehouse') {
                    const storeStock = base.filter(d => d.location_id === that.invoice.location_id && !isMecca(d));
                    let meccaPickup = base.filter(d => isMecca(d) && isCustomerPickup(d));
                    if (!meccaPickup.length) {
                        const seen = new Set();
                        meccaPickup = base.filter(d => isMecca(d) && (d.sublocation_id === 162 || (d.sublocation_name || '').toUpperCase().includes('CUSTOMER PICK')))
                            .filter(d => (seen.has(d.value) ? false : seen.add(d.value)));
                    }
                    const out = [...storeStock, ...meccaPickup];
                    return out.length ? out : base.filter(d => d.location_id == that.invoice.location_id);
                }

                if (this.isOnlineStoreLocation()) {
                    let out = base.filter(isCustomerPickup);
                    if (!out.length) out = base.filter(d => subName(d).includes('customer') && subName(d).includes('pick') && !subName(d).includes('transfer'));
                    if (!out.length) out = base.filter(d => d.sublocation_id === 162);
                    return out;
                }

                return base.filter(d => d.location_id == that.invoice.location_id && !(sourceContext === 'store' && isMecca(d)));
            }

            if (order_type === 'home_delivery') return base.filter(isCustomerDelivery);

            if (order_type === 'delivery_from_store') {
                const out = base.filter(isCustomerPickup);
                base.filter(isCustomerDelivery).forEach(l => { if (!out.some(x => x.value === l.value)) out.push(l); });
                return out;
            }
            return [];
        },

        shouldShowBackorder(item) {
            if (item.sourceContext !== 'warehouse') return false;
            if (item.available !== undefined) return Number(item.available) <= 0;
            const p = (this.searchitems.result || []).find(x => x.product_id === item.product_id && x.product_sku === item.product_sku);
            return p ? Number(p.available) <= 0 : false;
        },

        returnKeyOf(item) {
            if (item._return_key) return item._return_key;
            if (item.itemid !== undefined && item.itemid !== null && item.itemid !== '') return 'id:' + item.itemid;
            if (item.id !== undefined && item.id !== null) return 'row:' + item.id;
            return 'line:' + this.invoice.items.indexOf(item) + ':' + item.product_sku;
        },
        isMarkedForReturn(item) {
            const key = this.returnKeyOf(item);
            return this.refund_items.some(r => r._return_key === key);
        },
        unmarkReturn(item) {
            const key = this.returnKeyOf(item);
            this.refund_items = this.refund_items.filter(r => r._return_key !== key);
            this.current_refund_validated = false;
        },
        openReturnsFromCart() {
            this.$bvModal.hide('pos-modal-products');
            this.$bvModal.show('pos-modal-returns');
        },

        removeItem(idx) {
            const item = this.invoice.items[idx];
            if (!item) return;
            if (this.invoice.edit && item.existing == true) {
                const remaining = (parseInt(item.quantity) || 0) - (parseInt(item.refunded_so_far_quantity) || 0);
                if (remaining <= 0) {
                    this.posWarn(`${item.product_name || item.product_sku} has already been fully returned.`);
                    return;
                }
                this.current_refund_validated = false;
                if (!this.isMarkedForReturn(item)) {
                    const copy = JSON.parse(JSON.stringify(item));
                    copy._return_key = this.returnKeyOf(item);
                    copy.return_quantity = remaining;
                    this.refund_items.push(copy);
                    this.returnsForceSelect = false;
                    this.$bvToast.toast(`${item.product_name || item.product_sku} marked to ${this.get_item_status(item) === 'delivered' ? 'return' : 'cancel'}. Open Returns and exchange to validate.`,
                        { title: "Marked for return", autoHideDelay: 2500, variant: "info" });
                }
                return;
            }
            this.invoice.items.splice(idx, 1);
        },

        removeLayawayItem(item) {
            const that = this;
            this.$bvModal.msgBoxConfirm(`Remove ${item.product_sku || item.product_name} from this layaway?`, {
                title: 'Remove item from layaway', size: 'sm', buttonSize: 'sm', okVariant: 'danger',
                okTitle: 'Remove', cancelTitle: 'Cancel', footerClass: 'p-2', centered: true,
            }).then(value => {
                if (!value) return;
                that.loading = true;
                Requestor.postSyncP("/api/sales/remove_layaway_item/", {
                    invoice_id: that.invoice.invoice_id, itemid: item.itemid, product_sku: item.product_sku, username: that.userDetails.username,
                }, that,
                    function (response) {
                        if (response.data.error) {
                            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "warning" });
                        } else {
                            const index = that.invoice.items.findIndex(i => i.itemid === item.itemid && i.product_sku === item.product_sku);
                            if (index !== -1) that.invoice.items.splice(index, 1);
                            if (response.data.result && response.data.result.new_total) that.invoice.total = response.data.result.new_total;
                            that.$bvToast.toast("Item removed.", { title: "Success", autoHideDelay: 2000, variant: "success" });
                        }
                        that.loading = false;
                    },
                    function () {
                        that.$bvToast.toast("Error removing item. Please try again.", { title: "Error", autoHideDelay: 3000, variant: "danger" });
                        that.loading = false;
                    }
                );
            });
        },

        getExistingOrderTotal() {
            let total = 0.0;
            (this.invoice.items || []).forEach(i => {
                if (!i.existing || !this.show_item(i)) return;
                const remainingQty = (+i.quantity || 0) - (+i.refunded_so_far_quantity || 0);
                if (remainingQty <= 0) return;
                const sp = this.getEffectiveUnitPrice(i);
                const originalQty = +i.quantity || 1.0;
                const unitDiscount = originalQty > 0 ? (parseFloat(i.discount_offered) || 0.0) / originalQty : 0.0;
                total += (sp - unitDiscount) * remainingQty;
            });
            return Math.max(0, total);
        },
        getOriginalChargesTotal() {
            return (+this.invoice.delivery_charges || 0.0)
                + (+this.invoice.setup_charges || 0.0)
                + (+this.invoice.warranty_charges || 0.0)
                + (+this.invoice.whiteglove_charges || 0.0);
        },
        getExistingDiscount() {
            let total = 0.0;
            (this.invoice.items || []).forEach(i => {
                if (!i.existing || !this.show_item(i)) return;
                const remainingQty = (+i.quantity || 0) - (+i.refunded_so_far_quantity || 0);
                if (remainingQty <= 0) return;
                const originalQty = +i.quantity || 1.0;
                const unitDiscount = originalQty > 0 ? (parseFloat(i.discount_offered) || 0.0) / originalQty : 0.0;
                total += unitDiscount * remainingQty;
            });
            return total;
        },
        getExistingSubTotal() {
            return (this.getExistingOrderTotal() || 0.0) + (this.getOriginalChargesTotal() || 0.0);
        },
        getExistingSalesTax() {
            if (this.invoice.is_layaway) return 0.0;
            const stored = parseFloat(this.invoice.sales_tax);
            if (!isNaN(stored) && stored >= 0 && this.refund_items.length <= 0) {
                return stored;
            }
            return this.adjust_to_taxable_amount(this.getExistingSubTotal()) * this.getSalesTaxPerc();
        },
        getExistingGrandTotal() {
            return Math.round((this.getExistingSubTotal() + this.getExistingSalesTax()) * 100) / 100;
        },
        getOrderTotal() {
            let items = this.invoice.items;
            if (this.invoice.edit == true && this.refund_items.length <= 0) items = items.filter(d => d.existing == false);
            let total = 0.0;
            items.forEach(i => {
                const sp = this.getEffectiveUnitPrice(i);
                const qty = +i.quantity || 1.0;
                const dsc = parseFloat(i.discount_offered) || 0.0;
                total += (sp * qty) - dsc;
            });
            const giftCardDiscount = parseFloat(this.invoice.gift_card_discount) || 0;
            return Math.max(0, total - giftCardDiscount);
        },
        getChargesTotal() {
            let d = this.invoice.delivery_charges;
            let s = this.invoice.setup_charges;
            let w = this.invoice.warranty_charges;
            let wg = this.invoice.whiteglove_charges;
            if (this.invoice.edit == true && this.refund_items.length <= 0) {
                d = this.invoice.new_delivery_charges;
                s = this.invoice.new_setup_charges;
                w = this.invoice.new_warranty_charges;
                wg = this.invoice.new_whiteglove_charges;
            }
            return (+d || 0.0) + (+s || 0.0) + (+w || 0.0) + (+wg || 0.0);
        },
        getSubTotal() {
            return (this.getOrderTotal() || 0.0) + (this.getChargesTotal() || 0.0);
        },
        getTotalDiscount() {
            let items = this.invoice.items;
            if (this.invoice.edit == true && this.refund_items.length <= 0) items = items.filter(d => d.existing == false);
            let total = items.reduce((sum, i) => sum + (+i.discount_offered || 0.0), 0);
            total += parseFloat(this.invoice.gift_card_discount) || 0;
            return total;
        },
        getTotalSpecialDiscounts() {
            let items = this.invoice.items;
            if (this.invoice.edit == true && this.refund_items.length <= 0) items = items.filter(d => d.existing == false);
            let total = 0.0;
            items.forEach(item => {
                if (this.hasActiveDiscount(item)) {
                    const regular = parseFloat(item.selling_price) || 0;
                    const special = parseFloat(item.special_discount_price) || 0;
                    if (special < regular) total += (regular - special) * (+item.quantity || 1.0);
                }
            });
            return total;
        },
        getWarrantyTotal() {
            if (this.invoice.edit && this.refund_items.length <= 0) return parseFloat(this.invoice.new_warranty_charges) || 0;
            return parseFloat(this.invoice.warranty_charges) || 0;
        },
        getSalesTaxPerc() {
            return parseFloat(((parseFloat(this.invoice.tax_perc) || 0) / 100.0).toFixed(2));
        },
        adjust_to_taxable_amount(subtotal) {
            const nonTaxable = (pm) => {
                if (pm.value === "tax_exempt" || pm.value === "store_credit") return parseFloat(pm.amount) || 0;
                if (pm.value === "finance") {
                    const fc = this.finance_companies.find(f => f.value === pm.name);
                    const chargesTax = fc && fc.company_charges_tax_in_states && fc.company_charges_tax_in_states.includes(this.invoice.sold_from_state);
                    return chargesTax ? (parseFloat(pm.amount) || 0) : 0;
                }
                return 0;
            };
            let payments;
            if (this.invoice.edit === true) payments = [...(this.payment_history || []), ...(this.invoice.new_payment_methods || [])];
            else payments = this.invoice.payment_methods || [];
            const totalNonTaxable = payments.reduce((sum, pm) => sum + nonTaxable(pm), 0);
            return Math.max(0.0, subtotal - totalNonTaxable);
        },
        getSalesTax() {
            if (this.invoice.is_layaway) return 0.0;
            return this.adjust_to_taxable_amount(this.getSubTotal()) * this.getSalesTaxPerc();
        },
        getGrandTotal() {
            const total = (this.getSubTotal() + this.getSalesTax()) - this.getStoreCreditApplied();
            return Math.round(total * 100) / 100;
        },
        getTotalBalance() {
            let pms = this.invoice.payment_methods || [];
            if (this.invoice.edit && this.refund_items.length <= 0) pms = this.invoice.new_payment_methods || [];
            const paid = pms.reduce((s, d) => s + (parseFloat(d.amount) || 0.0), 0);
            return Math.round((this.getGrandTotal() - paid) * 100) / 100;
        },
        getTotalBalanceWithCredit() {
            const pms = (this.invoice.edit === true ? this.invoice.new_payment_methods : this.invoice.payment_methods) || [];
            const paid = pms.reduce((s, d) => s + (parseFloat(d.amount) || 0.0), 0);
            return Math.max(0, Math.round((this.getGrandTotal() - paid) * 100) / 100);
        },
        getTotalPayments() {
            if (this.invoice.edit && this.payment_history.length > 0) {
                return this.payment_history.reduce((t, pm) => t + (parseFloat(pm.amount) || 0.0), 0.0);
            }
            return (this.invoice.payment_methods || []).reduce((t, pm) => t + (parseFloat(pm.amount) || 0.0), 0.0);
        },
        getTotalPaymentsFromHistory() {
            return this.payment_history.reduce((t, pm) => t + (parseFloat(pm.amount) || 0.0), 0.0);
        },
        getPaymentMethods() {
            if (this.invoice.edit && this.payment_history.length > 0) return this.payment_history;
            return this.invoice.edit ? [] : (this.invoice.payment_methods || []);
        },

        getAvailableStoreCredit() {
            return parseFloat(this.invoice.available_credit_value) || 0.0;
        },
        getNetStoreCreditForTransaction() {
            if (!this.refund_items.length) return 0;
            let amount = this.refund_items.reduce((sum, d) => {
                const qty = parseInt(d.return_quantity) || 0;
                return sum + qty * parseFloat(d.selling_price) - qty * (parseFloat(d.unit_discount_value) || 0);
            }, 0);
            amount += (parseFloat(this.refund_charges.delivery_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.whiteglove_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.setup_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.warranty_refunded_currently) || 0);
            return amount;
        },
        getEffectiveStoreCredit() {
            return Math.round((this.getAvailableStoreCredit() + this.getNetStoreCreditForTransaction()) * 100) / 100;
        },
        getStoreCreditApplied() {
            const before = this.getSubTotal() + this.getSalesTax();
            return Math.round(Math.min(this.getEffectiveStoreCredit(), before) * 100) / 100;
        },
        getRemainingStoreCredit() {
            return Math.round(Math.max(0, this.getEffectiveStoreCredit() - this.getStoreCreditApplied()) * 100) / 100;
        },

        addPaymentMethod(e) {
            if (e) e.preventDefault();
            const list = this.invoice.edit == true ? this.invoice.new_payment_methods : this.invoice.payment_methods;
            list.push({
                idx: list.length, value: null, amount: null, tendered_amount: null, change_due: 0,
                approved_amount: null, properties: {}, name: null, available_amount: 0.0,
                store_credit_invoice_id: null, charged_tax_itself: null, ein: null, certificate: null,
                lease_id: null, misc_info: null, tax_exempt_password: null, passwordValid: null, showPassword: false,
            });
        },
        removePaymentMethod(pmidx) {
            if (this.invoice.edit == true) this.invoice.new_payment_methods.splice(pmidx, 1);
            else this.invoice.payment_methods.splice(pmidx, 1);
        },
        reset_pms_if_layaway() {
            if (this.invoice.is_layaway) this.invoice.payment_methods = [];
        },

        applyCash(list, pmidx) {
            const pm = list[pmidx];
            if (!pm) return;
            const tendered = parseFloat(pm.tendered_amount) || 0;
            const others = list.reduce((s, p, i) => (i !== pmidx ? s + (parseFloat(p.amount) || 0) : s), 0);
            const remaining = Math.max(0, this.getGrandTotal() - others);
            const applied = Math.round(Math.min(tendered, remaining) * 100) / 100;
            this.$set(pm, 'amount', applied);
            this.$set(pm, 'change_due', Math.round((tendered - applied) * 100) / 100);
            this.$set(pm, 'remaining_balance', remaining);
        },
        calculateCashChange(pmidx) { this.applyCash(this.invoice.payment_methods, pmidx); },
        calculateCashChangeNew(pmidx) { this.applyCash(this.invoice.new_payment_methods, pmidx); },
        cashDueFor(list, pmidx) {
            const others = list.reduce((s, p, i) => (i !== pmidx ? s + (parseFloat(p.amount) || 0) : s), 0);
            return Math.max(0, this.getGrandTotal() - others);
        },
        getCashAmountDue(pmidx) { return this.cashDueFor(this.invoice.payment_methods, pmidx); },
        getCashAmountDueNew(pmidx) { return this.cashDueFor(this.invoice.new_payment_methods, pmidx); },
        getCashChange(pmidx) { const pm = this.invoice.payment_methods[pmidx]; return pm ? (pm.change_due || 0) : 0; },
        getCashChangeNew(pmidx) { const pm = this.invoice.new_payment_methods[pmidx]; return pm ? (pm.change_due || 0) : 0; },
        getCashChangeState(pmidx) {
            const pm = this.invoice.payment_methods[pmidx];
            if (!pm || !pm.tendered_amount) return null;
            return (parseFloat(pm.tendered_amount) || 0) >= this.getCashAmountDue(pmidx);
        },
        getCashChangeStateNew(pmidx) {
            const pm = this.invoice.new_payment_methods[pmidx];
            if (!pm || !pm.tendered_amount) return null;
            return (parseFloat(pm.tendered_amount) || 0) >= this.getCashAmountDueNew(pmidx);
        },

        check_finance_company_charges_tax(pmethod) {
            if (pmethod.value !== "finance") return false;
            const fc = this.finance_companies.find(d => d.value == pmethod.name);
            if (!fc) return false;
            const chargesTax = !!(fc.company_charges_tax_in_states && fc.company_charges_tax_in_states.includes(this.invoice.sold_from_state));
            pmethod.charged_tax_itself = chargesTax;
            return chargesTax;
        },
        onFinanceCompanySelected(paymentMethod) {
            const chargesTax = this.check_finance_company_charges_tax(paymentMethod);
            this.$set(paymentMethod, 'charged_tax_itself', chargesTax);
            this.$bvToast.toast(
                chargesTax
                    ? `${paymentMethod.name} collects sales tax itself. Tax is removed for the financed amount.`
                    : `${paymentMethod.name} does not collect tax. The store collects sales tax.`,
                { title: "Tax notice", autoHideDelay: 3000, variant: chargesTax ? "info" : "warning" }
            );
        },

        checkTaxExemptPasswordRequirement(paymentMethod) {
            if (paymentMethod.name) {
                this.$set(paymentMethod, 'tax_exempt_password', null);
                this.$set(paymentMethod, 'passwordValid', null);
                this.$set(paymentMethod, 'showPassword', false);
            }
        },
        validateTaxExemptPassword(paymentMethod) {
            if (!paymentMethod.name || !paymentMethod.tax_exempt_password) {
                this.$set(paymentMethod, 'passwordValid', false);
                return;
            }
            const that = this;
            Requestor.postSyncP("/api/finance_company/validate_tax_exempt_password/", {
                company_name: paymentMethod.name, password: paymentMethod.tax_exempt_password,
            }, this,
                function (response) {
                    if (response.data.error) {
                        that.$set(paymentMethod, 'passwordValid', false);
                        that.$bvToast.toast(response.data.error, { title: "Validation error", autoHideDelay: 3000, variant: "warning" });
                    } else {
                        that.$set(paymentMethod, 'passwordValid', response.data.result.valid);
                        if (response.data.result.valid) that.$set(paymentMethod, 'showPassword', false);
                        else that.posWarn("Invalid password for this tax exempt company.");
                    }
                },
                function () {
                    that.$set(paymentMethod, 'passwordValid', false);
                    that.$bvToast.toast("Error validating password", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                }
            );
        },

        apply_store_credit(pmidx) {
            const list = this.invoice.edit == true ? this.invoice.new_payment_methods : this.invoice.payment_methods;
            const scInvoice = list[pmidx].store_credit_invoice_id;
            if (!scInvoice) {
                this.$vex.dialog.alert({ message: "Enter the invoice ID that holds the store credit." });
                return;
            }
            const that = this;
            Requestor.postAsyncP("/api/sales/get_available_credit/", { invoice_id: scInvoice }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    } else if (response.data.result.length > 0) {
                        const available = response.data.result[0].available_amount;
                        that.$set(list[pmidx], 'available_amount', available);
                        that.$set(list[pmidx], 'name', scInvoice);
                        that.$bvToast.toast(available > 0 ? "Store credit retrieved." : `No store credit available on ${scInvoice}`,
                            { title: available > 0 ? "Success" : "Notice", autoHideDelay: 1500, variant: available > 0 ? "success" : "warning" });
                    } else {
                        that.$bvToast.toast("No store credit found for that invoice.", { title: "Not found", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                function () {
                    that.$bvToast.toast("Error occurred while applying store credit.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            );
        },

        validate_payment_methods(payment_methods) {
            const errors = [];
            let sum = 0;
            const gt = parseFloat(this.getGrandTotal().toFixed(2));
            let methods = payment_methods || [];
            if (this.invoice.edit && this.refund_items.length <= 0) methods = this.invoice.new_payment_methods || [];

            if (this.getTotalDiscount() > this.getOrderTotal() + this.getTotalDiscount()) {
                errors.push("Total discount can't exceed the order total.");
            }

            methods.forEach(pm => {
                if (!pm.value || pm.amount === null || pm.amount === undefined || isNaN(parseFloat(pm.amount))) return;
                const amount = parseFloat(pm.amount);
                if (amount < 0) { errors.push("Payment amount cannot be negative."); return; }
                sum += amount;

                if (pm.value == "cash") {
                    const tendered = parseFloat(pm.tendered_amount) || 0;
                    if (tendered <= 0) errors.push("Please enter the amount tendered for the cash payment.");
                    if (tendered < amount) errors.push("Tendered amount can't be less than the amount applied.");
                    pm.name = "N/A";
                }
                if (pm.value == "cc") {
                    const last4 = String(pm.name || "").replace(/\s/g, "");
                    if (!last4 || !parseInt(last4) || last4.length < 4) errors.push("Credit card payments need the card's last 4 digits.");
                }
                if (pm.value == "finance") {
                    if (!pm.lease_id) errors.push("Finance payments need a lease/finance ID.");
                    if (!pm.name) errors.push("Finance payments need the finance company.");
                }
                if (pm.value == "tax_exempt") {
                    if (!pm.name) errors.push("Tax exempt payments need the company name.");
                    else if (!pm.tax_exempt_password) errors.push(`Password is required for tax exempt company ${pm.name}.`);
                    else if (pm.passwordValid === false) errors.push(`Invalid password for tax exempt company ${pm.name}.`);
                    else if (pm.passwordValid === null) errors.push(`Validate the password for tax exempt company ${pm.name}.`);
                }
                if (pm.value == "store_credit") {
                    if (amount > parseFloat(pm.available_amount)) errors.push("Store credit amounts must not exceed the available credit.");
                    if (!(parseFloat(pm.available_amount) > 0)) errors.push("No store credit can be applied with this information.");
                }
            });

            if (this.invoice.is_layaway == false) {
                const difference = Math.abs(parseFloat(sum.toFixed(2)) - gt);
                if (difference > 0.01) {
                    errors.push(`Total payments ($${sum.toFixed(2)}) must match the grand total ($${gt.toFixed(2)}). Difference: $${difference.toFixed(2)}`);
                }
            }
            return errors;
        },

        validate_products(products) {
            const errors = [];
            if (products.some(d => !d.quantity)) errors.push("Product quantity should be > 0.");

            const invalidDiscounts = products.filter(item => {
                if (!item.discount_offered || parseFloat(item.discount_offered) === 0) return false;
                return parseFloat(item.discount_offered) > this.calculateMaxDiscount(item);
            });
            if (invalidDiscounts.length) {
                errors.push(`Discounts exceed the allowed limit for: ${invalidDiscounts.map(i => `${i.product_sku} (max $${this.calculateMaxDiscount(i).toFixed(2)})`).join(', ')}`);
            }

            const only = this.search_in_my_location;
            products.forEach(p => {
                if (!p.order_type) errors.push("Please pick an order type for all items.");
                if (only && ['customer_takes', 'store_pickup', 'home_delivery'].includes(p.order_type) && p.sourceContext === 'store' && !p.locsub) {
                    errors.push(`Pick a 'Fulfill from' location for ${p.product_name || p.product_sku}.`);
                }
                if (p.sourceContext === 'warehouse' && p.order_type === 'customer_takes') errors.push("Take Home isn't allowed for warehouse-sourced items.");
                if (p.order_type === 'store_pickup' && !p.drop_locsub) errors.push("Pick a 'Customer pickup from' location for all Store Pickup items.");
                if (!p.delivery_date) errors.push("Please pick a pickup or delivery date for all items.");
                if (String(p.product_sku).split(" ").length > 1) errors.push("Product SKU can't contain spaces.");
            });
            if (this.hasStorePickupItem && !this.deliveryDamageWaiverAgreed) {
                errors.push("The customer must agree to the store pickup damage waiver (Terms tab).");
            }
            return errors;
        },

        async validateGiftCard() {
            if (!this.giftCardCode) { this.giftCardError = 'Enter a gift card code.'; return; }
            this.giftCardValidating = true;
            this.giftCardError = null;
            this.giftCardInfo = null;
            try {
                const response = await Requestor.postAsyncP('/api/giftcard/validate/', { code: this.giftCardCode }, this);
                if (response.data.error) { this.giftCardError = response.data.error; return; }
                if (response.data.valid) {
                    const giftCard = response.data.gift_card;
                    const balance = parseFloat(giftCard.remaining_balance) || 0;
                    if (balance <= 0) { this.giftCardError = 'This gift card has no remaining balance.'; return; }
                    const applyAmount = Math.min(balance, this.getGrandTotal());
                    if (applyAmount <= 0) { this.giftCardError = "The grand total is $0, so the gift card can't be applied."; return; }
                    this.giftCardApplied = true;
                    this.giftCardAmount = applyAmount;
                    this.giftCardRemaining = balance - applyAmount;
                    this.giftCardId = giftCard.id;
                    this.invoice.gift_card_discount = applyAmount;
                    this.invoice.gift_card_code = this.giftCardCode;
                    this.invoice.gift_card_id = this.giftCardId;
                    this.$bvToast.toast(`Gift card applied: $${applyAmount.toFixed(2)}`, { title: 'Gift card applied', variant: 'success' });
                }
            } catch (error) {
                console.error('Error validating gift card:', error);
                this.giftCardError = "Couldn't check the gift card. Try again.";
            } finally {
                this.giftCardValidating = false;
            }
        },
        removeGiftCard() {
            this.giftCardApplied = false;
            this.giftCardCode = '';
            this.giftCardAmount = 0;
            this.giftCardRemaining = 0;
            this.giftCardId = null;
            this.giftCardError = null;
            this.giftCardInfo = null;
            this.invoice.gift_card_discount = 0;
            this.invoice.gift_card_code = null;
            this.invoice.gift_card_id = null;
            this.showGiftCardPanel = false;
        },
        cancelGiftCardPanel() {
            this.removeGiftCard();
            this.showGiftCardPanel = false;
        },
        async redeemGiftCardAfterInvoice(invoice_id, code, amount) {
            try {
                const result = await Requestor.postAsyncP('/api/giftcard/redeem/', {
                    code: code, invoice_id: invoice_id, amount: parseFloat(amount),
                    performed_by: this.userDetails.username, notes: `Applied to invoice ${invoice_id}`,
                }, this);
                if (result.data.error) {
                    this.$bvToast.toast(`Gift card redemption failed: ${result.data.error}`, { title: 'Warning', variant: 'warning', autoHideDelay: 5000 });
                } else {
                    this.removeGiftCard();
                }
            } catch (error) {
                console.error('Error redeeming gift card:', error);
                this.$bvToast.toast('The sale was created but the gift card was not redeemed. Process it manually.', { title: 'Warning', variant: 'warning', autoHideDelay: 5000 });
            }
        },

        openInvoiceSignatureModal() {
            this.signatureItem = { type: 'invoice', invoice_id: this.invoice.invoice_id, customer_name: (this.invoice.customer || {}).fullname || 'Customer' };
            this.$bvModal.show('modal-signature');
            this.$nextTick(() => {
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.clearSignature();
                    if (this.invoice.signature_url) this.$refs.signaturePad.loadSignatureFromUrl(this.invoice.signature_url);
                }
            });
        },
        resetSignatureModal() {
            this.signatureItem = null;
            this.signatureUploading = false;
            if (this.$refs.signaturePad) this.$refs.signaturePad.clearSignature();
        },
        async saveSignature() {
            if (!this.signatureItem || !this.$refs.signaturePad) return;
            if (this.$refs.signaturePad.isEmpty()) { this.posWarn("Draw a signature first."); return; }
            this.signatureUploading = true;
            try {
                const signatureData = this.$refs.signaturePad.getSignatureData();
                if (!this.invoice.edit) {
                    this.$set(this.invoice, 'signature_temp_data', signatureData);
                    this.$set(this.invoice, 'has_signature', true);
                    this.$set(this.invoice, 'signature_uploaded_at', new Date().toISOString());
                    this.$bvModal.hide('modal-signature');
                    return;
                }
                const response = await this.uploadSignature({
                    image_data: signatureData, invoice_id: this.invoice.invoice_id,
                    signature_type: 'customer',
                    customer_name: (this.invoice.customer || {}).fullname || 'Customer',
                    username: this.userDetails.username,
                });
                if (response && response.success) {
                    this.$set(this.invoice, 'has_signature', true);
                    this.$set(this.invoice, 'signature_url', response.url);
                    this.$set(this.invoice, 'signature_uploaded_at', response.uploaded_at);
                    this.$bvToast.toast("Signature saved.", { title: "Success", autoHideDelay: 2000, variant: "success" });
                    this.$bvModal.hide('modal-signature');
                }
            } catch (error) {
                console.error("Error saving signature:", error);
                this.$bvToast.toast("Failed to save signature: " + (error.message || "Unknown error"), { title: "Error", autoHideDelay: 3000, variant: "danger" });
            } finally {
                this.signatureUploading = false;
            }
        },
        uploadSignature(data) {
            return new Promise((resolve, reject) => {
                Requestor.postSyncP("/api/sales/upload_signature/", data, this,
                    response => (response.data.error ? reject(new Error(response.data.error)) : resolve(response.data.result)),
                    error => reject(error)
                );
            });
        },

        buildDraftPayload(sendToCustomer) {
            return {
                draft_id: this.invoice.invoice_id || this.invoice.draft_id,
                username: this.userDetails.username,
                location_id: this.invoice.location_id,
                sublocation_id: this.invoice.sublocation_id,
                invoice_id: this.invoice.invoice_id,
                customer: this.invoice.customer,
                items: this.invoice.items,
                delivery_charges: this.invoice.delivery_charges,
                whiteglove_charges: this.invoice.whiteglove_charges,
                setup_charges: this.invoice.setup_charges,
                warranty_charges: this.invoice.warranty_charges,
                payment_methods: this.invoice.payment_methods,
                comment_txt: this.invoice.comment_txt,
                sold_from_state: this.invoice.sold_from_state,
                selected_locsub: this.invoice.selected_locsub,
                is_layaway: this.invoice.is_layaway,
                tax_perc: this.invoice.tax_perc,
                delivery_waiver_agreed: this.deliveryWaiverAgreed,
                space_preparation_agreed: this.spacePreparationAgreed,
                delivery_damage_waiver_agreed: this.deliveryDamageWaiverAgreed,
                terms_agreed: this.policyAgreed,
                warranty_refused: this.warrantyRefused,
                address_confirmation_agreed: this.invoice.address_confirmation_agreed,
                subtotal: this.getSubTotal(),
                taxable_subtotal: this.adjust_to_taxable_amount(this.getSubTotal()),
                sales_tax: this.getSalesTax(),
                total: this.getGrandTotal(),
                order_total: this.getOrderTotal(),
                total_discount: this.getTotalDiscount(),
                charges_total: this.getChargesTotal(),
                grand_total: this.getGrandTotal(),
                balance: this.getTotalBalance(),
                total_special_discounts: this.getTotalSpecialDiscounts(),
                store_credit_applied: this.getStoreCreditApplied(),
                remaining_store_credit: this.getRemainingStoreCredit(),
                send_to_customer: sendToCustomer,
                customer_email: sendToCustomer ? this.invoice.customer.email : undefined,
            };
        },
        saveDraftInvoice() {
            if (this.invoice.items.length === 0) { this.posWarn("Add at least one item before saving a draft."); return; }
            const that = this;
            this.loading = true;
            Requestor.postSyncP("/api/sales/save_draft_invoice/", this.buildDraftPayload(false), this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "warning" });
                    } else {
                        that.invoice.draft_id = response.data.result.draft_id;
                        if (!that.invoice.invoice_id) that.invoice.invoice_id = response.data.result.draft_id;
                        that.$bvToast.toast("Draft saved.", { title: "Draft saved", autoHideDelay: 2000, variant: "success" });
                    }
                    that.loading = false;
                },
                function () {
                    that.$bvToast.toast("Error saving draft. Please try again.", { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    that.loading = false;
                }
            );
        },
        sendDraftToCustomer() {
            if (this.invoice.items.length === 0) { this.posWarn("Add at least one item before emailing an estimate."); return; }
            if (!this.invoice.customer.email || !this.invoice.customer.email.includes('@')) { this.posWarn("Enter a valid customer email."); return; }
            this.$bvModal.msgBoxConfirm(`Email the estimate to ${this.invoice.customer.email}?`, {
                title: 'Email estimate', size: 'sm', buttonSize: 'sm', okVariant: 'success',
                okTitle: 'Email estimate', cancelTitle: 'Cancel', footerClass: 'p-2', centered: true,
            }).then(value => { if (value) this.executeSendDraftToCustomer(); });
        },
        executeSendDraftToCustomer() {
            const that = this;
            this.loadingDraftSend = true;
            Requestor.postSyncP("/api/sales/save_draft_invoice/", this.buildDraftPayload(true), this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "warning" });
                    } else {
                        if (!that.invoice.draft_id && response.data.result.draft_id) {
                            that.invoice.draft_id = response.data.result.draft_id;
                            if (!that.invoice.invoice_id) that.invoice.invoice_id = response.data.result.draft_id;
                        }
                        if (response.data.result.estimate_sent) {
                            that.$bvToast.toast(`Estimate emailed to ${response.data.result.estimate_sent_to}.`, { title: "Estimate emailed", autoHideDelay: 3000, variant: "success" });
                        } else {
                            that.$bvToast.toast(`Draft saved but the email failed: ${response.data.result.estimate_error || "unknown error"}`, { title: "Warning", autoHideDelay: 4000, variant: "warning" });
                        }
                    }
                    that.loadingDraftSend = false;
                },
                function () {
                    that.$bvToast.toast("Error saving or emailing the draft. Please try again.", { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    that.loadingDraftSend = false;
                }
            );
        },
        loadDraftInvoice(draftId) {
            const that = this;
            this.loading = true;
            Requestor.postSyncP("/api/sales/get_draft_invoice/", { draft_id: draftId }, this,
                function (response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "warning" });
                    } else {
                        const draft = response.data.result;
                        that.invoice = {
                            ...that.get_invoice_data(),
                            ...draft.data,
                            customer: (draft.data && draft.data.customer) || {},
                            draft_id: draft.draft_id,
                            invoice_id: draft.data.invoice_id || draft.draft_id,
                            location_id: draft.location_id,
                            sublocation_id: draft.sublocation_id,
                            edit: false,
                            username: draft.username || draft.data.username,
                        };
                        that.loadLocationContact();
                        that.deliveryWaiverAgreed = draft.data.delivery_waiver_agreed || false;
                        that.spacePreparationAgreed = draft.data.space_preparation_agreed || false;
                        that.deliveryDamageWaiverAgreed = draft.data.delivery_damage_waiver_agreed || false;
                        that.policyAgreed = draft.data.terms_agreed || false;
                        that.warrantyRefused = draft.data.warranty_refused || false;
                        if (that.invoice.customer && that.invoice.customer.zipcode) {
                            that.loadBlockedDeliveryDates();
                        }
                        that.$bvToast.toast("Draft loaded.", { title: "Draft loaded", autoHideDelay: 2000, variant: "success" });
                    }
                    that.loading = false;
                },
                function () {
                    that.$bvToast.toast("Error loading draft.", { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    that.loading = false;
                }
            );
        },

        processSignaturesBeforeSubmit() { return true; },
        submitInvoice: async function (e) {
            if (e) e.preventDefault();
            if (this.block_post_invoice) return;

            if (this.hasMerchandiseDue) {
                this.posWarn('Merchandise due must be received before submitting.');
                this.openPosTab('merchandisedue');
                return;
            }

            if (!this.policyAgreed) {
                this.$vex.dialog.alert({ message: "The customer must agree to the Terms & Conditions before submitting." });
                return;
            }

            if (this.invoice.selected_locsub) {
                const [locationId, sublocationId] = String(this.invoice.selected_locsub).split('|');
                this.invoice.location_id = parseInt(locationId);
                this.invoice.sublocation_id = parseInt(sublocationId);
            }

            const invoice = JSON.parse(JSON.stringify(this.invoice));
            invoice.items = invoice.items.map(item => ({
                ...item,
                source: item.source || item.sourceContext || 'unknown',
                as_is_image_url: item.as_is_image_url || null,
                image_url: item.image_url || null,
                is_as_is: item.is_as_is || false,
                damage_info: item.damage_info || null,
            }));
            invoice.policyAgreed = this.policyAgreed;
            invoice.warrantyRefused = this.warrantyRefused;
            invoice.delivery_waiver_agreed = this.deliveryWaiverAgreed;
            invoice.space_preparation_agreed = this.spacePreparationAgreed;
            invoice.delivery_damage_waiver_agreed = this.deliveryDamageWaiverAgreed;
            invoice.warranty_charges = +invoice.warranty_charges || 0.0;
            invoice.delivery_charges = +invoice.delivery_charges || 0.0;
            invoice.whiteglove_charges = +invoice.whiteglove_charges || 0.0;
            invoice.setup_charges = +invoice.setup_charges || 0.0;
            invoice.sales_tax = this.getSalesTax();
            invoice.tax_perc = this.getSalesTaxPerc();
            invoice.subtotal = this.getSubTotal();
            invoice.taxable_subtotal = this.adjust_to_taxable_amount(this.getSubTotal());
            invoice.total = this.getGrandTotal();

            const draftIdFromRoute = this.$route.query.draft_id;
            if (draftIdFromRoute && !invoice.draft_id) invoice.draft_id = draftIdFromRoute;

            const customerErrors = this.validate_customer_info(invoice.customer || {});
            if (customerErrors.length) { this.$vex.dialog.alert({ message: customerErrors[0] }); return; }
            const productErrors = this.validate_products(invoice.items);
            if (productErrors.length) { this.$vex.dialog.alert({ message: productErrors[0] }); return; }
            const paymentErrors = this.validate_payment_methods(invoice.payment_methods);
            if (paymentErrors.length) { this.$vex.dialog.alert({ message: paymentErrors[0] }); return; }

            invoice.payment_methods = invoice.payment_methods.map(d => { d.amount = +d.amount || 0.0; return d; });
            invoice.housetruck = (this.packet.housetrucks || []).length > 0 ? this.packet.housetrucks[0].value : null;

            const isFromDraft = !!(this.invoice.draft_id || draftIdFromRoute);
            invoice.username = (isFromDraft && this.invoice.username) ? this.invoice.username : this.userDetails.username;
            invoice.invoice_type = "INV";
            invoice.default_locsubs = this.default_locsubs;

            const gift = { applied: this.giftCardApplied, id: this.giftCardId, code: this.giftCardCode, amount: this.giftCardAmount };
            const that = this;

            this.block_post_invoice = true;
            this.$refs.loaderAnimation.show("Creating receipt...");
            await new Promise(resolve => setTimeout(resolve, 800));
            this.$refs.loaderAnimation.show("Saving sale and emailing customer...");

            Requestor.postSyncP("/api/sales/record_sale/", invoice, this,
                function (response) {
                    that.$refs.loaderAnimation.hide();
                    if (response.data.error) {
                        that.$vex.dialog.alert({ message: response.data.error });
                    } else {
                        const invoice_id = response.data.result.invoice_id;
                        if (gift.applied && gift.id) that.redeemGiftCardAfterInvoice(invoice_id, gift.code, gift.amount);

                        that.successMessage = isFromDraft ? 'Sale processed' : 'Sale created';
                        if (that.$refs.successAnimation) that.$refs.successAnimation.show();
                        that.printReceiptAndOpenDrawer();

                        if (invoice_id) {
                            setTimeout(() => that.$router.push({ path: "/sales/viewsale/" + invoice_id + "/" }), 2500);
                        }
                    }
                    that.block_post_invoice = false;
                    that.loading = false;
                },
                function (error) {
                    console.error(error);
                    that.$refs.loaderAnimation.hide();
                    that.$bvToast.toast("An error occurred while recording the sale. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                    that.block_post_invoice = false;
                    that.loading = false;
                }
            );
        },

        handleUpdate: async function () {
            const balance = Math.round(this.getTotalBalance() * 100) / 100;

            if (balance > 0.005 && !this.invoice.is_layaway) {
                this.$vex.dialog.alert({ message: `Cannot update the invoice. The customer has a balance of $${balance.toFixed(2)}. Add payment in the Payment tab.` });
                return;
            }
            if (balance > 0.005 && this.invoice.is_layaway) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `This layaway has a remaining balance of $${balance.toFixed(2)}. Update without collecting payment?`,
                    { title: 'Layaway update', size: 'sm', buttonSize: 'sm', okVariant: 'warning', okTitle: 'Update sale', cancelTitle: 'Cancel', footerClass: 'p-2', centered: true }
                );
                if (!confirmed) return;
            }

            this.loading = true;
            try {
                const remainingRefund = Math.round(this.getRemainingStoreCredit() * 100) / 100;

                if (remainingRefund > 0.005 && this.refund_methods.length > 0 && this.isRefundMethodsValid()) {
                    if (this.hasMerchandiseDue) {
                        this.posWarn('Refund is blocked while merchandise is pending receipt. Reselect instead, or wait until the return is received.');
                        this.loading = false;
                        return;
                    }
                    await this.submitRefundWithMethods();
                    await this.refreshStoreCredit();
                } else if (remainingRefund > 0.005 && this.refund_methods.length === 0 && !this.hasMerchandiseDue) {
                    const wantsRefund = await this.$bvModal.msgBoxConfirm(
                        `There is $${remainingRefund.toFixed(2)} in store credit. Process a refund first?`,
                        { title: 'Store credit available', size: 'sm', buttonSize: 'sm', okVariant: 'success', okTitle: 'Process refund', cancelTitle: 'Continue without refund', footerClass: 'p-2', centered: true }
                    );
                    if (wantsRefund) {
                        this.loading = false;
                        this.openPosTab('refundmethods');
                        return;
                    }
                }

                this.printExchangeReceipt();
                await new Promise(resolve => setTimeout(resolve, 1000));
                await this.updateInvoiceAsync();
            } catch (error) {
                console.error("Update process failed:", error);
                this.$bvToast.toast("Update failed: " + error.message, { title: "Error", variant: "danger" });
            } finally {
                this.loading = false;
            }
        },

        getPaymentMethodKey(payment) {
            if (!payment) return '';
            let key = `${payment.value || ''}|${parseFloat(payment.amount || 0).toFixed(2)}`;
            if (payment.value === 'cc' || payment.value === 'tax_exempt') key += `|${payment.name || ''}`;
            else if (payment.value === 'finance') key += `|${payment.name || ''}|${payment.lease_id || ''}`;
            else if (payment.value === 'store_credit') key += `|${payment.store_credit_invoice_id || ''}`;
            return key;
        },

        updateInvoiceAsync() {
            return new Promise((resolve, reject) => {
                const invoice = JSON.parse(JSON.stringify(this.invoice));
                invoice.items = invoice.items.map(item => ({ ...item, source: item.source || item.sourceContext || 'unknown' }));

                const existingKeys = new Set(this.payment_history.map(pm => this.getPaymentMethodKey(pm)));
                invoice.new_payment_methods = (invoice.new_payment_methods || []).filter(pm =>
                    pm.amount && parseFloat(pm.amount) !== 0 && !existingKeys.has(this.getPaymentMethodKey(pm)));

                invoice.warranty_charges = +invoice.new_warranty_charges || 0.0;
                invoice.delivery_charges = +invoice.new_delivery_charges || 0.0;
                invoice.whiteglove_charges = +invoice.new_whiteglove_charges || 0.0;
                invoice.setup_charges = +invoice.new_setup_charges || 0.0;
                invoice.sales_tax = this.getSalesTax();
                invoice.subtotal = this.getSubTotal();
                invoice.taxable_subtotal = this.adjust_to_taxable_amount(this.getSubTotal());
                invoice.total = this.getGrandTotal();

                const fail = (msg) => { this.$vex.dialog.alert({ message: msg }); reject(new Error(msg)); };

                const remainingStoreCredit = this.getRemainingStoreCredit();
                if (remainingStoreCredit > 0 && !this.hasMerchandiseDue) {
                    return fail(`Cannot update. $${remainingStoreCredit.toFixed(2)} of store credit must be refunded or used first.`);
                }
                const customerErrors = this.validate_customer_info(invoice.customer || {});
                if (customerErrors.length) return fail(customerErrors[0]);
                const productErrors = this.validate_products(invoice.items.filter(d => d.existing == false));
                if (productErrors.length) return fail(productErrors[0]);
                const paymentErrors = this.validate_payment_methods(invoice.new_payment_methods);
                if (paymentErrors.length) return fail(paymentErrors[0]);

                invoice.new_payment_methods = invoice.new_payment_methods.map(d => { d.amount = +d.amount || 0.0; return d; });
                invoice.housetruck = (this.packet.housetrucks || []).length > 0 ? this.packet.housetrucks[0].value : null;
                invoice.username = this.invoice.username || this.userDetails.username;
                invoice.invoice_type = "INV";
                invoice.default_locsubs = this.default_locsubs;

                const that = this;
                this.block_post_invoice = true;
                this.loading = true;
                this.showLoader("Updating sale...");
                Requestor.postSyncP("/api/sales/update_sale/", invoice, this,
                    function (response) {
                        that.hideLoader();
                        if (response.data.error) {
                            that.$vex.dialog.alert({ message: response.data.error });
                            reject(new Error(response.data.error));
                        } else {
                            that.successMessage = 'Sale updated';
                            if (that.$refs.successAnimation) that.$refs.successAnimation.show();
                            setTimeout(() => window.location.reload(), 2500);
                            resolve(response.data);
                        }
                        that.block_post_invoice = false;
                        that.loading = false;
                    },
                    function (error) {
                        that.hideLoader();
                        that.$bvToast.toast("An error occurred while updating the sale. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                        that.block_post_invoice = false;
                        that.loading = false;
                        reject(error);
                    }
                );
            });
        },

        printHtml(html) {
            const frame = document.createElement('iframe');
            frame.style.position = 'absolute';
            frame.style.width = '0';
            frame.style.height = '0';
            frame.style.border = 'none';
            document.body.appendChild(frame);
            const doc = frame.contentWindow.document;
            doc.open();
            doc.write(html);
            doc.close();
            frame.onload = () => {
                setTimeout(() => {
                    frame.contentWindow.focus();
                    frame.contentWindow.print();
                    setTimeout(() => document.body.removeChild(frame), 1000);
                }, 500);
            };
        },
        printReceipt() { this.printHtml(this.generateReceiptHTML()); },
        printReceiptAndOpenDrawer() { setTimeout(() => this.printReceipt(), 500); },
        printExchangeReceipt() { this.printHtml(this.generateExchangeReceiptHTML()); },
        printRefundReceipt() { this.printHtml(this.generateRefundReceiptHTML()); },

        receiptStyles() {
            return `
                @page { size: 80mm auto; margin: 0; }
                body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 0 auto; padding: 2mm; line-height: 1.3; }
                .header { text-align: center; font-weight: bold; margin-bottom: 3mm; }
                .store-name { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
                .banner { font-size: 15px; font-weight: bold; text-align: center; margin: 3mm 0; padding: 2mm; border: 1px dashed #000; }
                .divider { border-top: 1px dashed #000; margin: 2mm 0; }
                .row { display: flex; justify-content: space-between; margin: 1mm 0; }
                .b { font-weight: bold; }
                .section { font-weight: bold; border-bottom: 1px solid #000; margin-top: 3mm; padding-bottom: 1mm; }
                .small { font-size: 10px; margin-left: 2mm; margin-bottom: 1mm; }
                .grand { font-weight: bold; font-size: 16px; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 2mm 0; margin: 2mm 0; }
                .footer { text-align: center; margin-top: 5mm; font-size: 10px; }
                .thanks { font-size: 15px; font-weight: bold; margin: 3mm 0; }`;
        },
        receiptHeader() {
            const loc = this.invoice.location_address || this.getSelectedLocationName() || 'FURNITURE MECCA';
            const date = new Date().toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
            return `
                <div class="header">
                    <div class="store-name">FURNITURE MECCA</div>
                    <div>${loc}</div>
                    <div>${date}</div>
                </div>
                <div class="divider"></div>
                <div class="row"><span class="b">INVOICE #:</span><span class="b">${this.invoice.invoice_id || this.invoice.draft_id || 'DRAFT'}</span></div>
                <div class="row"><span class="b">SALESPERSON:</span><span class="b">${(this.invoice.username || this.userDetails.username || '').toUpperCase()}</span></div>
                <div class="row"><span class="b">CUSTOMER:</span><span class="b">${((this.invoice.customer || {}).fullname || 'Walk-in Customer').toUpperCase()}</span></div>`;
        },
        itemReceiptLines(items, sign) {
            return items.map(item => {
                const price = this.getEffectiveUnitPrice(item);
                const qty = parseInt(item.quantity) || 1;
                const discount = parseFloat(item.discount_offered) || 0;
                const total = price * qty - discount;
                const desc = (item.product_name || item.product_sku || 'Item');
                return `
                    <div class="row"><span class="b">${item.product_sku || ''}</span><span class="b">${sign}$${total.toFixed(2)}</span></div>
                    <div class="small">${desc.substring(0, 30)}${desc.length > 30 ? '...' : ''}<br>$${price.toFixed(2)} x ${qty}${discount > 0 ? ` (-$${discount.toFixed(2)})` : ''}${item.is_damaged ? ' AS-IS' : ''}</div>`;
            }).join('');
        },

        generateReceiptHTML() {
            let items = this.invoice.items || [];
            if (this.invoice.edit) items = items.filter(i => !i.existing || (i.quantity - (i.refunded_so_far_quantity || 0)) > 0);
            const n = v => (parseFloat(v) || 0).toFixed(2);
            const grandTotal = this.getGrandTotal();
            const totalPayments = this.getTotalPayments();
            const balance = this.invoice.edit ? this.getTotalBalance() : Math.round((grandTotal - totalPayments) * 100) / 100;
            const pms = this.getPaymentMethods();

            return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Receipt</title><style>${this.receiptStyles()}</style></head><body>
                ${this.receiptHeader()}
                <div class="section">ITEM DETAILS</div>
                ${this.itemReceiptLines(items, '')}
                <div class="divider"></div>
                <div class="row"><span class="b">TOTAL DISCOUNT:</span><span>$${n(this.getTotalDiscount())}</span></div>
                <div class="row"><span class="b">ORDER TOTAL:</span><span>$${n(this.getOrderTotal())}</span></div>
                <div class="row"><span class="b">DELIVERY:</span><span>$${n(this.invoice.delivery_charges)}</span></div>
                <div class="row"><span class="b">WHITE GLOVE:</span><span>$${n(this.invoice.whiteglove_charges)}</span></div>
                <div class="row"><span class="b">SETUP:</span><span>$${n(this.invoice.setup_charges)}</span></div>
                <div class="row"><span class="b">WARRANTY:</span><span>$${n(this.invoice.warranty_charges)}</span></div>
                <div class="row"><span class="b">SUBTOTAL:</span><span>$${n(this.getSubTotal())}</span></div>
                <div class="row"><span class="b">TAXABLE SUBTOTAL:</span><span>$${n(this.adjust_to_taxable_amount(this.getSubTotal()))}</span></div>
                <div class="row"><span class="b">SALES TAX:</span><span>$${n(this.getSalesTax())}</span></div>
                <div class="row"><span class="b">SPECIAL DISCOUNTS:</span><span>$${n(this.getTotalSpecialDiscounts())}</span></div>
                <div class="row grand"><span>GRAND TOTAL:</span><span>$${n(grandTotal)}</span></div>
                ${pms.length ? `
                    <div class="section">PAYMENTS</div>
                    ${pms.map(pm => `
                        <div class="row"><span class="b">${(pm.value || '').toUpperCase()}:</span><span>$${n(pm.amount)}</span></div>
                        ${pm.value === 'cc' && pm.name ? `<div class="small">CARD ****${pm.name}</div>` : ''}
                        ${(pm.value === 'finance' || pm.value === 'tax_exempt') && pm.name ? `<div class="small">${pm.name}${pm.lease_id ? ' (LEASE ' + pm.lease_id + ')' : ''}</div>` : ''}
                    `).join('')}
                    <div class="row b"><span>TOTAL PAID:</span><span>$${n(totalPayments)}</span></div>` : ''}
                <div class="row b"><span>BALANCE:</span><span>$${n(balance)}</span></div>
                ${this.invoice.comment_txt ? `<div class="divider"></div><div class="small"><span class="b">COMMENTS:</span> ${this.invoice.comment_txt}</div>` : ''}
                <div class="footer"><div class="thanks">THANK YOU FOR SHOPPING WITH US!</div></div>
                <div class="footer">- - - - - - - - - - - - - - - - - - - -</div>
            </body></html>`;
        },

        generateExchangeReceiptHTML() {
            const n = v => (parseFloat(v) || 0).toFixed(2);
            const refunded = this.refund_items || [];
            const newItems = this.invoice.items.filter(i => !i.existing);
            const refundTotal = refunded.reduce((s, i) => {
                const qty = parseInt(i.return_quantity) || 0;
                return s + (parseFloat(i.selling_price) || 0) * qty - qty * (parseFloat(i.unit_discount_value) || 0);
            }, 0);
            const newItemsTotal = this.getOrderTotal();
            const grandTotal = newItemsTotal + this.getChargesTotal() + this.getSalesTax();
            const creditApplied = this.getStoreCreditApplied();
            const net = refundTotal > 0 ? grandTotal - refundTotal - creditApplied : grandTotal - creditApplied;

            let type = 'ADDITIONAL PURCHASE';
            if (refundTotal > 0 && grandTotal > 0) type = net > 0 ? 'EXCHANGE - CUSTOMER PAYS DIFFERENCE' : (net < 0 ? 'EXCHANGE - REFUND DUE' : 'EQUAL EXCHANGE');
            else if (refundTotal > 0) type = 'FULL REFUND';

            return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Exchange receipt</title><style>${this.receiptStyles()}</style></head><body>
                ${this.receiptHeader()}
                <div class="banner">${type}</div>
                ${this.hasMerchandiseDue ? '<div class="banner">MERCHANDISE PENDING RECEIPT — EXCHANGE ITEMS ON HOLD</div>' : ''}
                ${refunded.length ? `<div class="section">RETURNED / CANCELLED</div>
                    ${refunded.map(i => {
                        const qty = parseInt(i.return_quantity) || 0;
                        const t = (parseFloat(i.selling_price) || 0) * qty - qty * (parseFloat(i.unit_discount_value) || 0);
                        return `<div class="row"><span>${(i.product_name || i.product_sku).substring(0, 25)}</span><span class="b">-$${t.toFixed(2)}</span></div>
                                <div class="small">SKU ${i.product_sku} | Qty ${qty}</div>`;
                    }).join('')}
                    <div class="row b"><span>RETURN TOTAL:</span><span>-$${n(refundTotal)}</span></div>` : ''}
                ${newItems.length ? `<div class="section">NEW / EXCHANGE ITEMS</div>${this.itemReceiptLines(newItems, '+')}
                    <div class="row b"><span>NEW ITEMS TOTAL:</span><span>+$${n(newItemsTotal)}</span></div>` : ''}
                <div class="divider"></div>
                <div class="row"><span>Merchandise:</span><span>$${n(newItemsTotal)}</span></div>
                <div class="row"><span>Delivery:</span><span>$${n(this.invoice.new_delivery_charges || 0)}</span></div>
                <div class="row"><span>White Glove:</span><span>$${n(this.invoice.new_whiteglove_charges || 0)}</span></div>
                <div class="row"><span>Setup:</span><span>$${n(this.invoice.new_setup_charges || 0)}</span></div>
                <div class="row"><span>Warranty:</span><span>$${n(this.invoice.new_warranty_charges || 0)}</span></div>
                <div class="row"><span>Sales tax:</span><span>$${n(this.getSalesTax())}</span></div>
                <div class="row b"><span>GRAND TOTAL:</span><span>$${n(grandTotal)}</span></div>
                <div class="row"><span>STORE CREDIT APPLIED:</span><span>-$${n(creditApplied)}</span></div>
                <div class="row grand"><span>${net > 0.005 ? 'BALANCE DUE' : (net < -0.005 ? 'REFUND DUE' : 'PAID IN FULL')}</span><span>$${n(Math.abs(net))}</span></div>
                ${this.refund_methods.length ? `<div class="section">REFUND METHODS</div>${this.refund_methods.map(m =>
                    `<div class="row"><span class="b">${(m.value || '').toUpperCase()}:</span><span>$${n(m.amount)}</span></div>`).join('')}` : ''}
                ${this.invoice.comment_txt ? `<div class="divider"></div><div class="small"><span class="b">COMMENTS:</span> ${this.invoice.comment_txt}</div>` : ''}
                <div class="footer"><div class="thanks">THANK YOU FOR YOUR BUSINESS!</div></div>
            </body></html>`;
        },

        generateRefundReceiptHTML() {
            const n = v => (parseFloat(v) || 0).toFixed(2);
            return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Refund receipt</title><style>${this.receiptStyles()}</style></head><body>
                ${this.receiptHeader()}
                <div class="banner">REFUND RECEIPT</div>
                ${(this.refund_items || []).length ? `<div class="section">RETURNED ITEMS</div>
                    ${this.refund_items.map(i => {
                        const qty = parseInt(i.return_quantity) || 0;
                        const t = (parseFloat(i.selling_price) || 0) * qty - qty * (parseFloat(i.unit_discount_value) || 0);
                        return `<div class="row"><span>${(i.product_name || i.product_sku).substring(0, 25)}</span><span class="b">$${t.toFixed(2)}</span></div>`;
                    }).join('')}` : ''}
                <div class="section">REFUND METHODS</div>
                ${this.refund_methods.map(m => `
                    <div class="row"><span class="b">${(m.value || '').toUpperCase()}</span><span>$${n(m.amount)}</span></div>
                    ${m.value === 'cc' && m.name ? `<div class="small">CARD ****${m.name}</div>` : ''}
                    ${m.value === 'finance' && m.name ? `<div class="small">${m.name}${m.lease_id ? ' | LEASE ' + m.lease_id : ''}</div>` : ''}
                `).join('')}
                <div class="row grand"><span>TOTAL REFUND:</span><span>$${n(this.getRemainingStoreCredit())}</span></div>
                <div class="footer"><div class="thanks">THANK YOU FOR YOUR BUSINESS!</div></div>
            </body></html>`;
        },

        refundLineCredit(ritem) {
            const qty = parseInt(ritem.return_quantity) || 0;
            return qty * ((parseFloat(ritem.selling_price) || 0) - (parseFloat(ritem.unit_discount_value) || 0));
        },
        keepReturnItem(ritem) {
            this.refund_items = this.refund_items.filter(d => d._return_key !== ritem._return_key);
            this.current_refund_validated = false;
        },
        openCartForReturns() {
            this.$bvModal.hide('pos-modal-returns');
            this.productModalTab = 1;
            this.$bvModal.show('pos-modal-products');
        },
        startNewReturn() {
            this.returnsForceSelect = true;
            this.exchangeChoice = null;
        },
        chooseRefundCredit() {
            if (this.hasMerchandiseDue) {
                this.posWarn('All returned merchandise must be marked received before refunding.');
                this.$bvModal.hide('pos-modal-returns');
                this.openPosTab('merchandisedue');
                return;
            }
            this.exchangeChoice = 'refund';
            this.$bvModal.hide('pos-modal-returns');
            this.$bvModal.show('pos-modal-refundmethods');
        },
        chooseReselect() {
            this.exchangeChoice = 'reselect';
            this.$bvModal.hide('pos-modal-returns');
            this.productModalTab = 0;
            this.$bvModal.show('pos-modal-products');
        },
        continueToSettlement() {
            const missing = this.newItems.find(i => !i.order_type || (i.order_type !== 'customer_takes' && !i.delivery_date));
            if (missing) {
                this.posWarn(`Pick an order type and date for ${missing.product_name || missing.product_sku}.`);
                this.productModalTab = 1;
                return;
            }
            this.$set(this.tabVisited, 'products', true);
            this.$bvModal.hide('pos-modal-products');
            this.$bvModal.show('pos-modal-returns');
        },
        settleExchange() {
            const outcome = this.exchangeSummary.outcome;

            if (outcome === 'refund' && this.hasMerchandiseDue) {
                this.posWarn('All returned merchandise must be marked received before refunding. Reselect instead, or wait for receipt.');
                this.$bvModal.hide('pos-modal-returns');
                this.openPosTab('merchandisedue');
                return;
            }

            this.$bvModal.hide('pos-modal-returns');
            if (outcome === 'refund') this.$bvModal.show('pos-modal-refundmethods');
            else if (outcome === 'pay') this.openPosTab('payment');
            else this.handleUpdate();
        },
        prefillRefundMethod() {
            this.calculateAvailableRefundAmounts();
            const due = this.getRemainingStoreCredit();
            if (due <= 0) return;
            if (!this.refund_methods.length) this.addRefundMethod();
            if (this.refund_methods.length === 1 && !(parseFloat(this.refund_methods[0].amount) > 0)) {
                const m = this.refund_methods[0];
                const options = this.getFilteredRefundMethodOptions();
                if (!m.value && options.length === 1) m.value = options[0].value;
                m.amount = due;
            }
        },
        roundTo(n, digits = 0) {
            const negative = n < 0;
            if (negative) n = n * -1;
            const m = Math.pow(10, digits);
            n = parseFloat((n * m).toFixed(11));
            n = (Math.round(n) / m).toFixed(digits);
            return negative ? (n * -1).toFixed(digits) : n;
        },
        validate_refund_products(refund_items) {
            for (const r of refund_items) {
                const qty = parseInt(r.return_quantity);
                if (qty < 0) return [{ message: "RETURN QUANTITY CAN'T BE LESS THAN 0" }];
                if ((r.refunded_so_far_quantity || 0) + qty > r.quantity) {
                    return [{ message: "A RETURNED QUANTITY IS GREATER THAN WHAT WAS BOUGHT. PLEASE REVIEW." }];
                }
            }
            return [];
        },
        validate_refund_charges(rc) {
            const errors = [];
            const check = (label, current, total, sofar) => {
                const allowed = (parseFloat(total) || 0) - (parseFloat(sofar) || 0);
                const cur = parseFloat(current) || 0;
                if (cur < 0) errors.push({ message: "ALL REFUND VALUES MUST BE 0 OR MORE" });
                else if (cur > allowed) {
                    errors.push({ message: allowed == 0 ? `REFUND ON ${label} NOT ALLOWED.` : `${label} REFUND SHOULD BE UNDER $${allowed.toFixed(2)}` });
                }
            };
            check('DELIVERY CHARGES', rc.delivery_refunded_currently, this.invoice.delivery_charges, rc.delivery_refunded_sofar);
            check('WHITE GLOVE CHARGES', rc.whiteglove_refunded_currently, this.invoice.whiteglove_charges, rc.whiteglove_refunded_sofar);
            check('SETUP CHARGES', rc.setup_refunded_currently, this.invoice.setup_charges, rc.setup_refunded_sofar);
            check('WARRANTY', rc.warranty_refunded_currently, this.invoice.warranty_charges, rc.warranty_refunded_sofar);
            return errors;
        },
        validate_refund() {
            this.showLoader("Validating return...");
            const itemErrors = this.validate_refund_products(this.refund_items);
            if (itemErrors.length) {
                this.hideLoader();
                this.$vex.dialog.alert({ message: itemErrors[0].message });
                return;
            }
            const chargeErrors = this.validate_refund_charges(this.refund_charges);
            if (chargeErrors.length) {
                this.hideLoader();
                this.$vex.dialog.alert({ message: chargeErrors[0].message });
                return;
            }

            let refund_amount = this.refund_items.reduce((sum, d) => {
                const qty = parseInt(d.return_quantity) || 0;
                return sum + qty * parseFloat(d.selling_price) - qty * (parseFloat(d.unit_discount_value) || 0);
            }, 0);
            refund_amount += (parseFloat(this.refund_charges.delivery_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.whiteglove_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.setup_refunded_currently) || 0) +
                (parseFloat(this.refund_charges.warranty_refunded_currently) || 0);

            if (refund_amount == 0) {
                this.hideLoader();
                this.$vex.dialog.alert({ message: "NOTHING TO REFUND" });
                return;
            }

            let taxReturn = 0.0;
            if (this.invoice.sales_tax) {
                const base = this.invoice.total - this.invoice.sales_tax;
                if (base > 0) taxReturn = (refund_amount / base) * this.invoice.sales_tax;
            }
            this.refund_charges.total_refunded_currently = this.roundTo(taxReturn + refund_amount, 2);
            this.refund_charges.tax_refunded_currently = this.roundTo(taxReturn, 2);
            this.current_refund_validated = true;
            this.hideLoader();
        },
        submit_refund_wrapper() {
            const that = this;
            this.loading = true;
            Requestor.getSync("/api/sales/find_if_refund_allowed/", { invoice_id: this.invoice.invoice_id },
                function (response) {
                    if (response.data.error) {
                        if (response.data.reason) that.$vex.dialog.alert({ message: response.data.reason });
                        else that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    } else if (response.data.refund_allowed) {
                        that.submit_refund();
                    } else {
                        that.$bvToast.toast("Refund not allowed", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                    that.loading = false;
                },
                function () {
                    that.$bvToast.toast("Error checking refund eligibility. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                    that.loading = false;
                }
            );
        },
        submit_refund() {
            const that = this;
            const refundItemsData = this.refund_items.map(d => {
                const perUnitPrice = parseFloat(d.selling_price) || 0;
                const unitDiscount = parseFloat(d.unit_discount_value) || 0;
                const original = that.invoice.items.find(i => i.itemid === d.itemid);
                return {
                    refund_id: null, invoice_id: that.invoice.invoice_id, product_id: -1,
                    itemid: d.itemid, product_sku: d.product_sku,
                    return_quantity: parseInt(d.return_quantity) || 0,
                    selling_price: perUnitPrice, unit_discount_value: unitDiscount,
                    per_unit_refund_amount: perUnitPrice - unitDiscount,
                    refunded_at_state: original ? original.state : 0,
                    reason: null, note: null, is_delivered: false, return_locsub: null,
                };
            });
            const itemTotal = Math.round(refundItemsData.reduce((s, i) => s + i.return_quantity * i.per_unit_refund_amount, 0) * 100) / 100;
            const data = {
                invoice_id: this.invoice.invoice_id,
                created_by: this.userDetails.username,
                is_layaway: this.invoice.is_layaway || false,
                approved: false,
                total_refunded_item_amount: itemTotal,
                total_refunded_sales_tax: parseFloat(this.refund_charges.tax_refunded_currently) || 0,
                total_refunded_delivery: parseFloat(this.refund_charges.delivery_refunded_currently) || 0.0,
                total_refunded_whiteglove: parseFloat(this.refund_charges.whiteglove_refunded_currently) || 0.0,
                total_refunded_setup: parseFloat(this.refund_charges.setup_refunded_currently) || 0.0,
                total_refunded_warranty: parseFloat(this.refund_charges.warranty_refunded_currently) || 0.0,
                refund_methods: [], refund_items: refundItemsData,
            };
            this.loading = true;
            this.showLoader("Recording return and creating store credit...");
            Requestor.postAsyncP("/api/sales/record_refund/", data, this,
                function (response) {
                    that.hideLoader();
                    if (response.data.error) {
                        that.$vex.dialog.alert({ message: response.data.error });
                    } else {
                        that.$bvToast.toast("Store credit created. Mark merchandise received to continue.", { title: "Store credit created", autoHideDelay: 3000, variant: "success" });
                        that.refund_items = [];
                        that.current_refund_validated = false;
                        that.returnsForceSelect = false;
                        that.exchangeChoice = null;
                        that.creditRefreshing = true;
                        setTimeout(() => {
                            that.reloadAllData();
                            that.refreshStoreCredit();
                            that.loadMerchandiseDue();
                        }, 1000);
                    }
                    that.loading = false;
                },
                function () {
                    that.hideLoader();
                    that.$bvToast.toast("Error recording the return. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                    that.loading = false;
                }
            );
        },

        addRefundMethod() {
            this.refund_methods.push({
                value: null, amount: 0.0, name: null, check_number: null, notes: null,
                lease_id: null, approved_amount: null, misc_info: null, ein: null, certificate: null,
            });
        },
        removeRefundMethod(rmidx) { this.refund_methods.splice(rmidx, 1); },
        calculateAvailableRefundAmounts() {
            const amounts = { cash: 0, cc: 0, finance: 0, store_credit: 0, tax_exempt: 0, check: 0 };
            const original = (this.payment_history && this.payment_history.length) ? this.payment_history : (this.invoice.payment_methods || []);
            original.forEach(pm => {
                const m = pm.value ? pm.value.toLowerCase() : null;
                if (m && Object.prototype.hasOwnProperty.call(amounts, m)) amounts[m] += parseFloat(pm.amount) || 0;
            });
            (this.refundsHistory || []).forEach(refund => {
                if (refund.detail && refund.detail.type === 'refund' && refund.ritems) {
                    refund.ritems.forEach(ri => {
                        const m = ri.value ? ri.value.toLowerCase() : null;
                        if (m && Object.prototype.hasOwnProperty.call(amounts, m)) amounts[m] = Math.max(0, amounts[m] - (parseFloat(ri.amount) || 0));
                    });
                }
            });
            if (this.invoice.available_credit_value) amounts.store_credit = parseFloat(this.invoice.available_credit_value) || 0;
            this.available_refund_amounts = amounts;
            return amounts;
        },
        getFilteredRefundMethodOptions() {
            return this.refund_method_options.filter(m => (this.available_refund_amounts[m.value] || 0) > 0);
        },
        updateRefundMethodDisplay() { this.calculateAvailableRefundAmounts(); },
        getRemainingAmountForMethod(method) { return this.available_refund_amounts[method] || 0; },
        showRefundWarning(refundMethod, methodType) {
            if (!refundMethod.amount || !refundMethod.value) return false;
            return (parseFloat(refundMethod.amount) || 0) > this.getRemainingAmountForMethod(methodType);
        },
        validateRefundAmount(refundMethod) {
            if (!refundMethod.value || !refundMethod.amount) return true;
            const amount = parseFloat(refundMethod.amount) || 0;
            const available = this.getRemainingAmountForMethod(refundMethod.value);
            if (amount > available) {
                this.posWarn(`Refund ($${amount.toFixed(2)}) exceeds the available ${refundMethod.value.toUpperCase()} amount ($${available.toFixed(2)}).`);
                return false;
            }
            return true;
        },
        validateRefundMethodAmounts() {
            const totals = {};
            this.refund_methods.forEach(m => {
                if (m.value && m.amount) totals[m.value] = (totals[m.value] || 0) + (parseFloat(m.amount) || 0);
            });
            for (const [method, requested] of Object.entries(totals)) {
                const available = this.getRemainingAmountForMethod(method);
                if (requested > available) {
                    return { valid: false, error: `Cannot refund $${requested.toFixed(2)} via ${method.toUpperCase()}. Only $${available.toFixed(2)} available.` };
                }
            }
            return { valid: true };
        },
        isRefundMethodsValid() {
            if (this.refund_methods.length === 0) return false;
            const allocated = this.refund_methods.reduce((s, m) => s + (parseFloat(m.amount) || 0.0), 0.0);
            if (Math.abs(allocated - this.getRemainingStoreCredit()) > 0.01) return false;
            return this.refund_methods.every(m =>
                m.value && m.amount && parseFloat(m.amount) > 0 &&
                !(m.value === 'cc' && !m.name) &&
                !(m.value === 'check' && !m.check_number));
        },
        validateAndSubmitRefund() {
            if (this.hasMerchandiseDue) {
                this.posWarn('All returned merchandise must be marked received before refunding.');
                this.$bvModal.hide('pos-modal-refundmethods');
                this.openPosTab('merchandisedue');
                return;
            }
            const v = this.validateRefundMethodAmounts();
            if (!v.valid) { this.$vex.dialog.alert({ message: v.error }); return; }
            if (!this.isRefundMethodsValid()) {
                this.$vex.dialog.alert({ message: "Make sure every refund method is filled in and the total matches the store credit to refund." });
                return;
            }
            this.submitRefundWithMethods().catch(() => {});
        },

        submitRefundWithMethods() {
            return new Promise((resolve, reject) => {
                if (this.hasMerchandiseDue) {
                    this.posWarn('All returned merchandise must be marked received before refunding.');
                    reject(new Error("Merchandise still pending receipt"));
                    return;
                }
                if (!this.isRefundMethodsValid()) {
                    this.$vex.dialog.alert({ message: "Make sure every refund method is filled in and the total matches the store credit to refund." });
                    reject(new Error("Refund methods not valid"));
                    return;
                }

                if (!this.refundOtp.verificationToken) {
                    const amount = this.getRemainingStoreCredit();
                    this.openRefundOtpModal(amount);
                    reject(new Error("Awaiting customer verification"));
                    return;
                }

                const pms = this.refund_methods.map(m => {
                    const d = {
                        value: m.value, amount: parseFloat(m.amount), name: m.name,
                        check_number: m.check_number, notes: m.notes, issue_amount: parseFloat(m.amount),
                    };
                    if (m.value === 'finance') {
                        d.lease_id = m.lease_id;
                        d.approved_amount = parseFloat(m.approved_amount) || null;
                        d.misc_info = m.misc_info;
                    }
                    if (m.value === 'tax_exempt') {
                        d.ein = m.ein;
                        d.certificate = m.certificate;
                    }
                    return d;
                });

                const receiptHtml = this.generateRefundReceiptHTML();
                this.processRefundWithPaymentMethods(pms)
                    .then(response => {
                        this.successMessage = 'Refund processed';
                        if (this.$refs.successAnimation) this.$refs.successAnimation.show();
                        this.printHtml(receiptHtml);
                        this.resetRefundOtp();
                        resolve(response);
                    })
                    .catch(reject);
            });
        },

        processRefundWithPaymentMethods(paymentMethods) {
            return new Promise((resolve, reject) => {
                const that = this;
                this.loading = true;
                this.showLoader(`Issuing refund of ${this.money(this.getRemainingStoreCredit())}...`);
                Requestor.postAsyncP("/api/sales/issue_sc_out/", {
                    invoice_id: this.invoice.invoice_id,
                    username: this.userDetails.username,
                    pms: paymentMethods,
                    amount: this.getRemainingStoreCredit(),
                    verification_token: this.refundOtp.verificationToken,
                }, this,
                    function (response) {
                        that.hideLoader();
                        if (response.data.error) {
                            that.$vex.dialog.alert({ message: response.data.error });
                            reject(new Error(response.data.error));
                        } else {
                            that.refund_methods = [];
                            that.current_refund_validated = false;
                            that.resetRefundOtp();
                            that.refreshInvoiceData();
                            that.loadMerchandiseDue();
                            resolve(response.data);
                        }
                        that.loading = false;
                    },
                    function (error) {
                        that.hideLoader();
                        that.$bvToast.toast("Error processing refund. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                        that.loading = false;
                        reject(error);
                    }
                );
            });
        },

        refreshAvailableRefundAmounts() {
            if (!this.invoice.invoice_id) return;
            this.showLoader("Refreshing available refund amounts...");
            this.loadPaymentMethodsOnly();
            this.loadRefundsHistory();
            setTimeout(() => this.hideLoader(), 800);
        },
        summarize_refunds_history(refunds) {
            let total = 0.0;
            (refunds || []).forEach(r => {
                if (r.detail && r.detail.type == 'return') {
                    total += (r.detail.total_refunded_item_amount +
                        r.detail.total_refunded_sales_tax +
                        r.detail.total_refunded_delivery +
                        (r.detail.total_refunded_whiteglove || 0) +
                        r.detail.total_refunded_setup +
                        r.detail.total_refunded_warranty) || 0.0;
                }
            });
            this.total_refunded_value = total;
        },

        loadPaymentMethodsOnly() {
            if (!this.invoice.invoice_id) return;
            const that = this;
            this.showLoader("Loading payment history...");
            Requestor.postAsyncP("/api/sales/get_invoice/", { invoice_id: this.invoice.invoice_id }, this,
                function (response) {
                    that.hideLoader();
                    if (response.data.result) {
                        const pms = response.data.result.payment_methods || [];
                        that.payment_history = pms;
                        that.original_payment_methods = pms;
                        that.invoice.payment_methods = pms;
                        that.initializeUsedRefundAmounts();
                        that.calculateAvailableRefundAmounts();
                    }
                },
                function (error) {
                    that.hideLoader();
                    console.error(error);
                }
            );
        },
        initializeUsedRefundAmounts() {
            const used = {};
            this.original_payment_methods.forEach(pm => {
                used[pm.value] = { original_amount: parseFloat(pm.amount) || 0, refunded_so_far: 0, remaining: parseFloat(pm.amount) || 0 };
            });
            this.used_refund_amounts = used;
        },
        loadRefundsHistory() {
            if (!this.invoice.invoice_id) return;
            const that = this;
            this.showLoader("Loading refund history...");
            Requestor.getSyncP("/api/sales/get_refunds_history/", { invoice_id: this.invoice.invoice_id }, this,
                function (response) {
                    that.hideLoader();
                    if (!response.data.error) {
                        that.refundsHistory = response.data.result || [];
                        that.calculateAvailableRefundAmounts();
                    }
                },
                function (error) {
                    that.hideLoader();
                    console.error("Error fetching refunds history:", error);
                }
            );
        },
        refreshStoreCredit() {
            if (!this.invoice.invoice_id) return Promise.resolve();
            const that = this;
            return new Promise(resolve => {
            Requestor.postAsyncP("/api/sales/get_available_credit/", { invoice_id: this.invoice.invoice_id }, this,
                function (response) {
                    if (response.data.result && response.data.result.length > 0) {
                        const r = response.data.result[0];
                        that.invoice.available_credit_value = r.available_amount != null ? r.available_amount : (r.available_credit_value || 0);
                        that.calculateAvailableRefundAmounts();
                    }
                    that.creditRefreshing = false;
                    resolve();
                },
                function (error) {
                    console.error("Error refreshing store credit:", error);
                    that.creditRefreshing = false;
                    resolve();
                }
            );
            });
        },
        refresh_available_store_credit() { this.refreshStoreCredit(); },
        refreshInvoiceData() {
            this.refreshStoreCredit();
            this.refundsSummaryKey += 1;
        },
        reloadAllData() {
            this.refund_items = [];
            this.current_refund_validated = false;
            this.showLoader("Refreshing sale...");
            this.reload_edit_invoice();
            this.refundsSummaryKey += 1;
        },
        reload_edit_invoice() {
            const that = this;
            this.loading = true;
            this.showLoader("Loading invoice details...");
            Requestor.getAsyncP("/api/sales/load_existing_invoice_for_edit/", { invoice_id: this.invoice.invoice_id, include_damage_info: true }, this,
                function (response) {
                    that.hideLoader();
                    if (response.data.result) {
                        const inv = response.data.result[0];
                        inv.edit = true;
                        inv.new_payment_methods = [];
                        inv.payment_methods = [];
                        if (!inv.customer) inv.customer = {};
                        if (inv.new_delivery_charges === undefined) inv.new_delivery_charges = 0;
                        if (inv.new_whiteglove_charges === undefined) inv.new_whiteglove_charges = 0;
                        if (inv.new_setup_charges === undefined) inv.new_setup_charges = 0;
                        if (inv.new_warranty_charges === undefined) inv.new_warranty_charges = 0;

                        (inv.items || []).forEach(item => {
                            if (item.damage_info) {
                                item.is_damaged = item.damage_info.is_damaged !== false;
                                item.source = 'damage';
                            } else if (item.is_damaged === true) {
                                item.damage_info = {
                                    damage_id: item.damage_id || null, is_damaged: true,
                                    damage_grade: item.damage_grade || null,
                                    damage_description: item.damage_description || '',
                                    damage_images: item.damage_images || [],
                                };
                                item.source = 'damage';
                            }
                            if (item.existing) item.sourceContext = (item.locsub || item.source_location_id) ? 'store' : 'warehouse';
                            if (!item.source) item.source = item.is_damaged ? 'damage' : (item.sourceContext || 'store');
                            if (!Object.prototype.hasOwnProperty.call(item, 'is_as_is')) item.is_as_is = false;
                        });

                        that.invoice = inv;
                        that.loadLocationContact();
                        that.deliveryWaiverAgreed = inv.delivery_waiver_agreed === true;
                        that.spacePreparationAgreed = inv.space_preparation_agreed === true;
                        that.deliveryDamageWaiverAgreed = inv.delivery_damage_waiver_agreed === true;
                        that.policyAgreed = inv.terms_agreed === true;
                        that.warrantyRefused = inv.warranty_refused === true;

                        if (inv.gift_card_code) {
                            that.giftCardCode = inv.gift_card_code;
                            that.giftCardAmount = parseFloat(inv.gift_card_discount) || 0;
                            that.giftCardApplied = true;
                        }

                        that.refund_items = [];
                        that.refund_charges = buildRefundCharges(inv);
                        that.current_refund_validated = false;

                        that.refreshStoreCredit();
                        that.loadPaymentMethodsOnly();
                        that.loadMerchandiseDue();
                    } else {
                        that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                    that.loading = false;
                },
                function () {
                    that.hideLoader();
                    that.$bvToast.toast("Error loading invoice. Please try again later.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                    that.loading = false;
                }
            );
        },
    },

    created() {
        this.fetchPolicy();
        if (localStorage.userDetails) {
            this.userDetails = JSON.parse(localStorage.userDetails);
            if (!this.userDetails.properties) this.userDetails.properties = {};
            if (localStorage.packet) {
                this.packet = JSON.parse(localStorage.packet);
                this.fetch_prereqs();
                this.fetchCategories();
                this.fetchSubcategories();
            }
        }
    },

    mounted() {
        this._onResize = () => { this.viewportWidth = window.innerWidth; };
        window.addEventListener('resize', this._onResize, { passive: true });
        window.addEventListener('orientationchange', this._onResize, { passive: true });
        const draftId = this.$route.query.draft_id;
        if (draftId) this.loadDraftInvoice(draftId);

        if (this.invoice.invoice_id) {
            this.$nextTick(() => {
                this.loadPaymentMethodsOnly();
                this.loadRefundsHistory();
                this.reload_edit_invoice();
                if (this.invoice.edit) this.loadMerchandiseDue();
            });
        }

        this.$nextTick(() => {
            if (this.packet && this.packet.locsubs && this.packet.locsubs.length) {
                this.autoSelectDefaultStore();
            } else {
                let tries = 0;
                const interval = setInterval(() => {
                    tries++;
                    if (this.packet && this.packet.locsubs && this.packet.locsubs.length) {
                        clearInterval(interval);
                        this.autoSelectDefaultStore();
                    } else if (tries >= 20) {
                        clearInterval(interval);
                    }
                }, 250);
            }
        });
    },

    beforeDestroy() {
        if (this._onResize) {
            window.removeEventListener('resize', this._onResize);
            window.removeEventListener('orientationchange', this._onResize);
        }
    },
}
</script>

<style scoped>
/* ==================== PAGE SHELL ==================== */
.pos-wrapper { position: relative; min-height: calc(100vh - 70px); }
.pos-background {
    position: fixed; inset: 0; z-index: -2;
    background:
        radial-gradient(900px 480px at 0% 0%, rgba(238, 122, 34, 0.14), transparent 60%),
        radial-gradient(900px 520px at 100% 10%, rgba(15, 110, 107, 0.12), transparent 60%),
        #EDEBE7;
}
.pos-container { position: relative; z-index: 1; }
.pos-main-container { max-width: 1440px; width: 96%; margin: 14px auto 0; padding-bottom: 48px; }
@media (max-width: 767px) { .pos-main-container { width: 100%; padding: 0 10px 30px; margin-top: 6px; } }

/* ==================== BANNERS ==================== */
.pos-store-warning,
.pos-merch-due-banner {
    display: flex; align-items: center; gap: 14px;
    border-radius: 16px; padding: 14px 18px; margin-bottom: 16px;
    font-size: 15px; line-height: 1.45; border: 1px solid transparent;
}
.pos-store-warning { background: #FCEBE8; color: #8E2A21; border-color: #F3C9C2; }
.pos-merch-due-banner { background: #FFF4DC; color: #7A520A; border-color: #F2DBA5; }
.pos-store-warning strong, .pos-merch-due-banner strong { font-weight: 800; }
.pos-banner-icon {
    width: 38px; height: 38px; border-radius: 12px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center; font-size: 18px;
    background: rgba(255, 255, 255, 0.7);
}
.pos-store-warning .btn, .pos-merch-due-banner .btn { margin-left: auto; white-space: nowrap; }
@media (max-width: 767px) {
    .pos-store-warning, .pos-merch-due-banner { flex-wrap: wrap; }
    .pos-store-warning .btn, .pos-merch-due-banner .btn { margin-left: 0; width: 100%; }
}

/* ==================== CUSTOMER ==================== */
.pos-customer--full { display: flex; flex-direction: column; gap: 14px; }
.pos-customer-row { display: grid; gap: 12px; align-items: stretch; }
.pos-customer-row--identity,
.pos-customer-row--address { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.pos-customer-row--chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pos-customer-row--chips:empty { display: none; }
.pos-customer-field {
    display: flex; flex-direction: column; gap: 5px; min-width: 0;
    background: #F7F5F2; border: 1px solid #ECE8E1; border-radius: 12px; padding: 12px 14px;
}
.pos-customer-label { font-size: 13px; font-weight: 600; color: #736C63; display: flex; align-items: center; gap: 6px; }
.pos-customer-label .b-icon { color: #EE7A22; font-size: 13px; }
.pos-customer-value { font-size: 16px; font-weight: 700; color: #221F1C; word-break: break-word; line-height: 1.35; }
.pos-customer-policy {
    display: flex; align-items: flex-start; gap: 12px; padding: 13px 16px;
    border-radius: 12px; font-size: 14.5px; line-height: 1.5; font-weight: 500;
}
.pos-customer-policy .b-icon { font-size: 18px; margin-top: 1px; flex-shrink: 0; }
.pos-customer-policy.is-ok   { background: #E2F2F0; color: #0B5552; }
.pos-customer-policy.is-warn { background: #FCEBE8; color: #8E2A21; }

@media (max-width: 1199px) {
    .pos-customer-row--identity, .pos-customer-row--address { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
    .pos-customer-row--identity, .pos-customer-row--address { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
    .pos-customer-row--identity, .pos-customer-row--address { grid-template-columns: 1fr; }
}

/* ==================== PRODUCT CARDS (sheet) ==================== */
.pos-product-cards { display: flex; flex-direction: column; gap: 16px; }
.pos-product-card {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(220px, 1fr) minmax(150px, 1fr) minmax(270px, 1.1fr);
    gap: 5px; align-items: stretch; background: #fff;
    border: 1px solid #ECE8E1; border-radius: 18px; padding: 6px;
}
.pos-product-card-image {
    width: 100%; min-height: 150px; border-radius: 14px;
    background: linear-gradient(160deg, #F7F3EE, #EFEAE3);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; color: #B9B1A6; font-size: 34px;
}
.pos-product-card-image img { width: 100%; height: 100%; max-height: 200px; object-fit: contain; display: block; }

.pos-product-card-info { display: flex; flex-direction: column; gap: 7px; justify-content: center; min-width: 0; background: #F7F5F2; border-radius: 14px; padding: 16px 18px }
.pos-product-card-title { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; color: #221F1C; line-height: 1.3; word-break: break-word; }
.pos-product-card-sku { font-size: 14px; color: #736C63; font-weight: 600; }
.pos-product-card-backorder {
    display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
    color: #C93C30; background: #FCEBE8; font-weight: 800; font-size: 13px; padding: 4px 10px; border-radius: 99px;
}
.pos-product-card-stock {
    display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
    font-size: 13.5px; color: #0F6E6B; background: #E2F2F0; font-weight: 700; padding: 4px 10px; border-radius: 99px;
}
.pos-product-card-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }

.pos-product-card-meta { display: flex; flex-direction: column; gap: 8px; justify-content: center;background: #F7F5F2; border-radius: 14px; padding: 16px 18px ;}
.pos-product-card-meta-row { display: flex; flex-direction: column; gap: 3px; }
.pos-product-card-meta-label { font-size: 13px; font-weight: 600; color: #736C63; display: flex; align-items: center; gap: 6px; }
.pos-product-card-meta-label .b-icon { color: #EE7A22; }
.pos-product-card-meta-value { font-size: 15.5px; font-weight: 700; color: #221F1C; word-break: break-word; }
.pos-product-card-meta-value2 { font-size: 12.5px; font-weight: 700; color: #221F1C; word-break: break-word; }

.pos-product-card-pricing {
    display: flex; flex-direction: column; gap: 7px; justify-content: center; min-width: 0;
    background: #F7F5F2; border-radius: 14px; padding: 16px 18px;
}
.pos-product-card-price-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 14.5px; }
.pos-product-card-price-label { color: #736C63; font-weight: 600; }
.pos-product-card-price-value { font-weight: 700; color: #221F1C; font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
.pos-product-card-divider { border-top: 1px dashed #DCD6CC; margin: 4px 0; }
.pos-product-card-price-value--strike { text-decoration: line-through; color: #A59D92; font-weight: 500; }
.pos-product-card-price-row--save .pos-product-card-price-label,
.pos-product-card-price-row--save .pos-product-card-price-value { color: #0F6E6B; font-weight: 800; }
.pos-product-card-price-row--save .b-icon { margin-right: 3px; font-size: 12px; }
.pos-product-card-price-row--total { border-top: 1.5px solid #E2DCD3; padding-top: 10px; margin-top: 4px; }
.pos-product-card-price-row--total .pos-product-card-price-label { font-weight: 800; color: #221F1C; font-size: 15px; }
.pos-product-card-price-row--total .pos-product-card-price-value { font-family: 'Sora', sans-serif; font-size: 23px; font-weight: 800; color: #D4620F; }

@media (max-width: 1199px) {
    .pos-product-card { grid-template-columns: 200px 1fr 1fr; }
    .pos-product-card-pricing { grid-column: 1 / -1; }
}
@media (max-width: 767px) {
    .pos-product-card { grid-template-columns: 1fr; }
    .pos-product-card-image { min-height: 200px; }
}

/* ==================== GROUP PRODUCT + INCLUDED ITEMS ==================== */
.pos-product-card.is-group {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    overflow: hidden;
}
.pos-product-card-main {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(220px, 1fr) minmax(150px, 1fr) minmax(270px, 1.1fr);
    gap: 5px;
    align-items: stretch;
    padding: 6px;
}
.pos-product-card:not(.is-group) .pos-product-card-main {
    display: contents;
}

@media (max-width: 1199px) {
    .pos-product-card-main { grid-template-columns: 200px 1fr 1fr; }
    .pos-product-card-main .pos-product-card-pricing { grid-column: 1 / -1; }
}
@media (max-width: 767px) {
    .pos-product-card-main { grid-template-columns: 1fr; }
}

/* ---------- Included items (child cards) ---------- */
.pos-product-children-wrap {
    border-top: 1px solid #E5E1DA;
    background: #FAF9F7;
    padding: 14px 80px 16px;
}
.pos-product-children-head {
    display: flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 800; color: #0F6E6B;
    margin-bottom: 12px;
}
.pos-product-children-head .pos-muted-inline { margin-left: 2px; }

.pos-child-list {
    position: relative;
    display: flex; flex-direction: column; gap: 12px;
    margin-left: 18px; padding-left: 22px;
    border-left: 2px dashed #CFC7BC;
}
.pos-child-card {
    position: relative;
    display: grid;
    grid-template-columns: 200px minmax(200px, 1.3fr) minmax(130px, 1fr) minmax(210px, 1fr);
    gap: 5px; align-items: stretch;
    background: #fff; border: 1px solid #ECE8E1; border-radius: 14px; padding: 5px;
}
.pos-child-card::before {
    content: ''; position: absolute; left: -24px; top: 50%;
    width: 22px; border-top: 2px dashed #CFC7BC;
}
.pos-child-card::after {
    content: ''; position: absolute; left: -29px; top: calc(50% - 4px);
    width: 8px; height: 8px; border-radius: 50%; background: #0F6E6B;
}

.pos-child-card-image {
    min-height: 120px; border-radius: 11px;
    background: linear-gradient(160deg, #F7F3EE, #EFEAE3);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; color: #B9B1A6; font-size: 28px; cursor: zoom-in;
}
.pos-child-card-image img { width: 100%; height: 100%; max-height: 150px; object-fit: contain; display: block; }

.pos-child-card-info,
.pos-child-card-meta,
.pos-child-card-pricing {
    display: flex; flex-direction: column; justify-content: center; gap: 6px;
    background: #F7F5F2; border-radius: 11px; padding: 12px 14px; min-width: 0;
}
.pos-child-card-title {
    font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700;
    color: #221F1C; line-height: 1.3; word-break: break-word;
}
.pos-child-card-line { font-size: 12.5px; color: #736C63; font-weight: 600; }
.pos-child-card-line strong { color: #34302B; text-transform: uppercase; }

.pos-child-card-meta .pos-product-card-meta-label { font-size: 12px; }
.pos-child-card-value { font-size: 14px; font-weight: 700; color: #221F1C; word-break: break-word; }
.pos-child-card-value--sm { font-size: 12px; }

.pos-child-card-pricing .pos-product-card-price-row { font-size: 13.5px; }
.pos-child-card-price-total { border-top: 1.5px solid #E2DCD3; padding-top: 8px; margin-top: 2px; }
.pos-child-card-price-total .pos-product-card-price-label { font-weight: 800; color: #221F1C; }
.pos-child-card-price-total .pos-product-card-price-value {
    font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800; color: #0F6E6B;
}

@media (max-width: 1199px) {
    .pos-child-card { grid-template-columns: 130px 1fr 1fr; }
    .pos-child-card-pricing { grid-column: 1 / -1; }
}
@media (max-width: 767px) {
    .pos-child-list { margin-left: 6px; padding-left: 14px; }
    .pos-child-card::before { left: -16px; width: 14px; }
    .pos-child-card::after { left: -21px; }
    .pos-child-card { grid-template-columns: 1fr; }
    .pos-child-card-image { min-height: 160px; }
}

/* ==================== SEARCH RESULT CARDS ==================== */
.product-cards-container { padding: 12px 0; width: 100%; }
.product-cards-grid { display: flex; flex-direction: column; gap: 16px; width: 100%; }
.product-card {
    background: #fff; border-radius: 18px; border: 1px solid #ECE8E1; overflow: hidden;
    width: 100%; display: flex; flex-direction: row; align-items: stretch; min-height: 210px;
    transition: border-color 0.15s ease;
}
.product-card:hover { border-color: #EE7A22; }
.product-card.out-of-stock { opacity: 0.78; border-color: #E9A49C; }

.product-card-image {
    position: relative; width: 270px; min-width: 270px;
    background: linear-gradient(160deg, #F7F3EE, #EFEAE3);
    display: flex; align-items: center; justify-content: center; overflow: hidden;
}
.product-card-image .image-wrapper { width: 100%; height: 100%; min-height: 210px; display: flex; align-items: center; justify-content: center; cursor: pointer; position: relative; }
.product-card-image img { max-width: 88%; max-height: 88%; object-fit: contain; transition: transform 0.3s ease; }
.product-card-image .image-wrapper:hover img { transform: scale(1.04); }
.product-card-image .image-overlay {
    position: absolute; inset: 0; background: rgba(34, 31, 28, 0.32); display: flex; align-items: center; justify-content: center;
    color: #fff; opacity: 0; transition: opacity 0.2s ease;
}
.product-card-image .image-wrapper:hover .image-overlay { opacity: 1; }
.product-card-image .no-image { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #B9B1A6; font-size: 13px; }
.product-card-image .damage-badge { position: absolute; top: 12px; left: 12px; font-size: 12px; padding: 5px 10px; border-radius: 99px; z-index: 2; }
.product-card-image .discount-badge { position: absolute; top: 12px; right: 12px; font-size: 12px; padding: 5px 10px; border-radius: 99px; font-weight: 800; z-index: 2; background: #0F6E6B !important; }

.product-card-info { flex: 1; padding: 20px 22px; display: flex; flex-direction: column; justify-content: center; gap: 10px; min-width: 0; }
.product-card-info .product-sku { font-size: 14.5px; font-weight: 700; color: #D4620F; word-break: break-word; }
.product-card-info .product-name { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; color: #221F1C; line-height: 1.3; word-break: break-word; }
.product-card-info .product-qty-box { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; background: #F7F5F2; border-radius: 99px; align-self: flex-start; }
.product-card-info .qty-box-label { font-size: 12.5px; font-weight: 600; color: #736C63; }
.product-card-info .qty-box-value { font-size: 15px; font-weight: 800; color: #221F1C; }

.product-card-actions { width: 250px; min-width: 250px; padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; background: #FAF9F7; border-left: 1px solid #F0ECE6; justify-content: center; }
.product-card-actions .price-block { display: flex; flex-direction: column; gap: 9px; }
.product-card-actions .price-row { display: flex; justify-content: space-between; align-items: center; }
.product-card-actions .price-label { font-size: 14px; font-weight: 600; color: #736C63; }
.product-card-actions .price-value-group { display: flex; align-items: center; gap: 6px; }
.product-card-actions .original-price { font-size: 14px; color: #A59D92; text-decoration: line-through; }
.product-card-actions .current-price { font-size: 18px; font-weight: 800; color: #221F1C; }
.product-card-actions .current-price.discounted { color: #C93C30; }
.product-card-actions .quantity-block { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 0; border-top: 1px solid #ECE8E1; border-bottom: 1px solid #ECE8E1; }
.product-card-actions .qty-label { font-size: 14px; font-weight: 600; color: #736C63; margin: 0; }
.product-card-actions .qty-input { width: 86px; text-align: center; font-weight: 700; font-size: 15px; border-radius: 10px; }
.product-card-actions .total-price { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800; color: #D4620F; }

.product-card-actions .stock-block { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.product-card-actions .stock-item { display: flex; flex-direction: column; align-items: center; padding: 9px 4px; background: #E2F2F0; border-radius: 10px; }
.product-card-actions .stock-item .stock-label { font-size: 10px; font-weight: 700; color: #4F5E5C; margin-bottom: 3px; text-align: center; }
.product-card-actions .stock-item .stock-value { font-size: 18px; font-weight: 800; color: #0B5552; }
.product-card-actions .stock-item.low { background: #FCEBE8; }
.product-card-actions .stock-item.low .stock-value { color: #C93C30; }

.product-card-actions .locations-block { background: #fff; border-radius: 10px; padding: 9px 11px; border: 1px solid #ECE8E1; max-height: 110px; overflow-y: auto; }
.product-card-actions .locations-header { font-size: 11.5px; font-weight: 700; color: #736C63; margin-bottom: 6px; }
.product-card-actions .locations-list { display: flex; flex-direction: column; gap: 4px; }
.product-card-actions .location-item { display: flex; justify-content: space-between; align-items: center; padding: 4px 7px; background: #F7F5F2; border-radius: 6px; font-size: 12px; }
.product-card-actions .location-name { display: flex; align-items: center; gap: 5px; font-weight: 600; color: #4A443D; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.product-card-actions .location-qty { font-weight: 800; color: #0F6E6B; padding: 1px 7px; background: #E2F2F0; border-radius: 99px; min-width: 26px; text-align: center; font-size: 12px; flex-shrink: 0; }
.product-card-actions .location-qty.low-stock { color: #C93C30; background: #FCEBE8; }
.product-card-actions .no-location-data { font-size: 12px; color: #A59D92; }
.product-card-actions .add-btn {
    border-radius: 12px; font-weight: 800; padding: 11px; font-size: 15px;
    background: #EE7A22 !important; border-color: #EE7A22 !important;
}
.product-card-actions .add-btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }

.location-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.location-dot.stock { background: #0F6E6B; }
.location-dot.floor { background: #221F1C; }
.location-dot.warehouse { background: #EE7A22; }
.location-dot.damage { background: #C93C30; }
.location-dot.backorder { background: #8A837A; }
.location-dot.default { background: #4A90B8; }

@media (max-width: 1199px) { .product-card-image { width: 200px; min-width: 200px; } }
@media (max-width: 991px) {
    .product-card { flex-wrap: wrap; }
    .product-card-image { width: 170px; min-width: 170px; }
    .product-card-actions { width: 50%; min-width: 0; flex: 1 1 50%; }
    .product-card-info .product-name { font-size: 18px; }
}
@media (max-width: 767px) {
    .product-card { flex-direction: column; }
    .product-card-image { width: 100%; min-width: 100%; height: 210px; }
    .product-card-image .image-wrapper { min-height: 210px; }
    .product-card-info { padding: 16px; }
    .product-card-actions { width: 100%; min-width: 100%; padding: 16px; border-left: 0; border-top: 1px solid #F0ECE6; }
}

/* ==================== SEARCH LOADER ==================== */
.product-search-loader { display: flex; justify-content: center; align-items: center; min-height: 240px; }
.loader-card { background: #fff; border-radius: 18px; padding: 30px 48px; text-align: center; border: 1px solid #ECE8E1; }
.loader-text { font-size: 15px; font-weight: 700; color: #221F1C; margin-bottom: 16px; }
.loader-progress { width: 200px; height: 4px; background: #F0ECE6; border-radius: 99px; overflow: hidden; }
.loader-progress-bar { width: 40%; height: 100%; background: #EE7A22; border-radius: 99px; animation: progressMove 1s ease-in-out infinite; }
@keyframes progressMove { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
@media (prefers-reduced-motion: reduce) { .loader-progress-bar { animation: none; } }

/* ==================== CART TABLES ==================== */
.ipt_tdr { vertical-align: middle; }
.ipt_tdhr { font-size: 12.5px; font-weight: 800; color: #736C63; padding: 10px 6px; text-align: center; background: #F7F5F2; }
td { font-size: 14px; background: inherit; border: 1px solid #E5E1DA; padding: 6px; text-align: center; }
.image-container { position: relative; display: inline-block; }
.no-image-placeholder {
    width: 80px; height: 56px; display: flex; flex-direction: column; align-items: center; justify-content: center;
    border: 1px dashed #DCD6CC; border-radius: 8px; background: #FAF9F7;
}
.product-image { transition: transform 0.2s ease; border-radius: 6px; }
.product-image:hover { transform: scale(1.05); }
.discount-disabled { background-color: #F0ECE6 !important; color: #8A837A !important; cursor: not-allowed !important; }
.image-upload-preview { text-align: center; }
.upload-actions { display: flex; gap: 5px; justify-content: center; }

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }

/* ==================== SPECIAL ORDER / NON-INVENTORY ==================== */
.special-order-image-section { background: #F7F5F2; padding: 12px 16px; border-radius: 14px; border: 1px solid #ECE8E1; }
.upload-placeholder { transition: border-color 0.2s ease, background 0.2s ease; border-radius: 12px !important; }
.upload-placeholder:hover { border-color: #EE7A22 !important; background: #FFF4EA !important; }
.image-preview-container, .image-preview-wrapper { position: relative; border-radius: 12px !important; }
.remove-image-btn { box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2); }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* ==================== TERMS ==================== */
.policy-content { font-size: 14.5px; line-height: 1.65; color: #3A352F; }
.policy-content >>> p { margin-bottom: 12px; }
.policy-content >>> h1,
.policy-content >>> h2,
.policy-content >>> h3 { margin: 16px 0 8px; color: #221F1C; font-size: 17px; font-family: 'Sora', sans-serif; }

/* ==================== REFUND METHODS ==================== */
.validation-summary {
    padding: 12px 16px; background: #FFF4DC; border: 1px solid #F2DBA5; border-radius: 12px;
    color: #7A520A; display: flex; align-items: center; gap: 10px; font-size: 14px;
}
.warning-icon { color: #C98A12; font-size: 18px; }

/* ==================== DISABLED CHOICE CARD ==================== */
.pos-choice.is-disabled { opacity: 0.55; cursor: not-allowed; background: #F7F5F2; }
.pos-choice.is-disabled:hover { border-color: #E5E1DA; }

/* ==================== REFUND OTP MODAL ==================== */
.refund-otp-email-row {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; border-radius: 14px;
    background: #FFF1E4;
    border: 1px solid #F5CFAE;
}
.refund-otp-icon { font-size: 22px; color: #D4620F; }
.refund-otp-label { font-size: 12.5px; font-weight: 600; color: #736C63; }
.refund-otp-email { font-size: 16px; font-weight: 800; color: #221F1C; }
.refund-otp-input {
    text-align: center;
    font-size: 26px !important;
    letter-spacing: 0.4em;
    font-family: 'Sora', 'Manrope', monospace;
    height: 56px;
    font-weight: 800;
}

/* ==================== CART (enhanced) ==================== */
.pos-cart { display: flex; flex-direction: column; gap: 16px; padding-bottom: 4px; }

.pos-cart-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--pos-line, #E5E1DA);
  border-radius: 14px;
}
.pos-cart-bar-stats { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 14px; font-weight: 700; color: var(--pos-muted, #736C63); }
.pos-cart-bar-stats .b-icon { color: var(--pos-accent, #EE7A22); }
.pos-cart-chip { font-size: 12px; padding: 3px 10px; border-radius: 99px; font-weight: 800; }
.pos-cart-chip.is-existing { background: #E2F2F0; color: #0B5552; }
.pos-cart-chip.is-new { background: #FFF1E4; color: #B5520B; }
.pos-cart-chip.is-warn { background: #FCEBE8; color: #8E2A21; }
.pos-cart-bar-actions { display: flex; align-items: baseline; gap: 10px; }
.pos-cart-bar-total { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: var(--pos-accent-dark, #D4620F); font-variant-numeric: tabular-nums; }
.pos-cart-bar-label { font-size: 12.5px; font-weight: 600; color: var(--pos-muted, #736C63); }

.pos-cart-section { display: flex; flex-direction: column; gap: 10px; }
.pos-cart-section-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; padding: 4px 2px; }
.pos-cart-section-title { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--pos-ink, #221F1C); display: flex; align-items: center; gap: 8px; }
.pos-cart-section-title .b-icon { color: var(--pos-accent, #EE7A22); }
.pos-cart-section-sub { font-size: 13.5px; color: var(--pos-muted, #736C63); font-weight: 600; }
.pos-cart-section-bulk { display: flex; align-items: center; gap: 8px; }

.pos-cart-list { display: flex; flex-direction: column; gap: 12px; }
.pos-cart-item {
  position: relative;
  display: grid;
  grid-template-columns: 96px minmax(0, 1.6fr) minmax(200px, 1fr) auto;
  gap: 14px;
  align-items: stretch;
  background: #fff;
  border: 1px solid var(--pos-line, #E5E1DA);
  border-left: 5px solid #0F6E6B;
  border-radius: 16px;
  padding: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pos-cart-item:hover { box-shadow: 0 4px 14px -8px rgba(34, 31, 28, 0.25); }

.pos-cart-section .pos-cart-item.is-as-is { border-left-color: #C93C30; background: #FFFCFC; }
.pos-cart-item.is-backorder { border-left-color: #C98A12; }
.pos-cart-item.is-returning {
  border-left-color: #C93C30;
  background: #FFF6F4;
  box-shadow: inset 0 0 0 1px #F3C9C2;
}

.pos-cart-item-image {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 12px;
  background: linear-gradient(160deg, #F7F3EE, #EFEAE3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #B9B1A6;
  font-size: 28px;
  cursor: zoom-in;
  flex-shrink: 0;
}
.pos-cart-item-image img { width: 100%; height: 100%; object-fit: contain; }
.pos-cart-item-num {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(34, 31, 28, 0.85);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 99px;
}

.pos-cart-item-info { display: flex; flex-direction: column; gap: 8px; min-width: 0; justify-content: center; }
.pos-cart-item-title {
  font-family: 'Sora', sans-serif;
  font-size: 16.5px;
  font-weight: 700;
  color: var(--pos-ink, #221F1C);
  line-height: 1.3;
  word-break: break-word;
}
.pos-cart-item-parent { font-size: 13px; font-weight: 600; color: var(--pos-muted, #736C63); margin-right: 4px; }
.pos-cart-item-meta { display: flex; flex-wrap: wrap; gap: 5px; }
.pos-cart-item-warning {
  font-size: 12.5px;
  color: #C93C30;
  background: #FCEBE8;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
}
.pos-cart-item-info-line {
  font-size: 12.5px;
  color: #0F6E6B;
  font-weight: 700;
}
.pos-cart-item-info-line small { color: var(--pos-muted, #736C63); font-weight: 500; }

.pos-cart-item-delivery {
  display: grid;
  grid-template-columns: 120px 160px 1fr;
  gap: 8px;
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px dashed var(--pos-line, #E5E1DA);
}
.pos-cart-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.pos-cart-field--wide { grid-column: span 3; }
.pos-cart-field-label {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--pos-muted, #736C63);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.pos-cart-hint { text-transform: none; font-weight: 600; color: #A59D92; font-size: 11px; margin-left: 4px; }
.pos-cart-readonly {
  font-size: 14px;
  font-weight: 700;
  color: var(--pos-ink, #221F1C);
  padding: 6px 10px;
  background: var(--pos-mist, #F7F5F2);
  border-radius: 8px;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
}
.pos-cart-item-delivery .mx-datepicker { width: 100% !important; }

.pos-cart-item-upload { display: flex; align-items: center; gap: 8px; margin-top: 4px; flex-wrap: wrap; }
.pos-cart-upload-thumb { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; cursor: pointer; border: 1px solid var(--pos-line, #E5E1DA); }

.pos-cart-item-pricing {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: var(--pos-mist, #F7F5F2);
  border-radius: 12px;
  padding: 10px 12px;
  min-width: 0;
  justify-content: center;
}
.pos-cart-price-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.pos-cart-price-row .pos-cart-field-label { text-transform: none; font-size: 12.5px; letter-spacing: 0; }
.pos-cart-price-input, .pos-cart-qty-input, .pos-cart-discount-input { width: 90px !important; text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; background: #fff !important; }
.pos-cart-price-row--total { border-top: 1.5px solid #E2DCD3; padding-top: 8px; margin-top: 4px; }
.pos-cart-price-row--total .pos-cart-field-label { font-weight: 800; color: var(--pos-ink, #221F1C); text-transform: none; font-size: 13px; letter-spacing: 0; }
.pos-cart-price-row--total strong { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800; color: var(--pos-accent-dark, #D4620F); font-variant-numeric: tabular-nums; }

.pos-cart-item-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: stretch;
  min-width: 110px;
}
.pos-cart-item-actions .btn { font-size: 13px; padding: 6px 10px; white-space: nowrap; }

.pos-cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  background: #FAF9F7;
  border: 1.5px dashed #D6CFC4;
  border-radius: 18px;
  color: var(--pos-muted, #736C63);
}
.pos-cart-empty p { margin: 0; font-size: 15px; font-weight: 600; }

.pos-cart-footer {
  position: sticky;
  bottom: -24px;
  z-index: 6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--pos-ink, #221F1C);
  color: #fff;
  border-radius: 16px;
  padding: 12px 16px;
  margin: 4px -4px 0;
  box-shadow: 0 -8px 24px -12px rgba(34, 31, 28, 0.45);
}
.pos-cart-footer-stats { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.pos-cart-footer-stats > div { display: flex; flex-direction: column; gap: 2px; }
.pos-cart-footer-stats span { font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.6); }
.pos-cart-footer-stats strong { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 700; font-variant-numeric: tabular-nums; }
.pos-cart-footer-stats .is-grand strong { font-size: 22px; color: #FFB06B; }
.pos-cart-footer-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pos-cart-balance {
  display: flex; flex-direction: column; align-items: flex-end; gap: 2px;
  padding: 6px 14px; border-radius: 12px;
  background: rgba(63, 182, 168, 0.14);
  border: 1px solid rgba(63, 182, 168, 0.35);
}
.pos-cart-balance span { font-size: 11.5px; font-weight: 600; color: rgba(255, 255, 255, 0.7); }
.pos-cart-balance strong { font-family: 'Sora', sans-serif; font-size: 18px; color: #7FDACF; font-variant-numeric: tabular-nums; }
.pos-cart-balance.is-due { background: rgba(238, 122, 34, 0.18); border-color: rgba(238, 122, 34, 0.5); }
.pos-cart-balance.is-due strong { color: #FFB06B; }
.pos-cart-footer .pos-btn-ghost.btn {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.pos-cart-list-enter-active, .pos-cart-list-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.pos-cart-list-enter, .pos-cart-list-leave-to { opacity: 0; transform: translateY(-6px); }
.pos-cart-list-move { transition: transform 0.25s ease; }

@media (max-width: 1199px) {
  .pos-cart-item { grid-template-columns: 84px minmax(0, 1.4fr) minmax(180px, 1fr) auto; gap: 12px; }
  .pos-cart-item-image { width: 84px; height: 84px; }
  .pos-cart-item-delivery { grid-template-columns: 1fr 1fr; }
  .pos-cart-field--wide { grid-column: span 2; }
}
@media (max-width: 991px) {
  .pos-cart-item { grid-template-columns: 84px 1fr; }
  .pos-cart-item-pricing, .pos-cart-item-actions { grid-column: 1 / -1; }
  .pos-cart-item-pricing { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 12px; padding: 10px 12px; }
  .pos-cart-price-row { flex: 1 1 120px; justify-content: space-between; }
  .pos-cart-price-row--total { flex-basis: 100%; border-top: 1px dashed #DCD6CC; padding-top: 8px; margin-top: 4px; }
  .pos-cart-item-actions { flex-direction: row; justify-content: flex-end; }
}
@media (max-width: 575px) {
  .pos-cart-item { grid-template-columns: 1fr; }
  .pos-cart-item-image { width: 100%; height: 160px; }
  .pos-cart-item-delivery { grid-template-columns: 1fr; }
  .pos-cart-field--wide { grid-column: span 1; }
  .pos-cart-footer { flex-direction: column; align-items: stretch; }
  .pos-cart-footer-actions { justify-content: space-between; }
  .pos-cart-footer-actions .btn { flex: 1; }
}

/* ==================== MERCHANDISE DUE — RECEIVE CELL ==================== */
.pos-md-table { min-width: 900px; }
.pos-md-receive-cell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 320px;
}
.pos-md-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pos-md-field--qty { width: 90px; flex-shrink: 0; }
.pos-md-field-label {
  font-size: 11.5px;
  font-weight: 700;
  color: #736C63;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.pos-md-locsub {
  min-width: 260px;
  font-weight: 600;
  font-size: 13px;
}
.pos-md-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 2px;
  padding: 4px 8px;
  border-radius: 8px;
}
.pos-md-hint.is-ok   { background: #E2F2F0; color: #0B5552; }
.pos-md-hint.is-warn { background: #FFF4DC; color: #7A520A; }
.pos-md-qty-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.pos-md-receive-btn {
  white-space: nowrap;
  height: 36px;
}
.pos-md-received-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 13px;
}
.pos-md-received-info > div {
  display: flex;
  align-items: center;
  gap: 6px;
}

</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');

/* ==================== TOKENS ==================== */
.pos-main-container,
.pos-modal {
  --pos-ink: #221F1C;
  --pos-ink-2: #34302B;
  --pos-accent: #EE7A22;
  --pos-accent-dark: #D4620F;
  --pos-accent-soft: #FFF1E4;
  --pos-teal: #0F6E6B;
  --pos-teal-soft: #E2F2F0;
  --pos-paper: #FFFFFF;
  --pos-mist: #F7F5F2;
  --pos-line: #E5E1DA;
  --pos-muted: #736C63;
  --pos-danger: #C93C30;
  --pos-danger-soft: #FCEBE8;
  --pos-warn: #C98A12;
  --pos-warn-soft: #FFF4DC;
  --pos-brass: var(--pos-accent);
  --pos-brass-light: #FFB06B;
  --pos-sage: var(--pos-teal);
  --pos-sage-soft: var(--pos-teal-soft);
  --pos-display: 'Sora', 'Manrope', system-ui, sans-serif;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  color: var(--pos-ink);
}

/* ==================== HEADER ==================== */
.pos-head {
  background: var(--pos-ink);
  color: #fff;
  border-radius: 24px;
  padding: 22px 26px 18px;
  margin-bottom: 16px;
  box-shadow: 0 18px 40px -18px rgba(34, 31, 28, 0.55);
}
.pos-main-container.is-editing .pos-head { box-shadow: inset 0 4px 0 var(--pos-teal), 0 18px 40px -18px rgba(34, 31, 28, 0.55); }

.pos-head-bar { display: flex; justify-content: space-between; align-items: center; gap: 22px; flex-wrap: wrap; }
.pos-brand-mark { display: flex; align-items: center; gap: 16px; }
.pos-brand-logo {
  width: 58px; height: 58px; border-radius: 16px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--pos-accent); color: #fff; font-size: 28px;
}
.pos-brand-name { font-family: var(--pos-display); font-size: 30px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.05; color: #fff; }
.pos-brand-sub { font-size: 14px; color: rgba(255, 255, 255, 0.62); font-variant-numeric: tabular-nums; margin-top: 3px; }
.pos-brand-sub .b-icon { margin-right: 4px; }

.pos-head-meta { display: flex; gap: 10px; flex-wrap: wrap; }
.pos-meta-block {
  display: flex; align-items: center; gap: 12px; min-width: 0;
  padding: 11px 16px; border-radius: 16px;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.09);
}
.pos-meta-block--wide { max-width: 420px; }
.pos-meta-icon {
  width: 38px; height: 38px; border-radius: 12px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(238, 122, 34, 0.18); color: #FFB06B; font-size: 17px;
}
.pos-meta-text { min-width: 0; }
.pos-brand-label { font-size: 12.5px; font-weight: 600; color: rgba(255, 255, 255, 0.55); }
.pos-brand-invoice { font-family: var(--pos-display); font-size: 19px; font-weight: 700; color: #fff; word-break: break-all; }
.pos-brand-strong { font-size: 15.5px; font-weight: 700; color: #fff; line-height: 1.35; word-break: break-word; }
.pos-meta-block .pos-brand-sub { margin-top: 1px; font-size: 13px; }

.pos-progress-wrap { display: flex; align-items: center; gap: 14px; margin-top: 18px; }
.pos-progress { flex: 1; height: 8px; border-radius: 99px; background: rgba(255, 255, 255, 0.1); overflow: hidden; }
.pos-progress-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--pos-teal), #3FB6A8); transition: width 0.4s ease; }
.pos-progress-label { font-size: 13px; font-weight: 700; color: rgba(255, 255, 255, 0.7); white-space: nowrap; }

.pos-head-actions {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; justify-content: flex-end;
  margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.pos-head .pos-btn-ghost,
.pos-head .pos-btn-ghost.btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}
.pos-head .pos-btn-ghost.btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12) !important; border-color: rgba(255, 255, 255, 0.35) !important; }
.pos-blockers { flex-basis: 100%; text-align: right; font-size: 14px; color: #FFB4A8; font-weight: 600; }

@media (max-width: 767px) {
  .pos-head { padding: 18px; border-radius: 20px; }
  .pos-brand-name { font-size: 24px; }
  .pos-head-meta { width: 100%; }
  .pos-meta-block { flex: 1 1 100%; max-width: none; }
  .pos-head-actions { justify-content: flex-start; }
  .pos-blockers { text-align: left; }
}

/* ==================== BUTTONS ==================== */
.pos-btn-primary,
.pos-btn-primary.btn,
.pos-btn-primary.btn:focus {
  background: var(--pos-accent) !important;
  border: 1px solid var(--pos-accent) !important;
  color: #fff !important;
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 15px;
  padding: 0.55rem 1.15rem;
  box-shadow: 0 8px 18px -8px rgba(238, 122, 34, 0.7);
}
.pos-btn-primary.btn-sm { font-size: 14px; padding: 0.42rem 0.95rem; }
.pos-btn-primary.btn:hover:not(:disabled) { background: var(--pos-accent-dark) !important; border-color: var(--pos-accent-dark) !important; }
.pos-btn-lg.btn { font-size: 16px; padding: 0.75rem 1.4rem; border-radius: 14px !important; }
.pos-btn-ghost,
.pos-btn-ghost.btn {
  background: #fff !important;
  color: var(--pos-ink) !important;
  border: 1px solid var(--pos-line) !important;
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 15px;
  padding: 0.55rem 1.1rem;
}
.pos-btn-ghost.btn-sm { font-size: 14px; padding: 0.42rem 0.9rem; }
.pos-btn-ghost.btn:hover:not(:disabled) { border-color: var(--pos-accent) !important; color: var(--pos-accent-dark) !important; }
.pos-btn-primary.btn:disabled, .pos-btn-ghost.btn:disabled { opacity: 0.45; box-shadow: none; }
.pos-btn-primary .b-icon, .pos-btn-ghost .b-icon { margin-right: 3px; }

.pos-link.btn {
  color: var(--pos-accent-dark) !important;
  background: var(--pos-accent-soft);
  font-weight: 700; font-size: 14px;
  padding: 6px 14px !important;
  border-radius: 99px;
  text-decoration: none !important;
  white-space: nowrap;
}
.pos-link.btn:hover { background: #FFE2C7; }
.pos-main-container .btn:focus-visible, .pos-modal .btn:focus-visible { outline: 2px solid var(--pos-accent); outline-offset: 2px; box-shadow: none !important; }

/* ==================== TABS (pill stepper) ==================== */
.pos-tabs {
  position: sticky; top: 0; z-index: 20;
  display: flex; gap: 8px; overflow-x: auto;
  padding: 10px; margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--pos-line);
  border-radius: 99px;
  scrollbar-width: thin;
}
.pos-tab {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 10px;
  border: 1px solid var(--pos-line); background: #fff; color: var(--pos-ink-2);
  padding: 9px 18px 9px 9px; border-radius: 99px;
  font-size: 15px; font-weight: 700; cursor: pointer; white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.pos-tab:hover { border-color: var(--pos-accent); }
.pos-tab:focus-visible { outline: 2px solid var(--pos-accent); outline-offset: 2px; }
.pos-tab-index {
  width: 28px; height: 28px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; background: var(--pos-mist); color: var(--pos-muted);
}
.pos-tab.is-done { background: var(--pos-teal-soft); border-color: #BFE1DD; color: #0B5552; }
.pos-tab.is-done .pos-tab-index { background: var(--pos-teal); color: #fff; }
.pos-tab.is-next { background: var(--pos-accent); border-color: var(--pos-accent); color: #fff; box-shadow: 0 8px 18px -8px rgba(238, 122, 34, 0.8); }
.pos-tab.is-next .pos-tab-index { background: #fff; color: var(--pos-accent-dark); }
.pos-tab.is-blocked { background: var(--pos-warn-soft); border-color: #F2DBA5; color: #7A520A; }
.pos-tab.is-blocked .pos-tab-index { background: var(--pos-warn); color: #fff; }
.pos-tab.is-locked { opacity: 0.4; }
.pos-tab-count {
  background: var(--pos-accent-soft); color: var(--pos-accent-dark);
  border-radius: 99px; font-size: 12px; padding: 2px 9px; font-weight: 800;
}
.pos-tab.is-next .pos-tab-count { background: rgba(255, 255, 255, 0.22); color: #fff; }
.pos-tab.is-done .pos-tab-count { background: #fff; color: var(--pos-teal); }
@media (max-width: 767px) { .pos-tabs { border-radius: 18px; } }

/* ==================== SHEET + TILES ==================== */
.pos-sheet { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 18px; }
.pos-tile {
  background: var(--pos-paper); border: 1px solid var(--pos-line);
  border-radius: 20px; padding: 22px 24px; min-width: 0;
  box-shadow: 0 1px 0 rgba(34, 31, 28, 0.03);
}
.pos-tile--wide { grid-column: 1 / -1; }
.pos-tile--wide2 { grid-column: span 1; }
.pos-tile--summary { border-color: #F5CFAE; box-shadow: inset 0 4px 0 var(--pos-accent); }
.pos-tile.is-empty { background: rgba(255, 255, 255, 0.66); border: 1.5px dashed #D6CFC4; box-shadow: none; }

.pos-sheet-row { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
@media (max-width: 991px) { .pos-sheet-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 599px) { .pos-sheet-row { grid-template-columns: 1fr; } }

.pos-tile-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 18px; }
.pos-tile-title { display: flex; align-items: center; gap: 14px; min-width: 0; }
.pos-tile-icon {
  width: 46px; height: 46px; border-radius: 14px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--pos-accent); color: #fff; font-size: 20px;
}
.pos-tile-icon--warn { background: var(--pos-warn); }
.pos-tile.is-empty .pos-tile-icon { background: var(--pos-accent-soft); color: var(--pos-accent); }
.pos-tile-head h3 { font-family: var(--pos-display); font-size: 20px; font-weight: 700; margin: 0; color: var(--pos-ink); letter-spacing: -0.01em; }
.pos-tile-sub { font-size: 14px; color: var(--pos-muted); margin: 3px 0 0; }
.pos-muted-inline {
  display: inline-block; vertical-align: middle; margin-left: 8px;
  font-family: 'Manrope', sans-serif; font-size: 12.5px; font-weight: 800;
  color: var(--pos-accent-dark); background: var(--pos-accent-soft); padding: 3px 10px; border-radius: 99px;
}
.pos-empty { color: var(--pos-muted); font-size: 15px; margin: 0; line-height: 1.55; }
.pos-empty--pad { padding: 28px 4px; }

.pos-tile-cta {
  width: 100%; padding: 13px; border-radius: 14px;
  border: 1.5px solid var(--pos-accent); background: #fff; color: var(--pos-accent-dark);
  font-weight: 800; font-size: 15px; cursor: pointer;
}
.pos-tile-cta:hover { background: var(--pos-accent-soft); }
.pos-balance-line { display: flex; justify-content: space-between; align-items: baseline; margin-top: 16px; }
.pos-balance-line span { color: var(--pos-muted); font-weight: 600; }
.pos-balance-line strong { font-family: var(--pos-display); font-size: 22px; font-variant-numeric: tabular-nums; }
.pos-balance-line strong.is-due { color: var(--pos-danger); }

.pos-dl { display: grid; grid-template-columns: 1fr auto; gap: 10px 18px; margin: 0; font-size: 15.5px; }
.pos-dl dt { color: var(--pos-muted); font-weight: 600; }
.pos-dl dd { margin: 0; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; word-break: break-word; }
.pos-dl .pos-dl-strong { color: var(--pos-ink); font-weight: 800; font-size: 17px; border-top: 1.5px dashed var(--pos-line); padding-top: 12px; }
.pos-dl dd.pos-dl-grand { font-family: var(--pos-display); font-size: 24px; color: var(--pos-accent-dark); }
.pos-dl .is-due, .pos-dl dd.is-due { color: var(--pos-danger); }
.pos-dl .is-good { color: var(--pos-teal); }
.pos-dl .is-good .b-icon { margin-right: 4px; font-size: 12px; }
.pos-dl .pos-dl-sub { background: var(--pos-mist); margin: 0 -24px; padding: 7px 24px; color: var(--pos-ink); font-weight: 700; }
.pos-dl-savings { background: var(--pos-teal-soft); border-radius: 12px; padding: 10px 14px; }

.pos-customer { font-size: 15px; line-height: 1.55; }
.pos-customer-name { font-size: 18px; font-weight: 800; }
.pos-customer-addr { margin-top: 6px; color: var(--pos-muted); }

.pos-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12.5px; padding: 3px 11px; border-radius: 99px;
  background: var(--pos-mist); color: var(--pos-ink); font-weight: 700; margin-right: 4px;
  border: 1px solid var(--pos-line);
}
.pos-chip.is-warn { background: var(--pos-danger-soft); color: var(--pos-danger); border-color: #F3C9C2; }
.pos-chip.is-ok { background: var(--pos-teal-soft); color: var(--pos-teal); border-color: #BFE1DD; }

.pos-lines { display: flex; flex-direction: column; }
.pos-line { display: grid; grid-template-columns: 52px 1fr auto; gap: 12px; align-items: center; padding: 12px 0; border-bottom: 1px solid var(--pos-mist); }
.pos-line:last-child { border-bottom: 0; }
.pos-line-thumb { width: 52px; height: 52px; border-radius: 12px; background: var(--pos-mist); display: flex; align-items: center; justify-content: center; overflow: hidden; color: var(--pos-muted); }
.pos-line-thumb img { width: 100%; height: 100%; object-fit: cover; }
.pos-line-title { font-weight: 800; font-size: 16px; }
.pos-line-sub { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 14px; color: var(--pos-muted); margin-top: 2px; }
.pos-line-amt { text-align: right; font-size: 14px; color: var(--pos-muted); font-variant-numeric: tabular-nums; }
.pos-line-amt strong { display: block; font-size: 17px; color: var(--pos-ink); }

.pos-checks { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
.pos-checks li { display: flex; gap: 10px; align-items: center; font-size: 15px; color: var(--pos-muted); padding: 6px 10px; border-radius: 10px; }
.pos-checks li .b-icon { font-size: 17px; flex-shrink: 0; }
.pos-checks li.is-ok { color: var(--pos-ink); background: var(--pos-mist); }
.pos-checks li.is-ok .b-icon { color: var(--pos-teal); }

.pos-id-summary { display: flex; gap: 14px; align-items: flex-start; }
.pos-id-summary img { width: 96px; height: 64px; object-fit: cover; border-radius: 10px; border: 1px solid var(--pos-line); cursor: pointer; }
.pos-id-summary .pos-dl { flex: 1; }
.pos-comment { white-space: pre-wrap; font-size: 15px; margin: 0; line-height: 1.6; max-width: 70ch; background: var(--pos-mist); padding: 12px 14px; border-radius: 12px; }

.pos-activity { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px; }
.pos-activity-btn {
  text-align: left; border: 1px solid var(--pos-line); background: var(--pos-mist);
  border-radius: 14px; padding: 14px 16px; cursor: pointer; color: var(--pos-ink);
  display: flex; flex-direction: column; gap: 2px;
}
.pos-activity-btn:hover { border-color: var(--pos-accent); background: #fff; }
.pos-activity-icon { color: var(--pos-accent); font-size: 18px; margin-bottom: 6px; }
.pos-activity-btn strong { display: block; font-family: var(--pos-display); font-size: 20px; font-variant-numeric: tabular-nums; }
.pos-activity-btn span { font-size: 14px; color: var(--pos-muted); }

/* ==================== STICKY TOTALS BAR ==================== */
.pos-totals-bar {
  position: sticky; bottom: 12px; z-index: 15; margin-top: 20px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: var(--pos-ink); color: #fff;
  border-radius: 22px; padding: 14px 16px 14px 24px;
  box-shadow: 0 20px 40px -14px rgba(34, 31, 28, 0.6);
}
.pos-totals-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 32px; }
.pos-totals-item { display: flex; align-items: center; gap: 10px; }
.pos-totals-item > .b-icon { font-size: 22px; color: #FFB06B; }
.pos-totals-item span { display: block; font-size: 12.5px; font-weight: 600; color: rgba(255, 255, 255, 0.58); }
.pos-totals-item strong { font-family: var(--pos-display); font-size: 19px; font-weight: 700; font-variant-numeric: tabular-nums; }
.pos-totals-item.is-grand strong { font-size: 24px; color: #FFB06B; }
.pos-totals-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pos-totals-balance {
  padding: 8px 16px; border-radius: 14px;
  background: rgba(63, 182, 168, 0.14); border: 1px solid rgba(63, 182, 168, 0.35);
}
.pos-totals-balance span { display: block; font-size: 12.5px; font-weight: 600; color: rgba(255, 255, 255, 0.65); }
.pos-totals-balance strong { font-family: var(--pos-display); font-size: 20px; color: #7FDACF; font-variant-numeric: tabular-nums; }
.pos-totals-balance.is-due { background: rgba(238, 122, 34, 0.14); border-color: rgba(238, 122, 34, 0.45); }
.pos-totals-balance.is-due strong { color: #FFB06B; }
.pos-totals-bar .is-due strong { color: #FFB06B; }
@media (max-width: 767px) {
  .pos-totals-bar { border-radius: 18px; padding: 14px 16px; }
  .pos-totals-actions, .pos-totals-actions .btn { width: 100%; }
}

/* ==================== MODALS ==================== */
.pos-modal.modal-content { border: 0; border-radius: 24px; overflow: hidden; box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5); }
.pos-modal .modal-header { background: var(--pos-ink); color: #fff; border: 0; padding: 18px 26px; box-shadow: inset 0 -3px 0 var(--pos-accent); }
.pos-modal .modal-title { font-family: var(--pos-display); font-weight: 700; font-size: 21px; }
.pos-modal .modal-header .close { color: #fff; opacity: 0.8; text-shadow: none; font-size: 28px; }
.pos-modal .modal-body { background: #F4F2EE; padding: 24px 26px; font-size: 15px; }
.pos-modal .modal-footer { background: #fff; border-top: 1px solid var(--pos-line); padding: 14px 26px; }
.pos-modal-foot { display: flex; width: 100%; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.pos-modal-foot-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.pos-foot-note { font-size: 14px; color: var(--pos-muted); font-weight: 600; }
.pos-modal-lead { color: var(--pos-muted); margin-bottom: 18px; max-width: 66ch; line-height: 1.55; font-size: 15px; }
.pos-label, .pos-modal .pos-label { font-size: 14px; font-weight: 700; color: var(--pos-ink); margin-bottom: 6px; display: block; }
.pos-panel { background: #fff; border: 1px solid var(--pos-line); border-radius: 18px; padding: 20px; }
.pos-readonly { background: var(--pos-mist); border: 1px solid var(--pos-line); border-radius: 10px; padding: 9px 12px; font-weight: 700; font-size: 15px; }
.pos-inline-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
.pos-confirm { margin-top: 10px; padding: 14px 16px; background: var(--pos-accent-soft); border: 1px solid #F5CFAE; border-radius: 14px; font-size: 15px; }

.pos-modal .form-control,
.pos-modal .custom-select {
  border-color: var(--pos-line); border-radius: 10px;
  font-size: 15px; min-height: 42px; color: var(--pos-ink);
}
.pos-modal .form-control-sm,
.pos-modal .custom-select-sm { min-height: 36px; font-size: 14px; border-radius: 9px; }
.pos-modal .form-control:focus,
.pos-modal .custom-select:focus { border-color: var(--pos-accent); box-shadow: 0 0 0 0.2rem rgba(238, 122, 34, 0.18); }
.pos-modal .input-group-text { background: var(--pos-mist); border-color: var(--pos-line); color: var(--pos-muted); font-weight: 700; }
.pos-modal .vue-simple-suggest .input-wrapper input { border-color: var(--pos-line); border-radius: 10px; min-height: 42px; font-size: 15px; }
.pos-modal .custom-control-label { font-size: 15px; }
.pos-modal .custom-control-input:checked ~ .custom-control-label::before { background-color: var(--pos-accent); border-color: var(--pos-accent); }
.pos-modal .custom-control-input:focus ~ .custom-control-label::before { box-shadow: 0 0 0 0.2rem rgba(238, 122, 34, 0.2); }

.pos-subtabs .pos-subtabs-nav { border-bottom: 1px solid var(--pos-line); gap: 4px; }
.pos-subtabs .pos-subtabs-nav .nav-link { border: 0; color: var(--pos-muted); font-weight: 700; font-size: 15px; padding: 11px 18px; border-bottom: 3px solid transparent; border-radius: 0; }
.pos-subtabs .pos-subtabs-nav .nav-link.active { color: var(--pos-ink); background: transparent; border-bottom-color: var(--pos-accent); }
.pos-subtabs .pos-tab-count { margin-left: 4px; }
.pos-pilltabs .nav-pills { gap: 6px; }
.pos-pilltabs .nav-pills .nav-link { color: var(--pos-ink); font-weight: 700; font-size: 14px; border-radius: 99px; padding: 7px 16px; background: #fff; border: 1px solid var(--pos-line); }
.pos-pilltabs .nav-pills .nav-link.active { background: var(--pos-ink); color: #fff; border-color: var(--pos-ink); }
.pos-search-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.pos-search-input { max-width: 380px; }
.pos-modal .product-cards-container { width: 100% !important; margin-left: 0 !important; padding: 12px 0 0; }
.pos-modal .product-cards-grid { padding: 0; }
.pos-cart-heading { font-family: var(--pos-display); font-size: 17px; font-weight: 700; margin-bottom: 10px; }

.pos-mini-table, .pos-doc-table { width: 100%; min-width: 0 !important; border-collapse: collapse; font-size: 15px; }
.pos-mini-table th, .pos-doc-table th { font-size: 13px; font-weight: 700; color: var(--pos-muted); padding: 10px 12px; border-bottom: 1.5px solid var(--pos-line); text-align: left; }
.pos-mini-table td, .pos-doc-table td { border: 0 !important; border-bottom: 1px solid var(--pos-mist) !important; padding: 12px; vertical-align: middle; background: transparent !important; text-align: left; }

.pos-policy { max-height: 300px; overflow-y: auto; }
.pos-policy .policy-content { max-height: none; padding: 0; }
.pos-agree .custom-control-label { font-weight: 500; line-height: 1.5; }
.pos-signature-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; border-top: 1px solid var(--pos-line); padding-top: 16px; margin-top: 8px; }

.pos-pay-summary { display: flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--pos-line); border-radius: 18px; margin-bottom: 14px; overflow: hidden; }
.pos-pay-summary > div { flex: 1 1 160px; padding: 14px 18px; border-right: 1px solid var(--pos-line); }
.pos-pay-summary > div:last-child { border-right: 0; }
.pos-pay-summary span { display: block; font-size: 13px; font-weight: 600; color: var(--pos-muted); }
.pos-pay-summary strong { font-family: var(--pos-display); font-size: 23px; font-variant-numeric: tabular-nums; }
.pos-pay-summary .is-due strong { color: var(--pos-danger); }
.pos-pay-summary-actions { display: flex; align-items: center; justify-content: flex-end; }
.pos-alert { border-radius: 14px; font-size: 14.5px; border: 0; background: #E7F0F7; color: #234A66; }
.pos-pay-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 14px; }
.pos-pay-card { background: #fff; border: 1px solid var(--pos-line); border-radius: 18px; padding: 16px; }
.pos-pay-card-head { display: flex; gap: 6px; align-items: center; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px dashed var(--pos-line); }
.pos-pay-add {
  border: 1.5px dashed #E8B48A; background: rgba(255, 241, 228, 0.5); border-radius: 18px;
  min-height: 130px; color: var(--pos-accent-dark); font-weight: 800; font-size: 15px; cursor: pointer;
}
.pos-pay-add2 {
  border: 1.5px dashed #E8B48A; background: rgba(255, 241, 228, 0.5); border-radius: 18px;
  min-height: 30px; margin-top: 10px; color: var(--pos-accent-dark); font-weight: 800; font-size: 15px; cursor: pointer;
}
.pos-pay-add:hover { border-color: var(--pos-accent); background: var(--pos-accent-soft); }

.pos-history-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; background: #fff; border: 1px solid var(--pos-line); border-radius: 14px; padding: 14px 16px; margin-bottom: 10px; }
.pos-history-row strong { font-size: 17px; font-variant-numeric: tabular-nums; }

.pos-id-upload { display: flex; gap: 18px; align-items: flex-start; flex-wrap: wrap; margin-top: 6px; }
.pos-id-preview img { width: 200px; height: 128px; object-fit: cover; border-radius: 14px; border: 1px solid var(--pos-line); }
.pos-id-placeholder {
  width: 200px; height: 128px; border: 1.5px dashed #E8B48A; border-radius: 14px;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  color: var(--pos-accent-dark); cursor: pointer; background: rgba(255, 241, 228, 0.5); font-size: 14px; font-weight: 700;
}

.pos-doc { background: #fff; border: 1px solid var(--pos-line); border-radius: 18px; padding: 30px 34px; }
.pos-doc-head { display: flex; justify-content: space-between; gap: 24px; flex-wrap: wrap; padding-bottom: 20px; border-bottom: 3px solid var(--pos-accent); }
.pos-doc-brand { font-family: var(--pos-display); font-size: 28px; font-weight: 800; letter-spacing: -0.01em; }
.pos-doc-sub { color: var(--pos-muted); font-size: 15px; }
.pos-doc-meta { display: grid; grid-template-columns: auto auto; gap: 5px 18px; margin: 0; font-size: 15px; }
.pos-doc-meta dt { color: var(--pos-muted); font-weight: 600; }
.pos-doc-meta dd { margin: 0; font-weight: 800; text-align: right; }
.pos-doc-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; padding: 20px 0; border-bottom: 1px solid var(--pos-line); font-size: 15px; line-height: 1.6; }
.pos-doc h4 { font-size: 14px; font-weight: 800; color: var(--pos-accent-dark); margin: 0 0 6px; }
.pos-doc-h { margin-top: 22px !important; }
.pos-doc-strong { font-weight: 800; color: var(--pos-ink); }
.pos-doc-bottom { display: grid; grid-template-columns: 1fr minmax(270px, 350px); gap: 28px; margin-top: 22px; }
.pos-doc-notes { display: flex; flex-direction: column; gap: 18px; font-size: 15px; }
.pos-doc-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; font-variant-numeric: tabular-nums; }
.pos-doc-totals { display: grid; grid-template-columns: 1fr auto; gap: 7px 16px; margin: 0; font-size: 15px; align-content: start; background: var(--pos-mist); border-radius: 16px; padding: 18px; }
.pos-doc-totals dt { color: var(--pos-muted); font-weight: 600; }
.pos-doc-totals dd { margin: 0; text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; }
.pos-doc-totals .is-grand { font-family: var(--pos-display); font-size: 19px; font-weight: 800; color: var(--pos-accent-dark); border-top: 1.5px dashed var(--pos-line); padding-top: 10px; }
.pos-doc-totals .is-due { color: var(--pos-danger); font-weight: 800; }

.pos-steps { list-style: none; display: flex; gap: 8px; padding: 0; margin: 0 0 18px; flex-wrap: wrap; }
.pos-steps li {
  display: flex; align-items: center; gap: 10px; flex: 1 1 190px;
  padding: 11px 14px; border-radius: 99px; background: #fff; border: 1px solid var(--pos-line);
  font-size: 15px; font-weight: 700; color: var(--pos-muted);
}
.pos-steps li span {
  width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; background: var(--pos-mist);
}
.pos-steps li.is-active { border-color: var(--pos-accent); color: var(--pos-ink); }
.pos-steps li.is-active span { background: var(--pos-accent); color: #fff; }
.pos-steps li.is-done { color: var(--pos-teal); background: var(--pos-teal-soft); border-color: #BFE1DD; }
.pos-steps li.is-done span { background: var(--pos-teal); color: #fff; }

.pos-credit-hero {
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px; flex-wrap: wrap;
  background: var(--pos-ink); color: #fff; border-radius: 18px; padding: 20px 24px; margin-bottom: 14px;
}
.pos-credit-hero span { font-size: 15px; opacity: 0.8; }
.pos-credit-hero strong { font-family: var(--pos-display); font-size: 34px; color: #7FDACF; font-variant-numeric: tabular-nums; }

.pos-choice-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 14px; }
.pos-choice {
  display: flex; flex-direction: column; align-items: flex-start; gap: 8px; text-align: left;
  background: #fff; border: 1.5px solid var(--pos-line); border-radius: 18px; padding: 18px 20px;
  color: var(--pos-ink); cursor: pointer;
}
.pos-choice .b-icon { color: var(--pos-accent); }
.pos-choice:hover { border-color: var(--pos-accent); }
.pos-choice:focus-visible { outline: 2px solid var(--pos-accent); outline-offset: 2px; }
.pos-choice strong { font-family: var(--pos-display); font-size: 17px; }
.pos-choice span { font-size: 14.5px; color: var(--pos-muted); line-height: 1.5; }
.pos-choice.is-selected { border-color: var(--pos-accent); background: var(--pos-accent-soft); }

.pos-settle-result { margin-top: 14px; border-radius: 14px; padding: 16px 18px; background: var(--pos-mist); }
.pos-settle-result > div { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.pos-settle-result span { font-weight: 700; }
.pos-settle-result strong { font-family: var(--pos-display); font-size: 26px; font-variant-numeric: tabular-nums; }
.pos-settle-result p { margin: 6px 0 0; font-size: 14.5px; color: var(--pos-muted); }
.pos-settle-result.is-refund { background: var(--pos-teal-soft); }
.pos-settle-result.is-refund strong { color: var(--pos-teal); }
.pos-settle-result.is-pay { background: var(--pos-danger-soft); }
.pos-settle-result.is-pay strong { color: var(--pos-danger); }

.pos-exchange-bar { display: flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--pos-line); border-radius: 18px; margin-bottom: 16px; overflow: hidden; }
.pos-exchange-bar > div { flex: 1 1 170px; padding: 12px 18px; border-right: 1px solid var(--pos-line); }
.pos-exchange-bar > div:last-child { border-right: 0; }
.pos-exchange-bar span { display: block; font-size: 13px; font-weight: 600; color: var(--pos-muted); }
.pos-exchange-bar strong { font-family: var(--pos-display); font-size: 21px; font-variant-numeric: tabular-nums; }
.pos-exchange-bar .is-refund strong { color: var(--pos-teal); }
.pos-exchange-bar .is-pay strong { color: var(--pos-danger); }

.pos-exchange-strip {
  display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  border-radius: 14px; padding: 14px 16px; margin-bottom: 14px; font-size: 15px;
  background: var(--pos-mist); border: 1px solid var(--pos-line); border-left: 5px solid var(--pos-ink);
}
.pos-exchange-strip.is-pending { border-left-color: var(--pos-warn); background: var(--pos-warn-soft); }
.pos-exchange-strip.is-refund { border-left-color: var(--pos-teal); background: var(--pos-teal-soft); }
.pos-exchange-strip.is-pay { border-left-color: var(--pos-danger); background: var(--pos-danger-soft); }
.is-good-text { color: var(--pos-teal); }

@media (max-width: 767px) {
  .pos-sheet { grid-template-columns: 1fr; }
  .pos-tile { padding: 18px; border-radius: 18px; }
  .pos-dl .pos-dl-sub { margin: 0 -18px; padding: 7px 18px; }
  .pos-line { grid-template-columns: 44px 1fr; }
  .pos-line-amt { grid-column: 2; text-align: left; }
  .pos-doc { padding: 20px; }
  .pos-doc-bottom { grid-template-columns: 1fr; }
  .pos-modal .modal-body { padding: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .pos-tab, .pos-progress-fill { transition: none; }
}

/* ==================== HISTORY GRID ==================== */
.pos-history-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
.pos-history-grid .pos-history-row {
  display: flex; flex-direction: column; align-items: stretch; gap: 7px;
  background: #fff; border: 1px solid var(--pos-line); border-top: 4px solid var(--pos-teal);
  border-radius: 14px; padding: 12px 13px; min-height: 104px; min-width: 0; overflow: hidden; margin-bottom: 0;
}
.pos-history-top { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; min-width: 0; }
.pos-history-method {
  font-size: 11px; font-weight: 800; letter-spacing: 0.02em;
  padding: 3px 8px; border-radius: 99px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 60%; flex-shrink: 1; color: var(--pos-teal); background: var(--pos-teal-soft);
}
.pos-history-amount { font-family: var(--pos-display); font-size: 15px; font-weight: 800; color: var(--pos-ink); font-variant-numeric: tabular-nums; white-space: nowrap; flex-shrink: 0; }
.pos-history-meta { font-size: 12.5px; color: var(--pos-muted); line-height: 1.35; min-height: 16px; overflow: hidden; }
.pos-history-meta span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pos-history-date {
  font-size: 12px; color: #958D82; margin-top: auto; padding-top: 6px; border-top: 1px dashed var(--pos-line);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums;
}
.pos-history-grid .pos-history-row.is-cash         { border-top-color: #0F6E6B; }
.pos-history-grid .pos-history-row.is-cc           { border-top-color: #34302B; }
.pos-history-grid .pos-history-row.is-finance      { border-top-color: #EE7A22; }
.pos-history-grid .pos-history-row.is-tax_exempt   { border-top-color: #7A4FC4; }
.pos-history-grid .pos-history-row.is-store_credit { border-top-color: #2B83B5; }
.pos-history-grid .pos-history-row.is-default      { border-top-color: #B7AFA4; }
.pos-history-grid .pos-history-row.is-cash .pos-history-method         { background: #E2F2F0; color: #0B5552; }
.pos-history-grid .pos-history-row.is-cc .pos-history-method           { background: #ECE8E3; color: #34302B; }
.pos-history-grid .pos-history-row.is-finance .pos-history-method      { background: #FFF1E4; color: #B5520B; }
.pos-history-grid .pos-history-row.is-tax_exempt .pos-history-method   { background: #EEE6F9; color: #5E37A3; }
.pos-history-grid .pos-history-row.is-store_credit .pos-history-method { background: #E2EFF7; color: #1F6690; }
.pos-history-grid .pos-history-row.is-default .pos-history-method      { background: #F1EEEA; color: #736C63; }
@media (max-width: 1300px) { .pos-history-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
@media (max-width: 1100px) { .pos-history-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (max-width: 767px)  { .pos-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

/* ==================== CHARGES MODAL ==================== */
.pos-charge-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.pos-charge-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.pos-charge-field .pos-label { margin-bottom: 0; font-size: 14px; color: var(--pos-ink); }
.pos-charge-field .input-group-text { font-size: 14px; }
.pos-charge-field .form-control { font-variant-numeric: tabular-nums; font-weight: 700; text-align: right; font-size: 16px; }
.pos-charge-layaway { display: flex; flex-direction: column; gap: 6px; padding-top: 16px; border-top: 1px dashed var(--pos-line); }
.pos-charge-layaway .pos-label { margin-bottom: 0; font-size: 14px; }
.pos-charge-hint { font-size: 13px; color: var(--pos-muted); line-height: 1.4; margin-top: 2px; }
.pos-breakdown dd:last-of-type { font-size: 17px; }
@media (max-width: 575px) { .pos-charge-grid { grid-template-columns: 1fr; } }

/* =====================================================================
   ICON ALIGNMENT HARDENING
   Something global in the app overrides `display` on these boxes, so
   flex centering is ignored. Icons are centered by absolute positioning
   and text (tab numbers, "FM") by line-height, which works whatever
   `display` value the box ends up with.
   ===================================================================== */
.pos-main-container .pos-brand-logo,
.pos-main-container .pos-meta-icon,
.pos-main-container .pos-banner-icon,
.pos-main-container .pos-tile-icon,
.pos-main-container .pos-tab-index,
.pos-modal .pos-tile-icon,
.pos-modal .pos-tab-index,
.pos-modal .pos-steps li span {
  position: relative !important;
  display: inline-block !important;
  flex: 0 0 auto !important;
  padding: 0 !important;
  margin: 0;
  text-align: center !important;
  vertical-align: middle !important;
  float: none !important;
  box-sizing: border-box !important;
  overflow: hidden;
}
.pos-main-container .pos-brand-logo {
  width: 58px !important; height: 58px !important; line-height: 58px !important;
  font-family: var(--pos-display); font-weight: 800; font-size: 22px !important; letter-spacing: -0.02em;
}
.pos-main-container .pos-meta-icon,
.pos-main-container .pos-banner-icon  { width: 38px !important; height: 38px !important; line-height: 38px !important; font-size: 17px !important; }
.pos-main-container .pos-tile-icon,
.pos-modal .pos-tile-icon             { width: 46px !important; height: 46px !important; line-height: 46px !important; font-size: 20px !important; }
.pos-main-container .pos-tab-index,
.pos-modal .pos-tab-index             { width: 28px !important; height: 28px !important; line-height: 28px !important; font-size: 13px !important; }
.pos-modal .pos-steps li span         { width: 26px !important; height: 26px !important; line-height: 26px !important; font-size: 13px !important; }

.pos-main-container .pos-brand-logo > svg,
.pos-main-container .pos-meta-icon > svg,
.pos-main-container .pos-banner-icon > svg,
.pos-main-container .pos-tile-icon > svg,
.pos-main-container .pos-tab-index > svg,
.pos-modal .pos-tile-icon > svg,
.pos-modal .pos-tab-index > svg {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  width: 1em !important;
  height: 1em !important;
  margin: 0 !important;
  transform: translate(-50%, -50%) !important;
  display: block !important;
}

.pos-main-container .pos-product-card-image,
.pos-main-container .pos-child-card-image,
.pos-modal .pos-cart-item-image,
.pos-main-container .pos-line-thumb,
.pos-modal .pos-line-thumb { position: relative !important; }
.pos-main-container .pos-product-card-image > svg,
.pos-main-container .pos-child-card-image > svg,
.pos-modal .pos-cart-item-image > svg,
.pos-main-container .pos-line-thumb > svg,
.pos-modal .pos-line-thumb > svg {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

.pos-main-container svg.b-icon,
.pos-modal svg.b-icon {
  width: 1em !important;
  height: 1em !important;
  vertical-align: -0.125em !important;
  float: none !important;
  overflow: visible;
}

/* =====================================================================
   RESPONSIVE — phones, iPads, Samsung Galaxy Tab E (SM-T560NU)
   Breakpoints (CSS px):
     ≥1200   desktop, Galaxy Tab E landscape (1280), iPad Pro landscape
     992–1199 iPad landscape (1024–1180), iPad Pro portrait (1024)
     768–991  iPad portrait (768–834), Galaxy Tab E portrait (800)
     576–767  large phones landscape, small tablets
     <576     phones portrait
     <380     small phones (iPhone SE, Galaxy A-series compact)
   ===================================================================== */

/* ---------- Global safety: nothing may push the page sideways ---------- */
.pos-wrapper { overflow-x: hidden; }
.pos-main-container, .pos-modal { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
.pos-main-container img, .pos-modal img { max-width: 100%; }
.pos-modal .modal-body { overflow-x: hidden; }
.pos-modal .table-responsive,
.pos-modal [style*="overflow-x: auto"] { -webkit-overflow-scrolling: touch; }

/* Grids that used fixed minimums now shrink on narrow screens */
.pos-pay-grid      { grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr)); }
.pos-choice-grid   { grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr)); }
.pos-activity      { grid-template-columns: repeat(auto-fill, minmax(min(180px, 100%), 1fr)); }
.pos-doc-grid      { grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr)); }

/* Tabs scroll smoothly with a finger and snap to each pill */
.pos-tabs {
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;
}
.pos-tab { scroll-snap-align: start; }

/* Sticky totals bar respects the iPhone home indicator */
.pos-totals-bar { bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }

/* ---------- Touch devices (phones + tablets): bigger targets ---------- */
@media (pointer: coarse) {
  .pos-main-container .btn,
  .pos-modal .btn:not(.close) { min-height: 44px; }
  .pos-main-container .btn-sm,
  .pos-modal .btn-sm { min-height: 40px; }
  .pos-tab { min-height: 46px; }
  .pos-modal .form-control,
  .pos-modal .custom-select { min-height: 44px; }
  .pos-modal .form-control-sm,
  .pos-modal .custom-select-sm { min-height: 40px; }
  .pos-modal .custom-control-label::before,
  .pos-modal .custom-control-label::after { width: 1.25rem; height: 1.25rem; top: 0.1rem; }
  .pos-modal .custom-control-label { padding-left: 0.35rem; }
  .pos-modal .modal-header .close { min-width: 44px; min-height: 44px; }
  .pos-link.btn { min-height: 40px; display: inline-flex; align-items: center; }
  .pos-tabs { scrollbar-width: none; }
  .pos-tabs::-webkit-scrollbar { display: none; }
  /* No hover-lift jitter on touch */
  .pos-cart-item:hover { box-shadow: none; }
  .product-card-image .image-wrapper:hover img { transform: none; }
}

/* ---------- Modals: tablet widths ---------- */
@media (min-width: 576px) and (max-width: 1199.98px) {
  .pos-dialog.modal-lg,
  .pos-dialog.modal-xl { max-width: 94vw; }
  .pos-dialog.modal-md,
  .pos-dialog:not(.modal-lg):not(.modal-xl):not(.modal-sm) { max-width: min(620px, 94vw); }
}

/* ---------- ≤1199: iPad landscape / iPad Pro portrait ---------- */
@media (max-width: 1199.98px) {
  .pos-main-container { width: 97%; }
  .pos-head-bar { align-items: flex-start; }
  .pos-head-meta { width: 100%; }
  .pos-meta-block { flex: 1 1 220px; }
  .pos-meta-block--wide { max-width: none; flex-basis: 300px; }

  .pos-product-children-wrap { padding: 14px 24px 16px; }
  .pos-history-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }

  .pos-doc { padding: 24px; }
  .pos-doc-bottom { grid-template-columns: 1fr minmax(240px, 320px); gap: 20px; }
}

/* ---------- ≤991: iPad portrait, Galaxy Tab E portrait (800px) ---------- */
@media (max-width: 991.98px) {
  .pos-head { padding: 20px; }
  .pos-brand-name { font-size: 26px; }
  .pos-head-actions { justify-content: flex-start; }
  .pos-blockers { text-align: left; }

  .pos-tile { padding: 20px; }
  .pos-dl .pos-dl-sub { margin: 0 -20px; padding: 7px 20px; }

  /* Sheet product cards: image + info side by side, meta + pricing below */
  .pos-product-card,
  .pos-product-card-main { grid-template-columns: 160px minmax(0, 1fr); }
  .pos-product-card-meta { grid-column: 1 / -1; flex-direction: row; flex-wrap: wrap; gap: 12px 24px; }
  .pos-product-card-meta-row { flex: 1 1 160px; }
  .pos-product-card-pricing { grid-column: 1 / -1; }
  .pos-product-card-image { min-height: 140px; }

  .pos-child-card { grid-template-columns: 110px minmax(0, 1fr); }
  .pos-child-card-meta,
  .pos-child-card-pricing { grid-column: 1 / -1; }

  .pos-history-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }

  .pos-doc-bottom { grid-template-columns: 1fr; }

  /* Totals bar: stats on one line, actions on the next */
  .pos-totals-bar { padding: 12px 14px; gap: 10px; }
  .pos-totals-stats { width: 100%; justify-content: space-between; gap: 6px 18px; }
  .pos-totals-actions { width: 100%; justify-content: flex-end; }

  /* Search result cards */
  .product-card-info .product-name { font-size: 18px; }

  /* Merchandise due receive cell */
  .pos-md-receive-cell { min-width: 280px; }
  .pos-md-locsub { min-width: 0; width: 100%; }
}

/* ---------- ≤767: large phones / small tablets ---------- */
@media (max-width: 767.98px) {
  .pos-main-container { width: 100%; }
  .pos-head { padding: 16px; border-radius: 18px; margin-bottom: 12px; }
  .pos-head-bar { gap: 14px; }
  .pos-brand-mark { gap: 12px; }
  .pos-main-container .pos-brand-logo {
    width: 46px !important; height: 46px !important; line-height: 46px !important; font-size: 18px !important;
  }
  .pos-brand-name { font-size: 22px; }
  .pos-brand-sub { font-size: 13px; }
  .pos-meta-block { padding: 10px 12px; }
  .pos-brand-invoice { font-size: 17px; }

  /* Header buttons: tidy 2-column grid */
  .pos-head-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .pos-head-actions .btn { width: 100%; white-space: normal; }
  .pos-head-actions .pos-blockers { grid-column: 1 / -1; }

  .pos-tabs { margin-bottom: 14px; padding: 8px; }
  .pos-tab { font-size: 14px; padding: 7px 14px 7px 7px; }

  .pos-store-warning, .pos-merch-due-banner { padding: 12px 14px; font-size: 14px; }

  .pos-tile-head { flex-wrap: wrap; margin-bottom: 14px; }
  .pos-tile-head h3 { font-size: 18px; }
  .pos-muted-inline { margin-left: 0; margin-top: 4px; }

  .pos-dl { font-size: 15px; gap: 8px 12px; }
  .pos-dl dd.pos-dl-grand { font-size: 21px; }

  .pos-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }

  .pos-product-card,
  .pos-product-card-main { grid-template-columns: 1fr; }
  .pos-product-card-image { min-height: 180px; }
  .pos-product-card-title { font-size: 17px; }
  .pos-product-card-price-row--total .pos-product-card-price-value { font-size: 20px; }
  .pos-product-children-wrap { padding: 12px 12px 14px; }

  /* Modals: inputs at 16px so iOS Safari doesn't zoom in on focus */
  .pos-modal .form-control,
  .pos-modal .custom-select,
  .pos-modal .vue-simple-suggest .input-wrapper input,
  .pos-modal input,
  .pos-modal select,
  .pos-modal textarea { font-size: 16px !important; }

  .pos-modal .modal-header { padding: 14px 16px; }
  .pos-modal .modal-title { font-size: 18px; }
  .pos-modal .modal-footer { padding: 10px 16px; }
  .pos-modal-foot { flex-direction: column-reverse; align-items: stretch; }
  .pos-modal-foot > .btn,
  .pos-modal-foot-actions { width: 100%; }
  .pos-modal-foot-actions { justify-content: stretch; }
  .pos-modal-foot-actions .btn { flex: 1 1 0; }
  .pos-modal-foot-actions .pos-foot-note { flex-basis: 100%; text-align: center; }
  .pos-modal-foot .pos-link.btn { align-self: center; }

  .pos-subtabs .pos-subtabs-nav { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .pos-subtabs .pos-subtabs-nav .nav-link { padding: 10px 14px; white-space: nowrap; }
  .pos-pilltabs .nav-pills { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; -webkit-overflow-scrolling: touch; }
  .pos-pilltabs .nav-pills .nav-link { white-space: nowrap; }

  .pos-search-row { flex-direction: column; align-items: stretch; }
  .pos-search-input { max-width: none; }
  .pos-search-row .btn { width: 100%; }

  .pos-pay-summary > div { flex: 1 1 45%; padding: 10px 14px; border-bottom: 1px solid var(--pos-line); }
  .pos-pay-summary strong { font-size: 19px; }
  .pos-exchange-bar > div { flex: 1 1 100%; border-right: 0; border-bottom: 1px solid var(--pos-line); }
  .pos-credit-hero strong { font-size: 28px; }
  .pos-steps li { flex: 1 1 100%; }

  .pos-cart-footer { bottom: -16px; margin: 4px 0 0; }
  .pos-cart-bar-total { font-size: 19px; }

  .pos-id-preview img, .pos-id-placeholder { width: 100%; max-width: 280px; }

  .pos-signature-row .btn { width: 100%; }
}

/* ---------- <576: phones — modals become full-screen sheets ---------- */
@media (max-width: 575.98px) {
  .pos-dialog.modal-dialog {
    margin: 0;
    max-width: 100%;
    width: 100%;
    min-height: 100%;
    height: 100%;
    align-items: stretch;
  }
  .pos-dialog .modal-content.pos-modal {
    border-radius: 0;
    min-height: 100%;
    max-height: 100%;
  }
  .pos-dialog.modal-dialog-scrollable { max-height: 100%; height: 100%; }
  .pos-dialog .modal-footer { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); }
  .pos-modal .modal-body { padding: 14px 12px; }
  .pos-panel { padding: 14px; border-radius: 14px; }

  .pos-head-meta { flex-direction: column; }
  .pos-meta-block, .pos-meta-block--wide { flex: 1 1 auto; width: 100%; }
  .pos-progress-wrap { flex-direction: column; align-items: stretch; gap: 6px; }
  .pos-progress-label { text-align: right; }

  .pos-store-warning, .pos-merch-due-banner { flex-wrap: wrap; }
  .pos-store-warning .btn, .pos-merch-due-banner .btn { width: 100%; margin-left: 0; }

  .pos-tile { padding: 16px; border-radius: 16px; }
  .pos-dl .pos-dl-sub { margin: 0 -16px; padding: 7px 16px; }
  .pos-sheet, .pos-sheet-row { gap: 12px; }

  .pos-customer-field { padding: 10px 12px; }
  .pos-customer-value { font-size: 15px; }

  .pos-product-card-meta { flex-direction: column; }

  .pos-child-list { margin-left: 4px; padding-left: 12px; }
  .pos-child-card { grid-template-columns: 1fr; }
  .pos-child-card::before { left: -14px; width: 12px; }
  .pos-child-card::after { left: -19px; }
  .pos-child-card-image { min-height: 140px; }

  .pos-history-grid { grid-template-columns: 1fr; }
  .pos-history-grid .pos-history-row { min-height: 0; }

  /* Totals bar: compact; only grand total + balance + main action */
  .pos-totals-bar { bottom: calc(6px + env(safe-area-inset-bottom, 0px)); border-radius: 16px; padding: 10px 12px; }
  .pos-totals-stats { gap: 4px 14px; }
  .pos-totals-item:not(.is-grand):not(.pos-totals-item--items) { display: none !important; }
  .pos-totals-item.is-grand strong { font-size: 20px; }
  .pos-totals-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .pos-totals-actions .pos-totals-balance { grid-column: 1 / -1; display: flex; justify-content: space-between; align-items: baseline; }
  .pos-totals-actions .pos-btn-lg { grid-column: 1 / -1; }
  .pos-totals-actions .btn { width: 100%; }

  /* Cart items */
  .pos-cart-item { padding: 10px; gap: 10px; border-left-width: 4px; }
  .pos-cart-item-image { height: 140px; }
  .pos-cart-item-pricing { flex-direction: column; align-items: stretch; }
  .pos-cart-price-row { flex: 1 1 auto; }
  .pos-cart-price-input, .pos-cart-qty-input, .pos-cart-discount-input { width: 120px !important; }
  .pos-cart-item-actions { flex-direction: row; }
  .pos-cart-item-actions .btn { flex: 1 1 0; }
  .pos-cart-section-bulk { flex-wrap: wrap; width: 100%; }
  .pos-cart-section-bulk .mx-datepicker { flex: 1 1 100%; width: 100% !important; }
  .pos-cart-footer-stats { gap: 12px; width: 100%; justify-content: space-between; }
  .pos-cart-footer-actions { width: 100%; }
  .pos-cart-balance { flex: 1 1 100%; flex-direction: row; justify-content: space-between; align-items: baseline; }

  /* Search result cards */
  .product-card-image { height: 180px; }
  .product-card-image .image-wrapper { min-height: 180px; }
  .product-card-info .product-name { font-size: 17px; }

  /* Payments */
  .pos-pay-summary > div { flex: 1 1 100%; border-right: 0; }
  .pos-pay-card { padding: 14px; }

  /* Invoice preview */
  .pos-doc { padding: 16px; border-radius: 14px; }
  .pos-doc-head { flex-direction: column; gap: 12px; }
  .pos-doc-meta dd { text-align: left; }
  .pos-doc-brand { font-size: 22px; }

  /* Refund OTP */
  .refund-otp-email-row { flex-wrap: wrap; }
  .refund-otp-email-row .btn { width: 100%; margin-left: 0 !important; }
  .refund-otp-input { font-size: 22px !important; letter-spacing: 0.3em; }

  /* Merchandise due */
  .pos-md-receive-cell { min-width: 240px; }
  .pos-md-qty-row { flex-wrap: wrap; }

  /* Charges */
  .pos-charge-grid { grid-template-columns: 1fr; }
}

/* ---------- <380: small phones ---------- */
@media (max-width: 379.98px) {
  .pos-main-container { padding: 0 6px 24px; }
  .pos-head { padding: 14px; }
  .pos-brand-name { font-size: 19px; }
  .pos-head-actions { grid-template-columns: 1fr; }
  .pos-tab { font-size: 13px; }
  .pos-customer-row--identity,
  .pos-customer-row--address { grid-template-columns: 1fr; }
  .pos-totals-actions { grid-template-columns: 1fr; }
}

/* ---------- Phones in landscape (short screens) ---------- */
@media (max-height: 500px) and (orientation: landscape) {
  .pos-tabs { position: static; }
  .pos-totals-bar { position: static; margin-top: 16px; }
  .pos-cart-bar, .pos-cart-footer { position: static; }
  .pos-dialog.modal-dialog { margin: 0 auto; max-width: 100%; }
  .pos-dialog .modal-content.pos-modal { border-radius: 0; }
}

/* ---------- Galaxy Tab E / iPad landscape: keep tabs + totals compact ---------- */
@media (min-width: 992px) and (max-width: 1366px) and (max-height: 900px) and (orientation: landscape) {
  .pos-head { padding: 18px 22px 14px; }
  .pos-progress-wrap { margin-top: 12px; }
  .pos-head-actions { margin-top: 12px; padding-top: 12px; }
  .pos-tabs { margin-bottom: 14px; }
}

/* ---------- Print (View invoice → Print) ---------- */
@media print {
  .pos-tabs, .pos-totals-bar, .pos-head-actions { display: none !important; }
}

</style>


// WEBPACK FOOTER //
// src/components/Sales/NewSale.vue